import { sql, desc, relations, type Table } from "drizzle-orm";
export { bankFeedAccounts, bankFeedSessions } from "./bank-feeds-schema";
export { marketPullRuns, marketPullResults, marketPullNotifications } from "./market-auto-pull-schema";
import { pgTable, text, varchar, integer, timestamp, serial, boolean, numeric, jsonb, json, doublePrecision, index, uniqueIndex, foreignKey, check, date, primaryKey, bigint, bigserial, type AnyPgColumn, customType } from "drizzle-orm/pg-core";

// ─── pgvector custom type ─────────────────────────────────────────────────────
// Used by the knowledge_chunks table (Composer KB retrieval foundation).
// Embedding dimension matches text-embedding-004 (768). The driver format is
// Postgres vector literal "[v1,v2,...]"; fromDriver parses it back to number[].
const vector768 = customType<{ data: number[]; driverData: string }>({
  dataType() { return "vector(768)"; },
  toDriver(value: number[]): string { return `[${value.join(",")}]`; },
  fromDriver(value: string): number[] {
    return String(value).replace(/[\[\]]/g, "").split(",").map(Number);
  },
});
import { createInsertSchema as _createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { messageHasBookingRequestLinkToken } from "./booking-request-link-token";
import {
  BOOKING_FORM_FOLLOWUP_TRIGGER,
  BOOKING_FORM_FOLLOWUP_LINK_REQUIRED_ERROR,
} from "./booking-form-followup";

// ─────────────────────────────────────────────────────────────────────────────
// Identifier-length convention (READ BEFORE ADDING A NEW TABLE OR FK)
//
// Postgres silently truncates any identifier to 63 chars. Drizzle auto-names an
// inline `.references()` FK `<table>_<column>_<foreignTable>_<foreignColumn>_fk`,
// so a long table/column pair can generate a 64+ char name that Postgres then
// truncates — after which drizzle thinks the constraint is missing on EVERY
// `db:push` and the schema sync churns forever. The same applies to long
// index / unique / check / primary-key names.
//
// Rule: if a generated name would exceed 63 chars, declare it explicitly with a
// short name, e.g.
//   foreignKey({ name: "sub_status_events_admin_user_id_fk", columns: [...], foreignColumns: [...] })
//
// This is enforced automatically (no manual vigilance required) by
// `scripts/check-fk-names.ts`, which loads this schema and fails when any
// identifier is > 63 chars. That guard runs in three places:
//   1. `npm run db:push` / `node scripts/db-sync.mjs` — before any DB write.
//   2. The `schema-hygiene` validation workflow (the dev validation cycle).
//   3. `scripts/post-merge.sh` — before a merge reaches the shared app.
// (Task #2659)
// ─────────────────────────────────────────────────────────────────────────────

// Wrapper around drizzle-zod's createInsertSchema that re-types the result
// using the table's $inferInsert. drizzle-zod 0.7 with the current
// drizzle-orm/zod versions produces a ZodObject whose shape's `keyof` resolves
// to `never`, which floods the editor with ~228 spurious "boolean is not
// assignable to never" errors on every chained `.pick({...})` / `.omit({...})`.
// Re-typing here preserves runtime behavior and the generated Zod schemas
// while restoring usable types for `.pick`, `.omit`, and `.extend`.
const createInsertSchema = <T extends Table>(table: T) => {
  type Insert = T["$inferInsert"];
  type Shape = { [K in keyof Insert]-?: z.ZodType<Insert[K]> };
  return _createInsertSchema(table) as unknown as z.ZodObject<
    Shape,
    "strip",
    z.ZodTypeAny,
    Insert,
    Insert
  >;
};

// Optional date schema that:
//   - accepts ISO strings, `Date`, `null`, or `undefined`
//   - normalizes empty strings to `null` (legacy clients send "" for "no date")
//   - coerces strings to `Date` so the value is shaped for Drizzle's
//     `timestamp()` insert type, which wants `Date | null | undefined`
// Used for nullable timestamp columns where `z.string()` was previously
// loose enough to accept empty strings (Task #1215 cleanup).
const optionalCoercedDate = z.preprocess(
  (v) => (v === "" || v === undefined ? null : v),
  z.coerce.date().nullable(),
).optional();

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
});

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export const leads = pgTable("leads", {
  id: serial("id").primaryKey(),
  fullName: text("full_name").notNull(),
  email: text("email").notNull(),
  company: text("company"),
  numberOfHouses: integer("number_of_houses"),
  totalRooms: integer("total_rooms"),
  journeyStage: text("journey_stage"),
  selectedPlan: text("selected_plan").default("Host").notNull(),
  howDidYouHearAboutUs: text("how_did_you_hear_about_us"),
  interestedInManagement: boolean("interested_in_management"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  invitedAt: timestamp("invited_at"),
  archivedAt: timestamp("archived_at"),
});

export const LEAD_JOURNEY_STAGES = [
  "searching",
  "under_contract",
  "renovating",
  "operating_self_managed",
  "operating_with_pm",
] as const;
export type LeadJourneyStage = typeof LEAD_JOURNEY_STAGES[number];

export const LEAD_JOURNEY_STAGE_LABELS: Record<LeadJourneyStage, string> = {
  searching: "I don't have a CoLiving home yet and am still searching.",
  under_contract: "I have my first CoLiving home under contract.",
  renovating: "I have my first CoLiving home under renovation or furnishing right now.",
  operating_self_managed: "I have a house in operation with members and I manage myself.",
  operating_with_pm: "I have a house in operation with members and I have a property manager.",
};

export const insertLeadSchema = createInsertSchema(leads).omit({
  id: true,
  createdAt: true,
  invitedAt: true,
}).extend({
  fullName: z.string().min(0),
  email: z.string().email("Please enter a valid email address"),
  company: z.string().optional().or(z.literal("")),
  numberOfHouses: z.coerce.number().optional().nullable(),
  totalRooms: z.coerce.number().optional().nullable(),
  journeyStage: z.enum(LEAD_JOURNEY_STAGES, {
    errorMap: () => ({ message: "Please select where you are in your CoLiving journey" }),
  }),
  // Legacy tier names kept so old lead rows/clients stay valid; new waitlist
  // submissions send the modular account kinds (Operator / PropertyManager).
  selectedPlan: z.enum(["Research", "Host", "Growth", "Professional", "Enterprise", "Interest", "Operator", "PropertyManager"]),
  howDidYouHearAboutUs: z.string().max(500, "Please keep this under 500 characters").optional().nullable().or(z.literal("")),
  interestedInManagement: z.boolean().nullable().optional(),
});

export type InsertLead = z.infer<typeof insertLeadSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Conference leads (Task #3072) — co-living conference signup flow. Each row is
// captured the moment a visitor submits Step 1 of /coliving-conference/signup,
// before OTP verification or account creation. `joinOrder` is a dedicated serial
// so the admin "Conference Leads" view can show submission order ("#1 to sign
// up") independent of the primary key. `userId` is backfilled once the lead
// completes account creation (verify-signup), letting the admin view show which
// leads converted.
export const CONFERENCE_STAGES = [
  "researching",
  "renovating",
  "live",
  "scaling",
] as const;
export type ConferenceStage = typeof CONFERENCE_STAGES[number];

export const CONFERENCE_STAGE_LABELS: Record<ConferenceStage, string> = {
  researching: "Researching",
  renovating: "Renovating",
  live: "Properties Live",
  scaling: "Scaling",
};

export const conferenceLeads = pgTable("conference_leads", {
  id: serial("id").primaryKey(),
  joinOrder: serial("join_order").notNull().unique(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  padsplitUser: boolean("padsplit_user").default(false).notNull(),
  colivingStage: text("coliving_stage").notNull(),
  roomCount: integer("room_count"),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertConferenceLeadSchema = createInsertSchema(conferenceLeads).omit({
  id: true,
  joinOrder: true,
  userId: true,
  createdAt: true,
}).extend({
  name: z.string().min(1, "Please enter your name").max(120, "Name is too long"),
  email: z.string().email("Please enter a valid email address").max(254, "Email is too long"),
  padsplitUser: z.boolean(),
  colivingStage: z.enum(CONFERENCE_STAGES, {
    errorMap: () => ({ message: "Please select where you are at" }),
  }),
  roomCount: z.coerce.number().int().min(0).max(100000).optional().nullable(),
});

export type InsertConferenceLead = z.infer<typeof insertConferenceLeadSchema>;
export type ConferenceLead = typeof conferenceLeads.$inferSelect;
export type Lead = typeof leads.$inferSelect;

export const featureRequests = pgTable("feature_requests", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  description: text("description").notNull(),
  roomsManaged: integer("rooms_managed"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertFeatureRequestSchema = createInsertSchema(featureRequests).omit({
  id: true,
  createdAt: true,
}).extend({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Please enter a valid email"),
  description: z.string().min(10, "Please provide more detail"),
  roomsManaged: z.coerce.number().min(1, "Must have at least 1 room").optional(),
});

export type InsertFeatureRequest = z.infer<typeof insertFeatureRequestSchema>;
export type FeatureRequest = typeof featureRequests.$inferSelect;

// Contact Us inquiries — submitted from the public pricing page Enterprise/PM Pro columns.
export const contactUsInquiries = pgTable("contact_us_inquiries", {
  id: serial("id").primaryKey(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  companyName: text("company_name"),
  email: text("email").notNull(),
  phone: text("phone"),
  roomsUnderManagement: integer("rooms_under_management"),
  numberOfOwners: integer("number_of_owners"),
  plan: text("plan").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertContactUsInquirySchema = createInsertSchema(contactUsInquiries).omit({
  id: true,
  createdAt: true,
}).extend({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().optional().or(z.literal("")),
  companyName: z.string().optional().or(z.literal("")),
  roomsUnderManagement: z.coerce.number().int().min(1, "Must be at least 1").optional().nullable(),
  numberOfOwners: z.coerce.number().int().min(0, "Must be 0 or more").optional().nullable(),
  plan: z.enum(["Enterprise", "PMPro"]),
});

export type InsertContactUsInquiry = z.infer<typeof insertContactUsInquirySchema>;
export type ContactUsInquiry = typeof contactUsInquiries.$inferSelect;

export const blogPosts = pgTable("blog_posts", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  slug: text("slug").unique(),
  excerpt: text("excerpt"),
  metaDescription: text("meta_description"),
  content: text("content").notNull(),
  imageUrl: text("image_url"),
  tags: text("tags").array(),
  outrankeId: text("outranke_id").unique(),
  // v3 build B15: Markdown import + in-repo daily automation.
  featured: boolean("featured").notNull().default(false),
  category: text("category"),
  author: text("author"),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  // 'import' | 'automation' | 'outrank' | 'admin' — where the post came from.
  source: text("source"),
  // The content/blog-topics.json topic the automation wrote this post for.
  automationTopic: text("automation_topic"),
  // Core page the post links to at the end: 'messages' | 'maintenance' |
  // 'locks' | 'leasing' | 'numbers' (frontend F17), or null.
  corePage: text("core_page"),
  updatedAt: timestamp("updated_at", { withTimezone: true }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertBlogPostSchema = createInsertSchema(blogPosts).omit({
  id: true,
}).extend({
  title: z.string().min(1, "Title is required"),
  content: z.string().min(1, "Content is required"),
  slug: z.string().optional(),
  excerpt: z.string().optional(),
  metaDescription: z.string().optional(),
  imageUrl: z.string().optional(),
  tags: z.array(z.string()).optional(),
  outrankeId: z.string().optional(),
  createdAt: z.coerce.date().optional(),
});

export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;
export type BlogPost = typeof blogPosts.$inferSelect;


export const ACCOUNT_TYPES = [
  'Research',
  'Host',
  'Growth',
  'Professional',
  'Enterprise',
  'PMPro',
  'Interest',
  'Founder25',
  'Founder50',
  'Founder100',
] as const;

export type AccountType = typeof ACCOUNT_TYPES[number];

// Display names for subscription tiers. The internal AccountType value is kept
// stable (e.g. 'Enterprise') to avoid migrating persisted selected_plan rows;
// every user-facing label routes through this map. 'Enterprise' is presented as
// "Property Manager" and the new top tier 'PMPro' as "PM Pro".
export const PLAN_DISPLAY_NAMES: Record<AccountType, string> = {
  Research: 'Research',
  Host: 'Host',
  Growth: 'Growth',
  Professional: 'Professional',
  Enterprise: 'Property Manager',
  PMPro: 'PM Pro',
  Interest: 'Interest',
  Founder25: 'Founder 25',
  Founder50: 'Founder 50',
  Founder100: 'Founder 100',
};

export function planDisplayName(plan: string | null | undefined): string {
  if (plan && Object.prototype.hasOwnProperty.call(PLAN_DISPLAY_NAMES, plan)) {
    return PLAN_DISPLAY_NAMES[plan as AccountType];
  }
  return plan ?? '';
}

export const FOUNDER_TIERS: AccountType[] = ['Founder25', 'Founder50', 'Founder100'];
export type FounderTier = 'Founder25' | 'Founder50' | 'Founder100';

export const FOUNDER_TIER_FREE_ROOMS: Record<FounderTier, number> = {
  Founder25: 25,
  Founder50: 50,
  Founder100: 100,
};

export const FOUNDER_TIER_PRICE_USD: Record<FounderTier, number> = {
  Founder25: 3000,
  Founder50: 6000,
  Founder100: 12000,
};

// Total Founders Circle spots available per tier. The scarcity counter shown on
// /founders-circle is computed live as capacity − actual bookings (founder_intents
// rows for that tier), so "Only X available" reflects real demand, not a hardcoded
// number. Smaller (cheaper) tiers carry more spots than the premium 100-room tier.
export const FOUNDER_TIER_CAPACITY: Record<FounderTier, number> = {
  Founder25: 10,
  Founder50: 50,
  Founder100: 10,
};

export const FOUNDER_OVERAGE_DISCOUNT = 0.20;

export const FOUNDER_STATUSES = ['active', 'under_review', 'revoked'] as const;
export type FounderStatus = typeof FOUNDER_STATUSES[number];

export function isFounderTier(accountType: AccountType): boolean {
  return FOUNDER_TIERS.includes(accountType);
}

export const FREE_TIERS: AccountType[] = ['Research', 'Host'];
export const PAID_TIERS: AccountType[] = ['Growth', 'Professional', 'Enterprise', 'PMPro', ...FOUNDER_TIERS];
export const WAITLIST_TIERS: AccountType[] = ['Interest'];
export const ALL_LIVE_TIERS: AccountType[] = [...FREE_TIERS, ...PAID_TIERS];

/**
 * Identifies the per-room paid tiers (Growth and Professional). These plans
 * are billed per active room with no room minimum.
 */
export function isGrowthOrProfessionalTier(accountType: AccountType): boolean {
  return accountType === 'Growth' || accountType === 'Professional';
}

/**
 * Returns the number of rooms that should actually be billed for the given
 * plan. Billing is per active room for every plan with no minimum floor, so
 * this just sanitizes the count (no negatives, whole rooms only). The `plan`
 * argument is retained for call-site compatibility.
 */
export function chargeableRoomsForPlan(_plan: AccountType, actualRooms: number): number {
  return Math.max(0, Math.floor(actualRooms));
}

export function isPaidTier(accountType: AccountType): boolean {
  return PAID_TIERS.includes(accountType);
}

export function isLiveAccount(accountType: AccountType): boolean {
  return ALL_LIVE_TIERS.includes(accountType);
}

const TIER_ORDER: AccountType[] = [
  'Research', 'Host', 'Growth', 'Professional', 'Enterprise', 'PMPro'
];

export function hasFeatureAccess(
  accountType: AccountType,
  requiredTier: AccountType
): boolean {
  // Founder tiers inherit Professional-level feature access.
  const effectiveAccount: AccountType = isFounderTier(accountType) ? 'Professional' : accountType;
  const effectiveRequired: AccountType = isFounderTier(requiredTier) ? 'Professional' : requiredTier;
  const userIdx = TIER_ORDER.indexOf(effectiveAccount);
  const reqIdx  = TIER_ORDER.indexOf(effectiveRequired);
  if (userIdx === -1 || reqIdx === -1) return false;
  return userIdx >= reqIdx;
}

export const INTERNAL_DOMAINS = [
  'swdincorp.com',
  'padpulse.io',
  'levelupliving.co',
  'colivingoperations.com',
] as const;

export function isInternalDomain(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase().trim();
  return !!domain && (INTERNAL_DOMAINS as readonly string[]).includes(domain);
}

export const USER_STATUSES = ['active', 'waitlist', 'suspended', 'invited', 'archived'] as const;
export type UserStatus = typeof USER_STATUSES[number];

export const MONTHLY_PRICE_PER_THERMOSTAT = 2.5;

export const MONTHLY_PRICES_PER_ROOM: Record<AccountType, number | null> = {
  Research: 0,
  Host: 0,
  Growth: 12,
  Professional: 18,
  Enterprise: null,
  PMPro: null,
  Interest: 0,
  Founder25: null,
  Founder50: null,
  Founder100: null,
};

export const ANNUAL_PRICES_PER_ROOM_PER_MONTH: Record<AccountType, number | null> = {
  Research: 0,
  Host: 0,
  Growth: 10,
  Professional: 15,
  Enterprise: null,
  PMPro: null,
  Interest: 0,
  Founder25: null,
  Founder50: null,
  Founder100: null,
};

export const HOST_MAX_ACTIVE_PROPERTIES = 1;
export const HOST_MAX_ROOMS_PER_PROPERTY = 10;

/**
 * Flat (non-per-room) monthly subscription prices in USD for the entry tiers.
 *
 * Research ($29) and Host ($49) are the first FLAT-priced subscriptions: they
 * bill a fixed amount at quantity 1, NOT per-room (unlike Growth/Professional).
 *
 * The plan NAME is shared between the free and paid variants — a "free Research"
 * account is simply a `Research` plan with no active paid subscription. Whether
 * a flat-tier account is treated as paid (e.g. for search entitlements) is
 * determined by its `subscriptionStatus`, never by the plan name alone. This is
 * why Research/Host are deliberately NOT added to `PAID_TIERS`/`isPaidTier`
 * (doing so would gate free Research behind a subscription via `requireTier`).
 */
export const FLAT_PLAN_MONTHLY_PRICE_USD: Partial<Record<AccountType, number>> = {
  Research: 29,
  Host: 49,
};

export const FLAT_PRICED_TIERS: AccountType[] = ['Research', 'Host'];

/**
 * Length (in days) of the advertised free trial for new Host subscribers.
 * Applied as `subscription_data.trial_period_days` on the Host checkout path
 * only — Research and per-room tiers do NOT get a trial. (Task #3990)
 */
export const HOST_TRIAL_PERIOD_DAYS = 14;

/** True for the flat-priced entry tiers (Research/Host). */
export function isFlatPricedTier(plan: AccountType): boolean {
  return plan === 'Research' || plan === 'Host';
}

/** True for the one tier that gets the advertised 14-day free trial (Host). */
export function tierHasFreeTrial(plan: AccountType): boolean {
  return plan === 'Host';
}

/**
 * Returns the flat monthly price (USD) for a flat-priced tier, or null for any
 * per-room / founder / non-flat plan.
 */
export function flatPlanMonthlyPriceUsd(plan: AccountType): number | null {
  return FLAT_PLAN_MONTHLY_PRICE_USD[plan] ?? null;
}

// ─── Buy Box search entitlements (Task #3874) ────────────────────────────────
// Buy Box city scans (Zillow-via-Apify) are the one real Apify cost driver. Free
// Research accounts get a tight quota; every paid plan gets "unlimited" daily
// scans with a high silent fair-use per-scan ceiling so no single scan runs away
// in cost.

/** Free Research: at most this many Buy Box city scans per local calendar day. */
export const FREE_RESEARCH_DAILY_SCAN_LIMIT = 1;
/** Free Research: hard cap on properties returned per scan. */
export const FREE_RESEARCH_MAX_RESULTS_PER_SCAN = 100;
/** Paid plans: silent fair-use cap on properties returned per scan. */
export const PAID_MAX_RESULTS_PER_SCAN = 1000;
/**
 * Paid plans: when a paid account's running monthly Buy Box scan count crosses
 * this threshold, raise an admin fair-use alert (reuses the Apify cost-alert
 * path). The user-facing "unlimited" promise is unchanged — this only notifies
 * admins of potentially abusive volume.
 */
export const PAID_MONTHLY_SCAN_ALERT_THRESHOLD = 100;

/**
 * Returns true when an account is entitled to unlimited Buy Box search (no
 * daily cap, high fair-use per-scan ceiling).
 *
 * - Per-room / founder paid tiers (Growth, Professional, Enterprise, PMPro,
 *   Founders) always qualify.
 * - Flat entry tiers (Research, Host) qualify ONLY with an active paid
 *   subscription (`active`/`trialing`/`comped`). Free Research and lapsed
 *   accounts fall back to the free quota.
 */
export function hasUnlimitedSearch(args: {
  plan: AccountType;
  subscriptionStatus: string | null | undefined;
}): boolean {
  const { plan, subscriptionStatus } = args;
  if (isPaidTier(plan)) return true;
  if (isFlatPricedTier(plan)) {
    const status = subscriptionStatus ?? '';
    return status === 'active' || status === 'trialing' || status === 'comped';
  }
  return false;
}

/** Max properties returned per scan for the account's entitlement. */
export function maxResultsPerScan(args: {
  plan: AccountType;
  subscriptionStatus: string | null | undefined;
}): number {
  return hasUnlimitedSearch(args)
    ? PAID_MAX_RESULTS_PER_SCAN
    : FREE_RESEARCH_MAX_RESULTS_PER_SCAN;
}

export const LAUNCH_DATE_DISPLAY = 'June 1, 2026';

// Task #3972 — Unified assignment notifications. The notification event kinds
// that fan out to a recipient across channels, and the per-channel knobs each
// one exposes under My Profile → Notifications. `member_message_received`
// (OneSignal Phase 2) rides the same matrix: it's not an "assignment", but
// sharing the prefs model gives it the same user-facing toggles, resolver
// legacy-tolerance, and dispatcher for free.
export const ASSIGNMENT_NOTIFICATION_TYPES = [
  "team_inbox_event",
  "mention",
  "task_assigned",
  "todo_assigned",
  "member_message_assigned",
  "member_message_received",
] as const;
export type AssignmentNotificationType = typeof ASSIGNMENT_NOTIFICATION_TYPES[number];

export const ASSIGNMENT_NOTIFICATION_CHANNELS = ["email", "sms", "push"] as const;
export type AssignmentNotificationChannel = typeof ASSIGNMENT_NOTIFICATION_CHANNELS[number];

export type AssignmentChannelPrefs = { email: boolean; sms: boolean; push: boolean };
export type AssignmentNotificationPrefs = Record<AssignmentNotificationType, AssignmentChannelPrefs>;

export const assignmentChannelPrefsSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
  push: z.boolean(),
});
export const assignmentNotificationPrefsSchema = z.object({
  team_inbox_event: assignmentChannelPrefsSchema,
  mention: assignmentChannelPrefsSchema,
  task_assigned: assignmentChannelPrefsSchema,
  todo_assigned: assignmentChannelPrefsSchema,
  member_message_assigned: assignmentChannelPrefsSchema,
  member_message_received: assignmentChannelPrefsSchema,
});
// PATCH shape — any subset of types, any subset of channels.
export const assignmentNotificationPrefsPatchSchema = z.object({
  team_inbox_event: assignmentChannelPrefsSchema.partial().optional(),
  mention: assignmentChannelPrefsSchema.partial().optional(),
  task_assigned: assignmentChannelPrefsSchema.partial().optional(),
  todo_assigned: assignmentChannelPrefsSchema.partial().optional(),
  member_message_assigned: assignmentChannelPrefsSchema.partial().optional(),
  member_message_received: assignmentChannelPrefsSchema.partial().optional(),
});
export type AssignmentNotificationPrefsPatch = z.infer<typeof assignmentNotificationPrefsPatchSchema>;

// Default = in-app always (handled outside prefs) + email ON, push ON (browser
// permission prompt fires on first dashboard visit), sms OFF until phone
// verified. A NULL or partial stored value resolves back to these.
export function defaultAssignmentNotificationPrefs(): AssignmentNotificationPrefs {
  const base = (): AssignmentChannelPrefs => ({ email: true, sms: false, push: true });
  return {
    team_inbox_event: { email: false, sms: false, push: true },
    mention: base(),
    task_assigned: base(),
    todo_assigned: base(),
    member_message_assigned: base(),
    // Per-message events fire far more often than assignments, so email
    // defaults OFF (no template exists either — the email switch is disabled
    // in the UI). Push ON: this is the OneSignal Android channel.
    member_message_received: { email: false, sms: false, push: true },
  };
}

// Merge a client-supplied subset over a fully-resolved base. The profile
// PATCH accepts the PARTIAL shape (any subset of types/channels) and merges
// server-side, so a client running a previously cached bundle that predates a
// newer notification type can still save — its 4-type payload updates the
// types it knows about and preserves the stored value of every omitted type
// instead of resetting it (or being rejected outright by a full-shape schema).
export function mergeAssignmentNotificationPrefs(
  base: AssignmentNotificationPrefs,
  patch: AssignmentNotificationPrefsPatch,
): AssignmentNotificationPrefs {
  const out = {} as AssignmentNotificationPrefs;
  for (const type of ASSIGNMENT_NOTIFICATION_TYPES) {
    out[type] = { ...base[type], ...(patch[type] ?? {}) };
  }
  return out;
}

// Normalize whatever is stored in the jsonb column (NULL / {} / partial / legacy)
// into a fully-populated prefs object so callers never branch on missing keys.
export function resolveAssignmentNotificationPrefs(raw: unknown): AssignmentNotificationPrefs {
  const out = defaultAssignmentNotificationPrefs();
  if (!raw || typeof raw !== "object") return out;
  for (const type of ASSIGNMENT_NOTIFICATION_TYPES) {
    const incoming = (raw as Record<string, unknown>)[type];
    if (incoming && typeof incoming === "object") {
      for (const ch of ASSIGNMENT_NOTIFICATION_CHANNELS) {
        const v = (incoming as Record<string, unknown>)[ch];
        if (typeof v === "boolean") out[type][ch] = v;
      }
    }
  }
  return out;
}

// Quo messaging — see appUsers.messagingProvider.
export const MESSAGING_PROVIDERS = ["none", "twilio", "quo"] as const;
export type MessagingProvider = typeof MESSAGING_PROVIDERS[number];

export const appUsers = pgTable("app_users", {
  id: serial("id").primaryKey(),
  email: text("email").notNull().unique(),
  name: text("name"),
  fullName: text("full_name"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  phone: text("phone"),
  profilePictureUrl: text("profile_picture_url"),
  password: text("password"),
  referralCode: text("referral_code").unique(),
  signupRefCode: text("signup_ref_code"),
  company: text("company"),
  numberOfHouses: integer("number_of_houses"),
  totalRooms: integer("total_rooms"),
  approved: boolean("approved").default(false).notNull(),
  selectedPlan: text("selected_plan").default("Research").notNull(),
  status: text("status").default("active").notNull(),
  invitedBy: integer("invited_by"),
  inviteAcceptedAt: timestamp("invite_accepted_at"),
  waitlistedAt: timestamp("waitlisted_at"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  subscriptionStatus: text("subscription_status").default("none"),
  // Task #3874 — Grandfather flag for accounts that were on the (formerly free)
  // Host plan before Host became a $49/mo paid tier. Legacy free-Host users keep
  // their property management for free (billed $0, not prompted to pay) but get
  // the free Buy Box search quota, not the paid "unlimited search" entitlement.
  legacyFreeHost: boolean("legacy_free_host").default(false).notNull(),
  subscriptionPeriodStart: timestamp("subscription_period_start"),
  subscriptionPeriodEnd: timestamp("subscription_period_end"),
  billingCycle: text("billing_cycle").default("monthly"),
  scheduledPlan: text("scheduled_plan"),
  scheduledCycle: text("scheduled_cycle"),
  // Effective date for a pending downgrade / cancel, captured from
  // Stripe's current_period_end at the moment the change was scheduled.
  scheduledChangeAt: timestamp("scheduled_change_at"),
  // Idempotency marker for the heads-up email sent before
  // `scheduledChangeAt`. Reset to NULL whenever the scheduled change
  // is set, changed, or cleared so a new pending change re-arms it.
  scheduledChangeReminderSentAt: timestamp("scheduled_change_reminder_sent_at"),
  // ─── Modular pricing (see shared/modular-pricing.ts) ────────────────────────
  // The modular model supersedes `selectedPlan`-tier billing. Legacy tier
  // columns above are kept as read-only ballast; accounts with a non-null
  // `accountKind` are on the modular model and every new billing/entitlement
  // decision keys off these columns plus `account_modules` rows.
  // 'operator' | 'property_manager'. NULL = account not yet migrated.
  accountKind: text("account_kind"),
  // 'essentials' | 'pro'. NULL until a base plan is picked.
  planTier: text("plan_tier"),
  modularPlanActivatedAt: timestamp("modular_plan_activated_at", { withTimezone: true }),
  // 14-day full-access trial. Entitlements stay wide-open until this passes,
  // then collapse to plan + purchased modules. Set at signup/migration.
  trialEndsAt: timestamp("trial_ends_at", { withTimezone: true }),
  // The PadPulse Promise: stamped by the webhook on the FIRST invoice with
  // amount_paid > 0, exactly once. `guaranteeEligibleUntil` = that + 30 days.
  firstPaidInvoiceAt: timestamp("first_paid_invoice_at", { withTimezone: true }),
  guaranteeEligibleUntil: timestamp("guarantee_eligible_until", { withTimezone: true }),
  // One guarantee per account, lifetime. Never reset once true.
  guaranteeUsed: boolean("guarantee_used").default(false).notNull(),
  guaranteeUsedAt: timestamp("guarantee_used_at", { withTimezone: true }),
  // Offboarded accounts are archived, never deleted: all data (members,
  // messages, properties, …) is retained and login is blocked while
  // status = 'archived'. Un-archive via admin user-status tooling.
  archivedAt: timestamp("archived_at", { withTimezone: true }),
  // Flat add-on toggles (per-room modules live in `account_modules`).
  cpmListingActive: boolean("cpm_listing_active").default(false).notNull(),
  // States a CPM-listed property manager covers (two-letter codes), shown in
  // the public Certified PM directory (/certified-property-managers).
  cpmStatesCovered: text("cpm_states_covered").array(),
  revenueAddonActive: boolean("revenue_addon_active").default(false).notNull(),
  researchAddonActive: boolean("research_addon_active").default(false).notNull(),
  // Account-level dismissal for the floating Get Started checklist. Stamped on
  // the OWNER row and checked by the GET /api/app/getting-started route so that
  // all team members on the same account see the same dismissed state. NULL means
  // the checklist is still visible. Stored as timestamptz for audit purposes.
  getStartedDismissedAt: timestamp("get_started_dismissed_at", { withTimezone: true }),
  assistantName: text("assistant_name"),
  assistantPassword: varchar("assistant_password", { length: 255 }),
  assistantInvitationAcceptedAt: timestamp("assistant_invitation_accepted_at"),
  // Host-controlled branding for how the PadPulse assistant appears to PadSplit
  // members. `assistantDisplayName` overrides the generic claimed first name;
  // `assistantDisplayImageUrl` is a FULL public `https://…/public-objects/…` URL
  // to an uploaded logo (publicly fetchable — no auth — because the external
  // consumer reads it directly). `assistantUpdatedAt` is stamped whenever the
  // display name or image changes.
  //
  // These three columns are a RECORD-ONLY contract: the Replit side only
  // persists them; the external warm-browser backend reads them per host and
  // pushes rows whose `assistantUpdatedAt` is within the last hour into
  // PadSplit. The Replit side never pushes. See docs/assistant-branding-sync.md.
  assistantDisplayName: text("assistant_display_name"),
  assistantDisplayImageUrl: text("assistant_display_image_url"),
  assistantUpdatedAt: timestamp("assistant_updated_at"),
  smsConsent: boolean("sms_consent").default(false).notNull(),
  smsConsentAt: timestamp("sms_consent_at"),
  // Task #3972 — Phone proven via an SMS one-time code. SMS notification channels
  // stay disabled until this is true (consent alone is not enough to text).
  phoneVerified: boolean("phone_verified").default(false).notNull(),
  phoneVerifiedAt: timestamp("phone_verified_at", { withTimezone: true }),
  // Task #3972 — Per-type/per-channel assignment-notification preferences.
  // NULL = host has never touched the matrix → resolveAssignmentNotificationPrefs
  // falls back to the shared defaults (email on, push on, sms off).
  assignmentNotificationPrefs: jsonb("assignment_notification_prefs").$type<AssignmentNotificationPrefs>(),
  journeyStage: text("journey_stage"),
  onboardingCompletedAt: timestamp("onboarding_completed_at"),
  // Task #1630 — Frontend onboarding-wizard-finished flag, kept separate from
  // `onboardingCompletedAt` so the bot can own that field without competing
  // with the in-app wizard. All in-app onboarding endpoints write only this
  // column; `onboardingCompletedAt` stays exposed read-only for the bot.
  onboardingWizardCompletedAt: timestamp("onboarding_wizard_completed_at"),
  // Task #3971 — One-shot idempotency marker for the stage-based setup-todo
  // seed that runs once after onboarding. Set the first time we seed the
  // host's `journeyStage`-appropriate checklist; its presence makes every
  // later seed attempt a no-op so the host never gets duplicate to-dos.
  onboardingTodosSeededAt: timestamp("onboarding_todos_seeded_at", { withTimezone: true }),
  // Task #3606 — Tracks starter-content seeds (booking template, account
  // defaults, furnishing catalog) that failed to seed during verify-signup.
  // Non-null/non-empty means the host's account setup is incomplete; this
  // drives the host-facing "finish setting up your account" banner and is
  // cleared once a re-run (POST /api/app/account/finish-setup) succeeds.
  starterSeedFailedSeeds: text("starter_seed_failed_seeds").array(),
  // Set to now() while a starter-content seed run is in flight and cleared
  // (set null) when the run finishes (success or fail). Lets the host app show
  // a "your account is being set up…" pending state on the Buy Box /
  // Underwriting pages instead of an empty-looking form while the
  // fire-and-forget approval-path seeds are still populating.
  starterSeedPendingAt: timestamp("starter_seed_pending_at"),
  founderStatus: text("founder_status"),
  founderTier: text("founder_tier"),
  founderFreeRooms: integer("founder_free_rooms"),
  founderActivatedAt: timestamp("founder_activated_at"),
  // Stripe subscription id for founder overage rooms (rooms above founderFreeRooms).
  // The subscription uses a per-unit price; quantity = max(0, billableRooms − founderFreeRooms).
  // NULL until the founder adds a card and the subscription is created on activation.
  founderOverageSubscriptionId: text("founder_overage_subscription_id"),
  // Set to the timestamp of the most recent invoice.payment_failed event for the
  // founder overage subscription. Cleared when the overage invoice is paid or when
  // the overage subscription transitions back to 'active'. NULL = no failure pending.
  founderOveragePaymentFailedAt: timestamp("founder_overage_payment_failed_at", { withTimezone: true }),
  isAdmin: boolean("is_admin").default(false).notNull(),
  // Independent of admin privileges: administrators can stop operational
  // alert emails without losing access or muting alerts for other admins.
  adminAlertEmailsEnabled: boolean("admin_alert_emails_enabled").default(true).notNull(),
  expenseLogPreference: text("expense_log_preference").default("manual").notNull(),
  // Task #2122 — Per-user trigger character for the inline canned-response
  // menu in the messages reply box. NULL = unset (client falls back to "/"),
  // "" = disabled, anything else = single character that opens the menu.
  cannedResponseTrigger: text("canned_response_trigger"),
  // Task #3053 — Stable per-host Sifely webhook token held during the Connect
  // Account wizard BEFORE a connection row exists, so the webhook URL shown in
  // Step 1 persists across revisits (minted once, only changed on explicit
  // rotate). Promoted onto ttlock_connections.sifelyWebhookToken and cleared
  // once the connection is created.
  sifelyPendingWebhookToken: text("sifely_pending_webhook_token"),
  teamEmailNotificationsEnabled: boolean("team_email_notifications_enabled").default(true).notNull(),
  // Task #947 — Per-host opt-out for the email that fans out when an
  // unknown number texts the host's per-host messaging number. Defaults to
  // ON so existing deploys keep getting alerted; hosts can mute it from
  // the Notifications tab if it gets noisy. The in-app notification fires
  // regardless so the bell badge still surfaces unmatched inbound SMS.
  unmatchedSmsEmailEnabled: boolean("unmatched_sms_email_enabled").default(true).notNull(),
  // Task #1011 — Per-host opt-out for the daily PadSplit push-failure email
  // digest. Task #961 surfaces individual push failures via in-app
  // notifications; this flag controls only the once-a-day rollup email so
  // hosts who don't open PadPulse daily still get alerted (e.g. expired
  // PadSplit credentials affecting many tasks at once). Defaults to ON so
  // existing deploys keep getting alerted; hosts can mute it from the
  // Notifications tab. The in-app `padsplit_push_failed` notification fires
  // regardless of this flag so the bell badge still surfaces failures.
  padsplitPushFailureEmailEnabled: boolean("padsplit_push_failure_email_enabled").default(true).notNull(),
  // Task #1930 — Per-host opt-out for the monthly Mercury statements
  // email digest. Task #1919 surfaces each new statement via an in-app
  // notification; this flag controls only the rollup email so hosts who
  // don't open PadPulse daily still get alerted when new statements
  // land. Defaults to ON so existing deploys keep getting alerted; hosts
  // can mute it from the Notifications tab. The in-app
  // `mercury_statement_available` notification fires regardless of this
  // flag so the bell badge still surfaces new statements.
  mercuryStatementsEmailEnabled: boolean("mercury_statements_email_enabled").default(true).notNull(),
  // Task #4041 — Per-host opt-out for the email that fires when a Mercury credit
  // card crosses the >80% utilization "near its limit" threshold. The in-app
  // Team Inbox alert fires regardless of this flag; this controls only the
  // email heads-up so hosts who aren't looking at Financials still get warned
  // before a maxed card declines a vendor payment. Defaults to ON.
  mercuryCardNearLimitEmailEnabled: boolean("mercury_card_near_limit_email_enabled").default(true).notNull(),
  // Send the user an email when one of their Buy Box scans finishes.
  // Defaults to ON so existing users get notified; can be muted from the
  // Notifications tab. The report card in-app remains accurate either way.
  buyBoxEmailEnabled: boolean("buy_box_email_enabled").default(true).notNull(),
  totpSecret: text("totp_secret"),
  totpEnabled: boolean("totp_enabled").default(false).notNull(),
  totpBackupCodes: text("totp_backup_codes").array(),
  totpEnrolledAt: timestamp("totp_enrolled_at", { withTimezone: true }),
  preferredTheme: text("preferred_theme").default("system").notNull(),
  // Task #2909 — Account-level IANA timezone the host sets once in
  // Account/Settings. It is the single source of truth for every scheduled
  // feature that fires in the host's local time (low-battery lock reminders,
  // service-form schedules, etc.). Feature-specific timezone columns read from
  // this default unless the host has explicitly overridden them. Defaults to
  // the platform default (America/Chicago) so existing hosts keep behaving
  // sensibly until they pick their zone.
  timezone: text("timezone").default("America/Chicago").notNull(),
  // Task #866 — Per-host messaging Twilio number for Direct Member outbound
  // SMS and inbound replies. The shared TWILIO_PHONE_NUMBER stays reserved
  // for vendor-facing SMS; member messaging is per-host so replies route
  // back to the right host's inbox. The `Sid` column tracks the Twilio
  // `PNxxxx…` resource ID so we can release the number from Twilio later
  // even if the local phone string is rotated or cleared.
  messagingPhoneNumber: text("messaging_phone_number").unique(),
  messagingPhoneNumberSid: text("messaging_phone_number_sid"),
  messagingPhoneAssignedAt: timestamp("messaging_phone_assigned_at"),
  // Quo messaging — which outbound SMS provider this host account uses for
  // direct-member messages: 'quo' (a row in quo_connections), 'twilio' (the
  // per-host messagingPhoneNumber above) or 'none'. Exactly one provider can
  // be active; the connect routes refuse with 409 PROVIDER_ACTIVE otherwise.
  messagingProvider: text("messaging_provider").$type<MessagingProvider>().notNull().default("none"),
  knowledgeBase: text("knowledge_base"),
  aiMemory: text("ai_memory"),
  sopLink: text("sop_link"),
  tosAcknowledgedAt: timestamp("tos_acknowledged_at"),
  tosNotifiedAt: timestamp("tos_notified_at"),
  stripeDefaultPaymentMethodId: text("stripe_default_payment_method_id"),
  paymentMethodCollectedAt: timestamp("payment_method_collected_at"),
  tosNotifyAttempts: integer("tos_notify_attempts").default(0).notNull(),
  tosNotifyLastError: text("tos_notify_last_error"),
  privacyPolicyAcknowledgedVersion: text("privacy_policy_acknowledged_version"),
  privacyPolicyNotifiedVersion: text("privacy_policy_notified_version"),
  // Task #1368 — Database-only super-admin flag. Set directly in SQL;
  // no UI to grant or revoke. Gates the Developer Tools sidebar section
  // and impersonation routes. Three designated emails are seeded via the
  // migration that introduces this column.
  superAdmin: boolean("super_admin").default(false).notNull(),
  // Admin Access Layer — per-user /admin panel credentials replacing the
  // shared ADMIN_PASSWORD. Separate from the host `password` column; same
  // scrypt `salt:hash` format via server/utils/scrypt-password.ts. Lockout:
  // 5 consecutive failures set admin_locked_until = now() + 30 minutes.
  adminPasswordHash: text("admin_password_hash"),
  adminLastLoginAt: timestamp("admin_last_login_at"),
  adminLoginAttempts: integer("admin_login_attempts").default(0).notNull(),
  adminLockedUntil: timestamp("admin_locked_until"),
  // Admin Access Layer — granular admin capabilities beneath the DB-only
  // super_admin master override (which implies all of them). Values are
  // validated against ADMIN_CAPABILITIES in shared/admin-permissions.ts on
  // every write; granted/revoked only via the super-admin Admin Users UI.
  // NOTE: this needs a real DATABASE-level default (not `$defaultFn`, which is
  // app-side only): Replit's publish flow diffs the dev DB against prod and a
  // NOT NULL column with no DB default on a populated table makes its generated
  // migration TRUNCATE app_users. The raw-SQL default matches what Postgres
  // reports back on introspection, so `db:push` stays diff-free (the known
  // literal-`.default([])` serialization loop doesn't apply).
  adminPermissions: text("admin_permissions").array().notNull().default(sql`'{}'::text[]`),
  // Task #1531 — Per-host toggle to exclude inactive rooms from occupancy
  // calculations. Defaults to OFF so existing behavior is preserved; hosts
  // can enable it from Settings > Notifications or from the dashboard
  // occupancy card when they notice inactive rooms dragging down the %.
  excludeInactiveRoomsFromOccupancy: boolean("exclude_inactive_rooms_from_occupancy").default(false).notNull(),
  // Task #4675 follow-up — Per-host toggle to surface INACTIVE-status members'
  // chat history in the Messages search. Defaults to OFF (hidden) so historic
  // stubs/past stays don't pollute search results; hosts flip it on from
  // Settings > Notifications ("Show inactive messages") when they need to dig
  // up an old conversation.
  showInactiveMessages: boolean("show_inactive_messages").default(false).notNull(),
  // Task #1828 — Active promo code application. Points to the user_promo_codes
  // row that represents the currently-applied promo for this account. Stored as
  // a plain integer (no FK constraint) to avoid a circular reference between
  // app_users and user_promo_codes.
  activePromoCodeId: integer("active_promo_code_id"),
  // Task #2351 — One-shot marker set when the three template pricing models
  // (cloned from source IDs 5/7/15) are auto-seeded on the user's first
  // visit to the pricing page. Once set, we never re-seed even if the user
  // later deletes all their models (emptiness after seeding is treated as
  // intentional).
  defaultPricingModelsSeededAt: timestamp("default_pricing_models_seeded_at"),
  // Demo org flag — when true, all external egress calls (PadSplit, Twilio,
  // Resend, Stripe, TTLock, Seam) are suppressed or stubbed for this org.
  // Set via scripts/freeze-demo.js; never modified through normal app flows.
  isDemo: boolean("is_demo").default(false).notNull(),
  // Plan chosen during the login-page sign-up flow. For public (waitlisted)
  // sign-ups this is the *requested* plan — it stays here until an admin
  // approves the account, at which point it is promoted to selectedPlan.
  // For internal / auto-approved sign-ups it is applied directly to
  // selectedPlan at verify-signup and this column is left NULL.
  requestedPlan: text("requested_plan"),
  // Billing interval chosen alongside a paid requestedPlan. Carried through
  // to billingCycle when the requestedPlan is activated on approval.
  requestedBillingInterval: text("requested_billing_interval"),
  // Host-chosen cutoff for auto-resolving old imported member messages.
  // Any inbound member message older than this timestamp is auto-resolved by
  // the recurring sweep (system resolution: resolvedBy = NULL). NULL = not set.
  // Interpreted in the host's existing account timezone.
  msgAutoResolveCutoff: timestamp("msg_auto_resolve_cutoff", { withTimezone: true }),
  // When true, the first-visit "Auto-resolve old messages" prompt on the
  // Messages page has been explicitly dismissed by the host. Once dismissed
  // the dialog does not auto-open again, but remains reachable from Settings.
  msgAutoResolvePromptDismissed: boolean("msg_auto_resolve_prompt_dismissed").default(false).notNull(),
  // Activity-log fields for the auto-resolve sweep. Updated whenever a sweep
  // (the recurring cron OR the immediate sweep on cutoff save) actually resolves
  // one or more messages for this host. `lastSweptAt` is the time of that sweep;
  // `lastSweptCount` is the number of messages it resolved. Lets the Settings
  // dialog show "Last ran: <date> — N messages resolved". NULL = never swept.
  msgAutoResolveLastSweptAt: timestamp("msg_auto_resolve_last_swept_at", { withTimezone: true }),
  msgAutoResolveLastSweptCount: integer("msg_auto_resolve_last_swept_count"),
  // ── v3 per-property pricing (docs/build/padpulse-build-backend.md) ──
  // 'v3' = billed on shared/pricing.ts (per active property). NULL = legacy /
  // modular account that has not been moved yet; never billed by v3 code.
  billingModel: text("billing_model").$type<BillingModel>(),
  // Role chosen at signup: host | property_manager | researcher.
  signupRole: text("signup_role").$type<SignupRole>(),
  // Property count the user typed at signup (trial quantity before any
  // property rows exist; funnel reporting).
  signupPropertyCount: integer("signup_property_count"),
  signupAttribution: jsonb("signup_attribution").$type<SignupAttribution>(),
  trialStartedAt: timestamp("trial_started_at", { withTimezone: true }),
  // Set once when an existing account is given a fresh v3 trial (B20).
  trialRestartedAt: timestamp("trial_restarted_at", { withTimezone: true }),
  // Lifetime plans (SYRE deal, June founders). Allowance = properties covered.
  lifetimeAllowance: integer("lifetime_allowance"),
  lifetimePurchasedAt: timestamp("lifetime_purchased_at", { withTimezone: true }),
  lifetimeSource: text("lifetime_source"),
  lifetimePaymentRef: text("lifetime_payment_ref"),
  lifetimePaymentIntentId: text("lifetime_payment_intent_id"),
  // Separate monthly subscription carrying a lifetime account's billable
  // extras (properties above the allowance, owner portal, device locks).
  lifetimeAddonSubscriptionId: text("lifetime_addon_subscription_id"),
  pausedAt: timestamp("paused_at", { withTimezone: true }),
  pastDueSince: timestamp("past_due_since", { withTimezone: true }),
  // Flag only — paused 30+ days. Nothing is ever deleted automatically.
  retentionExpiredAt: timestamp("retention_expired_at", { withTimezone: true }),
  // Plan D31/D32 (option B): an existing saved card is held — NOT used for the
  // v3 trial — until the user clicks "Keep using my card ending 4242".
  unconfirmedCardPmId: text("unconfirmed_card_pm_id"),
  unconfirmedCardLast4: text("unconfirmed_card_last4"),
  // B14: account-level opt-in to the off-PadSplit member portal (Beta).
  memberPortalOptInAt: timestamp("member_portal_opt_in_at", { withTimezone: true }),
  padsplitConnectedAt: timestamp("padsplit_connected_at", { withTimezone: true }),
  firstPropertyAddedAt: timestamp("first_property_added_at", { withTimezone: true }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  // Long auto-generated name kept in sync with the production constraint
  // name (see scripts/deploy-migrate.sql). Renaming would force db:push to
  // drop+recreate the FK against live data.
  invitedByFk: foreignKey({
    name: "app_users_invited_by_app_users_id_fk",
    columns: [table.invitedBy],
    foreignColumns: [table.id],
  }).onDelete("set null"),
}));

// Feature Roadmap — DB-backed roadmap items shown publicly and in-app; logged-in
// users can upvote (one toggleable vote per user per item, enforced by the unique
// index on roadmap_votes below). Admins manage items via /app/admin/roadmap.
export const ROADMAP_STATUSES = ["considering", "planned", "in_progress", "shipped"] as const;
export type RoadmapStatus = (typeof ROADMAP_STATUSES)[number];

export const roadmapItems = pgTable("roadmap_items", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  status: text("status").notNull().default("planned"),
  sortOrder: integer("sort_order").notNull().default(0),
  isPublished: boolean("is_published").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertRoadmapItemSchema = createInsertSchema(roadmapItems).omit({
  id: true,
  createdAt: true,
}).extend({
  title: z.string().min(2, "Title is required").max(200, "Title is too long"),
  description: z.string().max(4000, "Description is too long").optional().nullable(),
  status: z.enum(ROADMAP_STATUSES).default("planned"),
  sortOrder: z.coerce.number().int().optional(),
  isPublished: z.boolean().optional(),
});

export const updateRoadmapItemSchema = insertRoadmapItemSchema.partial();

export type InsertRoadmapItem = z.infer<typeof insertRoadmapItemSchema>;
export type UpdateRoadmapItem = z.infer<typeof updateRoadmapItemSchema>;
export type RoadmapItem = typeof roadmapItems.$inferSelect;

export const roadmapVotes = pgTable("roadmap_votes", {
  id: serial("id").primaryKey(),
  roadmapItemId: integer("roadmap_item_id").notNull(),
  userId: integer("user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  itemFk: foreignKey({
    columns: [t.roadmapItemId],
    foreignColumns: [roadmapItems.id],
    name: "roadmap_votes_item_id_fk",
  }).onDelete("cascade"),
  userFk: foreignKey({
    columns: [t.userId],
    foreignColumns: [appUsers.id],
    name: "roadmap_votes_user_id_fk",
  }).onDelete("cascade"),
  itemUserUniq: uniqueIndex("roadmap_votes_item_user_uniq").on(t.roadmapItemId, t.userId),
}));

export type RoadmapVote = typeof roadmapVotes.$inferSelect;

// Bootstrap fallback values for the Terms of Service. Historically these were
// the source of truth; as of Task #628 every published ToS version is stored
// in the `tos_versions` table and the latest row is queried at request time.
// These constants now only exist to seed the table at startup if it is empty
// (so existing deploys that haven't been backfilled keep working) and as a
// fallback for code paths that need a date when no version row exists yet.
export const TOS_LAST_UPDATED_AT = "2026-04-22T00:00:00.000Z";
export const TOS_LAST_UPDATED_LABEL = "June 20, 2026";
export const TOS_LATEST_CHANGE_SUMMARY =
  "Section 8 now uses generic wording for third-party service providers. A new Section 9A has been added covering AI Features and Data Use.";
// Bootstrap-only markdown content for the very first published ToS version.
// Once admins publish a new version from `Admin → ToS Versions`, that newer
// row supersedes this constant entirely. The /terms page renders this with
// react-markdown, so headings (`##`), bold (`**`), and bullet lists work.
export const TOS_LATEST_FULL_TEXT = `*__Independence Notice:__ PadPulse is an independent third-party software platform. PadPulse is not affiliated with, endorsed by, sponsored by, or otherwise connected to PadSplit, Inc. Any references to PadSplit are solely to describe the property-management workflows that PadPulse helps hosts manage. "PadSplit" is a trademark of its respective owner.*

## 1. Acceptance of Terms

By accessing or using PadPulse (the "Service"), provided by PadPulse LLC, a Wyoming limited liability company, with its principal place of business at 539 W. Commerce St, Suite 304, Dallas, TX 75208 (the "Company," "we," "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.

If you are accepting these Terms on behalf of a company or other legal entity, you represent that you have authority to bind that entity to these Terms, and "you" refers to that entity.

## 2. Description of Service

PadPulse provides software tools designed to help independent property hosts manage their rental operations, including but not limited to member communications, occupancy tracking, payment monitoring, maintenance coordination, and reporting.

## 3. Eligibility and Accounts

You must be at least 18 years old and capable of forming a binding contract to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to provide accurate, current information and to update it as needed.

## 4. Subscription Plans and Billing

PadPulse offers both a free tier and paid subscription plans. Paid subscriptions are billed in advance on a recurring basis (monthly or annually) through our payment processor, Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel.

You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. Except where required by law, fees are non-refundable. We reserve the right to change pricing with at least 30 days' notice to active subscribers.

## 5. Acceptable Use

You agree not to: (a) use the Service for any unlawful purpose; (b) violate the rights of tenants, members, or third parties; (c) upload malicious code or attempt to disrupt the Service; (d) reverse engineer or copy the Service; (e) use the Service to send spam or unsolicited communications; (f) misrepresent your affiliation with any person or entity; (g) scrape, crawl, or use automated means to extract data from the Service without our express written consent; or (h) access or attempt to access any portion of the Service or its underlying systems in a manner not expressly authorized by these Terms.

## 6. Host Responsibilities

You are solely responsible for your compliance with all applicable laws, including landlord-tenant laws, fair housing laws, tax obligations, and any agreements you have with third-party platforms (including PadSplit). PadPulse is a tool; it does not provide legal, tax, or property-management advice. You are responsible for the accuracy of any data you input and for any communications you send through the Service.

## 7. Vendor Directory, Vendor Marketplace, and User-Submitted Content

PadPulse allows hosts to maintain a private vendor directory — a list of service providers (e.g., cleaners, maintenance contractors, landscapers) that the host has chosen to track within the platform. The following terms govern the relationship between a host's vendor directory and the PadPulse public vendor marketplace, and apply to all content you submit to the Service:

- **Vendors are independent businesses.** Any vendor a host adds to their PadPulse vendor directory is an independent third party. Adding a vendor to your directory does not create an employment relationship, partnership, or any other legal relationship between you and PadPulse, or between PadPulse and that vendor.
- **Vendors may independently opt into the public marketplace.** Any vendor added to a host's directory may, at their sole discretion, sign up for a PadPulse vendor account and choose to appear on the PadPulse public vendor marketplace. This decision is made entirely by the vendor and is independent of your relationship with that vendor.
- **Hosts do not control marketplace participation.** You, as a host, do not have the ability to approve, block, or otherwise influence whether a vendor you have added to your directory chooses to appear on the public PadPulse vendor marketplace.
- **No exclusivity.** Adding a vendor to your directory does not grant you any exclusive rights over that vendor's services or restrict that vendor from working with other hosts, platforms, or clients. No exclusivity of any kind is created by adding a vendor to your directory.
- **No endorsement.** A vendor's appearance on the public PadPulse marketplace does not constitute an endorsement by PadPulse of that vendor's services, and PadPulse is not responsible for the quality, safety, or legality of any vendor's work.
- **Your content.** You are solely responsible for all content, data, listings, descriptions, photos, and communications you submit through the Service ("User Content"). You represent and warrant that you own or have the necessary rights to submit all User Content, and that your User Content does not infringe the intellectual property, privacy, or other rights of any third party, and does not violate any applicable law. PadPulse reserves the right to remove User Content that violates these Terms or applicable law.
- **Vendor content.** Vendors who create PadPulse accounts are solely responsible for the accuracy, legality, and completeness of their marketplace listings and all content they submit. PadPulse does not verify vendor credentials, licenses, or qualifications, and makes no representation regarding the quality or fitness of any vendor's services.

## 8. Third-Party Services

The Service integrates with third-party service providers, including for payment processing, messaging, and storage. Your use of these integrations may be subject to those providers' own terms. We are not responsible for the acts or omissions of third-party providers.

## 9. Intellectual Property

PadPulse and its content, features, and functionality are owned by the Company and are protected by U.S. and international copyright, trademark, and other intellectual property laws. You retain ownership of data you submit to the Service and grant us a limited license to use it solely to operate and improve the Service.

**Feedback.** If you provide suggestions, ideas, or other feedback about the Service ("Feedback"), you grant PadPulse a perpetual, irrevocable, royalty-free, worldwide license to use, reproduce, modify, and incorporate that Feedback into the Service or other products without restriction or compensation to you.

## 9A. AI Features and Data Use

PadPulse offers AI-powered features (such as AI-assisted listing descriptions, automated recommendations, and market analysis) that process data you submit to generate outputs on your behalf.

**Your data, your ownership.** You retain full ownership of all data, content, and information you submit to the Service. Your use of AI Features does not transfer or assign any ownership rights in your data to PadPulse or to any AI model provider.

**How AI Features use your data.** When you invoke an AI Feature, the content you submit for that request is processed by an AI model to generate the requested output. Your data is used solely to produce the output you requested and to operate and improve the Service as described in Our Privacy Policy.

**AI model training.** We do not use your data to train or fine-tune third-party AI foundation models, except where you have separately consented or as otherwise disclosed in Our Privacy Policy and the applicable AI provider's terms.

**Opt-out.** You may opt out of AI Feature data processing for improvement purposes at any time by contacting us at [info@padpulse.io](mailto:info@padpulse.io). Opting out does not affect your ability to use non-AI features of the Service.

**No autonomous action.** AI Features within the Service provide recommendations, drafts, and informational outputs only. No AI Feature takes autonomous action on your behalf — such as sending messages to members, submitting listings, or making payments — without your explicit review and confirmation.

## 10. Copyright Policy and Notices (DMCA)

PadPulse respects intellectual property rights and expects users to do the same. If you believe that content on the Service infringes your copyright, please send a written notice to our designated copyright agent containing: (a) a description of the copyrighted work you claim has been infringed; (b) a description of where the allegedly infringing material is located on the Service; (c) your contact information (address, telephone number, and email); (d) a statement that you have a good faith belief that the use is not authorized by the copyright owner, its agent, or the law; (e) a statement under penalty of perjury that your notice is accurate and that you are the copyright owner or authorized to act on the owner's behalf; and (f) your physical or electronic signature.

Designated Copyright Agent: PadPulse LLC, 539 W. Commerce St, Suite 304, Dallas, TX 75208 | Email: [info@padpulse.io](mailto:info@padpulse.io)

We will respond to valid DMCA notices in accordance with applicable law. Repeat infringers may have their accounts terminated.

## 11. Disclaimers

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.

## 12. Limitation of Liability

TO THE FULLEST EXTENT PERMITTED BY LAW, PADPULSE AND ITS OFFICERS, EMPLOYEES, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.

## 13. Indemnification

You agree to indemnify and hold harmless PadPulse from any claims, damages, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any third-party rights.

## 14. Termination

We may suspend or terminate your account at any time for violation of these Terms or for any other reason at our discretion. You may terminate your account at any time by canceling through your account settings.

## 15. Governing Law and Dispute Resolution

These Terms are governed by the laws of the State of Texas, without regard to conflict of law principles. Any dispute arising under these Terms shall be resolved exclusively in the state courts of Dallas County, Texas, or the U.S. District Court for the Northern District of Texas, Dallas Division, and you consent to personal jurisdiction in those courts.

## 16. Changes to These Terms

We may update these Terms from time to time. We will notify users of material changes by posting the updated Terms on this page and updating the "Last updated" date. Continued use of the Service constitutes acceptance.

## 17. General Provisions

**Severability.** If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will continue in full force and effect.

**Relationship of the Parties.** Nothing in these Terms creates any partnership, joint venture, employment, agency, franchise, or fiduciary relationship between you and PadPulse. PadPulse is an independent contractor. You are not an agent of PadPulse, and neither party may bind the other to any obligation.

**Assignment.** You may not assign or transfer your rights or obligations under these Terms without PadPulse's prior written consent. PadPulse may freely assign these Terms, including in connection with a merger, acquisition, or sale of assets, with notice to you.

**Entire Agreement.** These Terms, together with any applicable subscription order or addendum, constitute the entire agreement between you and PadPulse regarding the Service and supersede all prior or contemporaneous agreements, understandings, and representations relating to the same subject matter.

**Rights and Remedies.** Our failure to exercise or enforce any right or remedy under these Terms does not waive that right or remedy. All rights and remedies provided herein are cumulative and not exclusive of any other rights or remedies provided by law.

## 18. Contact

Questions? Contact us at [info@padpulse.io](mailto:info@padpulse.io).`;

// Bump these whenever the Privacy Policy is materially updated. The version
// is the source of truth; the label is shown in the in-app notice.
//
// WARNING: changing PRIVACY_POLICY_VERSION makes the boot-time notifier
// (server/index.ts → notifyUsersOfPrivacyPolicyUpdate) email every active
// user on the next server start/deploy. Bump only the label + summary when
// the update email should stay admin-triggered (Admin → "Terms & Privacy
// Notice", which reads the label + summary and never auto-sends).
export const PRIVACY_POLICY_VERSION = "2026-04-23";
export const PRIVACY_POLICY_LAST_UPDATED_LABEL = "October 8, 2026";
export const PRIVACY_POLICY_LATEST_CHANGE_SUMMARY =
  "We've added a section explaining how bank and credit-card data is handled when you choose to connect accounts through Stripe Financial Connections: what we receive (read-only), how we use it, how to disconnect, and how long we keep it.";

// Maximum times we will retry sending the ToS-update email to a single user
// for the same version before giving up. Failed sends do not stamp
// `tosNotifiedAt`, so the periodic cron job naturally retries until either
// the send succeeds or this attempt cap is reached.
export const TOS_NOTIFY_MAX_ATTEMPTS = 5;

// Tracks per-version state of the bulk ToS-update email blast so that:
//   - Admins receive exactly one confirmation summary per published version
//   - We can monitor totals (eligible / sent / failed) across runs
//   - Repeated cron passes & retries update the same row instead of
//     creating duplicates.
export const tosNotificationRuns = pgTable("tos_notification_runs", {
  version: text("version").primaryKey(),
  firstStartedAt: timestamp("first_started_at").defaultNow().notNull(),
  lastRunAt: timestamp("last_run_at").defaultNow().notNull(),
  runCount: integer("run_count").default(0).notNull(),
  totalEligibleSeen: integer("total_eligible_seen").default(0).notNull(),
  totalSent: integer("total_sent").default(0).notNull(),
  totalFailed: integer("total_failed").default(0).notNull(),
  summaryEmailSentAt: timestamp("summary_email_sent_at"),
});
export type TosNotificationRun = typeof tosNotificationRuns.$inferSelect;

// Task #628: Every published Terms of Service version is stored here so that
// admins can publish new versions from the UI (no code deploy required) and
// users can review past Terms. The row with the largest `effectiveAt` is the
// currently published version — the in-app banner, ToS notifier, and public
// /terms page all read from it.
export const tosVersions = pgTable("tos_versions", {
  id: serial("id").primaryKey(),
  // Use the same ISO-string format as the legacy `TOS_LAST_UPDATED_AT`
  // constant so existing comparisons (e.g. `tosNotificationRuns.version`)
  // and existing per-user `tos_acknowledged_at` / `tos_notified_at`
  // timestamps keep matching the new version identifier.
  effectiveAt: timestamp("effective_at").notNull().unique(),
  label: text("label").notNull(),
  changeSummary: text("change_summary").notNull(),
  fullText: text("full_text").notNull(),
  publishedByUserId: integer("published_by_user_id").references(() => appUsers.id, {
    onDelete: "set null",
  }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export type TosVersion = typeof tosVersions.$inferSelect;
export const insertTosVersionSchema = createInsertSchema(tosVersions)
  .omit({ id: true, createdAt: true, publishedByUserId: true })
  .extend({
    effectiveAt: z.coerce.date(),
    label: z.string().trim().min(1, "Label is required").max(100),
    changeSummary: z.string().trim().min(1, "Change summary is required"),
    fullText: z.string().trim().min(1, "Full text is required"),
  });
export type InsertTosVersion = z.infer<typeof insertTosVersionSchema>;

// Per-Privacy-Policy-version state for the bulk update-email blast — mirrors
// `tos_notification_runs` so the admin "Last Run" card stays accurate across
// server restarts (the boot-time auto-send no longer disappears from the UI
// just because the in-memory cache was cleared).
export const privacyPolicyNotificationRuns = pgTable("privacy_policy_notification_runs", {
  version: text("version").primaryKey(),
  firstStartedAt: timestamp("first_started_at").defaultNow().notNull(),
  lastRunAt: timestamp("last_run_at").defaultNow().notNull(),
  runCount: integer("run_count").default(0).notNull(),
  totalEligibleSeen: integer("total_eligible_seen").default(0).notNull(),
  totalSent: integer("total_sent").default(0).notNull(),
  totalFailed: integer("total_failed").default(0).notNull(),
  lastTriggeredBy: text("last_triggered_by"),
  lastFailuresJson: text("last_failures_json"),
  lastEligibleCount: integer("last_eligible_count").default(0).notNull(),
  lastSentCount: integer("last_sent_count").default(0).notNull(),
  lastFailedCount: integer("last_failed_count").default(0).notNull(),
});
export type PrivacyPolicyNotificationRun = typeof privacyPolicyNotificationRuns.$inferSelect;

// Task #4218 — Durable, append-only history of every "Terms & Privacy update"
// admin blast (Task #4202). Unlike the ToS / Privacy-Policy notifiers, this
// notice is a deliberately un-versioned, ad-hoc one-off (a "silent
// correction"), so it has no version primary key — each run is its own row.
// This replaces the old in-memory `lastRun` snapshot that reset on restart,
// giving admins a full audit trail (when, by whom, eligible/sent/failed
// counts, and the per-send failure list) that survives server restarts.
export const termsPrivacyNoticeRuns = pgTable("terms_privacy_notice_runs", {
  id: serial("id").primaryKey(),
  finishedAt: timestamp("finished_at").defaultNow().notNull(),
  triggeredBy: text("triggered_by").notNull(),
  eligibleCount: integer("eligible_count").default(0).notNull(),
  sentCount: integer("sent_count").default(0).notNull(),
  failedCount: integer("failed_count").default(0).notNull(),
  failuresJson: text("failures_json"),
});
export type TermsPrivacyNoticeRun = typeof termsPrivacyNoticeRuns.$inferSelect;

export const JOURNEY_STAGES = [
  "searching",
  "under_contract",
  "renovating",
  "operating_self_managed",
  "operating_with_pm",
] as const;
export type JourneyStage = typeof JOURNEY_STAGES[number];

export const JOURNEY_STAGE_LABELS: Record<JourneyStage, string> = {
  searching: "I don't have a CoLiving home yet and am still searching.",
  under_contract: "I have my first CoLiving home under contract.",
  renovating: "I have my first CoLiving home under renovation or furnishing right now.",
  operating_self_managed: "I have a house in operation with members and I manage myself.",
  operating_with_pm: "I have a house in operation with members and I have a property manager.",
};

export const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;
export const E164_PHONE_MESSAGE = "Phone must be in international format (e.g. +14155551234)";

export const onboardingProfileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(60, "First name is too long"),
  lastName: z.string().min(1, "Last name is required").max(60, "Last name is too long"),
  phone: z.string().regex(E164_PHONE_REGEX, E164_PHONE_MESSAGE),
  smsConsent: z.literal(true, { errorMap: () => ({ message: "You must agree to receive SMS messages." }) }),
  profilePictureUrl: z.string().max(5_000_000).optional(),
});
export type OnboardingProfileInput = z.infer<typeof onboardingProfileSchema>;

export const onboardingJourneySchema = z.object({
  journeyStage: z.enum(JOURNEY_STAGES),
});
export type OnboardingJourneyInput = z.infer<typeof onboardingJourneySchema>;

export const updateOnboardingPlanSchema = z.object({
  selectedPlan: z.enum(["Host", "Growth", "Professional"], {
    errorMap: () => ({ message: "Choose a valid plan." }),
  }),
  billingCycle: z.enum(["monthly", "annual"]).default("monthly"),
});
export type UpdateOnboardingPlanInput = z.infer<typeof updateOnboardingPlanSchema>;

export const names = pgTable("names", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  sex: varchar("sex", { length: 1 }),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
}, (table) => ({
  nameUniq: uniqueIndex("idx_names_name_unique").on(table.name),
  unclaimed: index("idx_names_unclaimed").on(table.id).where(sql`user_id IS NULL`),
  userIdUniq: uniqueIndex("idx_names_user_id_unique").on(table.userId).where(sql`user_id IS NOT NULL`),
}));

export type Name = typeof names.$inferSelect;

export const insertAppUserSchema = createInsertSchema(appUsers).omit({
  id: true,
  createdAt: true,
}).extend({
  email: z.string().email(),
  name: z.string().optional(),
  fullName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  phoneVerified: z.boolean().optional(),
  phoneVerifiedAt: optionalCoercedDate,
  assignmentNotificationPrefs: assignmentNotificationPrefsSchema.optional(),
  profilePictureUrl: z.string().optional(),
  password: z.string().optional(),
  referralCode: z.string().optional(),
  company: z.string().optional(),
  numberOfHouses: z.coerce.number().min(1).optional(),
  totalRooms: z.coerce.number().min(1).optional(),
  approved: z.boolean().optional(),
  selectedPlan: z.enum(ACCOUNT_TYPES).default('Host').optional(),
  status: z.enum(USER_STATUSES).default('active').optional(),
  invitedBy: z.number().optional(),
  inviteAcceptedAt: optionalCoercedDate,
  waitlistedAt: optionalCoercedDate,
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().optional().nullable(),
  subscriptionStatus: z.string().optional(),
  billingCycle: z.string().optional(),
  assistantName: z.string().optional().nullable(),
  assistantDisplayName: z.string().optional().nullable(),
  assistantDisplayImageUrl: z.string().optional().nullable(),
  assistantUpdatedAt: optionalCoercedDate,
  // assistant_password is owned by the warm-browser system; never set from here.
});

export const REFERRAL_STATUSES = ["pending", "signed_up", "active"] as const;
export type ReferralStatus = typeof REFERRAL_STATUSES[number];

export const REFERRAL_TIERS = ["founder_partner", "regular_host"] as const;
export type ReferralTier = typeof REFERRAL_TIERS[number];

export const referrals = pgTable("referrals", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "cascade" }),
  partnerId: integer("partner_id"),
  referredEmail: text("referred_email").notNull(),
  referredUserId: integer("referred_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  tierAtSignup: text("tier_at_signup"),
  signupAt: timestamp("signup_at"),
  commissionWindowEnd: timestamp("commission_window_end"),
  status: text("status").default("pending").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertReferralSchema = createInsertSchema(referrals).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number().optional().nullable(),
  partnerId: z.number().optional().nullable(),
  referredEmail: z.string().email(),
  status: z.enum(REFERRAL_STATUSES).default("pending").optional(),
});

export type InsertReferral = z.infer<typeof insertReferralSchema>;
export type Referral = typeof referrals.$inferSelect;

// ─── Affiliate Referral Click Tracking (Task #2466) ──────────────────────────
// One row per visit to a referral link (?ref=<code>). Powers the affiliate
// dashboard "click stats". partnerId/userId are resolved at write time from the
// code so the dashboard can aggregate without a join; both are nullable because
// an unknown/expired code is still recorded for diagnostics.
export const referralClicks = pgTable("referral_clicks", {
  id: serial("id").primaryKey(),
  referralCode: text("referral_code").notNull(),
  partnerId: integer("partner_id"),
  userId: integer("user_id"),
  ipHash: text("ip_hash"),
  userAgent: text("user_agent"),
  landingPath: text("landing_path"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  codeIdx: index("idx_referral_clicks_code").on(table.referralCode),
  partnerIdx: index("idx_referral_clicks_partner").on(table.partnerId),
  createdAtIdx: index("idx_referral_clicks_created_at").on(table.createdAt),
}));

export const insertReferralClickSchema = createInsertSchema(referralClicks).omit({
  id: true,
  createdAt: true,
}).extend({
  referralCode: z.string().min(1),
});

export type InsertReferralClick = z.infer<typeof insertReferralClickSchema>;
export type ReferralClick = typeof referralClicks.$inferSelect;

// ─── Affiliate Quarterly Payouts (Task #2466) ────────────────────────────────
// Manual payout stub for affiliate/founder-circle partners. Admin records a
// quarterly payout amount per partner and marks it paid. One payout per
// partner+quarter (unique). This is intentionally a lightweight manual ledger,
// separate from the accrual-driven refer-host statements.
export const AFFILIATE_PAYOUT_STATUSES = ["pending", "paid"] as const;
export type AffiliatePayoutStatus = typeof AFFILIATE_PAYOUT_STATUSES[number];

export const affiliatePayouts = pgTable("affiliate_payouts", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull(),
  periodQuarter: text("period_quarter").notNull(),
  amountCents: integer("amount_cents").default(0).notNull(),
  status: text("status").default("pending").notNull(),
  notes: text("notes"),
  paidAt: timestamp("paid_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  partnerIdx: index("idx_affiliate_payouts_partner").on(table.partnerId),
  partnerQuarterUniq: uniqueIndex("affiliate_payouts_partner_quarter_uniq").on(table.partnerId, table.periodQuarter),
}));

export const insertAffiliatePayoutSchema = createInsertSchema(affiliatePayouts).omit({
  id: true,
  paidAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerId: z.number().int().positive(),
  periodQuarter: z.string().regex(/^\d{4}-Q[1-4]$/, "Quarter must look like 2026-Q1"),
  amountCents: z.number().int().nonnegative().default(0),
  status: z.enum(AFFILIATE_PAYOUT_STATUSES).default("pending").optional(),
  notes: z.string().max(2000).optional().nullable(),
});

export type InsertAffiliatePayout = z.infer<typeof insertAffiliatePayoutSchema>;
export type AffiliatePayout = typeof affiliatePayouts.$inferSelect;

export const signupSchema = z.object({
  fullName: z.string().min(1, "Full name is required").max(120, "Full name is too long"),
  email: z.string().email("Please enter a valid email").max(254, "Email is too long"),
  phone: z.string().regex(E164_PHONE_REGEX, E164_PHONE_MESSAGE),
  smsConsent: z.literal(true, { errorMap: () => ({ message: "You must agree to receive SMS messages." }) }),
  acceptedTerms: z.literal(true, { errorMap: () => ({ message: "You must agree to the Terms and Conditions." }) }),
  company: z.string().max(120, "Company name is too long").optional().or(z.literal("")),
  journeyStage: z.enum(JOURNEY_STAGES, {
    errorMap: () => ({ message: "Please select where you are in your CoLiving journey" }),
  }),
  numberOfHouses: z.coerce.number().optional().nullable(),
  totalRooms: z.coerce.number().optional().nullable(),
  // Plan / billing selections added by the login-page sign-up flow.
  // Optional so existing callers (e.g. older mobile clients) don't break.
  selectedPlan: z.enum(['Research', 'Host', 'Growth', 'Professional']).optional(),
  billingInterval: z.enum(['monthly', 'annual']).optional(),
  // Modular pricing: the account kind picked at signup (operator vs.
  // property manager). Optional so pre-modular clients keep working — when
  // absent, the kind is derived from the legacy selectedPlan.
  accountKind: z.enum(['operator', 'property_manager']).optional(),
});

export type SignupData = z.infer<typeof signupSchema>;
export type InsertAppUser = z.infer<typeof insertAppUserSchema>;
export type AppUser = typeof appUsers.$inferSelect;

// Task #689: Audit trail of every Suspend/Reinstate flip on an app user.
// Captures the previous status, the new status, the admin-supplied reason,
// and who performed the change so admins can later see *why* an account
// was suspended (and by whom). One row is written per status change.
export const appUserStatusEvents = pgTable("app_user_status_events", {
  id: serial("id").primaryKey(),
  appUserId: integer("app_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  previousStatus: text("previous_status").notNull(),
  newStatus: text("new_status").notNull(),
  reason: text("reason"),
  adminUserId: integer("admin_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  adminLabel: text("admin_label"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  appUserIdIdx: index("app_user_status_events_app_user_id_idx").on(table.appUserId, table.createdAt.desc()),
}));

export const insertAppUserStatusEventSchema = createInsertSchema(appUserStatusEvents).omit({
  id: true,
  createdAt: true,
}).extend({
  reason: z.string().optional().nullable(),
  adminUserId: z.number().int().optional().nullable(),
  adminLabel: z.string().optional().nullable(),
});

export type InsertAppUserStatusEvent = z.infer<typeof insertAppUserStatusEventSchema>;
export type AppUserStatusEvent = typeof appUserStatusEvents.$inferSelect;

// Task #1291: The set of values that may appear in `app_users.subscription_status`.
// These are not enforced as a Postgres enum (the column is `text`), but are the
// canonical list used by Stripe webhook handlers, the admin override tool, and
// the requireTier billing gate. `comped` is the only value that is *never* set
// by Stripe — it is reserved for manual admin overrides where a user has paid
// out-of-band (or is being granted free access) and therefore has no Stripe
// subscription. requireTier treats `active`, `trialing`, and `comped` as
// "subscription is good".
export const SUBSCRIPTION_STATUSES = [
  'none',
  'active',
  'trialing',
  'comped',
  'past_due',
  'canceled',
  // v3: Stripe paused the subscription (trial ended with no card) — read-only.
  'paused',
] as const;
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number];

// Task #1291: Audit trail for every admin-driven change to a user's
// `subscription_status`. Captures previous/new values, the admin-supplied
// reason (required by the API), and the acting admin so we have a real
// record of comp grants, manual cancellations, etc. Mirrors the
// app_user_status_events pattern used for Suspend/Reinstate.
export const appUserSubscriptionStatusEvents = pgTable("app_user_subscription_status_events", {
  id: serial("id").primaryKey(),
  appUserId: integer("app_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  previousStatus: text("previous_status").notNull(),
  newStatus: text("new_status").notNull(),
  reason: text("reason").notNull(),
  source: text("source").notNull(), // e.g. 'admin_override', 'grant_founder', 'founder_intent_activation'
  adminUserId: integer("admin_user_id"),
  adminLabel: text("admin_label"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  appUserIdIdx: index("app_user_subscription_status_events_app_user_id_idx").on(table.appUserId, table.createdAt.desc()),
  // The auto-generated name for this FK is 64 chars which Postgres silently
  // truncates to 63, causing drizzle to think the constraint is missing on
  // every push. Explicit short name keeps schema and DB in sync permanently.
  adminUserFk: foreignKey({
    name: "sub_status_events_admin_user_id_fk",
    columns: [table.adminUserId],
    foreignColumns: [appUsers.id],
  }).onDelete("set null"),
}));

export const insertAppUserSubscriptionStatusEventSchema = createInsertSchema(appUserSubscriptionStatusEvents).omit({
  id: true,
  createdAt: true,
}).extend({
  adminUserId: z.number().int().optional().nullable(),
  adminLabel: z.string().optional().nullable(),
});

export type InsertAppUserSubscriptionStatusEvent = z.infer<typeof insertAppUserSubscriptionStatusEventSchema>;
export type AppUserSubscriptionStatusEvent = typeof appUserSubscriptionStatusEvents.$inferSelect;

// Shape returned by GET /api/admin/app-users — base AppUser plus the
// most recent status-change event (if any) so the Admin Users tab can
// surface the latest suspension reason and date inline.
// Task #1291: Also includes the latest subscription-status override event
// so the admin UI can show who comped the user and why.
export type AppUserWithStatusEvent = AppUser & {
  latestStatusEvent: AppUserStatusEvent | null;
  latestSubscriptionStatusEvent: AppUserSubscriptionStatusEvent | null;
};

export const adminAuditLog = pgTable("admin_audit_log", {
  id: serial("id").primaryKey(),
  changedBy: text("changed_by").notNull(),
  targetEmail: text("target_email").notNull(),
  fieldChanged: text("field_changed").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export type AdminAuditLog = typeof adminAuditLog.$inferSelect;
export const insertAdminAuditLogSchema = createInsertSchema(adminAuditLog).omit({ id: true, changedAt: true });
export type InsertAdminAuditLog = z.infer<typeof insertAdminAuditLogSchema>;

export const billingProrationEvents = pgTable("billing_proration_events", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  roomsAdded: integer("rooms_added").notNull(),
  thermostatsAdded: integer("thermostats_added").notNull().default(0),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  eventAt: timestamp("event_at").defaultNow().notNull(),
  processedAt: timestamp("processed_at"),
  stripeInvoiceItemId: text("stripe_invoice_item_id"),
  notes: text("notes"),
});

export type BillingProrationEvent = typeof billingProrationEvents.$inferSelect;

export const TEAM_ROLES = ["owner", "admin", "member", "viewer"] as const;
export type TeamRole = typeof TEAM_ROLES[number];

// Task #3233 — Centralized team audit log action types. State-changing actions
// taken by team members within a tenant; only these are recorded (no read/view
// actions). Used both server-side (recordAuditEvent) and client-side (filter
// dropdown + human-readable labels).
export const AUDIT_ACTIONS = [
  "message_sent",
  "task_created",
  "task_updated",
  "form_submitted",
  "expense_logged",
  "vendor_paid",
  "vendor_payment_voided",
  "settings_changed",
  "member_profile_edited",
  "rule_created",
  "rule_modified",
  "task_batch_created",
  "task_batch_dispatched",
  "task_batch_cancelled",
  "task_reminded",
  "task_duplicated",
  "owner_approval_approved",
  "owner_approval_rejected",
] as const;
export type AuditAction = typeof AUDIT_ACTIONS[number];

export const PLAN_SEAT_LIMITS: Record<string, number | null> = {
  Research: 1,
  Host: 1,
  Growth: 3,
  Professional: 10,
  Enterprise: null,
  PMPro: null,
  Interest: 0,
  Founder25: 10,
  Founder50: 10,
  Founder100: 10,
};

export function getPlanSeatLimit(plan: string | null | undefined, billingModel?: string | null): number | null {
  // v3 per-property plans include unlimited users (backend plan D16).
  if (billingModel === "v3") return null;
  if (plan && Object.prototype.hasOwnProperty.call(PLAN_SEAT_LIMITS, plan)) {
    return PLAN_SEAT_LIMITS[plan];
  }
  return PLAN_SEAT_LIMITS["Host"];
}

export const founderIntents = pgTable("founder_intents", {
  id: serial("id").primaryKey(),
  email: text("email").notNull(),
  tier: text("tier").notNull(),
  ipHash: text("ip_hash"),
  paidStatus: text("paid_status").default("unpaid").notNull(),
  // Records HOW a founder intent became paid so the admin UI can distinguish
  // webhook-driven (Mercury AR `invoice.paid`) confirmations from a manual
  // admin toggle. 'mercury' = Mercury AR webhook, 'manual' = admin switch,
  // null = unknown/legacy (paid before this column existed). (Task #3243)
  paidSource: text("paid_source"),
  fullName: text("full_name"),
  paymentMethod: text("payment_method").default("invoice").notNull(),
  paymentReference: text("payment_reference"),
  paidAt: timestamp("paid_at"),
  accessGrantedAt: timestamp("access_granted_at"),
  agreementVersion: text("agreement_version"),
  agreementAcceptedAt: timestamp("agreement_accepted_at"),
  activatedAt: timestamp("activated_at"),
  activatedUserId: integer("activated_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  // Billing cycle chosen at registration (monthly | annual). Persisted to
  // app_users.billingCycle on activation.
  billingCycle: text("billing_cycle"),
  // Stripe PaymentMethod id captured at signup (optional — required before
  // overage billing can begin). Attached to the user's Stripe customer on activation.
  stripePaymentMethodId: text("stripe_payment_method_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  emailIdx: index("founder_intents_email_idx").on(table.email),
  createdAtIdx: index("founder_intents_created_at_idx").on(table.createdAt),
}));

export const insertFounderIntentSchema = createInsertSchema(founderIntents).omit({
  id: true,
  createdAt: true,
  activatedAt: true,
  activatedUserId: true,
  paidStatus: true,
  paidSource: true,
  paidAt: true,
  accessGrantedAt: true,
}).extend({
  email: z.string().email(),
  tier: z.enum(['Founder25', 'Founder50', 'Founder100']),
  ipHash: z.string().optional().nullable(),
  fullName: z.string().optional().nullable(),
  paymentMethod: z.enum(['invoice', 'card']).optional(),
  paymentReference: z.string().optional().nullable(),
  agreementVersion: z.string().optional().nullable(),
  agreementAcceptedAt: z.date().optional().nullable(),
  billingCycle: z.enum(['monthly', 'annual']).optional().nullable(),
  stripePaymentMethodId: z.string().optional().nullable(),
});

export type InsertFounderIntent = z.infer<typeof insertFounderIntentSchema>;
export type FounderIntent = typeof founderIntents.$inferSelect;

// Append-only audit trail for every paid/unpaid change on a founder intent.
// A founder intent becoming paid (or being reverted to unpaid) is a
// money-related action, so we record who flipped it (admin id + label, or the
// Mercury automation), the previous→new paid status, and when — mirroring the
// app_user_subscription_status_events pattern used for comp/cancel overrides.
// 'manual' = admin Switch toggle, 'mercury' = Mercury AR invoice webhook.
export const founderIntentPaidEvents = pgTable("founder_intent_paid_events", {
  id: serial("id").primaryKey(),
  founderIntentId: integer("founder_intent_id").notNull(),
  previousStatus: text("previous_status").notNull(),
  newStatus: text("new_status").notNull(),
  source: text("source").notNull(), // 'manual' | 'mercury'
  adminUserId: integer("admin_user_id"),
  adminLabel: text("admin_label"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  founderIntentIdIdx: index("founder_intent_paid_events_intent_id_idx").on(table.founderIntentId, table.createdAt.desc()),
  // Explicit short FK names (auto-generated names would exceed Postgres's 63-char
  // identifier limit and cause drizzle to re-emit DROP+ADD on every push).
  founderIntentFk: foreignKey({
    name: "founder_paid_events_intent_id_fk",
    columns: [table.founderIntentId],
    foreignColumns: [founderIntents.id],
  }).onDelete("cascade"),
  adminUserFk: foreignKey({
    name: "founder_paid_events_admin_user_id_fk",
    columns: [table.adminUserId],
    foreignColumns: [appUsers.id],
  }).onDelete("set null"),
}));

export const insertFounderIntentPaidEventSchema = createInsertSchema(founderIntentPaidEvents).omit({
  id: true,
  createdAt: true,
}).extend({
  adminUserId: z.number().int().optional().nullable(),
  adminLabel: z.string().optional().nullable(),
});

export type InsertFounderIntentPaidEvent = z.infer<typeof insertFounderIntentPaidEventSchema>;
export type FounderIntentPaidEvent = typeof founderIntentPaidEvents.$inferSelect;

// Shape returned by GET /api/admin/founder-intents — base FounderIntent plus
// the most recent paid-status change event (if any) so the Founder Signups tab
// can show who marked it paid without an extra round trip per row.
export type FounderIntentWithPaidEvent = FounderIntent & {
  latestPaidEvent: FounderIntentPaidEvent | null;
};

// Task #2039: Anonymous tier-CTA click tracking on /founders-circle. Captures
// every click on a tier "Pay by invoice"/"Pay by card" button even when the
// visitor bails out of the agreement modal without submitting an email, so we
// can measure top-of-funnel interest per tier and modal drop-off.
export const founderTierClicks = pgTable("founder_tier_clicks", {
  id: serial("id").primaryKey(),
  tier: text("tier").notNull(),
  ipHash: text("ip_hash"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  tierIdx: index("founder_tier_clicks_tier_idx").on(table.tier),
  createdAtIdx: index("founder_tier_clicks_created_at_idx").on(table.createdAt),
}));

export const insertFounderTierClickSchema = createInsertSchema(founderTierClicks).omit({
  id: true,
  createdAt: true,
}).extend({
  tier: z.enum(['Founder25', 'Founder50', 'Founder100']),
  ipHash: z.string().optional().nullable(),
});
export type InsertFounderTierClick = z.infer<typeof insertFounderTierClickSchema>;
export type FounderTierClick = typeof founderTierClicks.$inferSelect;

export const founderTierClickRequestSchema = z.object({
  tier: z.enum(['Founder25', 'Founder50', 'Founder100']),
});
export type FounderTierClickRequest = z.infer<typeof founderTierClickRequestSchema>;

export const founderIntentRequestSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  tier: z.enum(['Founder25', 'Founder50', 'Founder100']),
});
export type FounderIntentRequest = z.infer<typeof founderIntentRequestSchema>;

export const founderRegisterSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required").max(120),
  email: z.string().email("Please enter a valid email"),
  tier: z.enum(['Founder25', 'Founder50', 'Founder100']),
  paymentMethod: z.enum(['invoice', 'card']),
  agreementVersion: z.string().min(1, "Agreement version required"),
  billingCycle: z.enum(['monthly', 'annual']).default('monthly'),
  stripePaymentMethodId: z.string({ required_error: "A Stripe payment method is required for overage billing" }).min(1, "A Stripe payment method is required for overage billing"),
});
export type FounderRegisterInput = z.infer<typeof founderRegisterSchema>;

export const grantFounderSchema = z.object({
  tier: z.enum(['Founder25', 'Founder50', 'Founder100']),
});
export type GrantFounderInput = z.infer<typeof grantFounderSchema>;

export const TEAM_MEMBER_STATUSES = ["pending", "accepted", "declined"] as const;
export type TeamMemberStatus = typeof TEAM_MEMBER_STATUSES[number];

export const APP_AREAS = ["messages", "members", "properties", "underwriting", "image_generator", "pricing", "maintenance", "financials", "devices", "furnishing", "team", "invite", "padsplit_sync", "owners"] as const;
export type AppArea = typeof APP_AREAS[number];

export const PERMISSION_LEVELS = ["disabled", "view", "edit"] as const;
export type PermissionLevel = typeof PERMISSION_LEVELS[number];

export type AreaPermissions = Record<AppArea, PermissionLevel>;

export const DEFAULT_PERMISSIONS: AreaPermissions = {
  properties: "view",
  underwriting: "view",
  image_generator: "view",
  messages: "disabled",
  pricing: "disabled",
  maintenance: "view",
  devices: "view",
  financials: "view",
  members: "view",
  furnishing: "view",
  padsplit_sync: "disabled",
  team: "disabled",
  invite: "disabled",
  owners: "disabled",
};

const areaPermissionSchema = z.object({
  properties: z.enum(PERMISSION_LEVELS).default("view"),
  underwriting: z.enum(PERMISSION_LEVELS).default("view"),
  image_generator: z.enum(PERMISSION_LEVELS).default("view"),
  messages: z.enum(PERMISSION_LEVELS).default("disabled"),
  pricing: z.enum(PERMISSION_LEVELS).default("disabled"),
  maintenance: z.enum(PERMISSION_LEVELS).default("view"),
  devices: z.enum(PERMISSION_LEVELS).default("view"),
  financials: z.enum(PERMISSION_LEVELS).default("view"),
  members: z.enum(PERMISSION_LEVELS).default("view"),
  furnishing: z.enum(PERMISSION_LEVELS).default("view"),
  padsplit_sync: z.enum(PERMISSION_LEVELS).default("disabled"),
  team: z.enum(PERMISSION_LEVELS).default("disabled"),
  invite: z.enum(PERMISSION_LEVELS).default("disabled"),
  owners: z.enum(PERMISSION_LEVELS).default("disabled"),
});

export const teamMembers = pgTable("team_members", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  inviteeUserId: integer("invitee_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  email: text("email").notNull(),
  name: text("name"),
  role: text("role").default("member").notNull(),
  permissions: jsonb("permissions").$type<AreaPermissions>().default(sql`'{}'::jsonb`),
  propertyGroups: jsonb("property_groups").$type<string[] | null>(),
  // Per-member booking-request alert preferences (Task #2563). Default true =
  // current behavior (every members-area teammate is notified). Hosts can opt
  // an individual teammate out of the bell and/or email fan-out.
  bookingRequestBellAlerts: boolean("booking_request_bell_alerts").default(true).notNull(),
  bookingRequestEmailAlerts: boolean("booking_request_email_alerts").default(true).notNull(),
  status: text("status").default("pending").notNull(),
  invitedAt: timestamp("invited_at").defaultNow().notNull(),
  acceptedAt: timestamp("accepted_at"),
  removedAt: timestamp("removed_at"),
  lastInviteSentAt: timestamp("last_invite_sent_at"),
}, (table) => ({
  inviteeUserIdIdx: index("idx_team_members_invitee_user_id").on(table.inviteeUserId),
  emailStatusIdx: index("idx_team_members_email_status").on(table.email, table.status),
}));

export const insertTeamMemberSchema = createInsertSchema(teamMembers).omit({
  id: true,
  invitedAt: true,
  acceptedAt: true,
  removedAt: true,
  lastInviteSentAt: true,
}).extend({
  email: z.string().email("Please enter a valid email").max(254, "Email is too long"),
  name: z.string().max(120, "Name is too long").optional().or(z.literal("")),
  role: z.string().max(60, "Role name is too long").default("member").optional(),
  permissions: areaPermissionSchema.optional(),
  propertyGroups: z.array(z.string()).nullable().optional(),
  status: z.enum(["pending", "accepted", "declined", "removed"]).default("pending").optional(),
  inviteeUserId: z.number().nullable().optional(),
});

export type InsertTeamMember = z.infer<typeof insertTeamMemberSchema>;
export type TeamMember = typeof teamMembers.$inferSelect;

export const TEAM_MANAGER_ROLES = ["owner", "admin"] as const;
export function canManageTeam(role: string | null | undefined): boolean {
  if (!role) return false;
  return (TEAM_MANAGER_ROLES as readonly string[]).includes(role);
}

export const roleProfiles = pgTable("role_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  permissions: jsonb("permissions").$type<AreaPermissions>().notNull(),
  propertyGroups: jsonb("property_groups").$type<string[] | null>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRoleProfileSchema = createInsertSchema(roleProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1, "Profile name is required"),
  permissions: areaPermissionSchema,
  propertyGroups: z.array(z.string()).nullable().optional(),
});

export type InsertRoleProfile = z.infer<typeof insertRoleProfileSchema>;
export type RoleProfile = typeof roleProfiles.$inferSelect;

export const authCodes = pgTable("auth_codes", {
  id: serial("id").primaryKey(),
  email: text("email").notNull(),
  code: text("code").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  used: boolean("used").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertAuthCodeSchema = createInsertSchema(authCodes).omit({
  id: true,
  createdAt: true,
});
export type InsertAuthCode = z.infer<typeof insertAuthCodeSchema>;
export type AuthCode = typeof authCodes.$inferSelect;

// Task #3972 — SMS one-time codes for phone verification. Keyed by normalized
// E.164 phone + purpose ("signup" before an account exists, "profile" for an
// authenticated existing user). Short-lived, single-use (used_at), rate-limited
// at the route layer. Mirrors the email auth_codes / vendor_auth_codes pattern.
export const phoneVerificationCodes = pgTable("phone_verification_codes", {
  id: serial("id").primaryKey(),
  normalizedPhone: text("normalized_phone").notNull(),
  code: text("code").notNull(),
  purpose: text("purpose").notNull().default("signup"),
  appUserId: integer("app_user_id").references(() => appUsers.id, { onDelete: "cascade" }),
  expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
  usedAt: timestamp("used_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (t) => ({
  phoneIdx: index("phone_verification_codes_phone_idx").on(t.normalizedPhone),
  expiresAtIdx: index("phone_verification_codes_expires_at_idx").on(t.expiresAt),
}));
export type PhoneVerificationCode = typeof phoneVerificationCodes.$inferSelect;

export const loginHistory = pgTable("login_history", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceHash: text("device_hash").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  loginAt: timestamp("login_at").defaultNow().notNull(),
}, (table) => ({
  userDeviceIdx: index("idx_login_history_user_device").on(table.userId, table.deviceHash),
}));

export type LoginHistory = typeof loginHistory.$inferSelect;

export const trustedDevices = pgTable("trusted_devices", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceHash: text("device_hash").notNull(),
  userAgent: text("user_agent"),
  lastIp: text("last_ip"),
  lastLocation: text("last_location"),
  firstSeenAt: timestamp("first_seen_at", { withTimezone: true }).defaultNow().notNull(),
  lastSeenAt: timestamp("last_seen_at", { withTimezone: true }).defaultNow().notNull(),
  trustedAt: timestamp("trusted_at", { withTimezone: true }),
}, (table) => ({
  userIdx: index("idx_trusted_devices_user").on(table.userId),
  userHashIdx: index("idx_trusted_devices_user_hash").on(table.userId, table.deviceHash),
}));

export type TrustedDevice = typeof trustedDevices.$inferSelect;

// Task #3972 — Web Push (VAPID) subscriptions. One row per browser/device push
// endpoint. `endpoint` is the browser push-service URL (globally unique);
// p256dh + auth are the client keys web-push needs to encrypt a payload. Dead
// endpoints (404/410 on send) are pruned by the dispatcher.
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  endpoint: text("endpoint").notNull(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
}, (t) => ({
  endpointUnique: uniqueIndex("push_subscriptions_endpoint_unique").on(t.endpoint),
  userIdx: index("push_subscriptions_user_idx").on(t.userId),
}));
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
});
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type PushSubscriptionRecord = typeof pushSubscriptions.$inferSelect;

// Per-user, per-day activity tracking. One row per (user, date); page_hits is
// incremented on each authenticated API hit via INSERT … ON CONFLICT DO UPDATE
// so writes are cheap and the table stays narrow. Only real user sessions are
// recorded — impersonation and demo orgs are excluded at the middleware layer.
export const userActivityLog = pgTable("user_activity_log", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  activityDate: date("activity_date").notNull(),
  // Coarse feature section the request hit (e.g. "properties", "rooms",
  // "financials"), derived from the `/api/app/<section>` path prefix. Nullable
  // for legacy rows written before this column existed; new writes always pass
  // a non-null value so the per-(user, day, section) upsert dedups correctly.
  section: text("section"),
  pageHits: integer("page_hits").notNull().default(1),
  // Wall-clock time of the most recent request recorded for this
  // (user, day, section). Used to collapse a burst of queries + interval
  // polls into a single "visit": a page-hit is only counted when the gap
  // since this timestamp exceeds the visit window (see upsertUserActivity),
  // so leaving a tab open polling in the background no longer inflates the
  // count. Nullable for legacy rows written before this column existed.
  lastSeenAt: timestamp("last_seen_at"),
}, (t) => ({
  userDateSectionUnique: uniqueIndex("user_activity_log_user_date_section_unique").on(t.userId, t.activityDate, t.section),
  userIdx: index("user_activity_log_user_idx").on(t.userId),
  dateIdx: index("user_activity_log_date_idx").on(t.activityDate),
}));
export type UserActivityLog = typeof userActivityLog.$inferSelect;

export const ENGAGEMENT_BUCKETS = ["Active", "Cooling off", "At risk", "Dormant"] as const;
export type EngagementBucket = typeof ENGAGEMENT_BUCKETS[number];

export type UserActivityStats = {
  lastActive: string | null;
  daysSinceActive: number | null;
  activeDays7: number;
  activeDays30: number;
  avgPageHitsPerDay7: number;
  avgPageHitsPerDay30: number;
  engagementBucket: EngagementBucket;
  // Distinct coarse sections this user has ever touched (all-time), e.g.
  // ["dashboard", "properties"]. NULL-section legacy rows surface as "other".
  // Lets the Admin Users table filter to dashboard-only / never-touched-X users
  // in place without an extra round trip per row.
  touchedSections: string[];
};

// One calendar day of activity for a single user, used by the admin
// churn-watch drill-down. `hits` aggregates page_hits across all sections for
// that day; the series is always zero-filled for every day in the window so the
// bar chart shows gaps as empty bars rather than collapsing them.
export type DetailedUserActivityDay = {
  date: string;
  hits: number;
};

// Payload for GET /api/admin/app-users/:id/activity — a zero-filled 30-day
// daily hit series plus the same aggregate stats that drive the engagement
// bucket in the users table, so the drill-down and the row badge always agree.
export type DetailedUserActivity = {
  days: DetailedUserActivityDay[];
  stats: UserActivityStats;
};

// Per-user snapshot of the last engagement bucket the churn-risk digest cron
// observed. The daily cron compares each user's freshly-computed bucket against
// this stored value to detect a worsening transition (e.g. Cooling off → At
// risk, At risk → Dormant) and only alerts on the delta. One row per user; the
// row is upserted every run so the next run compares against the latest known
// bucket. Seeded silently on a user's first observation so an existing backlog
// of already-dormant users never floods the first digest.
export const userEngagementAlertState = pgTable("user_engagement_alert_state", {
  userId: integer("user_id").primaryKey().references(() => appUsers.id, { onDelete: "cascade" }),
  lastBucket: text("last_bucket").notNull(),
  checkedAt: timestamp("checked_at").notNull().defaultNow(),
});
export type UserEngagementAlertState = typeof userEngagementAlertState.$inferSelect;

// Per-feature-section usage breakdown for a single user, grouped from
// user_activity_log. One entry per coarse section the user ever touched
// (e.g. "properties", "financials"), with hit totals for the last 7/30 days
// and all-time. `lastActive` is the most recent day that section was hit.
export type UserSectionActivityEntry = {
  section: string;
  hits7: number;
  hits30: number;
  hitsAll: number;
  lastActive: string | null;
};

export type UserSectionActivity = {
  sections: UserSectionActivityEntry[];
  totalHitsAll: number;
};

export const PADSPLIT_PROPERTY_TYPES = ["House", "Apartments", "ADU", "Townhouse", "Duplex/Multiplex", "Other"] as const;
export type PadsplitPropertyType = typeof PADSPLIT_PROPERTY_TYPES[number];

export const RENOVATION_STATUSES = ["not_started", "in_progress", "complete"] as const;
export type RenovationStatus = typeof RENOVATION_STATUSES[number];

// The full set of values that may appear in `properties.status`. This is the
// single source of truth — `insertPropertySchema.status` derives its enum from
// this array, so adding a status here is the only place to declare a new one.
export const PROPERTY_STATUSES = ["active", "underwriting", "renovations", "archive", "launch", "acquisition", "sold", "disposed"] as const;
export type PropertyStatus = typeof PROPERTY_STATUSES[number];

// Statuses that mark a property as inactive/terminal — these properties are
// excluded from Pulse briefing signals so e.g. an archived (or sold/disposed)
// property does not keep surfacing.
export const INACTIVE_PROPERTY_STATUSES = ["archive", "sold", "disposed"] as const satisfies readonly PropertyStatus[];
export type InactivePropertyStatus = typeof INACTIVE_PROPERTY_STATUSES[number];

// Statuses that mark a property as active (eligible for Pulse briefing signals).
export const ACTIVE_PROPERTY_STATUSES = ["active", "underwriting", "renovations", "launch", "acquisition"] as const satisfies readonly PropertyStatus[];
export type ActivePropertyStatus = typeof ACTIVE_PROPERTY_STATUSES[number];

// Compile-time exhaustiveness guard: every PropertyStatus MUST be classified as
// exactly one of active or inactive. If you add a new value to
// PROPERTY_STATUSES without also adding it to ACTIVE_PROPERTY_STATUSES or
// INACTIVE_PROPERTY_STATUSES, the type alias below fails to compile and forces
// you to decide whether the new status keeps surfacing in Pulse briefings.
type _AssertTrue<T extends true> = T;
type _PropertyStatusesFullyClassified = _AssertTrue<
  [PropertyStatus] extends [ActivePropertyStatus | InactivePropertyStatus] ? true : false
>;
type _PropertyStatusesDisjoint = _AssertTrue<
  [ActivePropertyStatus & InactivePropertyStatus] extends [never] ? true : false
>;

const INACTIVE_PROPERTY_STATUS_SET: ReadonlySet<string> = new Set(INACTIVE_PROPERTY_STATUSES);

// Naming convention for terminal statuses. Any status whose name implies the
// property is no longer operating (sold, disposed, archived, closed, …) is
// considered terminal and MUST stay out of Pulse briefings. These substrings
// power two safety nets below: a load-time assertion that a declared terminal
// status was actually classified inactive, and a runtime fail-closed default in
// `isActivePulseProperty` for terminal-looking statuses that slipped through
// unmapped.
const TERMINAL_PROPERTY_STATUS_HINTS = [
  "sold",
  "dispos", // dispose / disposed / disposition
  "archiv", // archive / archived
  "closed",
  "terminat", // terminate / terminated
  "delet", // delete / deleted
  "cancel", // cancel / cancelled
  "withdraw", // withdraw / withdrawn
  "expired",
  "inactive",
  "dead",
] as const;

// True when a status name looks terminal (property no longer operating).
export function looksTerminalPropertyStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return TERMINAL_PROPERTY_STATUS_HINTS.some((hint) => s.includes(hint));
}

// Load-time convention check (Task #2853): the compile-time exhaustiveness guard
// above forces every new PROPERTY_STATUSES value to be classified active xor
// inactive, but it cannot tell whether a terminal-sounding status (e.g. a future
// "sold"/"disposed") was carelessly dropped into ACTIVE_PROPERTY_STATUSES. This
// asserts that any declared status whose name looks terminal is classified
// inactive so it stays out of briefings. PROPERTY_STATUSES is a compile-time
// constant, so this is deterministic — if it passes in CI it passes in prod.
for (const status of PROPERTY_STATUSES) {
  if (looksTerminalPropertyStatus(status) && !INACTIVE_PROPERTY_STATUS_SET.has(status)) {
    throw new Error(
      `Property status "${status}" looks terminal but is not in INACTIVE_PROPERTY_STATUSES. ` +
        `Add it to INACTIVE_PROPERTY_STATUSES so it stays out of Pulse briefings.`,
    );
  }
}

// Returns true when a property should be included in Pulse briefing signals.
// Accepts any object with an optional `status` field (rows are loosely typed in
// some call sites).
//
// Failure modes (Task #2853):
//  - missing/null/empty status      -> fail OPEN (active): a loosely-typed or
//    legacy row should not silently disappear from a briefing.
//  - declared inactive status       -> fail CLOSED (dropped).
//  - terminal-looking unmapped status (e.g. a future "sold"/"disposed" that was
//    added without updating INACTIVE_PROPERTY_STATUSES) -> fail CLOSED, so a
//    terminal status never leaks into briefings by accident.
//  - any other unknown status       -> fail OPEN (active), preserving the
//    benign-typo tolerance above.
export function isActivePulseProperty(p: { status?: string | null } | null | undefined): boolean {
  const status = p?.status;
  if (!status) return true;
  if (INACTIVE_PROPERTY_STATUS_SET.has(status)) return false;
  if (looksTerminalPropertyStatus(status)) return false;
  return true;
}

export const properties = pgTable("properties", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  address: text("address"),
  city: text("city"),
  roomCount: integer("room_count").default(0).notNull(),
  bathroomCount: integer("bathroom_count").default(0).notNull(),
  propertyGroup: text("property_group"),
  purchaseDate: text("purchase_date"),
  purchasePrice: text("purchase_price"),
  renovationDate: text("renovation_date"),
  launchDate: text("launch_date"),
  saleDate: text("sale_date"),
  salePrice: text("sale_price"),
  rate: text("rate"),
  description: text("description"),
  floorPlanUrl: text("floor_plan_url"),
  tourUrl: text("tour_url"),
  videoWalkthroughUrl: text("video_walkthrough_url"),
  status: text("status").default("active").notNull(),
  timezone: text("timezone"),
  padsplitPropertyId: text("padsplit_property_id"),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  yearBuilt: integer("year_built"),
  lotSize: text("lot_size"),
  propertyType: text("property_type"),
  managementType: text("management_type").default("padsplit"),
  wifiName: text("wifi_name"),
  wifiPassword: text("wifi_password"),
  currentTemperature: text("current_temperature"),
  nextCleanerDate: text("next_cleaner_date"),
  knowledgeBase: text("knowledge_base"),
  aiMemory: text("ai_memory"),
  parentPropertyId: integer("parent_property_id"),
  padsplitPropertyType: text("padsplit_property_type"),
  ownerOccupancy: boolean("owner_occupancy"),
  minWeeklyPrice: text("min_weekly_price"),
  maxWeeklyPrice: text("max_weekly_price"),
  launchedAt: timestamp("launched_at"),
  renovationStatus: text("renovation_status"),
  renovationNotes: text("renovation_notes"),
  launchWizardData: jsonb("launch_wizard_data"),
  launchWizardStep: text("launch_wizard_step"),
  roomPricingStrategy: text("room_pricing_strategy"),
  renovationStartDate: text("renovation_start_date"),
  permitsRequired: boolean("permits_required").default(false),
  permitDays: integer("permit_days").default(45),
  projectedLaunchDate: text("projected_launch_date"),
  renovationLocked: boolean("renovation_locked").default(false),
  sopLink: text("sop_link"),
  ispProvider: text("isp_provider"),
  // Member portal — Waste Pickup + ISP outage link (host-managed, surfaced on
  // the member dashboard's "Your House" card). All nullable/additive.
  trashPickupDay: text("trash_pickup_day"),
  recyclingPickupDay: text("recycling_pickup_day"),
  ispOutageUrl: text("isp_outage_url"),
  ispLabel: text("isp_label"),
  // Member portal — Digital House Guide. Host-authored house rules (plain text /
  // simple markdown) plus a list of shared documents (lease, appliance manuals,
  // house manual) stored in object storage with a public-read ACL so members —
  // who hold member sessions, not app sessions — can open them. Both additive.
  houseRules: text("house_rules"),
  houseDocuments: jsonb("house_documents").$type<{ name: string; path: string }[]>().default([]).notNull(),
  state: text("state"),
  country: text("country"),
  postalCode: text("postal_code"),
  thermostatHealthThresholdF: integer("thermostat_health_threshold_f").default(2).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  lastHarvested: timestamp("last_harvested"),
  // Task #3824 — Terminal-status Mercury-card cancel prompt. We NEVER cancel a
  // Mercury card automatically; instead, when a property enters a terminal
  // status (archive/sold/disposed) with linked Mercury cards we ask the host
  // whether to cancel. `mercuryCardPromptStatus` tracks that decision:
  //   'pending'   — host has been asked (notification surfaced), awaiting choice
  //   'snoozed'   — "Remind me in 30 days" (re-surfaced by the daily cron when
  //                 `mercuryCardPromptSnoozedUntil` passes)
  //   'dismissed' — "Not now" (never re-asked unless the property re-enters a
  //                 terminal status)
  //   null        — no decision pending (cleared on "Yes"/cancel, on leaving a
  //                 terminal status, or when no cards remain linked)
  mercuryCardPromptStatus: text("mercury_card_prompt_status"),
  mercuryCardPromptSnoozedUntil: timestamp("mercury_card_prompt_snoozed_until"),
  // Task #4650 — Designated PadSplit sender for background/cron sends to this
  // property's members. When set (and still a linked owner of the member),
  // warm-browser sends route through this host's PadSplit browser session
  // instead of the collapsed lowest-linked host (member.userId). Nullable —
  // NULL means "no designation, fall back to member.userId".
  padsplitSenderUserId: integer("padsplit_sender_user_id"),
  // Task #4705 — PadSplit auto-approval window for booking requests on this
  // property (hours). Must mirror the host's PadSplit setting (24h default,
  // 48h option). Anchors the "Booking Form Not Completed" follow-up trigger:
  // follow-up sendAt = request arrival + this window − 2h.
  autoApprovalWindowHours: integer("auto_approval_window_hours").default(24).notNull(),
}, (table) => ({
  // Long auto-generated name kept in sync with the production constraint
  // name (see scripts/deploy-migrate.sql). Renaming would force db:push to
  // drop+recreate the FK against live data.
  parentPropertyFk: foreignKey({
    name: "properties_parent_property_id_properties_id_fk",
    columns: [table.parentPropertyId],
    foreignColumns: [table.id],
  }).onDelete("set null"),
  // Task #4650 — explicit short name (auto-generated one would exceed 63 chars
  // conventions; every FK gets an explicit name per repo convention).
  padsplitSenderFk: foreignKey({
    name: "properties_padsplit_sender_user_id_fk",
    columns: [table.padsplitSenderUserId],
    foreignColumns: [appUsers.id],
  }).onDelete("set null"),
}));

export const propertyGroups = pgTable("property_groups", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  color: text("color"),
  ownerId: integer("owner_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdIdx: index("property_groups_user_id_idx").on(table.userId),
}));

export const insertPropertyGroupSchema = createInsertSchema(propertyGroups).omit({ id: true, createdAt: true }).extend({
  name: z.string().trim().min(1, "Group name is required").max(80, "Group name is too long"),
  color: z.string().max(20).optional().nullable(),
});
export type InsertPropertyGroup = z.infer<typeof insertPropertyGroupSchema>;
export type PropertyGroup = typeof propertyGroups.$inferSelect;

const optionalIanaTimezone = z
  .string()
  .refine((tz) => {
    if (!tz) return true;
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, "Must be a valid IANA timezone")
  .optional()
  .nullable();

export const insertPropertySchema = createInsertSchema(properties).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  // Server-managed prompt state — never set from a user-supplied insert/update
  // body (bulk import or PATCH). Mutated only via storage.setMercuryCardPromptState.
  mercuryCardPromptStatus: true,
  mercuryCardPromptSnoozedUntil: true,
}).extend({
  name: z.string().min(1, "Property name is required"),
  address: z.string().optional(),
  city: z.string().optional().nullable(),
  roomCount: z.coerce.number().min(0).default(0),
  bathroomCount: z.coerce.number().min(0).default(0),
  propertyGroup: z.string().optional(),
  purchaseDate: z.string().optional(),
  purchasePrice: z.string().optional(),
  renovationDate: z.string().optional(),
  launchDate: z.string().optional(),
  saleDate: z.string().optional().nullable(),
  salePrice: z.string().optional().nullable(),
  rate: z.string().optional(),
  description: z.string().optional(),
  floorPlanUrl: z.string().optional(),
  tourUrl: z.string().optional(),
  videoWalkthroughUrl: z.string().trim().refine((value) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return (url.protocol === "http:" || url.protocol === "https:") && !!url.hostname && !url.username && !url.password;
    } catch { return false; }
  }, "Use an HTTP or HTTPS video URL").nullable().optional(),
  status: z.enum(PROPERTY_STATUSES).default("active"),
  timezone: optionalIanaTimezone,
  padsplitPropertyId: z.string().optional(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  yearBuilt: z.coerce.number().optional(),
  lotSize: z.string().optional(),
  propertyType: z.string().optional(),
  knowledgeBase: z.string().optional().nullable(),
  parentPropertyId: z.number().optional().nullable(),
  padsplitPropertyType: z.string().optional().nullable(),
  ownerOccupancy: z.boolean().optional().nullable(),
  minWeeklyPrice: z.string().optional().nullable(),
  maxWeeklyPrice: z.string().optional().nullable(),
  launchedAt: z.coerce.date().optional().nullable(),
  // Task #4705 — only the two windows PadSplit offers.
  autoApprovalWindowHours: z.coerce.number().int()
    .refine((v) => v === 24 || v === 48, "Auto-approval window must be 24 or 48 hours")
    .default(24),
  renovationStatus: z.string().optional().nullable(),
  renovationNotes: z.string().optional().nullable(),
  launchWizardData: z.any().optional().nullable(),
  launchWizardStep: z.string().optional().nullable(),
  roomPricingStrategy: z.string().optional().nullable(),
  renovationStartDate: z.string().optional().nullable(),
  permitsRequired: z.boolean().optional().nullable(),
  permitDays: z.coerce.number().optional().nullable(),
  projectedLaunchDate: z.string().optional().nullable(),
  renovationLocked: z.boolean().optional().nullable(),
  sopLink: z.string().url("Must be a valid URL").or(z.literal("")).optional().nullable(),
  ispProvider: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  thermostatHealthThresholdF: z.coerce.number().int().min(1).max(10).default(2),
});

export type InsertProperty = z.infer<typeof insertPropertySchema>;
export type InsertPropertyWithCoords = InsertProperty & { latitude?: number | null; longitude?: number | null };
export type Property = typeof properties.$inferSelect;

export const appUsersProperties = pgTable("app_users_properties", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  role: text("role").default("owner").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userPropertyUniq: uniqueIndex("app_users_properties_user_property_idx").on(table.userId, table.propertyId),
}));

export const insertAppUsersPropertySchema = createInsertSchema(appUsersProperties).omit({
  id: true,
  createdAt: true,
});

export type InsertAppUsersProperty = z.infer<typeof insertAppUsersPropertySchema>;
export type AppUsersProperty = typeof appUsersProperties.$inferSelect;

export const underwritingAnalyses = pgTable("underwriting_analyses", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().unique().references(() => properties.id, { onDelete: "cascade" }),
  zipCode: text("zip_code"),
  currentBedrooms: integer("current_bedrooms"),
  currentBathrooms: integer("current_bathrooms"),
  futureBedrooms: integer("future_bedrooms"),
  futureBathrooms: integer("future_bathrooms"),
  privateBaths: integer("private_baths"),
  listingLink: text("listing_link"),
  neighborhoodRating: text("neighborhood_rating"),
  floorPlanLink: text("floor_plan_link"),
  propertyReportLink: text("property_report_link"),
  walkthroughVideoLink: text("walkthrough_video_link"),
  gdriveFolderLink: text("gdrive_folder_link"),
  sqft: integer("sqft"),
  garageSqft: integer("garage_sqft"),
  noGarage: boolean("no_garage"),
  propertyNotes: text("property_notes"),
  roomAdditionsQty: integer("room_additions_qty"),
  roomAdditionsCost: text("room_additions_cost"),
  bathAdditionsQty: integer("bath_additions_qty"),
  bathAdditionsCost: text("bath_additions_cost"),
  additionalRehabAmount: text("additional_rehab_amount"),
  bufferPercent: text("buffer_percent"),
  kitchenQty: integer("kitchen_qty"),
  kitchenAmount: text("kitchen_amount"),
  hvacQty: integer("hvac_qty"),
  hvacAmount: text("hvac_amount"),
  roofQty: integer("roof_qty"),
  roofAmount: text("roof_amount"),
  plumbingQty: integer("plumbing_qty"),
  plumbingAmount: text("plumbing_amount"),
  foundationRepairQty: integer("foundation_repair_qty"),
  foundationRepairAmount: text("foundation_repair_amount"),
  otherQty: integer("other_qty"),
  otherAmount: text("other_amount"),
  homePurchasePrice: text("home_purchase_price"),
  downpaymentPercent: text("downpayment_percent"),
  closingCostsPercent: text("closing_costs_percent"),
  furnishings: text("furnishings"),
  tempHoldingCost: text("temp_holding_cost"),
  dealSponsorFee: text("deal_sponsor_fee"),
  invoiceAuthorization: text("invoice_authorization"),
  monthsUntilOccupied: integer("months_until_occupied"),
  annualInterestRate: text("annual_interest_rate"),
  loanTermYears: integer("loan_term_years"),
  vacancyPercent: text("vacancy_percent"),
  landValue: text("land_value"),
  annualAppreciationAvg: text("annual_appreciation_avg"),
  padsplitManagementFeePercent: text("padsplit_management_fee_percent"),
  pmFeePercent: text("pm_fee_percent"),
  pmFeeType: text("pm_fee_type").default("percent"),
  padsplitZipLink: text("padsplit_zip_link"),
  furnishedSharedWeekly: text("furnished_shared_weekly"),
  furnishedSharedMonthly: text("furnished_shared_monthly"),
  furnishedPrivateWeekly: text("furnished_private_weekly"),
  furnishedPrivateMonthly: text("furnished_private_monthly"),
  roomsData: jsonb("rooms_data"),
  sewerWater: text("sewer_water"),
  gas: text("gas"),
  electricity: text("electricity"),
  internet: text("internet"),
  lawnCare: text("lawn_care"),
  cleaning: text("cleaning"),
  capitalImprovementsBudget: text("capital_improvements_budget"),
  miscRepairs: text("misc_repairs"),
  hoa: text("hoa"),
  annualPropertyTax: text("annual_property_tax"),
  annualHomeownersInsurance: text("annual_homeowners_insurance"),
  landValuePercent: text("land_value_percent"),
  annualPropertyTaxPercent: text("annual_property_tax_percent"),
  annualInsurancePercent: text("annual_insurance_percent"),
  rentEstimateLow: text("rent_estimate_low"),
  rentEstimateHigh: text("rent_estimate_high"),
  rentEstimateMedian: text("rent_estimate_median"),
  ownerName: text("owner_name"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  sourceType: text("source_type"),
  sourceListingId: integer("source_listing_id"),
  sourceArv: text("source_arv"),
});

const roomDataSchema = z.object({
  rent: z.string().optional(),
  notes: z.string().optional(),
  furnished: z.boolean().optional(),
  privateEntrance: z.boolean().optional(),
  privateBath: z.boolean().optional(),
  additions: z.string().optional(),
});

export const insertUnderwritingAnalysisSchema = createInsertSchema(underwritingAnalyses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  propertyId: z.number(),
  roomsData: z.array(roomDataSchema).optional(),
});

export type InsertUnderwritingAnalysis = z.infer<typeof insertUnderwritingAnalysisSchema>;
export type UnderwritingAnalysis = typeof underwritingAnalyses.$inferSelect;

export const roomPricingModels = pgTable("room_pricing_models", {
  id: serial("id").primaryKey(),
  sourcePricingModelId: integer("source_pricing_model_id"),
  ownerId: integer("owner_id"),
  name: varchar("name", { length: 255 }).notNull(),
  preChangeBoostPercent: numeric("pre_change_boost_percent"),
  minChangePercent: numeric("min_change_percent", { precision: 5, scale: 2 }),
  maxChangePercent: numeric("max_change_percent", { precision: 5, scale: 2 }),
  changeRatePercent: numeric("change_rate_percent", { precision: 5, scale: 2 }),
  changeRateHours: integer("change_rate_hours"),
  hasPromotion: boolean("has_promotion").default(false),
  promotionDiscountPercent: numeric("promotion_discount_percent", { precision: 5, scale: 2 }),
  promotionDurationWeeks: integer("promotion_duration_weeks"),
  eomAdjustmentRate: numeric("eom_adjustment_rate", { precision: 5, scale: 2 }),
  eomAdjustmentDays: integer("eom_adjustment_days"),
  vacancyAdjustmentPercent: numeric("vacancy_adjustment_percent", { precision: 5, scale: 2 }),
  vacancyAdjustmentDays: integer("vacancy_adjustment_days"),
  occupancyAdjustmentPercent: numeric("occupancy_adjustment_percent", { precision: 5, scale: 2 }),
  waiveMoveIn: boolean("waive_move_in").default(false),
  moveInFee: numeric("move_in_fee", { precision: 10, scale: 2 }).default("0"),
  createdAt: timestamp("created_at").default(sql`CURRENT_TIMESTAMP`),
  updatedAt: timestamp("updated_at").default(sql`CURRENT_TIMESTAMP`),
}, (t) => ({
  sourceUnique: uniqueIndex("rpm_source_unique").on(t.sourcePricingModelId),
  sourceFk: foreignKey({ name: "rpm_source_fk", columns: [t.sourcePricingModelId], foreignColumns: [pricingModels.id] }).onDelete("cascade"),
  ownerFk: foreignKey({ name: "rpm_owner_fk", columns: [t.ownerId], foreignColumns: [appUsers.id] }).onDelete("cascade"),
}));
export const rooms = pgTable("rooms", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  roomNumber: text("room_number").notNull(),
  status: text("status").default("vacant").notNull(),
  lastPrice: numeric("last_price"),
  bathroomType: text("bathroom_type").default("shared"),
  currentPrice: numeric("current_price"),
  recommendedPrice: numeric("recommended_price"),
  minPrice: numeric("min_price"),
  maxPrice: numeric("max_price"),
  repricingRule: text("repricing_rule"),
  pricingModelId: integer("pricing_model_id").references(() => roomPricingModels.id, { onDelete: "set null" }),
  nextPrice: numeric("next_price"),
  notes: text("notes"),
  inputImageUrl: text("input_image_url"),
  outputImageUrl: text("output_image_url"),
  roomType: text("room_type").default("bedroom"),
  padsplitRoomId: text("padsplit_room_id"),
  padsplitPromoId: text("padsplit_promo_id"),
  roomTitle: text("room_title"),
  roomDescription: text("room_description"),
  moveInFee: numeric("move_in_fee"),
  // Secondary-occupant placeholder. Points at the primary room when this row
  // exists solely so a second member can be assigned to the same physical
  // bedroom (PadSplit enforces one member per room). Shadow rows do NOT store
  // images — see `isPrimaryRoom` in `shared/fees.ts` for the full contract.
  shadowOfRoomId: integer("shadow_of_room_id"),
  associatedBedroomIds: integer("associated_bedroom_ids").array().$defaultFn(() => []),
  resetToMax: boolean("reset_to_max").default(false).notNull(),
  lastAppliedPromotion: timestamp("last_applied_promotion"),
  currentPromoRate: numeric("current_promo_rate", { precision: 10, scale: 2 }),
  manualChange: boolean("manual_change").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastHarvested: timestamp("last_harvested"),
  imageStatus: text("image_status").default("pending"),
  promptId: integer("prompt_id"),
  outputFileName: text("output_file_name"),
  imageNote: text("image_note"),
  // ── Analytics: room dimensions ───────────────────────────────────────────
  // Optional physical dimensions in feet. Stored so Phase 2 analytics can
  // compute sq-ft bands without re-asking. Computed squareFootage = widthFt ×
  // lengthFt when both are set; otherwise NULL. No sq-ft analytics run yet —
  // data is collected here so Phase 2 can begin computing immediately.
  widthFt: numeric("width_ft"),
  lengthFt: numeric("length_ft"),
  squareFootage: numeric("square_footage"),
  // PadSplit size category (cozy / standard / large). Acts as the analytics
  // grouping proxy when widthFt / lengthFt are not set.
  sizeCategory: text("size_category"),
  // ── PADSPLIT DATA WISHLIST ───────────────────────────────────────────────
  // Placeholder columns for high-priority fields not yet ingested from
  // PadSplit. All nullable; no ingestion logic exists yet. Each is tagged
  // PADSPLIT_WISHLIST so a global search surfaces the full set.
  // PADSPLIT_WISHLIST: days_vacant_per_room — ingest when available
  daysVacantPerRoom: integer("days_vacant_per_room"),
  // PADSPLIT_WISHLIST: booking_fee_per_move_in — ingest when available
  bookingFeePerMoveIn: numeric("booking_fee_per_move_in"),
  // PADSPLIT_WISHLIST: direct_booking_waiver — ingest when available
  directBookingWaiver: boolean("direct_booking_waiver"),
  // PADSPLIT_WISHLIST: application_count (booking requests received per room) — ingest when available
  applicationCount: integer("application_count"),
}, (table) => ({
  // Long auto-generated name kept in sync with the production constraint
  // name (see scripts/deploy-migrate.sql). Renaming would force db:push to
  // drop+recreate the FK against live data.
  shadowOfRoomFk: foreignKey({
    name: "rooms_shadow_of_room_id_rooms_id_fk",
    columns: [table.shadowOfRoomId],
    foreignColumns: [table.id],
  }).onDelete("set null"),
}));

export const roomHarvestCycles = pgTable("room_harvest_cycles", {
  cycleDate: date("cycle_date").primaryKey(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  batches: integer("batches").default(0).notNull(),
  totals: jsonb("totals").default({}).notNull(),
  alertSentAt: timestamp("alert_sent_at", { withTimezone: true }),
  dispatchNotBefore: timestamp("dispatch_not_before", { withTimezone: true }),
});
export const roomPricingHistory = pgTable("room_pricing_history", {
  id: serial("id").primaryKey(),
  roomId: integer("room_id").references(() => rooms.id),
  priceDate: date("price_date").notNull(),
  historyType: varchar("history_type", { length: 20 }).notNull(),
  priceUnit: varchar("price_unit", { length: 10 }).default("week"),
  currentRoomPrice: numeric("current_room_price", { precision: 10, scale: 2 }),
  nextOccupantPrice: numeric("next_occupant_price", { precision: 10, scale: 2 }),
  hasCurrentOccupant: boolean("has_current_occupant").default(false),
  adjustedCurrentPrice: numeric("adjusted_current_price", { precision: 10, scale: 2 }),
  adjustedNextPrice: numeric("adjusted_next_price", { precision: 10, scale: 2 }),
  moveInFee: numeric("move_in_fee", { precision: 10, scale: 2 }).default("0"),
  adjustedMoveInFee: numeric("adjusted_move_in_fee", { precision: 10, scale: 2 }),
  hasPromotion: boolean("has_promotion").default(false),
  promotionDiscountPercent: numeric("promotion_discount_percent", { precision: 5, scale: 2 }),
  promotionDurationWeeks: integer("promotion_duration_weeks"),
  promotionRoomPrice: numeric("promotion_room_price", { precision: 10, scale: 2 }),
  promotionStatus: varchar("promotion_status", { length: 20 }),
  pricingAnalysisStatus: varchar("pricing_analysis_status", { length: 20 }),
  pricingRecommendation: text("pricing_recommendation"),
  previousRule: varchar("previous_rule", { length: 100 }),
  newRule: varchar("new_rule", { length: 100 }),
  changedByUserId: integer("changed_by_user_id").references(() => appUsers.id),
  changedByName: varchar("changed_by_name", { length: 255 }),
  createdAt: timestamp("created_at").default(sql`CURRENT_TIMESTAMP`),
}, (table) => ({
  roomIdIdx: index("idx_room_pricing_history_room_id").on(table.roomId),
  priceDateIdx: index("idx_room_pricing_history_price_date").on(table.priceDate),
  historyTypeIdx: index("idx_room_pricing_history_history_type").on(table.historyType),
  createdAtIdx: index("idx_room_pricing_history_created_at").on(table.createdAt),
  historyTypeCheck: check(
    "room_pricing_history_history_type_check",
    sql`${table.historyType}::text = ANY (ARRAY['occupancy'::character varying::text, 'listing'::character varying::text, 'backfill'::character varying::text, 'rule_change'::character varying::text])`,
  ),
  pricingAnalysisStatusCheck: check(
    "room_pricing_history_pricing_analysis_status_check",
    sql`${table.pricingAnalysisStatus}::text = ANY (ARRAY['good'::character varying, 'high'::character varying, 'very_high'::character varying]::text[])`,
  ),
  promotionStatusCheck: check(
    "room_pricing_history_promotion_status_check",
    sql`${table.promotionStatus}::text = ANY (ARRAY['active'::character varying, 'inactive'::character varying, 'expired'::character varying]::text[])`,
  ),
}));

export const insertRoomPricingHistorySchema = createInsertSchema(roomPricingHistory).omit({
  id: true,
  createdAt: true,
});
export type InsertRoomPricingHistory = z.infer<typeof insertRoomPricingHistorySchema>;
export type RoomPricingHistory = typeof roomPricingHistory.$inferSelect;

export const roomStatusHistory = pgTable("room_status_history", {
  id: serial("id").primaryKey(),
  roomId: integer("room_id").references(() => rooms.id),
  statusChangeDate: timestamp("status_change_date").notNull(),
  status: varchar("status", { length: 20 }).notNull(),
  statusDurationDays: integer("status_duration_days"),
  changeReason: text("change_reason"),
  changedByUserId: integer("changed_by_user_id").references(() => appUsers.id),
  occupantName: varchar("occupant_name", { length: 255 }),
  occupantEmail: varchar("occupant_email", { length: 255 }),
  moveInDate: date("move_in_date"),
  moveOutDate: date("move_out_date"),
  leaseStartDate: date("lease_start_date"),
  leaseEndDate: date("lease_end_date"),
  maintenanceType: varchar("maintenance_type", { length: 50 }),
  maintenanceDescription: text("maintenance_description"),
  estimatedCompletionDate: date("estimated_completion_date"),
  actualCompletionDate: date("actual_completion_date"),
  createdAt: timestamp("created_at").default(sql`CURRENT_TIMESTAMP`),
}, (table) => ({
  roomIdIdx: index("idx_room_status_history_room_id").on(table.roomId),
  statusChangeDateIdx: index("idx_room_status_history_status_change_date").on(table.statusChangeDate),
  newStatusIdx: index("idx_room_status_history_new_status").on(table.status),
  createdAtIdx: index("idx_room_status_history_created_at").on(table.createdAt),
  newStatusCheck: check(
    "room_status_history_new_status_check",
    sql`${table.status}::text = ANY (ARRAY['vacant'::character varying::text, 'occupied'::character varying::text, 'maintenance'::character varying::text, 'moving_in'::character varying::text, 'moving_out'::character varying::text, 'unavailable'::character varying::text, 'inactive'::character varying::text, 'waiting_for_approval'::character varying::text, 'needs_flip'::character varying::text])`,
  ),
}));

export const insertRoomStatusHistorySchema = createInsertSchema(roomStatusHistory).omit({
  id: true,
  createdAt: true,
});
export type InsertRoomStatusHistory = z.infer<typeof insertRoomStatusHistorySchema>;
export type RoomStatusHistory = typeof roomStatusHistory.$inferSelect;

export const roomPricingSuggestions = pgTable("room_pricing_suggestions", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").references(() => properties.id),
  roomPricingModelsId: integer("room_pricing_models_id"),
  roomId: integer("room_id").references(() => rooms.id),
  day: integer("day").notNull(),
  createdAt: timestamp("created_at").default(sql`CURRENT_TIMESTAMP`),
  price: numeric("price", { precision: 10, scale: 2 }).notNull(),
  uuid: varchar("uuid", { length: 255 }),
  sentAt: timestamp("sent_at"),
  cancelledAt: timestamp("cancelled_at"),
  executedAt: timestamp("executed_at"),
  // Safe for platform-first schema publication: old rows must never become pending.
  // Job writers must explicitly supply pending after approved activation.
  warmBrowserDispatchStatus: varchar("warm_browser_dispatch_status", { length: 20 }).notNull().default("unknown"),
  warmBrowserDispatchedAt: timestamp("warm_browser_dispatched_at"),
  warmBrowserDispatchAttempts: integer("warm_browser_dispatch_attempts").notNull().default(0),
  warmBrowserLastError: text("warm_browser_last_error"),
  explanation: text("explanation"),
  simulatedSuggestions: jsonb("simulated_suggestions").notNull().default([]),
}, (table) => ({
  createdAtIdx: index("idx_room_pricing_suggestions_created_at").on(table.createdAt),
  dayIdx: index("idx_room_pricing_suggestions_day").on(table.day),
  executedAtIdx: index("idx_room_pricing_suggestions_executed_at").on(table.executedAt),
  propertyIdIdx: index("idx_room_pricing_suggestions_property_id").on(table.propertyId),
  roomIdIdx: index("idx_room_pricing_suggestions_room_id").on(table.roomId),
  roomPricingModelsIdIdx: index("idx_room_pricing_suggestions_room_pricing_models_id").on(table.roomPricingModelsId),
  sentAtIdx: index("idx_room_pricing_suggestions_sent_at").on(table.sentAt),
  uuidIdx: index("idx_room_pricing_suggestions_uuid").on(table.uuid),
  roomDayUnique: uniqueIndex("room_pricing_suggestions_room_day_unique").on(table.roomId, table.day),
  dispatchStatusCheck: check("rps_dispatch_status_check", sql`${table.warmBrowserDispatchStatus} IN ('pending', 'dispatched', 'failed', 'unknown')`),
}));

export const insertRoomPricingSuggestionSchema = createInsertSchema(roomPricingSuggestions).omit({
  id: true,
  createdAt: true,
});
export type InsertRoomPricingSuggestion = z.infer<typeof insertRoomPricingSuggestionSchema>;
export type RoomPricingSuggestion = typeof roomPricingSuggestions.$inferSelect;

// Task #1036 — Legacy messaging tables migrated from the old DB.
// `administrators` and `messages_pending` already exist on production
// (created manually long ago) but were never declared here, so the
// schema-deploy parity check was previously blind to them. `emails_sent`
// is brand new and is the actual table being migrated over from the
// legacy DB; declaring all three together gives `emails_sent` real
// `.references()` parents.
//
// Note: `administrators.id` and `messages_pending.id` are `integer NOT NULL`
// with NO column-default sequence on production (verified against the live
// DB) — Paul's worker still assigns its own ids on insert. They are
// declared as `integer("id").primaryKey()` here — NOT `serial()` — so the
// schema-deploy parity check stays clean. Only `emails_sent.id` (the
// genuinely new table) uses `serial()` because the legacy spec uses
// `nextval('emails_sent_id_seq')`.
//
// Task #1088: app-side enqueues into `messages_pending` no longer compute
// `max(id)+1`; they draw from `messages_pending_app_id_seq` (created by
// `scripts/deploy-migrate.sql` and `server/migrations/010_*`), a dedicated
// sequence that starts at 2,000,000,000 so app-allocated ids can never
// collide with worker-written ids or with each other under concurrency.
// The column itself stays plain INTEGER — the sequence is consumed
// explicitly in `enqueueOutboundMessage`, not as a column default.
export const administrators = pgTable("administrators", {
  id: integer("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  avatarUrl: varchar("avatar_url", { length: 500 }),
  email: varchar("email", { length: 255 }),
  role: varchar("role", { length: 50 }).default("admin"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  emailIdx: index("idx_administrators_email").on(table.email),
  nameIdx: index("idx_administrators_name").on(table.name),
  roleIdx: index("idx_administrators_role").on(table.role),
  isActiveIdx: index("idx_administrators_is_active").on(table.isActive),
  createdAtIdx: index("idx_administrators_created_at").on(table.createdAt),
}));

export const insertAdministratorSchema = createInsertSchema(administrators).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertAdministrator = z.infer<typeof insertAdministratorSchema>;
export type Administrator = typeof administrators.$inferSelect;

// Quo messaging — outbound transports a messages_pending row can be published
// through. Kept in shared/ so the client can label pending bubbles
// ("Sending via Quo…") from the same vocabulary the server stamps.
export const MESSAGE_TRANSPORTS = ["padsplit", "quo", "twilio"] as const;
export type MessageTransport = typeof MESSAGE_TRANSPORTS[number];

export const messagesPending = pgTable("messages_pending", {
  id: integer("id").primaryKey(),
  administratorId: integer("administrator_id").references(() => administrators.id),
  memberId: integer("member_id").references(() => members.id),
  propertyId: integer("property_id").references(() => properties.id),
  messageText: text("message_text"),
  sendAt: timestamp("send_at"),
  sentAt: timestamp("sent_at"),
  autoSend: boolean("auto_send").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  cancelledAt: timestamp("cancelled_at"),
  confirmedAt: timestamp("confirmed_at"),
  confirmedByUserId: integer("confirmed_by_user_id").references(() => appUsers.id),
  attempts: integer("attempts").default(0).notNull(),
  // Task #1556 — Human-readable error set when the PadPulse events API call
  // fails so the host can see why a PadSplit message wasn't delivered and
  // retry from the chat bubble. NULL means no known error (row is healthy /
  // delivered). Cleared to NULL on a successful retry.
  sendError: text("send_error"),
  // Task #1790 — Pointer to the member_chats row that this pending row was
  // reconciled into. Once set, the reconciler short-circuits and never
  // re-inserts. NULL means the row has not yet been materialized into
  // member_chats by the reconciler (the harvest job may still beat us to it,
  // in which case the reconciler detects the existing row, records this
  // pointer, and moves on without inserting).
  reconciledChatId: integer("reconciled_chat_id"),
  // Task #2960 — Stamped by the orphan auto-retry job
  // (`retryOrphanedPadsplitPending`) each time a "published but not sent"
  // row that sat stuck past the orphan threshold is re-dispatched. NULL means
  // the row was never auto-retried. When set, the chat bubble surfaces a
  // subtle "auto-resent after it got stuck" note so the host knows a silent
  // failure was detected and healed automatically.
  orphanRetriedAt: timestamp("orphan_retried_at"),
  // PadSplit send-UX — the app user who hit Send (impersonation-aware acting
  // user, resolved from the session at enqueue time). Powers auto-assignment of
  // a terminal-failure Team Inbox note and the failed-send bell notification to
  // the individual operator who attempted the send. NULL for legacy rows and
  // inbound/system-originated rows — callers fall back to org-wide routing.
  sentByUserId: integer("sent_by_user_id").references(() => appUsers.id),
  // Quo messaging — which transport the publisher used (or will use) for this
  // row: 'padsplit' (warm-browser Messenger), 'quo' (host's Quo inbox) or
  // 'twilio' (per-host Twilio number). Stamped at enqueue time by
  // resolveMemberTransport; NULL on legacy rows (callers re-derive).
  transport: text("transport").$type<MessageTransport>(),
  // Quo messaging — the provider's message id returned on a successful send
  // (Quo `AC…`). Mirrors member_chats.external_message_id on the reconciled
  // chat row. NULL until the send is accepted.
  providerMessageId: text("provider_message_id"),
}, (table) => ({
  administratorIdIdx: index("idx_messages_pending_administrator_id").on(table.administratorId),
  memberIdIdx: index("idx_messages_pending_member_id").on(table.memberId),
  propertyIdIdx: index("idx_messages_pending_property_id").on(table.propertyId),
  sendAtIdx: index("idx_messages_pending_send_at").on(table.sendAt),
  createdAtIdx: index("idx_messages_pending_created_at").on(table.createdAt),
  failedOutboundIdx: index("idx_messages_pending_failed_outbound")
    .on(table.sentByUserId)
    .where(sql`send_error IS NOT NULL AND sent_at IS NULL`),
  // Team Stats — first-response attribution joins messages_pending to the
  // member_chats row it reconciled into (reconciled_chat_id) per response row;
  // without this index each lookup is a seq scan.
  reconciledChatIdx: index("idx_messages_pending_reconciled_chat")
    .on(table.reconciledChatId)
    .where(sql`reconciled_chat_id IS NOT NULL`),
  // Task #4892 — active delivery-monitor candidate set. The monitor groups
  // every eligible account rather than applying a global message-row limit.
  deliveryMonitorIdx: index("idx_messages_pending_delivery_monitor")
    .on(table.sendAt, table.createdAt, table.memberId)
    .where(sql`reconciled_chat_id IS NULL`),
  deliveryMonitorPendingIdx: index("idx_messages_pending_delivery_monitor_pending")
    .on(table.sendAt, table.createdAt, table.memberId)
    .where(sql`reconciled_chat_id IS NULL AND auto_send = TRUE AND sent_at IS NULL AND cancelled_at IS NULL AND send_error IS NULL`),
  deliveryMonitorTerminalIdx: index("idx_messages_pending_delivery_monitor_terminal")
    .on(table.memberId, table.attempts, table.createdAt)
    .where(sql`reconciled_chat_id IS NULL AND auto_send = TRUE AND sent_at IS NULL AND send_error IS NOT NULL`),
  deliveryMonitorReconcileIdx: index("idx_messages_pending_delivery_monitor_reconcile")
    .on(table.sentAt, table.memberId)
    .where(sql`reconciled_chat_id IS NULL AND auto_send = TRUE AND sent_at IS NOT NULL AND cancelled_at IS NULL`),
}));

export const insertMessagesPendingSchema = createInsertSchema(messagesPending).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertMessagesPending = z.infer<typeof insertMessagesPendingSchema>;
export type MessagesPending = typeof messagesPending.$inferSelect;

export const messageDeliveryMonitorRoutes = pgTable("message_delivery_monitor_routes", {
  messagesPendingId: integer("messages_pending_id").primaryKey(),
  routedAccountId: integer("routed_account_id").notNull(),
  routingSource: varchar("routing_source", { length: 32 }).notNull(),
  recordedAt: timestamp("recorded_at").notNull().defaultNow(),
}, (table) => ({
  messageFk: foreignKey({
    name: "message_delivery_monitor_routes_messages_pending_id_fkey",
    columns: [table.messagesPendingId],
    foreignColumns: [messagesPending.id],
  }).onDelete("cascade"),
  accountFk: foreignKey({
    name: "message_delivery_monitor_routes_routed_account_id_fkey",
    columns: [table.routedAccountId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
}));
export const propertiesAdministrators = pgTable("properties_administrators", {
  id: serial("id").primaryKey(),
  administratorId: integer("administrator_id").references(() => administrators.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "cascade" }),
  archived: integer("archived"),
});

export const insertPropertiesAdministratorSchema = createInsertSchema(propertiesAdministrators).omit({
  id: true,
});
export type InsertPropertiesAdministrator = z.infer<typeof insertPropertiesAdministratorSchema>;
export type PropertiesAdministrator = typeof propertiesAdministrators.$inferSelect;

// Task #1029 — Paul's canonical chat history. The harvest job (member_message
// /store_workflow) writes inbound + outbound member↔host conversation rows
// into this table directly. The app:
//   - READS from member_chats to render the timeline.
//   - UPDATES `is_read` only.
//   - NEVER inserts into member_chats EXCEPT for SMS-only outbound to
//     non-PadSplit members (those rows can never come back via the harvest,
//     so we synthesize them with messageType='sms').
// Schema mirrors prod exactly — no FKs, no enum-on-column, no NOT NULL
// tightening. Defensive handling lives in the app code per task spec.
// `portal` — member-portal composer (server/routes/member-portal.ts) and the
// business-hours auto-reply. `quo` — SMS relayed through the host's Quo
// (OpenPhone) inbox; rows carry `external_message_id` (Quo's `AC…` id) so the
// webhook, history backfill and outbound publisher are all idempotent.
export const MEMBER_CHAT_SOURCES = ["padpulse", "padsplit", "sms", "system", "portal", "quo"] as const;
export type MemberChatSource = typeof MEMBER_CHAT_SOURCES[number];

// Inbound (member-authored) sender_type labels. The PadPulse app inserts inbound
// member rows as 'member', but the external PadSplit harvest writes them with
// PadSplit's own label 'user'. Any query that gates on "this is a message FROM the
// member" (unread counts, unresolved counts/lists, resolve/unresolve) must accept
// BOTH or it silently matches ~zero rows in production (where harvested 'user' rows
// dominate) — the cause of dead unread/unresolved badges and the resolve 403 cycle.
export const INBOUND_MEMBER_SENDER_TYPES = ["member", "user"] as const;

export const memberChats = pgTable("member_chats", {
  id: bigserial("id", { mode: "number" }).primaryKey(),
  memberId: bigint("member_id", { mode: "number" }).notNull(),
  propertyId: bigint("property_id", { mode: "number" }),
  senderType: varchar("sender_type", { length: 20 }).notNull(),
  senderId: bigint("sender_id", { mode: "number" }),
  senderName: varchar("sender_name", { length: 255 }).notNull(),
  senderAvatarUrl: text("sender_avatar_url"),
  messageText: text("message_text").notNull(),
  messageType: varchar("message_type", { length: 20 }).default("text").notNull(),
  isRead: boolean("is_read").default(false).notNull(),
  deliveryStatus: varchar("delivery_status", { length: 20 }).default("sent").notNull(),
  source: varchar("source", { length: 20 }).default("padpulse").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  // Task #2987 — Team-shared resolution signal. NULL = open (unresolved).
  // Set by any team member; visible to all teammates via polling.
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  resolvedBy: integer("resolved_by"),
  // Task #4670 — backported from prod: PadSplit's message id for harvested
  // messages. NULL for non-PadSplit rows. Uniqueness is stay-scoped via the
  // composite partial unique index below.
  padsplitMessageId: text("padsplit_message_id"),
  // Quo messaging — the provider's message id (`AC…`) for rows relayed through
  // a host's Quo inbox (source='quo'). Globally unique when present: the
  // inbound webhook, the history backfill and the outbound publisher all
  // upsert on this key, so arrival order and retries can never create two
  // copies of one SMS. NULL for every non-Quo row.
  externalMessageId: text("external_message_id"),
  // Quo messaging — provider error code when delivery_status lands on
  // 'failed' / 'undelivered' (e.g. carrier code "30006"). NULL otherwise.
  deliveryError: text("delivery_error"),
  // Quo messaging — structured payload for non-text rows. Today: Quo call
  // summaries (message_type='call'): direction, status, duration, summary /
  // next-step bullets, voicemail transcript, and the deep link into the Quo
  // conversation. NULL for ordinary text rows.
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
}, (table) => ({
  memberIdIdx: index("idx_member_chats_member_id").on(table.memberId),
  externalMessageUidx: uniqueIndex("member_chats_external_message_uidx")
    .on(table.externalMessageId)
    .where(sql`external_message_id IS NOT NULL`),
  createdAtIdx: index("idx_member_chats_created_at").on(table.createdAt),
  senderTypeSenderIdIdx: index("idx_member_chats_sender_type_sender_id").on(table.senderType, table.senderId),
  propertyIdIdx: index("idx_member_chats_property_id").on(table.propertyId),
  dedupeLookupIdx: index("idx_member_chats_dedupe_lookup").on(table.memberId, table.senderType, table.senderId, table.createdAt),
  // Task #4670 — backported from prod: stay-scoped uniqueness for harvested
  // PadSplit messages. Supersedes the earlier prod-only global unique index
  // on padsplit_message_id alone (dropped in scripts/deploy-migrate.sql).
  memberPadsplitMessageUidx: uniqueIndex("member_chats_member_padsplit_message_uidx")
    .on(table.memberId, table.padsplitMessageId)
    .where(sql`padsplit_message_id IS NOT NULL`),
  // Task #2987 — partial index powering unresolved-message queries
  // (getUnresolvedMessages / getUnresolvedMemberMessageCount). Mirrors the
  // CREATE INDEX in scripts/deploy-migrate.sql exactly so dev (db:push) and
  // prod stay in sync and the deploy-build parity check passes.
  unresolvedIdx: index("idx_member_chats_unresolved")
    .on(table.memberId, table.createdAt)
    .where(sql`resolved_at IS NULL AND sender_type = 'member'`),
  // Task #2074 — trigram GIN index to accelerate ILIKE substring search on
  // message_text (see DatabaseStorage.searchMemberChatsByContent). Lives
  // exclusively in scripts/deploy-migrate.sql (CREATE INDEX CONCURRENTLY
  // ... USING gin (message_text gin_trgm_ops)) because Drizzle Kit's
  // `.using("gin", sql\`... gin_trgm_ops\`)` regenerator strips the
  // operator class on db:push, producing "data type text has no default
  // operator class for access method gin". Allowlisted as a knownStaleIndex
  // in scripts/check-schema-deploy-parity-allowlist.json.
}));

export const insertMemberChatSchema = createInsertSchema(memberChats).omit({
  id: true,
  updatedAt: true,
});
export type InsertMemberChat = z.infer<typeof insertMemberChatSchema>;
export type MemberChat = typeof memberChats.$inferSelect;

// Task #1029 — Workflow sidecar for delivered chats (member_chats rows). Carries
// the assignee/due-date/maintenance-link/broadcast metadata that historically
// lived inline on the legacy `messages` table. Keyed 1:1 on member_chat_id.
export const memberChatWorkflow = pgTable("member_chat_workflow", {
  memberChatId: bigint("member_chat_id", { mode: "number" }).primaryKey(),
  assigneeId: integer("assignee_id").references(() => appUsers.id, { onDelete: "set null" }),
  assigneeName: text("assignee_name"),
  assignedAt: timestamp("assigned_at"),
  dueDate: timestamp("due_date"),
  maintenanceTaskId: integer("maintenance_task_id"),
  maintenanceLinkType: text("maintenance_link_type"),
  isBroadcast: boolean("is_broadcast").default(false).notNull(),
  broadcastId: text("broadcast_id"),
  waitingReplyAt: timestamp("waiting_reply_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  assigneeIdx: index("idx_member_chat_workflow_assignee").on(table.assigneeId),
  dueDateIdx: index("idx_member_chat_workflow_due_date").on(table.dueDate),
  maintenanceTaskIdx: index("idx_member_chat_workflow_maintenance_task").on(table.maintenanceTaskId),
  broadcastIdx: index("idx_member_chat_workflow_broadcast").on(table.broadcastId),
}));

export const insertMemberChatWorkflowSchema = createInsertSchema(memberChatWorkflow).omit({
  createdAt: true,
  updatedAt: true,
});
export type InsertMemberChatWorkflow = z.infer<typeof insertMemberChatWorkflowSchema>;
export type MemberChatWorkflow = typeof memberChatWorkflow.$inferSelect;

// ─── Team Inbox events ───────────────────────────────────────────────────────
// Unified Team Inbox — a shared, org-scoped queue of items that need a human.
//
// MEMBER MESSAGES ARE NOT STORED HERE. They are read in at query time from
// member_chats' own resolved_at/resolved_by (+ the member_chat_workflow assignee)
// — the single source of truth — so resolving on the messages page and in the
// inbox are the SAME action on the SAME field and can never drift. This table
// backs ONLY the new event types (vendor_message | service_form | supply_order).
// Resolve columns are named to mirror member_chats (resolved_at / resolved_by) so
// the read-time UNION ALL in GET /api/app/team-inbox is a clean projection.
//
// "org_id" = the owning host account (app_users.id), resolved from any teammate's
// session via getEffectiveOwnerId(). PadPulse has no separate `orgs` table.
export const TEAM_INBOX_EVENT_TYPES = [
  "vendor_message",
  "service_form",
  "supply_order",
  "booking_request",
  "maintenance",
  "vendor_task_update",
  "mercury_statement",
  "closing_statement",
  // Source #9 — a PadSplit outbound push that exhausted its retry budget.
  // Coalesced per (org, entity, push_type); auto-resolves on a later success.
  "padsplit_push_failure",
  // Task #4041 — a Mercury credit card crossed the >80% utilization "near its
  // limit" threshold. Coalesced per (org, card); auto-resolves when the card
  // drops back below the threshold on a later refresh.
  "mercury_card_near_limit",
  // Task #4266 — a common-space (property-level, no explicit member/room
  // assignment) smart lock is low on battery. Coalesced per (org, lock device);
  // auto-resolves when the battery recovers above the threshold.
  "lock_low_battery",
  // Cleaning operations alerts: a member who has not submitted a notified
  // proof after its scheduled date, or a generated slot with no assignee.
  "cleaning_proof_overdue",
  "cleaning_uncovered_slot",
  // Whole-house broadcast safety net. Delivery-incomplete is coalesced per
  // broadcast and auto-resolves once every recipient is confirmed; roster-gap
  // is one item per (broadcast, missed member) and offers a pre-filled send.
  "broadcast_delivery_incomplete",
  "broadcast_roster_gap",
] as const;
export type TeamInboxEventType = typeof TEAM_INBOX_EVENT_TYPES[number];

export const teamInboxEvents = pgTable("team_inbox_events", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(),
  title: text("title").notNull(),
  preview: text("preview"),
  sourceLabel: text("source_label"),
  entityType: text("entity_type"),
  entityId: text("entity_id"),
  link: text("link"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  // Shared, attributed resolve-state (NULL resolved_at = open).
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  resolvedBy: integer("resolved_by").references(() => appUsers.id, { onDelete: "set null" }),
  resolveNote: text("resolve_note"),
  // Assignment — orthogonal to resolve-state.
  assignedTo: integer("assigned_to").references(() => appUsers.id, { onDelete: "set null" }),
  assignedBy: integer("assigned_by").references(() => appUsers.id, { onDelete: "set null" }),
  assignedAt: timestamp("assigned_at", { withTimezone: true }),
  // Idempotency for emission — re-processing the same source event never
  // duplicates (recordTeamEvent upserts ON CONFLICT DO NOTHING on this key).
  dedupeKey: text("dedupe_key"),
  // Source #9 (padsplit_push_failure) extras. `severity` drives the red chip +
  // priority sort (critical sorts first in "Needs attention"); `autoResolved`
  // marks a system (non-human) resolve performed by clearPushFailure on a later
  // successful sync; `metadata` holds per-type extras (push_type, entity_type,
  // entity_id, attempt_count, first_failed_at, last_failed_at, last_error).
  severity: text("severity").notNull().default("normal"),
  autoResolved: boolean("auto_resolved").notNull().default(false),
  metadata: jsonb("metadata"),
}, (t) => ({
  dedupeKeyUnique: uniqueIndex("team_inbox_events_dedupe_key_unique").on(t.dedupeKey),
  openIdx: index("idx_tie_open").on(t.orgId, t.createdAt).where(sql`resolved_at IS NULL`),
  resolvedIdx: index("idx_tie_resolved").on(t.orgId, t.resolvedAt).where(sql`resolved_at IS NOT NULL`),
  assigneeIdx: index("idx_tie_assignee").on(t.orgId, t.assignedTo).where(sql`resolved_at IS NULL`),
}));

export const broadcastJobs = pgTable("broadcast_jobs", {
  id: text("id").primaryKey(),
  actorId: integer("actor_id").notNull(),
  requestKey: text("request_key").notNull(),
  requestHash: text("request_hash").notNull(),
  payload: jsonb("payload").$type<Record<string, any>>().notNull(),
  state: text("state").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  deadlineAt: timestamp("deadline_at", { withTimezone: true }).notNull(),
  error: text("error"),
}, (t) => ({ requestIdx: uniqueIndex("broadcast_jobs_actor_request").on(t.actorId, t.requestKey) }));

export const broadcastRecipients = pgTable("broadcast_recipients", {
  broadcastId: text("broadcast_id").notNull(),
  memberId: integer("member_id").notNull(),
  state: text("state").notNull(),
  pendingId: integer("pending_id"),
  attempts: integer("attempts").notNull().default(0),
  leaseToken: text("lease_token"),
  leaseUntil: timestamp("lease_until", { withTimezone: true }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  data: jsonb("data").$type<Record<string, any>>().notNull(),
  error: text("error"),
}, (t) => ({
  recipientIdx: uniqueIndex("broadcast_recipients_identity").on(t.broadcastId, t.memberId),
  pendingIdx: uniqueIndex("broadcast_recipients_pending").on(t.pendingId),
}));

export const messageBroadcasts = pgTable("message_broadcasts", {
  id: text("id").primaryKey(),
  orgId: integer("org_id").notNull(),
  actorUserId: integer("actor_user_id"),
  propertyIds: jsonb("property_ids").$type<number[]>().notNull(),
  ownerIds: jsonb("owner_ids").$type<number[]>().notNull(),
  messageText: text("message_text").notNull(),
  attachmentUrls: jsonb("attachment_urls").$type<string[] | null>(),
  scheduledFor: timestamp("scheduled_for", { withTimezone: true }),
  rosterCheck: jsonb("roster_check").notNull(),
  rosterFingerprint: text("roster_fingerprint").notNull(),
  acknowledgedFingerprint: text("acknowledged_fingerprint"),
  acknowledgedAt: timestamp("acknowledged_at", { withTimezone: true }),
  recipients: jsonb("recipients").$type<Array<{
    memberId: number; memberName: string; pendingId: number; propertyId: number;
    channel: "padsplit" | "quo" | "direct"; addedAt?: string;
    // broadcast = original blast (incl. residents auto-added at send time);
    // roster_gap = one-click send from the status view; auto_roster_gap = sent
    // automatically by the missed-resident sweep.
    source?: "broadcast" | "roster_gap" | "auto_roster_gap";
  }>>().notNull(),
  failures: jsonb("failures").$type<Array<{
    memberId: number; memberName: string; error: string;
    // auto_roster_gap = the missed-resident sweep tried and failed; never retried
    // automatically (a Team Inbox item asks a human instead).
    source?: "broadcast" | "auto_roster_gap"; at?: string;
  }>>().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  orgCreatedIdx: index("idx_message_broadcasts_org_created").on(t.orgId, t.createdAt),
  createdIdx: index("idx_message_broadcasts_created").on(t.createdAt),
}));
export const insertTeamInboxEventSchema = createInsertSchema(teamInboxEvents).omit({
  id: true,
  createdAt: true,
  resolvedAt: true,
  resolvedBy: true,
  resolveNote: true,
  assignedTo: true,
  assignedBy: true,
  assignedAt: true,
});
export type InsertTeamInboxEvent = z.infer<typeof insertTeamInboxEventSchema>;
export type TeamInboxEvent = typeof teamInboxEvents.$inferSelect;

// Source #10 — Internal team To-Dos. The operational checklist that ALSO
// surfaces as a Team Inbox source (kind 'todo'). Single source of truth: this
// table is UNIONed into getTeamInbox at read time (like member_chats), never
// copied into team_inbox_events. Mappings to the shared inbox shape:
//   done  <-> resolved   (done_at/done_by mirror resolved_at/resolved_by)
//   assignee <-> assigned-to (a to-do ALWAYS has an assignee; no unassigned)
// org_id = owning host account (app_users.id), resolved via getEffectiveOwnerId.
export const todos = pgTable("todos", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  // Required — a to-do always has an owner; the inbox never shows it "unassigned".
  assigneeId: integer("assignee_id").notNull().references(() => appUsers.id),
  // Optional property context, allowed for ALL property statuses (incl. inactive).
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  dueDate: date("due_date"),
  status: text("status").notNull().default("open"), // open | done
  // Task #3971 — stable key of the onboarding-todo template that seeded this
  // row (`<journeyStage>:<slug>`), or NULL for a hand-created to-do. Lets us
  // recognise/dedupe auto-seeded setup items without parsing their copy.
  templateKey: text("template_key"),
  createdBy: integer("created_by").notNull().references(() => appUsers.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  // Resolve-state mirrors team_inbox_events naming for a clean inbox projection.
  doneAt: timestamp("done_at", { withTimezone: true }),
  doneBy: integer("done_by").references(() => appUsers.id, { onDelete: "set null" }),
}, (t) => ({
  openByAssigneeIdx: index("idx_todos_open_assignee").on(t.orgId, t.assigneeId).where(sql`status = 'open'`),
  openByDueIdx: index("idx_todos_open_due").on(t.orgId, t.dueDate).where(sql`status = 'open'`),
}));

export const insertTodoSchema = createInsertSchema(todos).omit({
  id: true,
  createdAt: true,
  doneAt: true,
  doneBy: true,
  status: true,
});
export type InsertTodo = z.infer<typeof insertTodoSchema>;
export type Todo = typeof todos.$inferSelect;

// Task #3973 — Internal team comment thread on a to-do, mirroring the
// maintenance `task_comments` model: post comments, attach files, and @-mention
// org teammates. Internal team only (no vendor chat); no edit/delete. FK names
// (`todo_comments_todo_id_todos_id_fk`, `todo_comments_author_id_app_users_id_fk`)
// stay well under Postgres's 63-char limit, so inline `.references()` is fine.
export const todoComments = pgTable("todo_comments", {
  id: serial("id").primaryKey(),
  todoId: integer("todo_id").notNull().references(() => todos.id, { onDelete: "cascade" }),
  authorId: integer("author_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  mentions: jsonb("mentions").$type<number[]>().default([]),
  attachmentUrls: text("attachment_urls").array().$defaultFn(() => []).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertTodoCommentSchema = createInsertSchema(todoComments).omit({
  id: true,
  createdAt: true,
}).extend({
  todoId: z.number(),
  authorId: z.number(),
  content: z.string().min(1, "Comment cannot be empty"),
  mentions: z.array(z.number()).optional().nullable(),
  attachmentUrls: z.array(z.string()).optional().default([]),
});
export type InsertTodoComment = z.infer<typeof insertTodoCommentSchema>;
export type TodoComment = typeof todoComments.$inferSelect;

// Task #1029 — Workflow sidecar for QUEUED messages (messages_pending rows).
// Same fields as memberChatWorkflow. Migrated to memberChatWorkflow once the
// harvest brings back the matching member_chats row (reconciliation cron).
export const messagesPendingWorkflow = pgTable("messages_pending_workflow", {
  // Inline `.references()` would auto-name the FK
  // `messages_pending_workflow_messages_pending_id_messages_pending_id_fk`
  // (68 chars), which exceeds Postgres's 63-char identifier limit. We
  // declare the FK explicitly below with a short name instead.
  messagesPendingId: integer("messages_pending_id").primaryKey(),
  assigneeId: integer("assignee_id").references(() => appUsers.id, { onDelete: "set null" }),
  assigneeName: text("assignee_name"),
  assignedAt: timestamp("assigned_at"),
  dueDate: timestamp("due_date"),
  maintenanceTaskId: integer("maintenance_task_id"),
  maintenanceLinkType: text("maintenance_link_type"),
  isBroadcast: boolean("is_broadcast").default(false).notNull(),
  broadcastId: text("broadcast_id"),
  waitingReplyAt: timestamp("waiting_reply_at"),
  // Task #1208 — Track which messaging rule produced this pending row so the
  // Awaiting tab can attribute and filter by rule origin.
  // FK to messaging_rules(id) is enforced at the DB level via the named
  // constraint "msgs_pending_wf_rule_fk" (see scripts/deploy-migrate.sql).
  // We cannot use .references() here: messagingRules is declared later in this
  // file so it would be in the temporal dead zone when this pgTable() call runs;
  // and the auto-generated FK name would exceed Postgres's 63-char identifier
  // limit (65 chars). The foreignKey() helper in this table's config has the
  // same forward-reference problem.
  messagingRuleId: integer("messaging_rule_id"),
  // Task #4266 — automation origin marker for non-rule automated messages
  // (e.g. "low_battery"). Lets the delivered chat bubble show a "Sent by
  // Low Battery Auto-Notification" chip distinct from a messaging_rule chip.
  automationSource: text("automation_source"),
  // The PadPulse account that originated this message (rule owner, automation
  // owner, or the account an interactive send first routed through). PadSplit
  // delivery — including later due/retry/orphan passes — must go out through
  // THIS account's assistant, never another co-owner's. Null on legacy rows:
  // rule rows fall back to messaging_rules.user_id at publish time.
  originOwnerId: integer("origin_owner_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  assigneeIdx: index("idx_messages_pending_workflow_assignee").on(table.assigneeId),
  broadcastIdx: index("idx_messages_pending_workflow_broadcast").on(table.broadcastId),
  pendingFk: foreignKey({
    name: "msgs_pending_wf_pending_fk",
    columns: [table.messagesPendingId],
    foreignColumns: [messagesPending.id],
  }).onDelete("cascade"),
}));

export const insertMessagesPendingWorkflowSchema = createInsertSchema(messagesPendingWorkflow).omit({
  createdAt: true,
  updatedAt: true,
});
export type InsertMessagesPendingWorkflow = z.infer<typeof insertMessagesPendingWorkflowSchema>;
export type MessagesPendingWorkflow = typeof messagesPendingWorkflow.$inferSelect;

// Task #1029 — Attachment sidecar for member_chats. Paul's harvest doesn't
// carry attachment URLs today (Question for Paul #2), so we hold them
// app-side keyed on member_chat_id once the harvest creates the parent row.
// For queued outbound, attachment metadata is associated with the
// messages_pending row via attachOutboundFiles and copied across at
// reconciliation time.
export const memberChatAttachments = pgTable("member_chat_attachments", {
  id: serial("id").primaryKey(),
  memberChatId: bigint("member_chat_id", { mode: "number" }),
  // See messagesPendingWorkflow above for why we don't inline `.references()`
  // here — the auto-generated FK name would exceed Postgres's 63-char limit.
  messagesPendingId: integer("messages_pending_id"),
  url: text("url").notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
  // AI Draft Context Overhaul Phase 2 — vision-analysis cache. A Gemini
  // vision summary of this image, computed once and reused by every
  // subsequent draft (regenerates never re-run vision). Cache key is
  // (url, vision_model): a model change makes the summary stale and it is
  // re-analyzed on next use. NULL = never analyzed (or analysis failed —
  // failures are not cached).
  visionSummary: jsonb("vision_summary").$type<{
    description: string;
    ocrText: string | null;
    apparentIssue: string | null;
  }>(),
  visionModel: text("vision_model"),
  visionAnalyzedAt: timestamp("vision_analyzed_at", { withTimezone: true }),
}, (table) => ({
  memberChatIdx: index("idx_member_chat_attachments_chat").on(table.memberChatId),
  pendingIdx: index("idx_member_chat_attachments_pending").on(table.messagesPendingId),
  pendingFk: foreignKey({
    name: "member_chat_attach_pending_fk",
    columns: [table.messagesPendingId],
    foreignColumns: [messagesPending.id],
  }).onDelete("cascade"),
}));

export const insertMemberChatAttachmentSchema = createInsertSchema(memberChatAttachments).omit({
  id: true,
  uploadedAt: true,
});
export type InsertMemberChatAttachment = z.infer<typeof insertMemberChatAttachmentSchema>;
export type MemberChatAttachment = typeof memberChatAttachments.$inferSelect;

// Task #1029 — Replaces internal_threads. Keyed on member_chats.id so the
// host-to-host notes survive the table rename. Notification semantics
// carried over from the legacy table.
export const memberChatThreads = pgTable("member_chat_threads", {
  id: serial("id").primaryKey(),
  memberChatId: bigint("member_chat_id", { mode: "number" }).notNull(),
  authorId: integer("author_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  authorName: text("author_name").notNull(),
  body: text("body").notNull(),
  mentions: jsonb("mentions").$type<number[]>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  editedAt: timestamp("edited_at"),
}, (table) => ({
  memberChatIdx: index("idx_member_chat_threads_chat").on(table.memberChatId),
  authorIdx: index("idx_member_chat_threads_author").on(table.authorId),
}));

export const insertMemberChatThreadSchema = createInsertSchema(memberChatThreads).omit({
  id: true,
  createdAt: true,
  editedAt: true,
});
export type InsertMemberChatThread = z.infer<typeof insertMemberChatThreadSchema>;
export type MemberChatThread = typeof memberChatThreads.$inferSelect;

export const emailsSent = pgTable("emails_sent", {
  id: serial("id").primaryKey(),
  appUserId: integer("app_user_id").references(() => appUsers.id),
  day: integer("day").notNull(),
  emailType: varchar("email_type", { length: 64 }).notNull(),
  sentAt: timestamp("sent_at").defaultNow(),
  administratorId: integer("administrator_id").references(() => administrators.id),
  messagesPendingId: integer("messages_pending_id").references(() => messagesPending.id),
}, (table) => ({
  dayIdx: index("idx_emails_sent_day").on(table.day),
  emailTypeIdx: index("idx_emails_sent_email_type").on(table.emailType),
  // Partial unique indexes mirror the legacy spec: prevent duplicate emails
  // per (admin, pending message, type), per (user, day, type), and per
  // (user, pending message, type) respectively.
  adminMpTypeIdx: uniqueIndex("idx_emails_sent_admin_mp_type")
    .on(table.administratorId, table.messagesPendingId, table.emailType)
    .where(sql`${table.administratorId} IS NOT NULL AND ${table.messagesPendingId} IS NOT NULL`),
  userDayTypeIdx: uniqueIndex("idx_emails_sent_user_day_type")
    .on(table.appUserId, table.day, table.emailType)
    .where(sql`${table.appUserId} IS NOT NULL`),
  userMpTypeIdx: uniqueIndex("idx_emails_sent_user_mp_type")
    .on(table.appUserId, table.messagesPendingId, table.emailType)
    .where(sql`${table.appUserId} IS NOT NULL AND ${table.messagesPendingId} IS NOT NULL AND ${table.administratorId} IS NULL`),
}));

export const insertEmailsSentSchema = createInsertSchema(emailsSent).omit({
  id: true,
  sentAt: true,
});
export type InsertEmailsSent = z.infer<typeof insertEmailsSentSchema>;
export type EmailsSent = typeof emailsSent.$inferSelect;

export const insertRoomSchema = createInsertSchema(rooms).omit({
  id: true,
  createdAt: true,
}).extend({
  propertyId: z.number(),
  roomNumber: z.string().min(1, "Room number is required"),
  status: z.string().default("vacant"),
  lastPrice: z.string().optional(),
  bathroomType: z.string().optional(),
  currentPrice: z.string().optional(),
  recommendedPrice: z.string().optional(),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  repricingRule: z.string().nullable().optional(),
  nextPrice: z.string().optional(),
  notes: z.string().optional(),
  inputImageUrl: z.string().refine((value) => {
    if (/^\/(?!\/)/.test(value)) return true;
    try { return ["http:", "https:"].includes(new URL(value).protocol); } catch { return false; }
  }, "Use an http(s) image URL or an uploaded image path").nullable().optional(),
  outputImageUrl: z.string().optional(),
  roomType: z.string().optional().default("bedroom"),
  padsplitRoomId: z.string().optional(),
  padsplitPromoId: z.string().optional(),
  roomTitle: z.string().optional(),
  roomDescription: z.string().optional(),
  moveInFee: z.string().optional(),
  shadowOfRoomId: z.number().nullable().optional(),
  associatedBedroomIds: z.array(z.number()).optional().default([]),
  resetToMax: z.boolean().optional().default(false),
  lastAppliedPromotion: z.coerce.date().nullable().optional(),
  currentPromoRate: z.string().nullable().optional(),
  manualChange: z.boolean().optional().default(false),
  imageStatus: z.string().optional(),
  promptId: z.number().nullable().optional(),
  outputFileName: z.string().nullable().optional(),
  imageNote: z.string().nullable().optional(),
});

export type InsertRoom = z.input<typeof insertRoomSchema>;
export type Room = typeof rooms.$inferSelect;

export const propertyImages = pgTable("property_images", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  roomId: integer("room_id").references(() => rooms.id, { onDelete: "set null" }),
  imageUrl: text("image_url").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPropertyImageSchema = createInsertSchema(propertyImages).omit({
  id: true,
  createdAt: true,
});
export type InsertPropertyImage = z.infer<typeof insertPropertyImageSchema>;
export type PropertyImage = typeof propertyImages.$inferSelect;

export const uploadedImages = pgTable("uploaded_images", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  serviceFormLinkId: integer("service_form_link_id").references(() => serviceFormLinks.id, { onDelete: "set null" }),
  bookingRequestLinkId: integer("booking_request_link_id"),
  filename: text("filename").notNull(),
  mimeType: text("mime_type").notNull(),
  data: text("data"),
  objectPath: text("object_path"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "uploaded_images_booking_request_link_fk",
    columns: [table.bookingRequestLinkId],
    foreignColumns: [bookingRequestLinks.id],
  }).onDelete("set null"),
]);

export const insertUploadedImageSchema = createInsertSchema(uploadedImages).omit({
  id: true,
  createdAt: true,
});
export type InsertUploadedImage = z.infer<typeof insertUploadedImageSchema>;
export type UploadedImage = typeof uploadedImages.$inferSelect;

export const underwritingDefaults = pgTable("underwriting_defaults", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  downpaymentPercent: text("downpayment_percent"),
  closingCostsPercent: text("closing_costs_percent"),
  furnishingCostPerRoom: text("furnishing_cost_per_room"),
  monthsUntilOccupied: integer("months_until_occupied"),
  annualInterestRate: text("annual_interest_rate"),
  vacancyPercent: text("vacancy_percent"),
  landValuePercent: text("land_value_percent"),
  annualAppreciationAvg: text("annual_appreciation_avg"),
  pmFee: text("pm_fee"),
  pmFeeType: text("pm_fee_type").default("percent"),
  defaultFurnished: boolean("default_furnished").default(true),
  sewerWater: text("sewer_water"),
  gas: text("gas"),
  electricity: text("electricity"),
  electricityPerRoom: text("electricity_per_room"),
  internet: text("internet"),
  lawnCare: text("lawn_care"),
  cleaning: text("cleaning"),
  capitalImprovementsBudget: text("capital_improvements_budget"),
  miscRepairs: text("misc_repairs"),
  hoa: text("hoa"),
  annualInsurancePercent: text("annual_insurance_percent"),
  annualPropertyTaxPercent: text("annual_property_tax_percent"),
  roomAdditionsCost: text("room_additions_cost"),
  bathAdditionsCost: text("bath_additions_cost"),
  kitchenAmount: text("kitchen_amount"),
  hvacAmount: text("hvac_amount"),
  roofAmount: text("roof_amount"),
  plumbingAmount: text("plumbing_amount"),
  foundationRepairAmount: text("foundation_repair_amount"),
  otherAmount: text("other_amount"),
  additionalRehabAmount: text("additional_rehab_amount"),
  bufferPercent: text("buffer_percent"),
  // Task #869: declared as `text` (not `numeric`) so `npm run db:push --force`
  // can run cleanly on legacy dev DBs that still hold the original text
  // columns. Drizzle returns numeric values as JS strings anyway, so the
  // application code is type-agnostic here. Production has these as numeric;
  // the divergence is allowlisted in
  // scripts/check-schema-deploy-parity-allowlist.json under
  // `columnTypeOverrides`.
  optionFee: text("option_fee"),
  closingCosts: text("closing_costs"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertUnderwritingDefaultsSchema = createInsertSchema(underwritingDefaults).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
});

export type InsertUnderwritingDefaults = z.infer<typeof insertUnderwritingDefaultsSchema>;
export type UnderwritingDefaults = typeof underwritingDefaults.$inferSelect;

export const imagePrompts = pgTable("image_prompts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  promptText: text("prompt_text").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertImagePromptSchema = createInsertSchema(imagePrompts).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1, "Prompt name is required"),
  promptText: z.string().min(1, "Prompt text is required"),
});
export type InsertImagePrompt = z.infer<typeof insertImagePromptSchema>;
export type ImagePrompt = typeof imagePrompts.$inferSelect;

export const roomImageVariants = pgTable("room_image_variants", {
  id: serial("id").primaryKey(),
  roomId: integer("room_id").notNull().references(() => rooms.id, { onDelete: "cascade" }),
  outputImageUrl: text("output_image_url"),
  outputFileName: text("output_file_name"),
  promptId: integer("prompt_id").references(() => imagePrompts.id, { onDelete: "set null" }),
  status: text("status").default("completed").notNull(),
  note: text("note"),
  variantIndex: integer("variant_index").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  roomIdIdx: index("room_image_variants_room_id_idx").on(table.roomId),
}));

export const insertRoomImageVariantSchema = createInsertSchema(roomImageVariants).omit({
  id: true,
  createdAt: true,
}).extend({
  roomId: z.number(),
  outputImageUrl: z.string().optional().nullable(),
  outputFileName: z.string().optional().nullable(),
  promptId: z.number().optional().nullable(),
  status: z.string().default("completed"),
  note: z.string().optional().nullable(),
  variantIndex: z.number().min(0).default(0),
});
export type InsertRoomImageVariant = z.infer<typeof insertRoomImageVariantSchema>;
export type RoomImageVariant = typeof roomImageVariants.$inferSelect;

export const imageGeneratorSettings = pgTable("image_generator_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  aspectRatio: text("aspect_ratio").default("1920x1080").notNull(),
});

export const insertImageGeneratorSettingsSchema = createInsertSchema(imageGeneratorSettings).omit({
  id: true,
}).extend({
  userId: z.number(),
  aspectRatio: z.string().default("1920x1080"),
});
export type InsertImageGeneratorSettings = z.infer<typeof insertImageGeneratorSettingsSchema>;
export type ImageGeneratorSettings = typeof imageGeneratorSettings.$inferSelect;

// Task #1920 — Legacy `maintenance_tasks` + `maintenance_comments` were
// merged into the unified `tasks` / `task_comments` tables (defined further
// down with the former operations block). The maintenance-specific status,
// priority, source, and category enums were dropped along with them; see
// `TASK_STATUSES`, `TASK_PRIORITIES`, `TASK_SOURCES`, and `TASK_CATEGORIES`
// for the unified replacements. `maintenance_settings` (per-host SLA + default
// assignee) is intentionally preserved unchanged below.
//
// Task #1932's `sourceChatId` column (so unlink can rewrite the original
// link-comment in place) was migrated onto `taskComments` so the
// chat→ticket unlink rewrite behavior carries over to the unified table.

export type SlaConfig = {
  urgent: number;
  high: number;
  medium: number;
  low: number;
};

export const maintenanceSettings = pgTable("maintenance_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  defaultAssigneeId: integer("default_assignee_id").references(() => appUsers.id, { onDelete: "set null" }),
  slaConfig: jsonb("sla_config").$type<SlaConfig>(),
  // Task #4171 — Task Batching config.
  // When enabled, the auto-batch cron demotes "low" tasks untouched for
  // `autoBatchIdleDays` into the "batch" tier. The hold-window sweep resurfaces
  // batched tasks (back to "low") once they've sat for `batchHoldWindowDays`.
  autoBatchEnabled: boolean("auto_batch_enabled").default(false).notNull(),
  autoBatchIdleDays: integer("auto_batch_idle_days").default(14).notNull(),
  batchHoldWindowDays: integer("batch_hold_window_days").default(30).notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertMaintenanceSettingsSchema = createInsertSchema(maintenanceSettings).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  defaultAssigneeId: z.number().optional().nullable(),
  slaConfig: z.object({
    urgent: z.number().default(4),
    high: z.number().default(24),
    medium: z.number().default(72),
    low: z.number().default(168),
  }).optional().nullable(),
  autoBatchEnabled: z.boolean().optional(),
  autoBatchIdleDays: z.number().int().min(1).optional(),
  batchHoldWindowDays: z.number().int().min(1).optional(),
});

export type InsertMaintenanceSettings = z.infer<typeof insertMaintenanceSettingsSchema>;
export type MaintenanceSettings = typeof maintenanceSettings.$inferSelect;

export const VENDOR_CATEGORIES = ["plumber", "electrician", "hvac", "handyman", "cleaner", "landscaper", "pest_control", "painter", "general_contractor", "other"] as const;
export type VendorCategory = typeof VENDOR_CATEGORIES[number];

export const VENDOR_SERVICE_TYPES = ["cleaning", "handyman", "lawn", "pest", "plumbing", "electrical", "hvac", "painting", "landscaping", "inspection", "other"] as const;
export type VendorServiceType = typeof VENDOR_SERVICE_TYPES[number];

export const VENDOR_ROLES = ["cleaning", "maintenance", "property_manager", "other"] as const;
export type VendorRole = typeof VENDOR_ROLES[number];

export const MAINTENANCE_SPECIALTIES = [
  "electrical", "hvac", "handyman", "plumbing", "landscaping", "pest_control",
  "roof_repair", "laundry_repair", "painting", "appliance_repair", "locksmith",
  "flooring", "drywall", "carpentry", "window_repair", "garage_door",
] as const;
export type MaintenanceSpecialty = typeof MAINTENANCE_SPECIALTIES[number];

export const VENDOR_STATUS = ["active", "inactive"] as const;
export type VendorStatus = typeof VENDOR_STATUS[number];

export const VENDOR_RATE_TYPES = ["fixed", "hourly"] as const;
export type VendorRateType = typeof VENDOR_RATE_TYPES[number];

export const vendors = pgTable("vendors", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  businessName: text("business_name"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  phone: text("phone"),
  email: text("email"),
  address: text("address"),
  location: text("location"),
  category: text("category").notNull(),
  roles: jsonb("roles").default([]).notNull(),
  serviceTypes: jsonb("service_types").default([]).notNull(),
  serviceAreas: jsonb("service_areas").default([]).notNull(),
  defaultRateCents: integer("default_rate_cents"),
  rateType: text("rate_type").default("fixed"),
  // Task #2187 — richer pricing fields for vendor estimates
  flatRateOfferings: jsonb("flat_rate_offerings").default([]).notNull(),
  defaultTravelFeeCents: integer("default_travel_fee_cents"),
  status: text("status").default("active").notNull(),
  tags: jsonb("tags").default([]).notNull(),
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  propertyIds: jsonb("property_ids").default([]).notNull(),
  notes: text("notes"),
  isMarketplaceListed: boolean("is_marketplace_listed").default(false).notNull(),
  // Task #4449 — when true, the vendor is hidden from the public Vendor
  // Marketplace entirely (the portal Marketplace tab is hidden regardless of
  // ownership). Hosts set this when adding a vendor privately so they can only
  // be added by someone who already has the vendor's phone number.
  isMarketplaceBlocked: boolean("is_marketplace_blocked").default(false).notNull(),
  marketplaceTier: text("marketplace_tier"),
  marketplaceBio: text("marketplace_bio"),
  marketplacePhotoUrl: text("marketplace_photo_url"),
  workPhotoUrls: text("work_photo_urls").array(),
  // Stripe subscription wiring for the public Vendor Marketplace ($99/year).
  // The vendor only becomes/stays listed while marketplaceSubscriptionStatus is
  // `active` or `paused` — the webhook handler keeps isMarketplaceListed in
  // sync. `paused` is a vendor-initiated short break (Stripe pause_collection)
  // and DOES unlist them, but preserves the subscription so they can resume
  // without re-checking out.
  stripeCustomerId: text("stripe_customer_id"),
  marketplaceSubscriptionId: text("marketplace_subscription_id"),
  marketplaceSubscriptionStatus: text("marketplace_subscription_status"),
  marketplaceCurrentPeriodEnd: timestamp("marketplace_current_period_end"),
  marketplaceRenewalReminderSentAt: timestamp("marketplace_renewal_reminder_sent_at"),
  selfRegistered: boolean("self_registered").default(false).notNull(),
  aggregateRating: numeric("aggregate_rating"),
  aggregateQuality: numeric("aggregate_quality"),
  aggregateCommunication: numeric("aggregate_communication"),
  aggregateTimeliness: numeric("aggregate_timeliness"),
  aggregateProfessionalism: numeric("aggregate_professionalism"),
  totalReviewsCount: integer("total_reviews_count").default(0).notNull(),
  totalJobsCompleted: integer("total_jobs_completed").default(0).notNull(),
  priority: integer("priority"),
  // Task #1907 — Mercury ACH payout rail.
  // ACH routing/account stored in plaintext for v1 (host-owned data, same
  // sensitivity as the host's own Mercury credentials). Routing/account
  // numbers are required to lazily mint a Mercury recipient; once minted
  // we cache the recipient id so subsequent ACH payouts skip recipient
  // creation. Account type defaults to "checking" — Mercury supports
  // "checking" / "savings".
  achRoutingNumber: text("ach_routing_number"),
  achAccountNumber: text("ach_account_number"),
  achAccountType: text("ach_account_type"),
  mercuryRecipientId: text("mercury_recipient_id"),
  // Task #2103 — Vendor-wide in-app notification toggle for the
  // `service_form_sent` notification (introduced in Task #2100). When
  // disabled, neither the immediate send route nor the scheduled-forms
  // cron will create a `vendor_notifications` row for this vendor. SMS /
  // email service-form preferences continue to live on
  // `vendor_team_members` since those are per-person; this in-app
  // notification is shared by the whole vendor team's bell tray, so a
  // single per-vendor toggle is the source of truth. Defaults to true so
  // existing vendors keep getting the alert until they explicitly mute.
  notifyInAppFormSent: boolean("notify_in_app_form_sent").default(true).notNull(),
  // Task #4171 — Vendor-wide in-app notification toggle for batch dispatch
  // alerts (mirrors notifyInAppFormSent). When disabled, dispatching a task
  // batch to this vendor won't create a `vendor_notifications` row.
  notifyInAppBatchDispatch: boolean("notify_in_app_batch_dispatch").default(true).notNull(),
  // Pay-on-job marketplace model. `tosAcceptedAt` is stamped on first login
  // (via the welcome step); NULL means the vendor has never seen the ToS.
  // `marketplaceOptedIn` drives `isMarketplaceListed` for new vendors —
  // set true when the vendor accepts the welcome-step opt-in.
  tosAcceptedAt: timestamp("tos_accepted_at"),
  marketplaceOptedIn: boolean("marketplace_opted_in").default(false).notNull(),
  // Pro tier ($99/month) subscription tracking.
  // marketplaceTier = "pro" while the subscription is active; null otherwise.
  // marketplaceProSubscriptionId / Status mirror the Stripe subscription for
  // webhook routing — structured the same way as the legacy annual fields above.
  marketplaceProSubscriptionId: text("marketplace_pro_subscription_id"),
  marketplaceProSubscriptionStatus: text("marketplace_pro_subscription_status"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdIdx: index("vendors_user_id_idx").on(table.userId),
}));

export const insertVendorSchema = createInsertSchema(vendors).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1, "Vendor name is required"),
  businessName: z.string().optional().nullable(),
  firstName: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  email: z.string().email().optional().nullable().or(z.literal("")),
  address: z.string().optional().nullable(),
  location: z.string().optional().nullable(),
  category: z.enum(VENDOR_CATEGORIES),
  roles: z.array(z.string()).default([]),
  serviceTypes: z.array(z.string()).default([]),
  serviceAreas: z.array(z.string()).default([]),
  defaultRateCents: z.number().optional().nullable(),
  rateType: z.enum(VENDOR_RATE_TYPES).default("fixed"),
  flatRateOfferings: z.array(z.object({ name: z.string(), priceCents: z.number().int().min(0) })).default([]),
  defaultTravelFeeCents: z.number().int().min(0).optional().nullable(),
  status: z.enum(VENDOR_STATUS).default("active"),
  tags: z.array(z.string()).default([]),
  contactName: z.string().optional().nullable(),
  contactEmail: z.string().email().optional().nullable().or(z.literal("")),
  contactPhone: z.string().optional().nullable(),
  propertyIds: z.array(z.number()).default([]),
  notes: z.string().optional().nullable(),
  isMarketplaceListed: z.boolean().default(false),
  isMarketplaceBlocked: z.boolean().default(false),
  marketplaceTier: z.string().optional().nullable(),
  marketplaceBio: z.string().optional().nullable(),
  marketplacePhotoUrl: z.string().optional().nullable(),
  selfRegistered: z.boolean().default(false),
  priority: z.number().int().positive().optional().nullable(),
});
export type InsertVendor = z.infer<typeof insertVendorSchema>;
export type Vendor = typeof vendors.$inferSelect;

export const vendorHostRelationships = pgTable("vendor_host_relationships", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  hostId: integer("host_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  source: text("source").default("host_added").notNull(),
  status: text("status").default("active").notNull(),
  // Per-host overlay for relationship-linked vendors: the vendor row stays
  // owned by vendors.userId; these fields are THIS host's view (mirrors
  // vendors.roles / service_types / property_ids / priority) so a linked
  // host never writes role/property/priority data onto another tenant's row.
  roles: jsonb("roles").default([]).notNull(),
  serviceTypes: jsonb("service_types").default([]).notNull(),
  propertyIds: jsonb("property_ids").default([]).notNull(),
  priority: integer("priority"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVendorHostRelationshipSchema = createInsertSchema(vendorHostRelationships).omit({
  id: true,
  createdAt: true,
}).extend({
  // Optional (DB defaults to []): most relationship writes only set
  // vendorId/hostId/source; the overlay arrives later from onboarding.
  roles: z.array(z.string()).optional(),
  serviceTypes: z.array(z.string()).optional(),
  propertyIds: z.array(z.number()).optional(),
  priority: z.number().int().positive().optional().nullable(),
});
export type InsertVendorHostRelationship = z.infer<typeof insertVendorHostRelationshipSchema>;
export type VendorHostRelationship = typeof vendorHostRelationships.$inferSelect;

// A vendor as returned by the roster directory: either owned by the tenant
// (isLinked: false) or another tenant's vendor linked via an active
// vendor_host_relationships row (isLinked: true, sanitized to public fields
// with this host's overlay applied).
export type VendorWithLink = Vendor & { isLinked: boolean; relationshipId: number | null };

// ── Pay-on-job marketplace monthly fee records ───────────────────────────────
// Tracks the $19 marketplace fee charged once per vendor per calendar month
// when they receive their first marketplace-driven job (a job from a host other
// than their original inviting host). Unique (vendor_id, month_key) prevents
// double-charging. `paymentTransactionId` links to the payment that triggered
// the charge so the accounting trail is auditable.
export const marketplaceMonthlyFees = pgTable("marketplace_monthly_fees", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  monthKey: text("month_key").notNull(), // 'YYYY-MM'
  paymentTransactionId: integer("payment_transaction_id"),
  chargedCents: integer("charged_cents").notNull().default(1900),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  vendorIdIdx: index("marketplace_monthly_fees_vendor_id_idx").on(t.vendorId),
}));

export const insertMarketplaceMonthlyFeeSchema = createInsertSchema(marketplaceMonthlyFees).omit({
  id: true,
  createdAt: true,
});
export type InsertMarketplaceMonthlyFee = z.infer<typeof insertMarketplaceMonthlyFeeSchema>;
export type MarketplaceMonthlyFee = typeof marketplaceMonthlyFees.$inferSelect;

export const vendorDocuments = pgTable("vendor_documents", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  fileUrl: text("file_url").notNull(),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVendorDocumentSchema = createInsertSchema(vendorDocuments).omit({
  id: true,
  createdAt: true,
});
export type InsertVendorDocument = z.infer<typeof insertVendorDocumentSchema>;
export type VendorDocument = typeof vendorDocuments.$inferSelect;

export const vendorRateOverrides = pgTable("vendor_rate_overrides", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  taskType: text("task_type"),
  amountCents: integer("amount_cents").notNull(),
  rateType: text("rate_type").default("fixed").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVendorRateOverrideSchema = createInsertSchema(vendorRateOverrides).omit({
  id: true,
  createdAt: true,
});
export type InsertVendorRateOverride = z.infer<typeof insertVendorRateOverrideSchema>;
export type VendorRateOverride = typeof vendorRateOverrides.$inferSelect;

// ─── Vendor Team Members ────────────────────────────────────────────────────
export const VENDOR_TEAM_MEMBER_ROLES = ["owner", "admin", "member"] as const;
export type VendorTeamMemberRole = typeof VENDOR_TEAM_MEMBER_ROLES[number];

export const VENDOR_TEAM_MEMBER_STATUSES = ["active", "invited", "removed"] as const;
export type VendorTeamMemberStatus = typeof VENDOR_TEAM_MEMBER_STATUSES[number];

export const vendorTeamMembers = pgTable("vendor_team_members", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email"),
  phone: text("phone").notNull(),
  role: text("role").default("member").notNull(),
  status: text("status").default("invited").notNull(),
  invitedAt: timestamp("invited_at"),
  acceptedAt: timestamp("accepted_at"),
  removedAt: timestamp("removed_at"),
  // Task #2783 — per-team-member rates that auto-fill the accept-with-estimate
  // dialog. Null means "no per-member rate set" → fall back to the vendor-level
  // defaultRateCents / defaultTravelFeeCents / flatRateOfferings.
  hourlyRateCents: integer("hourly_rate_cents"),
  defaultTravelFeeCents: integer("default_travel_fee_cents"),
  flatRateOfferings: jsonb("flat_rate_offerings").default([]).notNull(),
  smsTaskAssigned: boolean("sms_task_assigned").default(true).notNull(),
  smsTaskMessage: boolean("sms_task_message").default(true).notNull(),
  smsFormMessage: boolean("sms_form_message").default(true).notNull(),
  emailTaskAssigned: boolean("email_task_assigned").default(true).notNull(),
  emailTaskMessage: boolean("email_task_message").default(true).notNull(),
  emailFormMessage: boolean("email_form_message").default(true).notNull(),
  // Task #1638 — Vendor opt-out for Stripe payout-status emails (active /
  // restricted / needs attention). Default true so existing vendors keep
  // hearing about disruptions to their payouts unless they explicitly mute.
  emailPayoutStatus: boolean("email_payout_status").default(true).notNull(),
  // Task #1501 — TOTP (two-factor authentication) for vendor portal logins.
  // Stored per team member so each team member has their own authenticator.
  // Vendor owners get an auto-seeded owner row via ensureVendorOwnerExists.
  totpSecret: text("totp_secret"),
  totpEnabled: boolean("totp_enabled").default(false).notNull(),
  totpBackupCodes: text("totp_backup_codes").array(),
  totpEnrolledAt: timestamp("totp_enrolled_at", { withTimezone: true }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("vendor_team_members_vendor_phone_idx").on(t.vendorId, t.phone),
]);

export const insertVendorTeamMemberSchema = createInsertSchema(vendorTeamMembers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  vendorId: z.number().int(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().min(10),
  email: z.string().email().optional().nullable(),
  role: z.enum(VENDOR_TEAM_MEMBER_ROLES).default("member"),
  status: z.enum(VENDOR_TEAM_MEMBER_STATUSES).default("invited"),
  hourlyRateCents: z.number().int().min(0).optional().nullable(),
  defaultTravelFeeCents: z.number().int().min(0).optional().nullable(),
  flatRateOfferings: z.array(z.object({ name: z.string(), priceCents: z.number().int().min(0) })).default([]),
});
export type InsertVendorTeamMember = z.infer<typeof insertVendorTeamMemberSchema>;
export type VendorTeamMember = typeof vendorTeamMembers.$inferSelect;

// ─── Vendor Property Team Assignments ────────────────────────────────────────
export const vendorPropertyTeamAssignments = pgTable("vendor_property_team_assignments", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  // FK declared at the table level to override the auto-generated name —
  // `vendor_property_team_assignments_team_member_id_vendor_team_members_id_fk`
  // is 73 chars and exceeds Postgres's 63-char identifier limit.
  teamMemberId: integer("team_member_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("vendor_property_team_assignments_vendor_property_idx").on(t.vendorId, t.propertyId),
  foreignKey({
    name: "vendor_property_team_assignments_member_fk",
    columns: [t.teamMemberId],
    foreignColumns: [vendorTeamMembers.id],
  }).onDelete("cascade"),
]);

export const insertVendorPropertyTeamAssignmentSchema = createInsertSchema(vendorPropertyTeamAssignments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  vendorId: z.number().int(),
  propertyId: z.number().int(),
  teamMemberId: z.number().int(),
});
export type InsertVendorPropertyTeamAssignment = z.infer<typeof insertVendorPropertyTeamAssignmentSchema>;
export type VendorPropertyTeamAssignment = typeof vendorPropertyTeamAssignments.$inferSelect;

// ─── Vendor Portal Tour Progress (Task #1328) ────────────────────────────────
// Persists per-vendor (and per team-member) state for the reusable guided
// product tour engine. Each (vendorId, teamMemberId, tourId) combination has
// at most one row; tours auto-start on first login when no row exists, and
// the Guide button always re-runs the tour regardless of stored status.
export const VENDOR_PORTAL_TOUR_STATUSES = ["in_progress", "completed", "skipped"] as const;
export type VendorPortalTourStatus = typeof VENDOR_PORTAL_TOUR_STATUSES[number];

export const vendorPortalTourProgress = pgTable("vendor_portal_tour_progress", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  // Nullable so a vendor session without a team member (legacy direct owner
  // sessions) still gets its own row, distinct from each team member's row.
  teamMemberId: integer("team_member_id"),
  tourId: text("tour_id").notNull(),
  status: text("status").default("in_progress").notNull(),
  lastStepIndex: integer("last_step_index").default(0).notNull(),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  skippedAt: timestamp("skipped_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  // Two unique indexes: one for rows with a teamMemberId (per member), and one
  // for owner-only rows (teamMemberId IS NULL). Postgres treats NULLs as
  // distinct in unique indexes, so we need a partial index on the NULL case.
  uniqueIndex("vendor_portal_tour_progress_vendor_member_tour_idx")
    .on(t.vendorId, t.teamMemberId, t.tourId)
    .where(sql`${t.teamMemberId} IS NOT NULL`),
  uniqueIndex("vendor_portal_tour_progress_vendor_tour_owner_idx")
    .on(t.vendorId, t.tourId)
    .where(sql`${t.teamMemberId} IS NULL`),
  foreignKey({
    name: "vendor_portal_tour_progress_member_fk",
    columns: [t.teamMemberId],
    foreignColumns: [vendorTeamMembers.id],
  }).onDelete("cascade"),
]);

export const insertVendorPortalTourProgressSchema = createInsertSchema(vendorPortalTourProgress).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  vendorId: z.number().int(),
  teamMemberId: z.number().int().nullable().optional(),
  tourId: z.string().min(1),
  status: z.enum(VENDOR_PORTAL_TOUR_STATUSES).default("in_progress"),
  lastStepIndex: z.number().int().nonnegative().default(0),
});
export type InsertVendorPortalTourProgress = z.infer<typeof insertVendorPortalTourProgressSchema>;
export type VendorPortalTourProgress = typeof vendorPortalTourProgress.$inferSelect;

// ─── Partner Portal Tour Progress (Task #3621) ───────────────────────────────
// Mirrors vendor_portal_tour_progress, keyed on (partnerId, tourId). The
// partner portal is single-actor per account (no team-member sub-rows), so the
// natural key is simpler. Powers the same reusable guided-tour engine so the
// GuideNudgeBanner can surface on partner pages. The `partners` table is
// declared later in this file, so the FK uses an inline lazy `.references(() =>
// partners.id)` thunk (deferred evaluation) rather than the eager foreignKey()
// helper, which would resolve `partners` before it exists.
export const PARTNER_PORTAL_TOUR_STATUSES = ["in_progress", "completed", "skipped"] as const;
export type PartnerPortalTourStatus = typeof PARTNER_PORTAL_TOUR_STATUSES[number];

export const partnerPortalTourProgress = pgTable("partner_portal_tour_progress", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  tourId: text("tour_id").notNull(),
  status: text("status").default("in_progress").notNull(),
  lastStepIndex: integer("last_step_index").default(0).notNull(),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  skippedAt: timestamp("skipped_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("partner_portal_tour_progress_partner_tour_idx")
    .on(t.partnerId, t.tourId),
]);

export const insertPartnerPortalTourProgressSchema = createInsertSchema(partnerPortalTourProgress).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerId: z.number().int(),
  tourId: z.string().min(1),
  status: z.enum(PARTNER_PORTAL_TOUR_STATUSES).default("in_progress"),
  lastStepIndex: z.number().int().nonnegative().default(0),
});
export type InsertPartnerPortalTourProgress = z.infer<typeof insertPartnerPortalTourProgressSchema>;
export type PartnerPortalTourProgress = typeof partnerPortalTourProgress.$inferSelect;

export const propertyVendorAssignments = pgTable("property_vendor_assignments", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  role: text("role").default("primary").notNull(),
  priorityOrder: integer("priority_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPropertyVendorAssignmentSchema = createInsertSchema(propertyVendorAssignments).omit({
  id: true,
  createdAt: true,
});
export type InsertPropertyVendorAssignment = z.infer<typeof insertPropertyVendorAssignmentSchema>;
export type PropertyVendorAssignment = typeof propertyVendorAssignments.$inferSelect;

export const vendorCreditCards = pgTable("vendor_credit_cards", {
  id: serial("id").primaryKey(),
  hostId: integer("host_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  stripePaymentMethodId: text("stripe_payment_method_id"),
  brand: text("brand"),
  last4: text("last4"),
  expMonth: integer("exp_month"),
  expYear: integer("exp_year"),
  nickname: text("nickname"),
  isDefault: boolean("is_default").default(false).notNull(),
  // Task #1904 — Mercury card auto-provisioning. When present, this row is a
  // mirror of a Mercury-issued card and rename/cancel actions propagate to
  // Mercury via updateCard/cancelCard. Manual Stripe cards leave both null.
  mercuryCardId: text("mercury_card_id"),
  mercuryCardStatus: text("mercury_card_status"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqHostMercuryCard: uniqueIndex("vendor_credit_cards_host_mercury_card_unique")
    .on(table.hostId, table.mercuryCardId)
    .where(sql`${table.mercuryCardId} IS NOT NULL`),
}));

export const insertVendorCreditCardSchema = createInsertSchema(vendorCreditCards).omit({
  id: true,
  createdAt: true,
}).extend({
  hostId: z.number(),
  stripePaymentMethodId: z.string().optional().nullable(),
  brand: z.string().optional().nullable(),
  last4: z.string().optional().nullable(),
  expMonth: z.number().optional().nullable(),
  expYear: z.number().optional().nullable(),
  nickname: z.string().optional().nullable(),
  isDefault: z.boolean().default(false),
  mercuryCardId: z.string().optional().nullable(),
  mercuryCardStatus: z.string().optional().nullable(),
});
export type InsertVendorCreditCard = z.infer<typeof insertVendorCreditCardSchema>;
export type VendorCreditCard = typeof vendorCreditCards.$inferSelect;

export const propertyCardAssignments = pgTable("property_card_assignments", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  vendorCreditCardId: integer("vendor_credit_card_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "property_card_assignments_vendor_card_fk",
    columns: [table.vendorCreditCardId],
    foreignColumns: [vendorCreditCards.id],
  }).onDelete("cascade"),
]);

export const insertPropertyCardAssignmentSchema = createInsertSchema(propertyCardAssignments).omit({
  id: true,
  createdAt: true,
});
export type InsertPropertyCardAssignment = z.infer<typeof insertPropertyCardAssignmentSchema>;
export type PropertyCardAssignment = typeof propertyCardAssignments.$inferSelect;

// ─────────────────────────────────────────────────────────────────────────────
// Task #1920 — Unified task model.
//
// Replaces the legacy `maintenance_tasks` + `operations_tasks` split (and
// their separate message/comment/checklist/photo child tables) with one
// `tasks` table fronted by four child tables:
//   - `task_messages`         host ↔ vendor chat thread (renamed from the
//                             old `task_messages` / `maintenance_task_messages`)
//   - `task_comments`         host-internal notes with @mentions of teammates
//                             (replaces `maintenance_comments`)
//   - `task_checklist_items`  per-task checklist (unchanged shape, now FK to
//                             unified tasks)
//   - `task_photos`           per-task photo evidence (unchanged shape, now
//                             FK to unified tasks)
//
// All status / priority / source / category enums are shared. Routes, UI,
// and write paths are migrated to this model in follow-up tasks; this task
// only lands the schema + storage layer.
// ─────────────────────────────────────────────────────────────────────────────

export const TASK_STATUSES = [
  "requests",
  "open",
  "assigned",
  "accepted",
  "in_progress",
  "on_hold",
  "needs_attention",
  "completed",
  "awaiting_approval",
  "approved",
  "paid",
  "rejected",
  "cancelled",
] as const;
export type TaskStatus = typeof TASK_STATUSES[number];

// Task #4171 — Statuses in which a task is finished and needs no further work:
// vendor-submitted-for-approval, host-approved, paid, host-rejected, or
// cancelled. `awaiting_approval` counts as terminal because the vendor has
// finished the job (it is awaiting only a host decision). Used by the batch
// completion roll-up and the auto-batch / hold-window crons so finished work
// never re-enters the Batch Pool. NOT a member's "active work" set (the vendor
// portal keeps its own list that treats awaiting_approval as still-active).
export const TERMINAL_TASK_STATUSES = [
  // `"complete"` is the legacy host-side finished string still written by the
  // operations PATCH route (its VALID_STATUSES) and treated as terminal by the
  // host UI; `"completed"` is the canonical TASK_STATUSES value. Both must be
  // here or a finished task in `"complete"` would (wrongly) re-enter the Batch
  // Pool / block batch-completion roll-up.
  "complete",
  "completed",
  "awaiting_approval",
  "approved",
  "paid",
  "rejected",
  "cancelled",
] as const;
export type TerminalTaskStatus = typeof TERMINAL_TASK_STATUSES[number];

// Task #4171 — "batch" is a deferred / non-urgent tier below "low". Tasks in
// the batch tier are held in the Batch Pool until a host groups & dispatches
// them as one work order. SLA timers intentionally do not apply to "batch".
export const TASK_PRIORITIES = ["urgent", "high", "medium", "low", "batch"] as const;
export type TaskPriority = typeof TASK_PRIORITIES[number];

export const TASK_SOURCES = [
  "manual",
  "padsplit",
  "sms_message",
  "service_form",
  "auto_rule",
] as const;
export type TaskSource = typeof TASK_SOURCES[number];

export const TASK_CATEGORIES = [
  "HVAC",
  "Plumbing",
  "Electrical",
  "Appliances",
  "Other",
  "Trash",
] as const;
export type TaskCategory = typeof TASK_CATEGORIES[number];

// "cleaner_visit" (Member Cleaning Build 2 Phase 4) — a scheduled visit by a
// professional cleaning vendor, overlaid on the cleaning calendars. It is a
// REAL vendor task: dispatch/chat/time/payment all ride the existing spine.
export const TASK_TYPES = ["cleaning", "maintenance", "inspection", "custom", "cleaner_visit"] as const;
export type TaskType = typeof TASK_TYPES[number];

// Task #4171 — Task Batches. A batch is a host-assembled group of low/batch
// priority tasks at one property, numbered per-property, dispatched to a single
// vendor as one work order through the existing vendor portal. Reuses the tasks
// table for the work itself (tasks.batchId) — this is metadata only.
export const TASK_BATCH_STATUSES = ["open", "dispatched", "completed", "cancelled"] as const;
export type TaskBatchStatus = typeof TASK_BATCH_STATUSES[number];

export const taskBatches = pgTable("task_batches", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Batches are property-scoped so the per-property batch number is meaningful.
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  // Per-property sequential number the host assigns/sees ("Batch #3").
  number: integer("number").notNull(),
  title: text("title"),
  status: text("status").default("open").notNull(),
  vendorId: integer("vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  notes: text("notes"),
  scheduledStart: timestamp("scheduled_start"),
  scheduledEnd: timestamp("scheduled_end"),
  // Task #4171 — opaque handle for an optional time-bound lock code issued on
  // dispatch via the existing lock service (e.g. `ttlock:<conn>:<lock>:<pwdId>`).
  lockCodeHandle: text("lock_code_handle"),
  sentToVendorAt: timestamp("sent_to_vendor_at"),
  completedAt: timestamp("completed_at"),
  createdBy: integer("created_by").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  userIdx: index("task_batches_user_id_idx").on(t.userId),
  propertyIdx: index("task_batches_property_id_idx").on(t.propertyId),
}));

export const insertTaskBatchSchema = createInsertSchema(taskBatches).omit({
  id: true,
  sentToVendorAt: true,
  completedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  propertyId: z.number().optional().nullable(),
  number: z.number(),
  title: z.string().optional().nullable(),
  status: z.enum(TASK_BATCH_STATUSES).default("open"),
  vendorId: z.number().optional().nullable(),
  notes: z.string().optional().nullable(),
  scheduledStart: optionalCoercedDate,
  scheduledEnd: optionalCoercedDate,
  createdBy: z.number().optional().nullable(),
});
export type InsertTaskBatch = z.infer<typeof insertTaskBatchSchema>;
export type TaskBatch = typeof taskBatches.$inferSelect;

export const tasks = pgTable("tasks", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  roomId: integer("room_id").references(() => rooms.id, { onDelete: "set null" }),
  memberId: integer("member_id").references((): AnyPgColumn => members.id, { onDelete: "set null" }),
  reporterMemberId: integer("reporter_member_id").references((): AnyPgColumn => members.id, { onDelete: "set null" }),
  status: text("status").default("open").notNull(),
  priority: text("priority").default("medium").notNull(),
  source: text("source").default("manual").notNull(),
  category: text("category"),
  type: text("type").default("custom").notNull(),
  vendorId: integer("vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  // FK declared at the table level so we can override the auto-generated name
  // (the implicit name `tasks_assigned_team_member_id_vendor_team_members_id_fk`
  // is 57 chars — safely under Postgres's 63-char limit — but we name it
  // explicitly to keep diffs deterministic).
  assignedTeamMemberId: integer("assigned_team_member_id"),
  assigneeId: integer("assignee_id").references(() => appUsers.id, { onDelete: "set null" }),
  paymentRequired: boolean("payment_required").default(false).notNull(),
  priceCents: integer("price_cents"),
  priceType: text("price_type").default("fixed"),
  hoursLogged: numeric("hours_logged"),
  // Task #2187 — vendor estimate & job time tracking
  lockedRateType: text("locked_rate_type"),
  lockedRateCents: integer("locked_rate_cents"),
  estimateHours: numeric("estimate_hours"),
  estimateCents: integer("estimate_cents"),
  travelFeeCents: integer("travel_fee_cents"),
  vendorAcceptedAt: timestamp("vendor_accepted_at"),
  vendorEstimateNote: text("vendor_estimate_note"),
  priceBreakdown: jsonb("price_breakdown"),
  scheduledStart: timestamp("scheduled_start"),
  scheduledEnd: timestamp("scheduled_end"),
  dueDate: timestamp("due_date"),
  completedAt: timestamp("completed_at"),
  approvedAt: timestamp("approved_at"),
  approvedBy: integer("approved_by").references(() => appUsers.id, { onDelete: "set null" }),
  // Task #2477 — real timestamp of when the task transitioned to "paid".
  // Stamped centrally in storage.updateTask on the non-paid → paid edge so
  // payment reporting ("paid this month") no longer mis-buckets on later edits
  // that bump updatedAt.
  paidAt: timestamp("paid_at"),
  padsplitTaskId: text("padsplit_task_id"),
  syncToPadsplit: boolean("sync_to_padsplit").default(false).notNull(),
  padsplitPushError: text("padsplit_push_error"),
  // Task #4748 — when the PadSplit-task watcher successfully dispatched
  // maintenance_request rules for this externally-written task (padsplitTaskId
  // set by the warm-browser harvest). Stamped only when ≥1 rule actually
  // enqueued a message, so a rule added later within the watcher's lookback
  // window still fires (zero-match passes are not permanently marked).
  maintenanceRequestDispatchedAt: timestamp("maintenance_request_dispatched_at"),
  attachmentUrls: text("attachment_urls").array().$defaultFn(() => []).notNull(),
  sentToVendorAt: timestamp("sent_to_vendor_at"),
  // Task #4171 — when a task entered the "batch" deferred tier (manually or via
  // the auto-batch idle cron). The hold-window sweep resurfaces stale batched
  // tasks by comparing this against maintenance_settings.batchHoldWindowDays.
  batchedAt: timestamp("batched_at"),
  // Nullable link to the task_batches group a task belongs to once a host adds
  // it to a Batch Pool group. set null on batch delete keeps the task alive.
  batchId: integer("batch_id").references(() => taskBatches.id, { onDelete: "set null" }),
  linkedApplianceId: integer("linked_appliance_id"),
  isSample: boolean("is_sample").default(false).notNull(),
  // Forms-on-tasks convergence — a task carrying a form template is a
  // "form-task": it renders the fillable form in the vendor portal and its
  // submissions attach via service_form_submissions.task_id. The Forms
  // tab/filter discriminator is simply `form_template_id IS NOT NULL`.
  formTemplateId: integer("form_template_id").references((): AnyPgColumn => serviceFormTemplates.id, { onDelete: "set null" }),
  // Per-task timer visibility. Plain tasks default true (preserves existing
  // behavior); form-tasks copy service_form_templates.time_tracking_default
  // at creation so acknowledgment-style forms don't surface a timer.
  timeTrackingEnabled: boolean("time_tracking_enabled").default(true).notNull(),
  archivedAt: timestamp("archived_at"),
  createdBy: integer("created_by").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  assignedTeamMemberFk: foreignKey({
    name: "tasks_assigned_team_member_fk",
    columns: [t.assignedTeamMemberId],
    foreignColumns: [vendorTeamMembers.id],
  }).onDelete("set null"),
  userIdx: index("tasks_user_id_idx").on(t.userId),
  vendorIdx: index("tasks_vendor_id_idx").on(t.vendorId),
  propertyIdx: index("tasks_property_id_idx").on(t.propertyId),
  linkedApplianceIdx: index("idx_tasks_linked_appliance_id").on(t.linkedApplianceId),
  formTasksIdx: index("tasks_form_template_user_idx").on(t.userId).where(sql`form_template_id IS NOT NULL`),
}));

export const insertTaskSchema = createInsertSchema(tasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sentToVendorAt: true,
  paidAt: true,
}).extend({
  userId: z.number(),
  title: z.string().min(1, "Title is required"),
  description: z.string().max(500, "Description must be 500 characters or fewer").optional().nullable(),
  propertyId: z.number().optional().nullable(),
  roomId: z.number().optional().nullable(),
  memberId: z.number().optional().nullable(),
  reporterMemberId: z.number().optional().nullable(),
  status: z.enum(TASK_STATUSES).default("open"),
  priority: z.enum(TASK_PRIORITIES).default("medium"),
  source: z.enum(TASK_SOURCES).default("manual"),
  category: z.string().optional().nullable(),
  type: z.enum(TASK_TYPES).default("custom"),
  vendorId: z.number().optional().nullable(),
  assigneeId: z.number().optional().nullable(),
  paymentRequired: z.boolean().default(false),
  priceCents: z.number().optional().nullable(),
  priceType: z.enum(VENDOR_RATE_TYPES).optional().nullable(),
  hoursLogged: z.string().optional().nullable(),
  lockedRateType: z.string().optional().nullable(),
  lockedRateCents: z.number().optional().nullable(),
  estimateHours: z.string().optional().nullable(),
  estimateCents: z.number().optional().nullable(),
  travelFeeCents: z.number().optional().nullable(),
  vendorAcceptedAt: optionalCoercedDate,
  vendorEstimateNote: z.string().optional().nullable(),
  priceBreakdown: z.any().optional().nullable(),
  scheduledStart: optionalCoercedDate,
  scheduledEnd: optionalCoercedDate,
  dueDate: z.coerce.date().optional().nullable(),
  completedAt: optionalCoercedDate,
  approvedAt: optionalCoercedDate,
  approvedBy: z.number().optional().nullable(),
  padsplitTaskId: z.string().optional().nullable(),
  syncToPadsplit: z.boolean().default(false),
  padsplitPushError: z.string().optional().nullable(),
  attachmentUrls: z.array(z.string()).optional().default([]),
  linkedApplianceId: z.number().int().positive().optional().nullable(),
  isSample: z.boolean().default(false),
  formTemplateId: z.number().optional().nullable(),
  timeTrackingEnabled: z.boolean().optional(),
  archivedAt: optionalCoercedDate,
  createdBy: z.number().optional().nullable(),
});
export type InsertTask = z.infer<typeof insertTaskSchema>;
export type Task = typeof tasks.$inferSelect;

// Lightweight projection of a member-reported maintenance task, surfaced as an
// inline card in that member's Messages timeline. Intentionally narrow — it
// carries only what the card renders / links with, never the full task row.
export type ReportedTaskSummary = {
  id: number;
  title: string;
  status: string;
  priority: string;
  source: string;
  propertyId: number | null;
  createdAt: Date;
};
// House-wide member-portal view: every member-reported ticket on a property,
// annotated with which member submitted it so housemates can see who reported
// what (and the current status). Used by the member dashboard maintenance card.
export type HouseReportedTaskSummary = ReportedTaskSummary & {
  reporterMemberId: number | null;
  reporterName: string | null;
};
// List/kanban responses include the count of chat messages linked to each
// task so cards can show a badge without an extra query (carry-over from
// legacy `MaintenanceTaskWithLinkedChats`).
export type TaskWithLinkedChats = Task & { linkedChatCount: number; unreadVendorMessageCount: number; activeTimerStartedAt: string | null; formTemplateName?: string | null; formSubmissionCount?: number };
// Vendor-portal list responses include the count of host messages the
// vendor hasn't yet read so cards can show an unread badge.
export type TaskWithUnread = Task & { unreadHostMessageCount: number; formTemplateName?: string | null; formSubmissionCount?: number };

// Task #2187 — vendor job time tracking
export const taskTimeEntries = pgTable("task_time_entries", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull(),
  vendorTeamMemberId: integer("vendor_team_member_id"),
  startedAt: timestamp("started_at").notNull(),
  endedAt: timestamp("ended_at"),
  durationSeconds: integer("duration_seconds"),
  isManual: boolean("is_manual").default(false).notNull(),
  note: text("note"),
  deletedAt: timestamp("deleted_at"),
  deleteNote: text("delete_note"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  taskIdIdx: index("task_time_entries_task_id_idx").on(t.taskId),
  vendorTeamMemberIdx: index("task_time_entries_vendor_team_member_idx").on(t.vendorTeamMemberId),
  taskFk: foreignKey({ columns: [t.taskId], foreignColumns: [tasks.id], name: "tte_task_id_fk" }).onDelete("cascade"),
  vendorMemberFk: foreignKey({ columns: [t.vendorTeamMemberId], foreignColumns: [vendorTeamMembers.id], name: "tte_vendor_team_member_id_fk" }).onDelete("set null"),
}));

// Task #2196 — Audit log of host-initiated task rejections (vendor-completed
// tasks that the host sent back). One row per rejection event — a task can be
// re-completed and re-rejected, in which case multiple rows accrue. Powers:
//   • Vendor "Alerts" panel red banner (joined via taskId)
//   • Admin "Rejected Tasks" table (host + vendor + comments)
//   • Outbound SMS to the assigned vendor team member at rejection time.
export const taskRejections = pgTable("task_rejections", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull(),
  hostUserId: integer("host_user_id").notNull(),
  vendorId: integer("vendor_id"),
  vendorTeamMemberId: integer("vendor_team_member_id"),
  reason: text("reason").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  taskIdIdx: index("task_rejections_task_id_idx").on(t.taskId),
  vendorIdIdx: index("task_rejections_vendor_id_idx").on(t.vendorId),
  hostUserIdIdx: index("task_rejections_host_user_id_idx").on(t.hostUserId),
  taskFk: foreignKey({ columns: [t.taskId], foreignColumns: [tasks.id], name: "tr_task_id_fk" }).onDelete("cascade"),
  hostFk: foreignKey({ columns: [t.hostUserId], foreignColumns: [appUsers.id], name: "tr_host_user_id_fk" }).onDelete("cascade"),
  vendorFk: foreignKey({ columns: [t.vendorId], foreignColumns: [vendors.id], name: "tr_vendor_id_fk" }).onDelete("set null"),
  vendorMemberFk: foreignKey({ columns: [t.vendorTeamMemberId], foreignColumns: [vendorTeamMembers.id], name: "tr_vendor_team_member_id_fk" }).onDelete("set null"),
}));

export const insertTaskRejectionSchema = createInsertSchema(taskRejections).omit({
  id: true,
  createdAt: true,
}).extend({
  reason: z.string().min(1, "A rejection reason is required").max(2000),
});
export type InsertTaskRejection = z.infer<typeof insertTaskRejectionSchema>;
export type TaskRejection = typeof taskRejections.$inferSelect;

export const insertTaskTimeEntrySchema = createInsertSchema(taskTimeEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  taskId: z.number(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  durationSeconds: z.number().int().min(0).optional().nullable(),
  isManual: z.boolean().default(false),
  note: z.string().optional().nullable(),
  vendorTeamMemberId: z.number().optional().nullable(),
});
export type InsertTaskTimeEntry = z.infer<typeof insertTaskTimeEntrySchema>;
export type TaskTimeEntry = typeof taskTimeEntries.$inferSelect;

export const taskMessages = pgTable("task_messages", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  senderId: integer("sender_id").notNull(),
  senderType: text("sender_type").default("host").notNull(),
  vendorTeamMemberId: integer("vendor_team_member_id").references(() => vendorTeamMembers.id, { onDelete: "set null" }),
  body: text("body").notNull(),
  attachments: jsonb("attachments").default([]).notNull(),
  readAtHost: timestamp("read_at_host"),
  readAtVendor: timestamp("read_at_vendor"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  taskIdIdx: index("task_messages_task_id_idx").on(t.taskId),
}));

export const insertTaskMessageSchema = createInsertSchema(taskMessages).omit({
  id: true,
  createdAt: true,
  readAtHost: true,
  readAtVendor: true,
}).extend({
  senderType: z.enum(["host", "vendor", "vendor_owner", "vendor_member", "system"]).default("host"),
  senderId: z.number().int(),
  vendorTeamMemberId: z.number().int().optional().nullable(),
});
export type InsertTaskMessage = z.infer<typeof insertTaskMessageSchema>;
export type TaskMessage = typeof taskMessages.$inferSelect;

// Host-internal notes (with @mentions of teammates). Replaces the legacy
// `maintenance_comments` table.
export const taskComments = pgTable("task_comments", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  authorId: integer("author_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  mentions: jsonb("mentions").$type<number[]>().default([]),
  attachmentUrls: text("attachment_urls").array().$defaultFn(() => []).notNull(),
  // Task #1932 — When a member chat is linked to this task, the comment we
  // post stores the source chat id here so the unlink path can find it and
  // rewrite its `content` in place ("Message from … was removed.") instead
  // of leaving a stale quote in the thread. NULL for all other comments.
  sourceChatId: integer("source_chat_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertTaskCommentSchema = createInsertSchema(taskComments).omit({
  id: true,
  createdAt: true,
}).extend({
  taskId: z.number(),
  authorId: z.number(),
  content: z.string().min(1, "Comment cannot be empty"),
  mentions: z.array(z.number()).optional().nullable(),
  attachmentUrls: z.array(z.string()).optional().default([]),
});
export type InsertTaskComment = z.infer<typeof insertTaskCommentSchema>;
export type TaskComment = typeof taskComments.$inferSelect;

// Task #2003 — Audit trail of every status change on a task.
// Written by createTask (initial status) and updateTask (every status transition).
// note: free-text annotation; "System" actor entries (changedByUserId=null) use
// this to explain why the platform triggered the transition automatically.
export const taskStatusChanges = pgTable("task_status_changes", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  fromStatus: text("from_status"),
  toStatus: text("to_status").notNull(),
  changedByUserId: integer("changed_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  changedByVendorTeamMemberId: integer("changed_by_vendor_team_member_id"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
  note: text("note"),
  actorType: text("actor_type"),
}, (t) => ({
  vtmFk: foreignKey({ columns: [t.changedByVendorTeamMemberId], foreignColumns: [vendorTeamMembers.id], name: "tsc_changed_by_vtm_fk" }).onDelete("set null"),
  taskIdIdx: index("idx_task_status_changes_task_id").on(t.taskId),
  changedAtIdx: index("idx_task_status_changes_changed_at").on(t.changedAt),
}));
export const insertTaskStatusChangeSchema = createInsertSchema(taskStatusChanges).omit({ id: true, changedAt: true });
export type InsertTaskStatusChange = z.infer<typeof insertTaskStatusChangeSchema>;
export type TaskStatusChange = typeof taskStatusChanges.$inferSelect;

// Task #4337 — Host-originated task field change log. This is the single
// contract between the Replit app (the request path) and the warm-browser
// background sync engine. The app appends one row per host-edited syncable
// field (which table, which row, which attribute, the old value, the new value,
// and when it changed); the engine reads unconsumed rows, pushes them back to
// PadSplit, then stamps `consumedAt` so an entry is never pushed twice.
//   • Only genuine host edits are logged — the inbound PadSplit sync writes
//     directly via storage and never reaches the host route, so PadSplit's own
//     values are never mistaken for host edits.
//   • Payment-lifecycle status transitions (completed / awaiting_approval /
//     approved / paid, plus the legacy "complete") are excluded at log time so
//     they can never be pushed back to PadSplit.
export const taskChangeLogs = pgTable("task_change_logs", {
  id: serial("id").primaryKey(),
  tableName: text("table_name").notNull(),
  rowId: integer("row_id").notNull(),
  attribute: text("attribute").notNull(),
  fromValue: text("from_value"),
  toValue: text("to_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
  // Nullable consumption marker — null means "not yet pushed to PadSplit by the
  // background sync engine". Stamped once the entry has been acked.
  consumedAt: timestamp("consumed_at"),
}, (t) => ({
  consumedAtIdx: index("idx_task_change_logs_consumed_at").on(t.consumedAt),
  tableRowIdx: index("idx_task_change_logs_table_row").on(t.tableName, t.rowId),
}));
export const insertTaskChangeLogSchema = createInsertSchema(taskChangeLogs).omit({ id: true, changedAt: true, consumedAt: true });
export type InsertTaskChangeLog = z.infer<typeof insertTaskChangeLogSchema>;
export type TaskChangeLog = typeof taskChangeLogs.$inferSelect;

export const taskChecklistItems = pgTable("task_checklist_items", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  orderIndex: integer("order_index").default(0).notNull(),
  text: text("text").notNull(),
  photoRequired: boolean("photo_required").default(false).notNull(),
  completedAt: timestamp("completed_at"),
  completedById: integer("completed_by_id").references(() => appUsers.id, { onDelete: "set null" }),
  photoUrl: text("photo_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertTaskChecklistItemSchema = createInsertSchema(taskChecklistItems).omit({
  id: true,
  createdAt: true,
  completedById: true,
});
export type InsertTaskChecklistItem = z.infer<typeof insertTaskChecklistItemSchema>;
export type TaskChecklistItem = typeof taskChecklistItems.$inferSelect;

// The unified task automation/recurrence engine. A rule with trigger
// "recurring" spawns tasks (or form-tasks when form_template_id is set) on a
// schedule — the schedule columns mirror the retired service_form_schedules
// shape so computeNextServiceFormSendAt-style next-run math carries over.
// Vendor-recipient rules spawn portal form-tasks; member/phone-recipient
// rules mint tokenized service_form_links (the member magic-link path).
// The legacy task_template_id column was dropped — it dangled at a
// task_templates table that never existed; spawned-task fields live in
// task_defaults instead.
export const taskRules = pgTable("task_rules", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  trigger: text("trigger").notNull(),
  filters: jsonb("filters").default({}).notNull(),
  formTemplateId: integer("form_template_id").references((): AnyPgColumn => serviceFormTemplates.id, { onDelete: "set null" }),
  taskDefaults: jsonb("task_defaults").$type<{
    title?: string;
    description?: string;
    priority?: string;
    type?: string;
    category?: string;
    priceCents?: number;
    paymentRequired?: boolean;
  }>().default({}).notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  memberId: integer("member_id").references((): AnyPgColumn => members.id, { onDelete: "set null" }),
  recipientPhone: text("recipient_phone"),
  recipientName: text("recipient_name"),
  frequency: text("frequency"),
  dayOfWeek: text("day_of_week"),
  dayOfMonth: integer("day_of_month"),
  timeOfDay: text("time_of_day").default("09:00"),
  timezone: text("timezone").default("America/Chicago"),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  usePrimaryVendor: boolean("use_primary_vendor").default(true).notNull(),
  primaryVendorId: integer("primary_vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  fallbackVendorId: integer("fallback_vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  fallbackEnabled: boolean("fallback_enabled").default(false).notNull(),
  fallbackTimerMinutes: integer("fallback_timer_minutes").default(30).notNull(),
  fallbackChain: jsonb("fallback_chain").default([]).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const TASK_RULE_FREQUENCIES = ["daily", "weekly", "biweekly", "monthly", "quarterly", "biannual"] as const;
export type TaskRuleFrequency = typeof TASK_RULE_FREQUENCIES[number];

export const insertTaskRuleSchema = createInsertSchema(taskRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastRunAt: true,
  nextRunAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1),
  trigger: z.string(),
  filters: z.record(z.any()).default({}),
  formTemplateId: z.number().optional().nullable(),
  taskDefaults: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    type: z.enum(TASK_TYPES).optional(),
    category: z.string().optional(),
    priceCents: z.number().int().optional(),
    paymentRequired: z.boolean().optional(),
  }).default({}),
  propertyId: z.number().optional().nullable(),
  memberId: z.number().optional().nullable(),
  recipientPhone: z.string().optional().nullable(),
  recipientName: z.string().optional().nullable(),
  frequency: z.enum(TASK_RULE_FREQUENCIES).optional().nullable(),
  dayOfWeek: z.string().optional().nullable(),
  dayOfMonth: z.number().int().min(1).max(31).optional().nullable(),
  timeOfDay: z.string().optional().nullable(),
  timezone: z.string().optional().nullable(),
  usePrimaryVendor: z.boolean().default(true),
  primaryVendorId: z.number().optional().nullable(),
  fallbackVendorId: z.number().optional().nullable(),
  fallbackEnabled: z.boolean().default(false),
  fallbackTimerMinutes: z.number().default(30),
  fallbackChain: z.array(z.any()).default([]),
  isActive: z.boolean().default(true),
});
export type InsertTaskRule = z.infer<typeof insertTaskRuleSchema>;
export type TaskRule = typeof taskRules.$inferSelect;

// Task #1920 — Legacy `task_messages` (operations) and
// `maintenance_task_messages` were unified into the single `task_messages`
// table defined above with the unified `tasks` block.

export const REVIEW_STATUSES = ["active", "edited", "hidden", "disputed"] as const;

export const vendorReviews = pgTable("vendor_reviews", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").references(() => tasks.id, { onDelete: "set null" }),
  hostId: integer("host_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  overallRating: integer("overall_rating").notNull(),
  qualityRating: integer("quality_rating"),
  communicationRating: integer("communication_rating"),
  timelinessRating: integer("timeliness_rating"),
  professionalismRating: integer("professionalism_rating"),
  publicComment: text("public_comment"),
  privateNote: text("private_note"),
  shareHostName: boolean("share_host_name").default(false).notNull(),
  status: text("status").default("active").notNull(),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  editedAt: timestamp("edited_at"),
  lockedAt: timestamp("locked_at"),
});

export const insertVendorReviewSchema = createInsertSchema(vendorReviews).omit({
  id: true,
  submittedAt: true,
}).extend({
  taskId: z.number().optional().nullable(),
  hostId: z.number(),
  vendorId: z.number(),
  overallRating: z.number().min(1).max(5),
  qualityRating: z.number().min(1).max(5).optional().nullable(),
  communicationRating: z.number().min(1).max(5).optional().nullable(),
  timelinessRating: z.number().min(1).max(5).optional().nullable(),
  professionalismRating: z.number().min(1).max(5).optional().nullable(),
  publicComment: z.string().optional().nullable(),
  privateNote: z.string().optional().nullable(),
  shareHostName: z.boolean().default(false),
});
export type InsertVendorReview = z.infer<typeof insertVendorReviewSchema>;
export type VendorReview = typeof vendorReviews.$inferSelect;

export const vendorReviewResponses = pgTable("vendor_review_responses", {
  id: serial("id").primaryKey(),
  reviewId: integer("review_id").notNull().references(() => vendorReviews.id, { onDelete: "cascade" }),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  body: text("body").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVendorReviewResponseSchema = createInsertSchema(vendorReviewResponses).omit({
  id: true,
  createdAt: true,
});
export type InsertVendorReviewResponse = z.infer<typeof insertVendorReviewResponseSchema>;
export type VendorReviewResponse = typeof vendorReviewResponses.$inferSelect;

export const paymentAccounts = pgTable("payment_accounts", {
  id: serial("id").primaryKey(),
  hostId: integer("host_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  stripeCustomerId: text("stripe_customer_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPaymentAccountSchema = createInsertSchema(paymentAccounts).omit({
  id: true,
  createdAt: true,
});
export type InsertPaymentAccount = z.infer<typeof insertPaymentAccountSchema>;
export type PaymentAccount = typeof paymentAccounts.$inferSelect;

export const VENDOR_PAYOUT_SPEEDS = ["standard", "instant"] as const;
export type VendorPayoutSpeed = typeof VENDOR_PAYOUT_SPEEDS[number];

export const vendorPayoutAccounts = pgTable("vendor_payout_accounts", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().unique().references(() => vendors.id, { onDelete: "cascade" }),
  stripeConnectId: text("stripe_connect_id"),
  status: text("status").default("pending").notNull(),
  country: text("country").default("US").notNull(),
  ownedByMemberId: integer("owned_by_member_id"),
  // Vendor-level default for how every payout is delivered. `standard` = free,
  // ~2 business days. `instant` = 1% fee, deducted from the vendor's payout
  // amount. Per-transaction overrides (chosen on the Payments tab) win over
  // this default. When the vendor flips to `instant`, the server also flips
  // the Stripe account schedule to `manual` so PadPulse can drive each payout
  // individually rather than letting Stripe auto-batch them.
  defaultPayoutSpeed: text("default_payout_speed").default("standard").notNull(),
  // Stripe's `requirements.disabled_reason` for the connected account. Set
  // when Stripe disables charges/payouts on the account (e.g.
  // "requirements.past_due", "rejected.fraud"). Cleared back to null when
  // the account is healthy again. Surfaced inline in the vendor portal so
  // restricted vendors see why their payouts are paused without re-opening
  // the Stripe dialog. Status is also flipped to "restricted" in that case.
  disabledReason: text("disabled_reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertVendorPayoutAccountSchema = createInsertSchema(vendorPayoutAccounts).omit({
  id: true,
  createdAt: true,
});
export type InsertVendorPayoutAccount = z.infer<typeof insertVendorPayoutAccountSchema>;
export type VendorPayoutAccount = typeof vendorPayoutAccounts.$inferSelect;

export const PAYMENT_STATUSES = [
  "pending",
  "processing",
  "succeeded",
  "failed",
  "requires_3ds",
  "refunded",
  // Partial refund: at least one refund has been recorded but the underlying
  // PaymentIntent still has a remaining balance. Distinct from `refunded` so
  // the UI can show a "Partially refunded — $X remaining" badge.
  "partially_refunded",
  // Task #1907 — Mercury ACH rail intermediate state: SendMoneyRequest staged,
  // host hasn't approved inside Mercury yet. Terminal "succeeded" still fires
  // when Mercury reports the transfer as settled.
  "awaiting_mercury_approval",
  // Host cancelled the SendMoneyRequest before approval (or it expired).
  "cancelled",
] as const;

// Task #1907 — Vendor payment rail. Stripe = card on file + Connect transfer
// (existing default). Mercury ACH = SendMoneyRequest staged in the host's
// Mercury account, host approves inside Mercury, ACH moves to the vendor's
// bank. The 3% PadPulse host-side fee still applies on both rails (collected
// separately for ACH; split inline for Stripe).
export const PAYMENT_RAILS = ["stripe", "mercury_ach"] as const;
export type PaymentRail = typeof PAYMENT_RAILS[number];

// Task #4074 — Manual (off-platform) vendor payments. When a host pays a vendor
// outside PadPulse (cash, check, Zelle, Venmo, or a card on file they swiped
// themselves) they record it here for the paper trail instead of running a
// Stripe/Mercury charge. One record per task (the unique index enforces it);
// edits update the same row. `cardId` is only set when `method === "card"` and
// references the card-on-file the host used physically — no charge is created.
export const MANUAL_PAYMENT_METHODS = ["card", "cash", "check", "zelle", "venmo"] as const;
export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

export const manualVendorPayments = pgTable("manual_vendor_payments", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  amountCents: integer("amount_cents").notNull(),
  method: text("method").notNull(),
  // Card-on-file the host used (only for method "card"); set null on card delete
  // so the historical payment record survives.
  cardId: integer("card_id").references(() => vendorCreditCards.id, { onDelete: "set null" }),
  note: text("note"),
  // Task #4085 — Optional proof-of-payment attachments (receipt, check image,
  // Zelle/Venmo screenshot). Stored as normalized `/objects/...` paths claimed
  // under the host's ACL; one manual payment can carry several files.
  proofUrls: text("proof_urls").array().$defaultFn(() => []).notNull(),
  recordedByUserId: integer("recorded_by_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uniqTask: uniqueIndex("manual_vendor_payments_task_id_unique").on(table.taskId),
}));

export const insertManualVendorPaymentSchema = createInsertSchema(manualVendorPayments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  taskId: z.number(),
  // Task #4284 — $0 is a valid manual payment (archive / historic close where
  // the host never actually pays anything through PadPulse).
  amountCents: z.number().int().nonnegative(),
  method: z.enum(MANUAL_PAYMENT_METHODS),
  cardId: z.number().int().positive().optional().nullable(),
  note: z.string().optional().nullable(),
  proofUrls: z.array(z.string()).optional().default([]),
  recordedByUserId: z.number(),
});
export type InsertManualVendorPayment = z.infer<typeof insertManualVendorPaymentSchema>;
export type ManualVendorPayment = typeof manualVendorPayments.$inferSelect;

export const paymentTransactions = pgTable("payment_transactions", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").references(() => tasks.id, { onDelete: "set null" }),
  hostId: integer("host_id").notNull().references(() => appUsers.id, { onDelete: "restrict" }),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "restrict" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  vendorCreditCardId: integer("vendor_credit_card_id"),
  vendorAmountCents: integer("vendor_amount_cents").notNull(),
  stripeFeeCents: integer("stripe_fee_cents"),
  hostTotalCents: integer("host_total_cents"),
  // Snapshot of the PM→owner upcharge resolved at approval time via
  // resolveEffectiveFeeConfig (PM defaults merged with the owning owner's
  // billing override). These are NOT part of the Stripe charge to the host
  // (PadPulse's platform fee lives in stripeFeeCents); they record the markup
  // the PM bills the property's owner so the owner invoice engine uses the
  // rate that was in effect when the work was approved. Null on legacy rows
  // and on tasks with no owner-linked property group.
  ownerUpchargePercent: numeric("owner_upcharge_percent"),
  ownerUpchargeCents: integer("owner_upcharge_cents"),
  status: text("status").default("pending").notNull(),
  stripeChargeId: text("stripe_charge_id"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  stripeTransferId: text("stripe_transfer_id"),
  // Deterministic key used for both the local pending-row dedupe AND Stripe's
  // PaymentIntent idempotency header so that a retried `/approve` POST never
  // produces two charges.
  idempotencyKey: text("idempotency_key"),
  // Decline / 3DS context surfaced from the latest payment_intent.* event,
  // used by the Operations Payments tab to render "Card declined: insufficient
  // funds" / "Action required" banners.
  failureCode: text("failure_code"),
  failureMessage: text("failure_message"),
  // Dispute lifecycle: 'open' once `charge.dispute.created` arrives,
  // 'won' / 'lost' / 'warning_closed' from `charge.dispute.closed`.
  // Refunds are blocked while this is `open`.
  disputeStatus: text("dispute_status"),
  // Running totals updated as `charge.refunded` events arrive. Both reflect
  // amounts in cents; `applicationFeeRefundedCents` is what PadPulse has
  // returned out of its fee.
  refundedAmountCents: integer("refunded_amount_cents").default(0).notNull(),
  applicationFeeRefundedCents: integer("application_fee_refunded_cents").default(0).notNull(),
  // Task #1907 — Payment rail. Defaults to 'stripe' for back-compat; Mercury
  // ACH rows set this to 'mercury_ach' and populate mercurySendMoneyRequestId
  // + mercuryStatus instead of the stripe* columns.
  paymentRail: text("payment_rail").default("stripe").notNull(),
  mercurySendMoneyRequestId: text("mercury_send_money_request_id"),
  // Mercury SendMoneyRequest lifecycle status — pending | approved | sent |
  // failed | cancelled. Mirrors Mercury's `status` field verbatim so the UI
  // can render a friendly badge without re-deriving.
  mercuryStatus: text("mercury_status"),
  approvedAt: timestamp("approved_at"),
  initiatedAt: timestamp("initiated_at"),
  settledAt: timestamp("settled_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "payment_transactions_vendor_card_fk",
    columns: [table.vendorCreditCardId],
    foreignColumns: [vendorCreditCards.id],
  }).onDelete("set null"),
  // Partial unique index on idempotency_key — Stripe PaymentIntent retry
  // dedupe (legacy rows have NULL keys and must not collide). Mirrors the
  // matching block in scripts/deploy-migrate.sql.
  uniqueIndex("payment_transactions_idempotency_key_unique")
    .on(table.idempotencyKey)
    .where(sql`${table.idempotencyKey} IS NOT NULL`),
  uniqueIndex("payment_transactions_mercury_smr_unique")
    .on(table.mercurySendMoneyRequestId)
    .where(sql`mercury_send_money_request_id IS NOT NULL`),
]);

export const insertPaymentTransactionSchema = createInsertSchema(paymentTransactions).omit({
  id: true,
  createdAt: true,
});
export type InsertPaymentTransaction = z.infer<typeof insertPaymentTransactionSchema>;
export type PaymentTransaction = typeof paymentTransactions.$inferSelect;

// Per-payout history. One row per Stripe `payouts.create` call we issue on a
// connected account. We use this to (a) attribute payout.paid / payout.failed
// webhooks back to the source PaymentTransaction, (b) show vendors whether
// their money has actually landed in their bank, and (c) record the 1% instant
// fee that Stripe deducts when `method='instant'`.
export const vendorPayouts = pgTable("vendor_payouts", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  paymentTransactionId: integer("payment_transaction_id"),
  stripePayoutId: text("stripe_payout_id").notNull().unique(),
  amountCents: integer("amount_cents").notNull(),
  method: text("method").default("standard").notNull(),
  instantFeeCents: integer("instant_fee_cents"),
  status: text("status").default("pending").notNull(),
  arrivalDate: timestamp("arrival_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "vendor_payouts_payment_transaction_fk",
    columns: [table.paymentTransactionId],
    foreignColumns: [paymentTransactions.id],
  }).onDelete("set null"),
]);

export const insertVendorPayoutSchema = createInsertSchema(vendorPayouts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertVendorPayout = z.infer<typeof insertVendorPayoutSchema>;
export type VendorPayout = typeof vendorPayouts.$inferSelect;

// Task #1244 — Per-vendor in-app notifications surfaced in the vendor portal
// bell tray. Mirrors the host `notifications` table but keyed on `vendorId`
// instead of `userId` because vendors authenticate against the vendor portal
// (not appUsers) and so cannot receive host notifications. Used to alert
// vendors about money events on their payment_transactions / vendor_payouts
// rows (payout failed, dispute opened/closed, instant payout paid).
export const vendorNotifications = pgTable("vendor_notifications", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  link: text("link"),
  read: boolean("read").default(false).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export const insertVendorNotificationSchema = createInsertSchema(vendorNotifications).omit({
  id: true,
  createdAt: true,
}).extend({
  vendorId: z.number(),
  type: z.string().min(1),
  title: z.string().min(1),
  message: z.string().min(1),
  link: z.string().optional().nullable(),
  read: z.boolean().default(false),
});
export type InsertVendorNotification = z.input<typeof insertVendorNotificationSchema>;
export type VendorNotification = typeof vendorNotifications.$inferSelect;

// processedStripeEvents — defined later in this file under the Task #1227
// billing-price-catalog block. Both Task #1184 (vendor payments) and Task
// #1227 (subscription billing) use the same dedupe table; one definition
// serves both webhook surfaces.

export const taskPhotos = pgTable("task_photos", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  checklistItemId: integer("checklist_item_id").references(() => taskChecklistItems.id, { onDelete: "set null" }),
  fileUrl: text("file_url").notNull(),
  s3Key: text("s3_key"),
  uploadedBy: integer("uploaded_by").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
  isDisputeEvidence: boolean("is_dispute_evidence").default(false).notNull(),
  scheduledDeletionAt: timestamp("scheduled_deletion_at"),
});

export const insertTaskPhotoSchema = createInsertSchema(taskPhotos).omit({
  id: true,
  uploadedAt: true,
});
export type InsertTaskPhoto = z.infer<typeof insertTaskPhotoSchema>;
export type TaskPhoto = typeof taskPhotos.$inferSelect;

export const SEAM_DEVICE_TYPES = ["smart_lock", "thermostat"] as const;
export type SeamDeviceType = typeof SEAM_DEVICE_TYPES[number];

export const seamConnections = pgTable("seam_connections", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  provider: text("provider").notNull(),
  accountEmail: text("account_email"),
  apiKey: text("api_key").notNull(),
  deviceType: text("device_type").notNull(),
  // Allowed status values: 'connected' (written by the OAuth/connect flow, used
  // by the thermostat poll cron), 'disconnected' (written on revoke/error).
  // Do NOT use 'active' — no code path writes that value and the poll cron
  // explicitly matches 'connected'. Keeping this comment prevents future drift.
  status: text("status").default("connected").notNull(),
  connectedAccountId: text("connected_account_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertSeamConnectionSchema = createInsertSchema(seamConnections).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  provider: z.string().min(1),
  accountEmail: z.string().email().optional().nullable(),
  apiKey: z.string().min(1),
  deviceType: z.enum(SEAM_DEVICE_TYPES),
  status: z.string().default("connected"),
  connectedAccountId: z.string().nullable().optional(),
});
export type InsertSeamConnection = z.infer<typeof insertSeamConnectionSchema>;
export type SeamConnection = typeof seamConnections.$inferSelect;

export const LOCK_CODE_FORMATS = ["random_number"] as const;
export type LockCodeFormat = typeof LOCK_CODE_FORMATS[number];

export const deviceSettings = pgTable("device_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  codeFormat: text("code_format").default("random_number").notNull(),
  codeDigits: integer("code_digits").default(4).notNull(),
  notifyFirstUse: boolean("notify_first_use").default(false).notNull(),
  messageLowBattery: boolean("message_low_battery").default(false).notNull(),
  lowBatteryThreshold: integer("low_battery_threshold").default(20).notNull(),
  lowBatteryNotifyTime: text("low_battery_notify_time").default("17:00").notNull(),
  // Task #2898 — IANA timezone used to convert lowBatteryNotifyTime (a wall-clock
  // "HH:MM") into the correct UTC instant before the daily battery cron decides
  // whether the notification window has opened. Defaults to the platform default
  // (America/Chicago) so existing hosts keep behaving sensibly until they pick one.
  timezone: text("timezone").default("America/Chicago").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertDeviceSettingsSchema = createInsertSchema(deviceSettings).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  codeFormat: z.enum(LOCK_CODE_FORMATS).default("random_number"),
  codeDigits: z.coerce.number().min(4).max(6).default(4),
  notifyFirstUse: z.boolean().default(false),
  messageLowBattery: z.boolean().default(false),
  lowBatteryThreshold: z.coerce.number().min(1).max(100).default(20),
  lowBatteryNotifyTime: z.string().regex(/^\d{2}:\d{2}$/, "Must be HH:MM format").default("17:00"),
  timezone: z
    .string()
    .min(1)
    .default("America/Chicago")
    .refine((tz) => {
      // Reject non-IANA strings so a bad value can't reach the cron and throw
      // inside Intl.DateTimeFormat for that host.
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    }, "Must be a valid IANA timezone"),
});
export type InsertDeviceSettings = z.infer<typeof insertDeviceSettingsSchema>;
export type DeviceSettings = typeof deviceSettings.$inferSelect;

export const lockBatteryNotifications = pgTable("lock_battery_notifications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceId: text("device_id").notNull(),
  deviceName: text("device_name"),
  locationName: text("location_name"),
  batteryLevel: integer("battery_level"),
  // Task #1699 — TTLock offline detection. The cron writes a row with
  // is_offline=true (and batteryLevel left null) when a TTLock lock has
  // no working gateway, so the /app/devices Notifications panel and
  // Lock Health card surface offline locks the same way they already
  // surface low-battery ones.
  isOffline: boolean("is_offline").default(false).notNull(),
  notifiedAt: timestamp("notified_at").defaultNow().notNull(),
  lastSmsSentAt: timestamp("last_sms_sent_at"),
});

export type LockBatteryNotification = typeof lockBatteryNotifications.$inferSelect;

export const lockMemberAssignments = pgTable("lock_member_assignments", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceId: text("device_id").notNull(),
  memberId: integer("member_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userDeviceUnique: uniqueIndex("lock_member_assignments_user_device_idx").on(table.userId, table.deviceId),
}));

export const insertLockMemberAssignmentSchema = createInsertSchema(lockMemberAssignments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLockMemberAssignment = z.infer<typeof insertLockMemberAssignmentSchema>;
export type LockMemberAssignment = typeof lockMemberAssignments.$inferSelect;

// Task #3501 — host-chosen passcode override for a member on the Devices →
// Members tab. When a host opens the "Code Name" dropdown and picks a specific
// passcode (because the auto-match guessed wrong), that choice is remembered
// here and honored ahead of the auto-matcher on subsequent loads. The chosen
// code is identified by a single opaque, provider-prefixed `codeId` string that
// the members-access endpoint emits per candidate — TTLock codes use
// `${connectionId}:${ttlockLockId}:${keyboardPwdId}` and Seam codes use
// `seam:${connectionId}:${deviceId}:${accessCodeId}`. Keeping it opaque lets the
// same override row pin a code on ANY provider's lock (TTLock, Sifely, Seam)
// without per-provider columns. One override per (host, member, lock/device) so
// a member whose room has multiple locks can pin a distinct code per lock.
export const memberCodeOverrides = pgTable("member_code_overrides", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  memberId: integer("member_id").notNull(),
  /** The drawer deviceId of the lock this override applies to. Derived
   * server-side from `codeId` (TTLock → `ttlock-${connectionId}-${ttlockLockId}`,
   * Seam → the deviceId segment of `seam:${connectionId}:${deviceId}:${codeId}`)
   * so a member's room with multiple locks can each carry their own pinned code. */
  deviceId: text("device_id").notNull(),
  codeId: text("code_id").notNull(),
  /** The passcode name on the lock immediately before the last push rename.
   * Stored so Last Used can still resolve unlock records that were keyed under
   * the old name (unlock records store `username` = the passcode's name at the
   * time of use, so a rename would otherwise blank out historical Last Used). */
  prevCodeName: text("prev_code_name"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userMemberDeviceUnique: uniqueIndex("member_code_overrides_user_member_device_idx").on(table.userId, table.memberId, table.deviceId),
}));

export const insertMemberCodeOverrideSchema = createInsertSchema(memberCodeOverrides).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertMemberCodeOverride = z.infer<typeof insertMemberCodeOverrideSchema>;
export type MemberCodeOverride = typeof memberCodeOverrides.$inferSelect;

// Task #941 — Hosts can mute the recurring lock-health notifications emitted
// by `lock-health-poller` when they're already aware of a lock's problem
// (e.g. battery delivery in transit). A mute can target a single device or
// an entire property, optionally scoped to a single condition (low_battery
// vs offline) and an expiry window. `expiresAt = null` means "until I
// unmute". Exactly one of (deviceId, propertyId) must be set; we don't
// enforce that at the DB level since the routes validate it.
export const lockHealthMutes = pgTable("lock_health_mutes", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // "device" mute: deviceId is the same string the poller emits in the
  // notification link (e.g. `ttlock-<connId>-<lockId>` or a Seam device_id).
  deviceId: text("device_id"),
  // "property" mute: blanket mute for every lock the poller resolves to
  // this property (via the same name-overlap heuristic used elsewhere).
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "cascade" }),
  // "low_battery" | "offline" | "all" — keeps offline alerts firing if a
  // host only wants to silence a known-low battery, and vice versa.
  condition: text("condition").notNull().default("all"),
  // null = mute indefinitely until manually cleared.
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  byUser: index("lock_health_mutes_user_idx").on(table.userId),
}));

export const insertLockHealthMuteSchema = createInsertSchema(lockHealthMutes).omit({
  id: true,
  createdAt: true,
}).extend({
  condition: z.enum(["low_battery", "offline", "all"]).default("all"),
  deviceId: z.string().min(1).nullable().optional(),
  propertyId: z.number().int().positive().nullable().optional(),
  expiresAt: z.date().nullable().optional(),
});
export type InsertLockHealthMute = z.infer<typeof insertLockHealthMuteSchema>;
export type LockHealthMute = typeof lockHealthMutes.$inferSelect;

export const members = pgTable("members", {
  id: serial("id").primaryKey(),
  // Nullable so a member row can exist without a linked host account. When a
  // member→host link is needed it can live in the `members_users` mapping table
  // instead of being forced onto every `members` row at insert time. The FK to
  // app_users (with cascade) still holds whenever user_id is set.
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  // Written by the harvest/members_workflow; nullable for manual-entry rows.
  displayName: text("display_name"),
  legalName: text("legal_name"),
  // Member-uploaded profile photo (object-storage path, e.g.
  // /objects/uploads/<uuid>) shown to housemates in the member portal. Stored
  // with a public-read ACL so roommates (who have member sessions, not app
  // sessions) can view it.
  photoUrl: text("photo_url"),
  email: text("email"),
  phone: text("phone"),
  // NOTE: legacy `property_id` column dropped — current property assignment
  // lives in `properties_members` (read via storage.getCurrentPropertyIdForMember).
  roomId: integer("room_id").references(() => rooms.id, { onDelete: "set null" }),
  status: text("status").default("active").notNull(),
  // Task #866 — Member type controls outbound messaging channel.
  //   "direct"   — Twilio SMS using the host's per-host messagingPhoneNumber.
  //   "padsplit" — PadSplit Messenger (currently a stubbed adapter).
  // Default is "direct" so existing manually-managed members continue to use
  // SMS unchanged. Members synced from PadSplit are upserted as "padsplit".
  type: text("type").default("direct").notNull(),
  moveInDate: timestamp("move_in_date"),
  // Task #2238 — scheduled move-out / cycle-end date pulled from PadSplit
  // when a member's tenancy is ending (move-out booked or notice given).
  // Surfaced as "Code End" on the Devices → Members tab. Nullable while the
  // member is still in residence with no scheduled departure.
  moveOutDate: timestamp("move_out_date"),
  padsplitMemberId: text("padsplit_member_id"),
  // Task #1566 — URL-derived occupant ID from PadSplit member profile links
  // (e.g. /members/{id}, /host/member/{id}). Distinct from padsplitMemberId
  // which may hold the true PadSplit member UID. Nullable; populated lazily
  // on the next sync for existing rows.
  occupantId: text("occupant_id"),
  carMake: text("car_make"),
  carModel: text("car_model"),
  carYear: text("car_year"),
  carColor: text("car_color"),
  licensePlate: text("license_plate"),
  licensePlateState: text("license_plate_state"),
  emergencyContactName: text("emergency_contact_name"),
  emergencyContactPhone: text("emergency_contact_phone"),
  emergencyContactRelationship: text("emergency_contact_relationship"),
  hasServiceAnimal: boolean("has_service_animal").default(false),
  serviceAnimalType: text("service_animal_type"),
  paymentStatus: text("payment_status").default("current"),
  weeklyRate: numeric("weekly_rate"),
  totalDue: numeric("total_due"),
  lastPaymentDate: timestamp("last_payment_date"),
  paymentFrequency: text("payment_frequency").default("weekly"),
  // 2026-04-29 — declared as `numeric` to match prod (which has been numeric
  // since migration 0044). Drizzle returns numeric columns as `string | null`
  // in TS so existing call sites (`parseFloat(String(m.averageRating ?? "0"))`,
  // string literals like `"4.8"`) remain type-compatible. The Task #869
  // workaround (declaring text to keep `db:push --force` happy on legacy dev
  // DBs that still had text) is no longer needed because the dev pre-push
  // auto-heal in server/db-schema-check.ts (line ~2640) now handles the
  // text->numeric cast idempotently before `db:push` runs.
  averageRating: numeric("average_rating"),
  padsplitProfileUrl: text("padsplit_profile_url"),
  // Folded over from the dropped `member_profiles` table.
  rating: numeric("rating"),
  currentDues: numeric("current_dues"),
  lateFrequency: text("late_frequency"),
  // Task #1378 — deterministic HMAC-SHA256 of the member's email (lowercase,
  // trimmed) derived from a stable sub-key.  Allows auth/me to check whether a
  // logged-in app_user is also a resident member (owned by a different host)
  // without decrypting every row.  NULL for rows created before this column was
  // added; populated on the next write or lazy-backfill read.
  emailSearchToken: text("email_search_token"),
  // Deterministic HMAC-SHA256 of the member's E.164-normalized phone, from a
  // stable sub-key. Powers passwordless member-portal login lookup by phone
  // without decrypting every row (mirrors emailSearchToken). NULL for rows
  // created before this column; populated on the next write or backfill read.
  phoneSearchToken: text("phone_search_token"),
  // ── PADSPLIT DATA WISHLIST ───────────────────────────────────────────────
  // Placeholder columns for high-priority member-level fields not yet
  // ingested from PadSplit. All nullable; no ingestion logic exists yet.
  // PADSPLIT_WISHLIST: on_time_payment_pct — ingest when available
  onTimePaymentPct: numeric("on_time_payment_pct"),
  // PADSPLIT_WISHLIST: late_fee_event_count — ingest when available
  lateFeeEventCount: integer("late_fee_event_count"),
  // PADSPLIT_WISHLIST: move_out_reason_code — ingest when available
  moveOutReasonCode: text("move_out_reason_code"),
  // PADSPLIT_WISHLIST: weekly_payment_history_json — ingest when available.
  // Intended to hold a JSON array of weekly payment records (amount, due date,
  // paid date, on-time flag) once PadSplit exposes per-cycle payment history.
  // Stored as TEXT (JSON) rather than JSONB so it does not need a cast heal.
  weeklyPaymentHistoryJson: text("weekly_payment_history_json"),
  // Task #4594 — the host's approve/reject decision on this member's most
  // recent booking request. NULL by default (no decision yet); flips to
  // "approved" / "rejected" only when the host acts on a linked
  // booking_request_submissions row. This is the single field the external
  // warm browser (Paul's team) watches to mirror the action inside PadSplit —
  // PadPulse only RECORDS the decision here, it never pushes to PadSplit.
  bookingRequestDecision: text("booking_request_decision"),
  // Task #4594 — when the host approved / rejected this member's most recent
  // booking request. Both NULL by default; set in lockstep with
  // bookingRequestDecision (on "approved": approvedAt = now, rejectedAt = NULL;
  // on "rejected": rejectedAt = now, approvedAt = NULL) so the external warm
  // browser can read a decision timestamp alongside the decision itself.
  bookingRequestApprovedAt: timestamp("booking_request_approved_at"),
  bookingRequestRejectedAt: timestamp("booking_request_rejected_at"),
  // Task #4705 — when this member's booking request first arrived (stamped at
  // the new_booking dispatch sites). Anchors the "Booking Form Not Completed"
  // follow-up: sendAt = this + property auto-approval window − 2h. Stamped
  // once (COALESCE) so repeated syncs never move the deadline anchor.
  bookingRequestArrivedAt: timestamp("booking_request_arrived_at"),
  // Task #4622 — soft-delete pointer set by storage.mergeMembers when this row
  // was identified as a duplicate of another member. Points at the canonical
  // members.id all dependents were re-pointed to. A merged row keeps its ID for
  // historical audit but is excluded from every lookup/dedup fallback (and,
  // because its members_users links are moved to the canonical row, it drops
  // out of all ownership-scoped queries). Bare INTEGER (no FK) mirroring the
  // merged_into_expense_id precedent.
  mergedIntoMemberId: integer("merged_into_member_id"),
  // Task #4748 — watcher-maintained snapshot of the last status the PadSplit
  // event watcher observed for this row. The external warm-browser harvest
  // writes `status` directly (and bumps updated_at hourly even without
  // changes), so the watcher cron compares status vs. this column to detect
  // real transitions (move_in / move_out) and stamps it after each pass.
  // NULL = never seen by the watcher (first pass stamps silently so nothing
  // historic fires). Only the watcher writes this column.
  lastSeenStatus: text("last_seen_status"),
  // Durable dedup stamps for the per-member messaging-rule dispatches. The
  // previous sentinel (an enqueued messages_pending row joined to its
  // messages_pending_workflow sidecar) evaporated when the reconcile job
  // migrated the sidecar onto the delivered member_chats row — so the 2-min
  // watcher re-fired new_booking every pass after delivery. These stamps
  // survive the pending-row lifecycle. new_booking and move_in are set only
  // when a dispatch actually enqueued ≥1 message, so a rule added later
  // within the watcher's lookback window still fires for a not-yet-messaged
  // member. move_out also protects the host notification and is set after
  // that notification succeeds, even when zero rules match.
  newBookingDispatchedAt: timestamp("new_booking_dispatched_at"),
  moveInDispatchedAt: timestamp("move_in_dispatched_at"),
  /** Durable move_out dedup stamp — prevents re-firing when a member's status
   * oscillates (e.g. PadSplit briefly writes a non-moved-out status then
   * moved-out again). Also protects the host notification and is set after
   * that notification succeeds, even when zero rules match. */
  moveOutDispatchedAt: timestamp("move_out_dispatched_at"),
  bookingFormFollowupScheduledAt: timestamp("booking_form_followup_scheduled_at"),
  // Host-edit stamp for the three name fields (name / display_name /
  // legal_name), set by PATCH /api/app/devices/members/:id whenever a host
  // actually changes one of them. Contract with the external warm-browser
  // harvest: while this is non-null the harvest must NOT overwrite any of the
  // three name columns (it keeps owning status/ids/dates/payment fields).
  // NULL = names are harvest-owned, as before.
  nameEditedByHostAt: timestamp("name_edited_by_host_at"),
  // Harvest-presence stamp, mirroring properties/rooms `last_harvested`.
  // Contract with the external harvest ECS: stamp now() on every member row
  // present in a snapshot (including padsplit_member_id-fallback matches),
  // even when nothing else changed. NULL until the ECS ships the write —
  // sweep logic must therefore use COALESCE(last_harvested, updated_at) so
  // it degrades gracefully to the updated_at signal until then. Only the
  // harvest writes this column; app code never bumps it.
  lastHarvested: timestamp("last_harvested"),
  // APP-OWNED terminal stamp for a booking request (mirrors the
  // name_edited_by_host_at contract: while non-null, the harvest must not
  // resurrect this row into the Booking Requests tab).
  //
  // Why it exists: PadSplit rejections are invisible to PadPulse — the harvest
  // conveys presence only, with no rejection vocabulary (see
  // docs/padsplit-harvest-contract-last-harvested.md ask (b), still PROPOSED).
  // A request the host rejected UPSTREAM keeps appearing in the harvest, so
  // the hourly retire sweep flips status → 'canceled' and the next harvest
  // pass rewrites it back to 'request' — ping-ponging forever, while the
  // fresh harvest stamp also starves archive-missing-padsplit-members of the
  // staleness signal it needs. Because status is harvest-owned we cannot win
  // that fight; instead we record the retirement in a column the harvest does
  // not write, and read "is this a live booking request?" through
  // effectiveMemberStatusBucket() (shared/member-status.ts) rather than from
  // members.status alone.
  //
  // Safe permanently: a genuine re-application always creates a NEW member row
  // (new occupant_id), so a sticky retirement can never suppress a future
  // request from the same person.
  bookingRequestRetiredAt: timestamp("booking_request_retired_at"),
  // Presentation only: do not use this for occupancy, locks, or authorization.
  applicantLifecycle: jsonb("applicant_lifecycle").$type<import("./member-lifecycle").MemberLifecycleEvidence>(),
  // APP-OWNED terminal stamp for a completed move-out (the moving-out-family
  // sibling of booking_request_retired_at, same contract: the harvest never
  // writes this column, and reads go through effectiveMemberStatusBucket()).
  //
  // Why it exists: PadSplit can keep listing a resident as "moving out" well
  // past their recorded move_out_date (extensions, delayed departures), and
  // the harvest re-stamps members.status with whatever PadSplit shows on
  // every pass. Writing 'inactive' into the harvest-owned status column after
  // the date therefore ping-pongs hourly against the harvest — and since
  // reconcile-room-statuses projects member state into rooms.status, every
  // bounce flapped real occupied rooms to vacant (2026-08-04 incident).
  // Instead, the demote sweep stamps this column once the row is BOTH past
  // its move-out date AND harvest-quiet, and classification treats a stamped
  // moving-out row as inactive without ever touching members.status.
  //
  // The stamp only suppresses the moving_out bucket: if the harvest later
  // writes an active-family status (the person stayed after all), that reads
  // through unchanged and wins.
  movedOutRetiredAt: timestamp("moved_out_retired_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  padsplitIdIdx: index("members_padsplit_id_idx").on(table.userId, table.padsplitMemberId),
  // Task #866 hardening — pin the allowed values at the DB level so a
  // misbehaving sync job or stray UPDATE can never sneak in a value the
  // outbound channel resolver doesn't know about. Keep in sync with
  // MEMBER_TYPES below and the matching CHECK in scripts/deploy-migrate.sql.
  typeCheck: check(
    "members_type_check",
    sql`${table.type} IN ('direct', 'padsplit')`,
  ),
  // Task #1378 — partial index on the deterministic email-search token used
  // by `isResidentMemberByEmail` (server/storage.ts) so resident lookups in
  // GET /api/app/auth/me stay O(1). Mirrors the matching
  // `CREATE INDEX IF NOT EXISTS members_email_search_token_idx` in
  // scripts/deploy-migrate.sql; check-schema-deploy-parity.ts requires both
  // sides to agree.
  emailSearchTokenIdx: index("members_email_search_token_idx")
    .on(table.emailSearchToken)
    .where(sql`${table.emailSearchToken} IS NOT NULL`),
  // Partial index on the deterministic phone-search token used by the member
  // portal's passwordless login lookup so phone matches stay O(1). Mirrors the
  // matching CREATE INDEX in scripts/deploy-migrate.sql; check-schema-deploy-
  // parity.ts requires both sides to agree.
  phoneSearchTokenIdx: index("members_phone_search_token_idx")
    .on(table.phoneSearchToken)
    .where(sql`${table.phoneSearchToken} IS NOT NULL`),
  // Task #4646 — stay-grain cutover (already applied to PRODUCTION on
  // 2026-07-13; names match prod's pg_indexes exactly). `occupant_id` is the
  // stable unique stay key: PARTIAL unique so direct members and split
  // inactive-stay stubs (occupant_id NULL) are unaffected. NEVER make this a
  // full unique index.
  occupantIdUidx: uniqueIndex("members_occupant_id_uidx")
    .on(table.occupantId)
    .where(sql`${table.occupantId} IS NOT NULL`),
  // Task #4646 — padsplit_member_id may repeat across rows (same person,
  // multiple stays/hosts). Plain partial index for person-history lookups;
  // intentionally NOT unique.
  padsplitMemberIdIdx: index("members_padsplit_member_id_idx")
    .on(table.padsplitMemberId)
    .where(sql`${table.padsplitMemberId} IS NOT NULL`),
}));

export const insertMemberSchema = createInsertSchema(members).omit({
  id: true,
  applicantLifecycle: true,
  createdAt: true,
  updatedAt: true,
  // Computed internally from the email — never set by callers.
  emailSearchToken: true,
  // Computed internally from the phone — never set by callers.
  phoneSearchToken: true,
}).extend({
  // Nullable/optional so a member can be created without a linked host account
  // (linkage can live in members_users instead). When set, still an app_users id.
  userId: z.number().optional().nullable(),
  name: z.string().min(1, "Member name is required"),
  displayName: z.string().optional().nullable(),
  legalName: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  phone: z.string().optional().nullable(),
  // Property assignment lives in `properties_members` (the legacy
  // `members.property_id` column was dropped in Task #1030). Callers
  // change it through the dedicated
  // `PUT /api/app/devices/members/:id/property` endpoint, not via the
  // member create/update payload (Task #1447). `roomId` stays on the
  // members row because it's still a column on the table.
  roomId: z.number().optional().nullable(),
  status: z.string().default("active"),
  type: z.enum(["direct", "padsplit"]).default("direct"),
  moveInDate: optionalCoercedDate,
}).superRefine((data, ctx) => {
  // 2026-07-14 hardening — a `type='padsplit'` member without a PadSplit
  // member id can never be deduped against the harvest (the sync keys on
  // padsplit_member_id/occupant_id), so every such row eventually gets
  // "pulled again" as a fresh duplicate stay. PadSplit rows are created by
  // the sync with their ids; host-entered members must be type 'direct'.
  if (data.type === "padsplit" && !data.padsplitMemberId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["padsplitMemberId"],
      message:
        'PadSplit members require a PadSplit member ID (they are created automatically by the PadSplit sync) — use type "direct" for host-entered members',
    });
  }
});
export type InsertMember = z.infer<typeof insertMemberSchema>;
export type Member = typeof members.$inferSelect;

// Mirrors the external harvest/members_workflow join table EXACTLY.
// Intentionally loose: no FKs, all columns nullable, `archived` is int 0/1,
// and no created_at/updated_at. Defensive handling lives in app code.
export const propertiesMembers = pgTable("properties_members", {
  id: serial("id").primaryKey(),
  memberId: integer("member_id"),
  propertyId: integer("property_id"),
  archived: integer("archived"),
}, (table) => ({
  memberArchivedIdx: index("properties_members_member_archived_idx").on(
    table.memberId,
    table.archived,
  ),
}));

export const membersUsers = pgTable("members_users", {
  id: serial("id").primaryKey(),
  memberId: integer("member_id"),
  userId: integer("user_id"),
}, (table) => ({
  // Task #4606 — a (member, host) pair must be unique. The Task #4604 refactor
  // left this table constraint-less, so the harvest re-linked the same host on
  // every sync tick and accumulated duplicate rows. The dedup backfill removes
  // existing duplicates before this index is created.
  memberUserUnique: uniqueIndex("members_users_member_user_unique").on(table.memberId, table.userId),
  // Task #4646 — host-scoped roster/lookup path (host user_id → stay rows).
  // Matches prod's members_users_user_member_idx created in the 2026-07-13
  // stay-grain migration.
  userMemberIdx: index("members_users_user_member_idx").on(table.userId, table.memberId),
}));

export const insertPropertiesMemberSchema = createInsertSchema(propertiesMembers).omit({
  id: true,
}).extend({
  memberId: z.number().optional().nullable(),
  propertyId: z.number().optional().nullable(),
  archived: z.number().optional().nullable(),
});
export type InsertPropertiesMember = z.infer<typeof insertPropertiesMemberSchema>;
export type PropertiesMember = typeof propertiesMembers.$inferSelect;

// Task #866 — Allowed values for `members.type`. Direct members are reachable
// over Twilio SMS using the host's per-host messagingPhoneNumber. PadSplit
// members are reached through the PadSplit Messenger adapter (stubbed today).
export const MEMBER_TYPES = ["direct", "padsplit"] as const;
export type MemberType = typeof MEMBER_TYPES[number];

export const FAN_MODES = ["auto", "on"] as const;
export type FanMode = typeof FAN_MODES[number];

export const thermostatPresets = pgTable("thermostat_presets", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  deviceIds: jsonb("device_ids").default([]).notNull(),
  summerHeatSetPoint: integer("summer_heat_set_point").default(68).notNull(),
  summerCoolSetPoint: integer("summer_cool_set_point").default(73).notNull(),
  winterHeatSetPoint: integer("winter_heat_set_point").default(68).notNull(),
  winterCoolSetPoint: integer("winter_cool_set_point").default(73).notNull(),
  fanMode: text("fan_mode").default("auto").notNull(),
  allowMemberControl: boolean("allow_member_control").default(true).notNull(),
  summerStartMonth: integer("summer_start_month").default(5).notNull(),
  summerStartDay: integer("summer_start_day").default(1).notNull(),
  summerEndMonth: integer("summer_end_month").default(9).notNull(),
  summerEndDay: integer("summer_end_day").default(30).notNull(),
  winterStartMonth: integer("winter_start_month").default(10).notNull(),
  winterStartDay: integer("winter_start_day").default(1).notNull(),
  winterEndMonth: integer("winter_end_month").default(4).notNull(),
  winterEndDay: integer("winter_end_day").default(30).notNull(),
  configurationMigrated: boolean("configuration_migrated").default(false).notNull(),
  providerResources: jsonb("provider_resources").default({}).notNull(),
  controlPolicyResults: jsonb("control_policy_results").default([]).notNull(),
  // D14: result of the last apply (server/services/thermostat-preset-applier.ts).
  lastAppliedAt: timestamp("last_applied_at"),
  lastAppliedSeason: text("last_applied_season"),
  lastApplyResults: jsonb("last_apply_results"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertThermostatPresetSchema = createInsertSchema(thermostatPresets).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1, "Preset name is required"),
  propertyId: z.number().optional().nullable(),
  deviceIds: z.array(z.string()).default([]),
  summerHeatSetPoint: z.coerce.number().min(50).max(90).default(68),
  summerCoolSetPoint: z.coerce.number().min(60).max(100).default(73),
  winterHeatSetPoint: z.coerce.number().min(50).max(90).default(68),
  winterCoolSetPoint: z.coerce.number().min(60).max(100).default(73),
  fanMode: z.enum(FAN_MODES).default("auto"),
  allowMemberControl: z.boolean().default(true),
  summerStartMonth: z.number().int().min(1).max(12).default(5),
  summerStartDay: z.number().int().min(1).max(31).default(1),
  summerEndMonth: z.number().int().min(1).max(12).default(9),
  summerEndDay: z.number().int().min(1).max(31).default(30),
  winterStartMonth: z.number().int().min(1).max(12).default(10),
  winterStartDay: z.number().int().min(1).max(31).default(1),
  winterEndMonth: z.number().int().min(1).max(12).default(4),
  winterEndDay: z.number().int().min(1).max(31).default(30),
});
export type InsertThermostatPreset = z.infer<typeof insertThermostatPresetSchema>;
export type ThermostatPreset = typeof thermostatPresets.$inferSelect;

export const thermostatSettings = pgTable("thermostat_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  allowMemberControl: boolean("allow_member_control").default(true).notNull(),
  allowGuestControl: boolean("allow_guest_control").default(false).notNull(),
  summerStartMonth: integer("summer_start_month").default(5).notNull(),
  summerStartDay: integer("summer_start_day").default(1).notNull(),
  summerEndMonth: integer("summer_end_month").default(9).notNull(),
  summerEndDay: integer("summer_end_day").default(30).notNull(),
  winterStartMonth: integer("winter_start_month").default(10).notNull(),
  winterStartDay: integer("winter_start_day").default(1).notNull(),
  winterEndMonth: integer("winter_end_month").default(4).notNull(),
  winterEndDay: integer("winter_end_day").default(30).notNull(),
  notifyOffline: boolean("notify_offline").default(true).notNull(),
  notifyOfflinePush: boolean("notify_offline_push").default(true).notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertThermostatSettingsSchema = createInsertSchema(thermostatSettings).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  allowMemberControl: z.boolean().default(true),
  allowGuestControl: z.boolean().default(false),
  summerStartMonth: z.coerce.number().min(1).max(12).default(5),
  summerStartDay: z.coerce.number().min(1).max(31).default(1),
  summerEndMonth: z.coerce.number().min(1).max(12).default(9),
  summerEndDay: z.coerce.number().min(1).max(31).default(30),
  winterStartMonth: z.coerce.number().min(1).max(12).default(10),
  winterStartDay: z.coerce.number().min(1).max(31).default(1),
  winterEndMonth: z.coerce.number().min(1).max(12).default(4),
  winterEndDay: z.coerce.number().min(1).max(31).default(30),
  notifyOffline: z.boolean().default(true),
  notifyOfflinePush: z.boolean().default(true),
});
export type InsertThermostatSettings = z.infer<typeof insertThermostatSettingsSchema>;
export type ThermostatSettings = typeof thermostatSettings.$inferSelect;

export const thermostatPropertyAssignments = pgTable("thermostat_property_assignments", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceId: text("device_id").notNull(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userDevicePropertyUnique: uniqueIndex("thermostat_property_assignments_user_device_property_unique").on(table.userId, table.deviceId, table.propertyId),
}));

export const insertThermostatPropertyAssignmentSchema = createInsertSchema(thermostatPropertyAssignments).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  deviceId: z.string().min(1),
  propertyId: z.number(),
});
export type InsertThermostatPropertyAssignment = z.infer<typeof insertThermostatPropertyAssignmentSchema>;
export type ThermostatPropertyAssignment = typeof thermostatPropertyAssignments.$inferSelect;

export const thermostatReadings = pgTable("thermostat_readings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceId: text("device_id").notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  temperatureF: numeric("temperature_f", { precision: 6, scale: 2 }),
  humidityPct: numeric("humidity_pct", { precision: 5, scale: 2 }),
  coolingSetPointF: numeric("cooling_set_point_f", { precision: 6, scale: 2 }),
  heatingSetPointF: numeric("heating_set_point_f", { precision: 6, scale: 2 }),
  recordedAt: timestamp("recorded_at").defaultNow().notNull(),
}, (table) => ({
  deviceTimeIdx: index("thermostat_readings_device_time_idx").on(table.deviceId, table.recordedAt),
  propertyTimeIdx: index("thermostat_readings_property_time_idx").on(table.propertyId, table.recordedAt),
}));

export const insertThermostatReadingSchema = createInsertSchema(thermostatReadings).omit({
  id: true,
  recordedAt: true,
}).extend({
  userId: z.number().int(),
  deviceId: z.string().min(1),
  propertyId: z.number().int().nullable().optional(),
  temperatureF: z.union([z.number(), z.string()]).nullable().optional(),
  humidityPct: z.union([z.number(), z.string()]).nullable().optional(),
  coolingSetPointF: z.union([z.number(), z.string()]).nullable().optional(),
  heatingSetPointF: z.union([z.number(), z.string()]).nullable().optional(),
});
export type InsertThermostatReading = z.infer<typeof insertThermostatReadingSchema>;
export type ThermostatReading = typeof thermostatReadings.$inferSelect;

export const thermostatDeviceOverrides = pgTable("thermostat_device_overrides", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceId: text("device_id").notNull(),
  customName: text("custom_name"),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userDeviceUnique: uniqueIndex("thermostat_device_overrides_user_device_idx").on(table.userId, table.deviceId),
}));

export const insertThermostatDeviceOverrideSchema = createInsertSchema(thermostatDeviceOverrides).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  deviceId: z.string().min(1),
  customName: z.string().nullable().optional(),
  propertyId: z.number().nullable().optional(),
});
export type InsertThermostatDeviceOverride = z.infer<typeof insertThermostatDeviceOverrideSchema>;
export type ThermostatDeviceOverride = typeof thermostatDeviceOverrides.$inferSelect;

// Operator-set pin of a Seam smart lock to either a property
// (shared/common-access door) or a single room. Exactly one of
// propertyId / roomId is set per row; clearing the assignment deletes
// the row so the GET handler can fall back to legacy name matching.
// Task #1729 — `ignored` marks a lock as explicitly excluded from all
// property lists, battery checks, and member auto-access. Works for both
// Seam and TTLock device ids (provider-agnostic).
export const lockDeviceOverrides = pgTable("lock_device_overrides", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deviceId: text("device_id").notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  roomId: integer("room_id").references(() => rooms.id, { onDelete: "set null" }),
  // Task #1729 — ignored=true means "not a PadSplit lock; skip everywhere".
  ignored: boolean("ignored").default(false).notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userDeviceUnique: uniqueIndex("lock_device_overrides_user_device_idx").on(table.userId, table.deviceId),
}));

export const insertLockDeviceOverrideSchema = createInsertSchema(lockDeviceOverrides).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  deviceId: z.string().min(1),
  propertyId: z.number().nullable().optional(),
  roomId: z.number().nullable().optional(),
  ignored: z.boolean().default(false).optional(),
});
export type InsertLockDeviceOverride = z.infer<typeof insertLockDeviceOverrideSchema>;
export type LockDeviceOverride = typeof lockDeviceOverrides.$inferSelect;

// Task #1029 / #1123 — the app reads chat history from `memberChats` (Paul's
// harvest target) and writes outbound through `messagesPending`. Workflow
// metadata lives on `memberChatWorkflow` and `messagesPendingWorkflow`;
// attachments on `memberChatAttachments`; internal notes on
// `memberChatThreads`. The legacy `conversations` and `messages` pgTables
// that previously lived here have been retired — see scripts/deploy-migrate.sql
// for the production DROP TABLE statements.

export const RULE_TYPES = ["event", "scheduled", "incoming_keywords", "business_hours"] as const;
export type RuleType = typeof RULE_TYPES[number];

// ─── Business Hours (Task #4707) ─────────────────────────────────────────────
// Per-day open/closed working hours for the "business_hours" after-hours
// auto-reply rule type. Times are wall-clock "HH:MM" in the rule's effective
// timezone (config.timezone override, else the host's app_users.timezone).
export type BusinessHoursDayConfig = {
  open: boolean;
  // "HH:MM" 24-hour wall-clock. Ignored when open=false.
  start: string;
  end: string;
};

export type BusinessHoursConfig = {
  // Optional active date range (inclusive, "YYYY-MM-DD" in the effective
  // timezone). Outside the range the rule never fires.
  startDate: string | null;
  endDate: string | null;
  // Optional IANA timezone override; null = use the host account timezone.
  timezone: string | null;
  days: Record<ScheduleDayOfWeek, BusinessHoursDayConfig>;
};

const hhmmSchema = z.string().regex(/^\d{2}:\d{2}$/, "Time must be in HH:MM format");

const businessHoursDaySchema = z.object({
  open: z.boolean(),
  start: hhmmSchema,
  end: hhmmSchema,
});

export const businessHoursConfigSchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").nullable(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").nullable(),
  timezone: z
    .string()
    .refine((tz) => {
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    }, "Must be a valid IANA timezone")
    .nullable(),
  days: z.object({
    monday: businessHoursDaySchema,
    tuesday: businessHoursDaySchema,
    wednesday: businessHoursDaySchema,
    thursday: businessHoursDaySchema,
    friday: businessHoursDaySchema,
    saturday: businessHoursDaySchema,
    sunday: businessHoursDaySchema,
  }),
});

export const MESSAGE_RECIPIENTS = ["trigger_person", "all_members"] as const;
export type MessageRecipients = typeof MESSAGE_RECIPIENTS[number];

export const MESSAGE_APPROVAL = ["send_automatically", "review_before_sending"] as const;
export type MessageApproval = typeof MESSAGE_APPROVAL[number];

export const SEND_TIMING = ["immediately", "after_delay"] as const;
export type SendTiming = typeof SEND_TIMING[number];

export const SCHEDULE_FREQUENCIES = ["daily", "weekly", "biweekly", "monthly", "quarterly", "specific_date"] as const;
export type ScheduleFrequency = typeof SCHEDULE_FREQUENCIES[number];

export const SCHEDULE_DAYS_OF_WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
export type ScheduleDayOfWeek = typeof SCHEDULE_DAYS_OF_WEEK[number];

export const SCHEDULE_WEEKS_OF_MONTH = ["first", "second", "third", "fourth", "last"] as const;
export type ScheduleWeekOfMonth = typeof SCHEDULE_WEEKS_OF_MONTH[number];

export type RuleQuietHours = { start: string; end: string };
export const ruleQuietHoursSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/, "Quiet hours start must be HH:MM"),
  end: z.string().regex(/^\d{2}:\d{2}$/, "Quiet hours end must be HH:MM"),
}).refine((v) => v.start !== v.end, { message: "Quiet hours start and end must differ" });

export const messagingRules = pgTable("messaging_rules", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  properties: text("properties"),
  scopeMode: text("scope_mode"),
  excludedPropertyIds: text("excluded_property_ids"),
  scopeRevision: integer("scope_revision").notNull().default(0),
  ruleType: text("rule_type").default("event").notNull(),
  notes: text("notes"),
  // B22 — rules are off by default and switched on one by one.
  enabled: boolean("enabled").default(false).notNull(),
  messageContent: text("message_content"),
  aiEnhancementInstructions: text("ai_enhancement_instructions"),
  // Optional single image attached to the rule; included on every automated send.
  attachmentImageUrl: text("attachment_image_url"),
  eventTrigger: text("event_trigger"),
  messageRecipients: text("message_recipients").default("trigger_person"),
  messageApproval: text("message_approval").default("send_automatically"),
  sendTiming: text("send_timing").default("immediately"),
  delayMinutes: integer("delay_minutes"),
  copyMessageContentFrom: text("copy_message_content_from"),
  // Task #4813 — Optional live link to a saved canned response. When set on an
  // event rule, the dispatcher reads the template's CURRENT body at send time
  // (edits to the template flow through automatically); messageContent is kept
  // as a fallback snapshot in case the template is later deleted/archived.
  // SET NULL (not cascade) so deleting a template degrades the rule to its
  // inline fallback text instead of deleting the rule.
  cannedResponseId: integer("canned_response_id").references(() => cannedResponses.id, { onDelete: "set null" }),
  // Comma-separated property_groups.id values. When set the rule fires only for
  // properties belonging to those groups (resolved at dispatch time via the
  // property's propertyGroup name). Takes precedence over / is mutually exclusive
  // with `properties` — the rule form saves one or the other, never both.
  propertyGroupIds: text("property_group_ids"),
  scheduleFrequency: text("frequency"),
  scheduleDayOfWeek: text("day"),
  scheduleWeekOfMonth: text("week"),
  scheduleDate: date("date"),
  scheduleTime: text("time"),
  scheduleTimezone: text("timezone"),
  keywords: text("keywords").array().$defaultFn(() => []),
  // Task #4707 — Business Hours after-hours auto-reply config. Only set when
  // ruleType === "business_hours"; null for every other rule type.
  businessHours: jsonb("business_hours").$type<BusinessHoursConfig>(),
  // B22 — optional quiet hours (account timezone, "HH:MM" start/end, may wrap
  // past midnight). An auto-send that falls inside them is held until the
  // window ends. Null = no quiet hours.
  quietHours: jsonb("quiet_hours").$type<RuleQuietHours>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertMessagingRuleSchema = createInsertSchema(messagingRules).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1, "Rule name is required"),
  properties: z.string().optional().nullable(),
  ruleType: z.enum(RULE_TYPES).default("event"),
  notes: z.string().optional().nullable(),
  enabled: z.boolean().default(false),
  messageContent: z.string().optional().nullable(),
  // Retired; accept legacy clients but never persist their instructions.
  aiEnhancementInstructions: z.string().optional().nullable().transform(() => null),
  attachmentImageUrl: z.string().optional().nullable(),
  eventTrigger: z.string().optional().nullable(),
  messageRecipients: z.enum(MESSAGE_RECIPIENTS).default("trigger_person"),
  messageApproval: z.enum(MESSAGE_APPROVAL).default("send_automatically"),
  sendTiming: z.enum(SEND_TIMING).default("immediately"),
  delayMinutes: z.number().int().optional().nullable(),
  copyMessageContentFrom: z.string().optional().nullable(),
  scheduleFrequency: z.enum(SCHEDULE_FREQUENCIES).optional().nullable(),
  scheduleDayOfWeek: z.enum(SCHEDULE_DAYS_OF_WEEK).optional().nullable(),
  scheduleWeekOfMonth: z.enum(SCHEDULE_WEEKS_OF_MONTH).optional().nullable(),
  scheduleDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").optional().nullable(),
  scheduleTime: z.string().regex(/^\d{2}:\d{2}$/, "Time must be in HH:MM format").optional().nullable(),
  scheduleTimezone: optionalIanaTimezone,
  keywords: z.array(z.string().trim().min(1, "Keywords cannot be blank")).optional().nullable(),
  businessHours: businessHoursConfigSchema.optional().nullable(),
  quietHours: ruleQuietHoursSchema.optional().nullable(),
  cannedResponseId: z.number().int().optional().nullable(),
  propertyGroupIds: z.string().optional().nullable(),
  scopeMode: z.enum(["all", "individual", "group"]).optional().nullable(),
  excludedPropertyIds: z.string().regex(/^(\d+(,\d+)*)?$/).optional().nullable(),
  scopeRevision: z.number().int().nonnegative().optional(),
}).superRefine((data, ctx) => {
  // Task #4813 — template linkage is an event-rule feature only. Keyword,
  // scheduled, and business-hours rules keep authoring text inline.
  if (data.cannedResponseId != null && data.ruleType !== "event") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cannedResponseId"], message: "Saved message templates can only be linked to event rules" });
  }
  if (data.sendTiming === "after_delay") {
    if (data.delayMinutes == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["delayMinutes"], message: "Delay duration is required when using 'After a Delay'" });
    } else if (data.delayMinutes < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["delayMinutes"], message: "Delay must be at least 1 minute" });
    }
  }
  if (data.ruleType === "scheduled") {
    if (!data.scheduleFrequency) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleFrequency"], message: "Frequency is required for scheduled rules" });
    }
    if (!data.scheduleTime) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleTime"], message: "Time is required for scheduled rules" });
    }
    if (data.scheduleFrequency === "weekly" || data.scheduleFrequency === "biweekly") {
      if (!data.scheduleDayOfWeek) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleDayOfWeek"], message: "Day is required for weekly/bi-weekly rules" });
      }
    }
    if (data.scheduleFrequency === "monthly" || data.scheduleFrequency === "quarterly") {
      if (!data.scheduleWeekOfMonth) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleWeekOfMonth"], message: "Week is required for monthly/quarterly rules" });
      }
      if (!data.scheduleDayOfWeek) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleDayOfWeek"], message: "Day is required for monthly/quarterly rules" });
      }
    }
    if (data.scheduleFrequency === "specific_date") {
      if (!data.scheduleDate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleDate"], message: "Date is required for specific date rules" });
      }
    }
  } else {
    if (data.scheduleFrequency != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleFrequency"], message: "scheduleFrequency must be null for non-scheduled rules" });
    }
    if (data.scheduleDayOfWeek != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleDayOfWeek"], message: "scheduleDayOfWeek must be null for non-scheduled rules" });
    }
    if (data.scheduleWeekOfMonth != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleWeekOfMonth"], message: "scheduleWeekOfMonth must be null for non-scheduled rules" });
    }
    if (data.scheduleDate != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleDate"], message: "scheduleDate must be null for non-scheduled rules" });
    }
    if (data.scheduleTime != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduleTime"], message: "scheduleTime must be null for non-scheduled rules" });
    }
  }
  if (data.ruleType === "incoming_keywords") {
    if (!data.keywords || data.keywords.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["keywords"], message: "At least one keyword or phrase is required for incoming message rules" });
    }
  } else {
    if (data.keywords != null && data.keywords.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["keywords"], message: "keywords must be empty for non-incoming-keyword rules" });
    }
  }
  // Task #4707 — Business Hours rules carry a businessHours config + message;
  // every other rule type must leave businessHours null.
  if (data.ruleType === "business_hours") {
    if (!data.businessHours) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["businessHours"], message: "Business hours are required for business hours rules" });
    } else {
      const bh = data.businessHours;
      const openDays = SCHEDULE_DAYS_OF_WEEK.filter((d) => bh.days[d]?.open);
      if (openDays.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["businessHours", "days"], message: "At least one day must be open" });
      }
      for (const day of openDays) {
        const cfg = bh.days[day];
        if (cfg.end <= cfg.start) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["businessHours", "days", day, "end"], message: "Closing time must be after opening time" });
        }
      }
      if (bh.startDate && bh.endDate && bh.endDate < bh.startDate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["businessHours", "endDate"], message: "End date must be on or after the start date" });
      }
    }
    if (!data.messageContent || !data.messageContent.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["messageContent"], message: "An auto-reply message is required for business hours rules" });
    }
  } else {
    if (data.businessHours != null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["businessHours"], message: "businessHours must be null for non-business-hours rules" });
    }
  }
  // NOTE: "New Booking" rules are NOT required to carry the
  // {{booking_request_link:<id>}} token. Without it, submissions aren't
  // tracked by PadPulse and approve/reject must be handled manually on
  // PadSplit — the client rule form warns via an explicit confirmation
  // dialog before saving such a rule, but the server accepts it.
  //
  // Task #4705 — UNLIKE New Booking, an ENABLED "Booking Form Not Completed"
  // rule MUST carry the booking-form link: its whole purpose is re-sending
  // the form before the auto-approval deadline. Hard server requirement —
  // both POST and PATCH parse the merged row through this schema, so a rule
  // on this trigger can't be created or re-enabled without the token.
  // (Disabling a legacy token-less rule stays allowed: enabled=false skips.)
  if (
    data.ruleType === "event" &&
    data.eventTrigger === BOOKING_FORM_FOLLOWUP_TRIGGER &&
    data.enabled &&
    !messageHasBookingRequestLinkToken(data.messageContent)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["messageContent"],
      message: BOOKING_FORM_FOLLOWUP_LINK_REQUIRED_ERROR,
    });
  }
  // Task #4705 — "Booking Form Not Completed" send time is computed from the
  // booking-request arrival + property auto-approval window (deadline − 2h),
  // NOT from rule timing. An "After a Delay" setting would be silently ignored
  // by the scheduler's sendAtOverride, so reject it here to keep the stored
  // rule honest with what actually happens.
  if (
    data.ruleType === "event" &&
    data.eventTrigger === BOOKING_FORM_FOLLOWUP_TRIGGER &&
    data.sendTiming === "after_delay"
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sendTiming"],
      message:
        "Booking Form Not Completed rules are always sent 2 hours before the auto-approval deadline — custom delays aren't supported for this trigger.",
    });
  }
});

export type InsertMessagingRule = z.infer<typeof insertMessagingRuleSchema>;
export type MessagingRule = typeof messagingRules.$inferSelect;

// ─── Business Hours auto-reply dedup ledger (Task #4707) ─────────────────────
// One row per (rule, member, channel). `sentAt` is bumped on every auto-reply;
// the evaluator suppresses a send when sentAt is already >= the start of the
// current closed window, guaranteeing at most one auto-reply per closed
// window per member per channel. Also makes the PadSplit inbound poller
// idempotent across re-scans/restarts.
export const businessHoursAutoReplies = pgTable("business_hours_auto_replies", {
  id: serial("id").primaryKey(),
  ruleId: integer("rule_id").notNull().references(() => messagingRules.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  memberId: bigint("member_id", { mode: "number" }).notNull(),
  // 'padsplit' (PadSplit Messenger via messages_pending) or 'portal'
  // (member-portal host chat via a direct member_chats insert).
  channel: text("channel").notNull(),
  sentAt: timestamp("sent_at", { withTimezone: true }).defaultNow().notNull(),
  // Task #4712 — for PORTAL-channel auto-replies, the member_chats row the
  // reply was posted as. Lets the host inbox timeline attribute that bubble
  // with an "Auto-reply (business hours)" chip. NULL for padsplit-channel
  // rows (those are attributed via the messages_pending workflow sidecar's
  // automation_source = 'business_hours') and for pre-#4712 rows.
  memberChatId: bigint("member_chat_id", { mode: "number" }),
  // Task #4791 — the rendered message text sent for this auto-reply, stored so
  // the activity log drawer can show a preview without joining messages_pending.
  messageText: text("message_text"),
}, (table) => ({
  ruleMemberChannelUidx: uniqueIndex("business_hours_auto_replies_rule_member_channel_uidx")
    .on(table.ruleId, table.memberId, table.channel),
}));

export type BusinessHoursAutoReply = typeof businessHoursAutoReplies.$inferSelect;

// ─── Keyword rules beta + auto-reply dedup ledger (Task #4721) ───────────────
// The "Incoming Message (Keywords & Phrases)" rule type ships behind a
// per-user beta grant while it rolls out. Client radio gating, the server
// create/update gate, and the matching engine all key off this one constant.
export const KEYWORD_RULES_BETA_KEY = "keyword_rules";

// One row per (rule, member). `sentAt` is bumped on every keyword auto-reply;
// the evaluator suppresses a send when sentAt is within the cooldown window,
// guaranteeing a member never gets the same keyword reply repeatedly in a
// short period. Also makes the inbound poller idempotent across re-scans.
export const keywordRuleAutoReplies = pgTable("keyword_rule_auto_replies", {
  id: serial("id").primaryKey(),
  ruleId: integer("rule_id").notNull().references(() => messagingRules.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  memberId: bigint("member_id", { mode: "number" }).notNull(),
  sentAt: timestamp("sent_at", { withTimezone: true }).defaultNow().notNull(),
  // Task #4791 — the rendered message text sent for this auto-reply, stored so
  // the activity log drawer can show a preview without joining messages_pending.
  messageText: text("message_text"),
}, (table) => ({
  ruleMemberUidx: uniqueIndex("keyword_rule_auto_replies_rule_member_uidx")
    .on(table.ruleId, table.memberId),
}));

export type KeywordRuleAutoReply = typeof keywordRuleAutoReplies.$inferSelect;

// Messages Auto Draft (private beta `auto_draft`) — account-wide settings,
// one row per owner app user. Absent row = off. send_delay is one of
// AUTO_DRAFT_SEND_DELAYS; "never" means drafts wait for a host to send them.
export const AUTO_DRAFT_SEND_DELAYS = ["5", "15", "30", "60", "never"] as const;
export type AutoDraftSendDelay = (typeof AUTO_DRAFT_SEND_DELAYS)[number];
export const autoDraftSettings = pgTable("auto_draft_settings", {
  userId: integer("user_id").primaryKey().references(() => appUsers.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  // B22 — default "never": AI drafts wait for approval unless the host picks a delay.
  sendDelay: text("send_delay").notNull().default("never"),
  // Last off→on flip. Only member messages that arrive after this get drafts,
  // so turning the feature on never back-drafts an old backlog.
  enabledAt: timestamp("enabled_at", { withTimezone: true }),
  updatedByUserId: integer("updated_by_user_id"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});
export type AutoDraftSettings = typeof autoDraftSettings.$inferSelect;

// Auto Draft per-message claim ledger. One row per (member, newest inbound
// chat) — the atomic INSERT … ON CONFLICT claim keeps overlapping job runs and
// multiple servers from drafting the same message twice. status: claimed |
// drafted | skipped | failed. Failed claims may be retried a bounded number of
// times (attempts). skip_reason (AutoDraftSkipReason in
// shared/auto-draft-activity.ts) records WHY a message got no draft so the
// settings page can show it. A "skipped" row with attempts = 0 is a pre-claim
// observation (host replied, rule handled, budget, …) and stays reclaimable;
// skipped rows with attempts ≥ 1 are final outcomes of a real attempt.
// cancel_reason (AutoDraftCancelReason) records WHY the linked waiting draft
// (pending_id) was cancelled before sending.
export const autoDraftClaims = pgTable("auto_draft_claims", {
  id: serial("id").primaryKey(),
  ownerId: integer("owner_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  memberId: bigint("member_id", { mode: "number" }).notNull(),
  inboundChatId: bigint("inbound_chat_id", { mode: "number" }).notNull(),
  status: text("status").notNull().default("claimed"),
  attempts: integer("attempts").notNull().default(1),
  pendingId: integer("pending_id"),
  skipReason: text("skip_reason"),
  cancelReason: text("cancel_reason"),
  claimedAt: timestamp("claimed_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  memberChatUidx: uniqueIndex("auto_draft_claims_member_chat_uidx")
    .on(table.memberId, table.inboundChatId),
  ownerClaimedIdx: index("auto_draft_claims_owner_claimed_idx")
    .on(table.ownerId, table.claimedAt),
}));
export type AutoDraftClaim = typeof autoDraftClaims.$inferSelect;

// ─── Member-message push dedup ledger (OneSignal Phase 2) ────────────────────
// One row per (recipient, message identity) claimed atomically (ON CONFLICT DO
// NOTHING) before a new-inbound-message push is sent. This is the structural
// duplicate guard the poller's recency window cannot provide: harvest re-runs
// re-insert inbound rows (sometimes with created_at shifted ~7h), and one real
// message legitimately fans out across a person's several stay rows (#4646/
// #4670) — all copies share padsplit_message_id, so `dedupe_key` uses
// `ps:<padsplit_message_id>` when present (collapsing fan-out + re-inserts)
// and falls back to `chat:<member_chats.id>` for app-inserted SMS/portal rows
// (single-row realtime inserts, no fan-out). Rows older than the poller's
// retention sweep are pruned — dedup only matters within the recency window.
export const memberMessagePushNotifies = pgTable("member_message_push_notifies", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  dedupeKey: text("dedupe_key").notNull(),
  // The member_chats row that triggered the claim — observability only; the
  // uniqueness rail is (user_id, dedupe_key).
  memberChatId: bigint("member_chat_id", { mode: "number" }),
  sentAt: timestamp("sent_at", { withTimezone: true }).defaultNow().notNull(),
  // Legacy claims are terminal, not replayed during the durable-outbox upgrade.
  nativeStatus: text("native_status").notNull().default("accepted"),
  nativeEvent: jsonb("native_event"),
  providerIdempotencyKey: text("provider_idempotency_key"),
  providerNotificationId: text("provider_notification_id"),
  attemptCount: integer("attempt_count").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }),
  leaseUntil: timestamp("lease_until", { withTimezone: true }),
  leaseToken: text("lease_token"),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  // Static privacy-safe reason codes from shared/native-push-diagnostics.
  // Provider acceptance is NOT evidence of receipt on a device.
  outcomeReason: text("outcome_reason"),
}, (table) => ({
  userDedupeUidx: uniqueIndex("member_message_push_notifies_user_dedupe_uidx")
    .on(table.userId, table.dedupeKey),
  nativeDueIdx: index("member_message_push_native_due_idx").on(table.nativeStatus, table.nextAttemptAt),
}));

export type MemberMessagePushNotify = typeof memberMessagePushNotifies.$inferSelect;

// ─── Canned Responses (Task #2107) ───────────────────────────────────────────
// Reusable message snippets shared across the account owner's team. Owned by
// the account owner (ownerUserId) and accessible to any team member with
// Messages permission. `createdByUserId` and `updatedByUserId` track which
// team member last touched the row so the "last edited by" attribution works.
export const cannedResponses = pgTable("canned_responses", {
  id: serial("id").primaryKey(),
  ownerUserId: integer("owner_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdByUserId: integer("created_by_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  updatedByUserId: integer("updated_by_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  body: text("body").notNull(),
  useCount: integer("use_count").notNull().default(0),
  lastUsedAt: timestamp("last_used_at"),
  // Task #2115 — Optional free-form category/folder name (e.g. "Move-In",
  // "Billing", "Maintenance") so teams can group their library. `null` /
  // empty means "Uncategorized" in the UI.
  category: text("category"),
  // Task #2119 — Soft-archive timestamp. Stale responses (zero-use, 90+ day
  // old) can be bulk-archived from the Usage Insights panel. Archived rows
  // are hidden from the library list and pickers but kept for audit so the
  // event table's foreign keys remain valid.
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  ownerUserIdIdx: index("idx_canned_responses_owner_user_id").on(table.ownerUserId),
}));

// Task #2119 — Per-insert usage events. The aggregate `use_count` /
// `last_used_at` columns on `canned_responses` answer "how often" but not
// "who", so we record one row per insert here with the team member's id.
// This powers the "by team member" breakdown on the Usage Insights panel
// and lets future coaching surfaces compute trends over time without
// reshaping the aggregate columns.
export const cannedResponseUses = pgTable("canned_response_uses", {
  id: serial("id").primaryKey(),
  cannedResponseId: integer("canned_response_id")
    .notNull()
    .references(() => cannedResponses.id, { onDelete: "cascade" }),
  ownerUserId: integer("owner_user_id")
    .notNull()
    .references(() => appUsers.id, { onDelete: "cascade" }),
  userId: integer("user_id")
    .notNull()
    .references(() => appUsers.id, { onDelete: "cascade" }),
  usedAt: timestamp("used_at").defaultNow().notNull(),
}, (table) => ({
  ownerUserIdIdx: index("idx_canned_response_uses_owner_user_id").on(table.ownerUserId),
  responseIdIdx: index("idx_canned_response_uses_response_id").on(table.cannedResponseId),
  usedAtIdx: index("idx_canned_response_uses_used_at").on(table.usedAt),
}));

export type CannedResponseUse = typeof cannedResponseUses.$inferSelect;

export const insertCannedResponseSchema = createInsertSchema(cannedResponses).omit({
  id: true,
  useCount: true,
  lastUsedAt: true,
  archivedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  ownerUserId: z.number(),
  createdByUserId: z.number(),
  updatedByUserId: z.number(),
  title: z.string().min(1, "Title is required").max(120, "Title must be 120 characters or fewer"),
  body: z.string().min(1, "Body is required").max(5000, "Body must be 5000 characters or fewer"),
  category: z.string().max(60, "Category must be 60 characters or fewer").nullable().optional(),
});

export type InsertCannedResponse = z.infer<typeof insertCannedResponseSchema>;
export type CannedResponse = typeof cannedResponses.$inferSelect;

// Task #2121 — Optional per-owner category metadata. Lets teams pin the order
// categories show in the picker/library (instead of plain alphabetical) and
// gives bulk-rename a stable home that survives renaming every row that used
// the old name. A row exists only when a team has touched it via the Manage
// Categories modal; the UI silently falls back to alphabetical for any
// category name that doesn't have a row yet.
export const cannedResponseCategories = pgTable("canned_response_categories", {
  id: serial("id").primaryKey(),
  ownerUserId: integer("owner_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("canned_response_categories_owner_name_idx").on(t.ownerUserId, t.name),
]);

export type CannedResponseCategory = typeof cannedResponseCategories.$inferSelect;

// Task #1029 — `pending_rule_messages`, `message_drafts`, `internal_threads`
// were dropped along with the legacy messaging stack. Rule-driven outbound
// queueing now flows through `messagesPending` (with optional sidecar metadata
// on `messagesPendingWorkflow`). Internal notes moved to `memberChatThreads`.

export const NOTIFICATION_TYPES = ["assignment", "mention", "maintenance", "due_date", "thread_reply", "booking_request", "sale_inquiry", "team_invitation", "voucher_status", "thermostat_health", "lock_low_battery", "lock_offline", "lock_unlock_failures", "unmatched_inbound_sms", "padsplit_push_failed", "ttlock_connection_broken", "city_ai_analysis_stale", "thermostat_polling_stalled_admin", "task_message", "form_message", "padsplit_bad_review", "padsplit_message_send_failed", "mercury_statement_available", "mercury_statement_sync_failed", "mercury_card_cancel_failed", "mercury_card_cancel_prompt", "mercury_card_sync_failed", "mercury_refresh_failed", "mercury_auto_merged", "mercury_account_removed", "mail_attachment_storage_fallback", "padsplit_member_moved_out", "padsplit_unlinked_property", "closing_statement_reminder", "starter_seed_failed", "member_move_out_requested"] as const;
export type NotificationType = typeof NOTIFICATION_TYPES[number];

// Task #978 — Structured `metadata` payload on notifications. The
// thermostat_health alerts used to bury the drift magnitude (and
// setpoint/kind) inside the human-readable `message` string, forcing
// the inbox UI to regex it back out to render the drift column. That
// coupling is brittle: any future copy tweak in the poller silently
// degrades the column to em-dashes. Persisting the typed payload
// alongside the message makes the contract explicit and lets the UI
// read the value directly. Each notification type defines its own
// metadata shape — the union is the authoritative catalogue.
export type ThermostatHealthNotificationMetadata = {
  kind: "thermostat_health";
  // Mirrors ThermostatHealthIssue in server/services/thermostat-poller.ts
  driftKind: "above_cool" | "below_heat";
  driftF: number;
  setPointF: number;
};
export type NotificationMetadata =
  | ThermostatHealthNotificationMetadata
  | Record<string, unknown>;

export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  type: text("type").default("assignment").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  link: text("link"),
  read: boolean("read").default(false).notNull(),
  metadata: jsonb("metadata").$type<NotificationMetadata>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  type: z.enum(NOTIFICATION_TYPES).default("assignment"),
  title: z.string().min(1),
  message: z.string().min(1),
  link: z.string().optional().nullable(),
  read: z.boolean().default(false),
  // Optional structured payload; when present, the UI prefers it over
  // parsing the message string. Loose typing here so future notification
  // types can carry their own metadata shape without a schema migration
  // every time. The strict per-type shape lives in the union above.
  metadata: z.record(z.any()).optional().nullable(),
});

export type InsertNotification = z.input<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

// Task #893 — When the inbound Twilio webhook receives an SMS on a host's
// per-host messaging number from a phone number that doesn't match any of
// that host's members, we used to silently drop the message (only a server
// log line). This table captures those orphan inbounds so the host can see
// them in the UI and act (assign to an existing member, ignore, or fix the
// member's phone). One row per inbound; the resolution columns flip from
// null to populated when the host (or a teammate) takes action.
export const UNMATCHED_INBOUND_SMS_STATUSES = ["pending", "assigned", "ignored"] as const;
export type UnmatchedInboundSmsStatus = typeof UNMATCHED_INBOUND_SMS_STATUSES[number];

export const unmatchedInboundSms = pgTable("unmatched_inbound_sms", {
  id: serial("id").primaryKey(),
  // The host whose per-host messaging number received the SMS (the row owner).
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  fromPhone: text("from_phone").notNull(),
  toPhone: text("to_phone").notNull(),
  body: text("body").notNull(),
  // Twilio's MessageSid for the inbound (nullable in case the webhook payload
  // omits it during tests / replays). Useful for support correlation.
  twilioMessageSid: text("twilio_message_sid"),
  status: text("status").default("pending").notNull(),
  resolvedMemberId: integer("resolved_member_id").references(() => members.id, { onDelete: "set null" }),
  // Task #1029: legacy column from when SMS was resolved to a `conversations`
  // row. Conversations table is gone; this now stores the resolving
  // `member_chats.id` (BIGINT in storage, retained as integer here for
  // backwards-compat with any historical rows). No FK because member_chats
  // ids are bigserial — keep loose like the rest of the messaging boundary.
  resolvedConversationId: integer("resolved_conversation_id"),
  resolvedByUserId: integer("resolved_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertUnmatchedInboundSmsSchema = createInsertSchema(unmatchedInboundSms).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  fromPhone: z.string().min(1),
  toPhone: z.string().min(1),
  body: z.string().min(1),
  twilioMessageSid: z.string().optional().nullable(),
  status: z.enum(UNMATCHED_INBOUND_SMS_STATUSES).default("pending"),
  resolvedMemberId: z.number().optional().nullable(),
  resolvedConversationId: z.number().optional().nullable(),
  resolvedByUserId: z.number().optional().nullable(),
  resolvedAt: z.coerce.date().optional().nullable(),
});

export type InsertUnmatchedInboundSms = z.infer<typeof insertUnmatchedInboundSmsSchema>;
export type UnmatchedInboundSms = typeof unmatchedInboundSms.$inferSelect;

// `member_profiles` was removed; its consumed fields now live on `members`
// and the table is DROPped in scripts/deploy-migrate.sql.

export const pricingModels = pgTable("pricing_models", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  preChangeBoostPercent: numeric("pre_change_boost_percent").default("10"),
  minPercent: numeric("min_percent").default("0"),
  maxPercent: numeric("max_percent").default("0"),
  changeRatePercent: numeric("change_rate_percent").default("0"),
  changeRateHours: integer("change_rate_hours").default(24),
  moveInFee: numeric("move_in_fee").default("0"),
  waiveMoveIn: text("waive_move_in").default("No"),
  promoPercent: numeric("promo_percent").default("0"),
  promoWeeks: text("promo_weeks").default("No promotion"),
  eomPercent: numeric("eom_percent").default("0"),
  eomDays: integer("eom_days").default(0),
  vacancyPercent: numeric("vacancy_percent").default("0"),
  vacancyDays: integer("vacancy_days").default(0),
  occupancyPercent: numeric("occupancy_percent").default("0"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPricingModelSchema = createInsertSchema(pricingModels).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  name: z.string().min(1, "Model name is required"),
  preChangeBoostPercent: z.string().optional(),
  minPercent: z.string().optional(),
  maxPercent: z.string().optional(),
  changeRatePercent: z.string().optional(),
  changeRateHours: z.number().optional(),
  moveInFee: z.string().optional(),
  waiveMoveIn: z.string().optional(),
  promoPercent: z.string().optional(),
  promoWeeks: z.string().optional(),
  eomPercent: z.string().optional(),
  eomDays: z.number().optional(),
  vacancyPercent: z.string().optional(),
  vacancyDays: z.number().optional(),
  occupancyPercent: z.string().optional(),
});

export type InsertPricingModel = z.infer<typeof insertPricingModelSchema>;
export type PricingModel = typeof pricingModels.$inferSelect;

export const expenses = pgTable("expenses", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  category: text("category").notNull(),
  subcategory: text("subcategory"),
  description: text("description"),
  amount: text("amount").notNull(),
  date: text("date").notNull(),
  vendor: text("vendor"),
  notes: text("notes"),
  renovationItemId: integer("renovation_item_id"),
  documentId: integer("document_id"),
  fileHash: text("file_hash"),
  isRecurring: boolean("is_recurring").default(false),
  recurringDay: integer("recurring_day"),
  recurringActive: boolean("recurring_active").default(true),
  voidedAt: timestamp("voided_at"),
  createdAt: timestamp("created_at").defaultNow(),
  externalSource: text("external_source"),
  externalId: text("external_id"),
  // Task #1903 — card last-4 and Mercury's own category captured at import
  // time. `cardLast4` powers the card → property auto-match (and backfill on
  // card add). `mercuryCategory` is preserved verbatim from Mercury so the
  // UI can surface "what Mercury called this" alongside the host-chosen
  // PadPulse category.
  cardLast4: text("card_last4"),
  mercuryCategory: text("mercury_category"),
  // Task #1908 — when a manual expense has been merged into a Mercury-imported
  // expense via the reconciliation flow, this points at the surviving Mercury
  // expense row. The manual row itself is also soft-deleted via voidedAt so
  // it disappears from P&L. Nullable; populated only by the merge endpoint.
  mergedIntoExpenseId: integer("merged_into_expense_id"),
  // Task #2125 — soft-exclude for Mercury-imported transactions. Excluded rows
  // are hidden from the active expense list but preserved for re-import dedup.
  excludedAt: timestamp("excluded_at"),
  // Revenue triage — when a HUMAN confirmed this row (categorize/set property/
  // explicit mark-reviewed). NULL = still in the "For review" queue. Never
  // stamped by imports, AI auto-categorization, category memory, or card
  // auto-match: machine guesses always require human confirmation.
  reviewedAt: timestamp("reviewed_at"),
  // Task #2123 / #2131 — which Mercury bank or IO Credit Card account this
  // transaction came from. Populated at import time from either the Mercury
  // transaction's `bankAccountId` field (bank-account webhooks + backfill) or
  // the explicit account id passed from the card sync. The expenses table
  // uses this to show an "Account" column per-row. Null for manually-entered
  // expenses, non-Mercury imports, and legacy imported rows.
  mercuryAccountId: text("mercury_account_id"),
  // Task #2166 — Provenance tag for the category column. NULL = legacy /
  // manual / unspecified. "ai_auto" means the Mercury importer set the
  // category from a high-confidence Gemini suggestion at import time so
  // hosts can audit and bulk-revert AI guesses without losing manual edits.
  categorySource: text("category_source"),
  // Task #2305 — Split transactions. When non-null, this row is a split
  // child line; splitParentId points at the parent expense row. The parent
  // keeps the original amount/date/vendor/source; each child carries its
  // own propertyId, category, amount, and notes. ON DELETE CASCADE so
  // deleting a parent removes all its children automatically.
  splitParentId: integer("split_parent_id"),
  // Task #3788 — Durable "Company Operations" marker. When true, the
  // transaction is intentionally not tied to a single property (company-wide
  // overhead) and is surfaced as its own P&L group. Distinct from a NULL
  // propertyId (which means "unassigned / needs a property"). This marker is
  // host-set and must survive Mercury re-syncs: the upsert path skips
  // re-stamping propertyId when this is true. Setting a property clears it.
  isCompanyLevel: boolean("is_company_level").default(false),
}, (t) => ({
  // Task #2305 — Speeds up the "find children of parent" lookup used by
  // the splits API and the parent-row badge derivation. Mirrors the index
  // created by scripts/deploy-migrate.sql on production.
  splitParentIdIdx: index("expenses_split_parent_id_idx").on(t.splitParentId),
  externalSourceIdUniq: uniqueIndex("expenses_external_source_id_unique")
    .on(t.userId, t.externalSource, t.externalId)
    .where(sql`external_id IS NOT NULL`),
  userMercuryCardIdx: index("expenses_user_mercury_card_idx")
    .on(t.userId, t.cardLast4)
    .where(sql`external_source = 'mercury' AND card_last4 IS NOT NULL AND property_id IS NULL`),
}));

export const insertExpenseSchema = createInsertSchema(expenses).omit({ id: true, createdAt: true });
export type InsertExpense = z.infer<typeof insertExpenseSchema>;
export type Expense = typeof expenses.$inferSelect;

// Task #2142 — Multi-attachment junction table. Backs the attachment drawer
// that lets hosts link one or more property documents to any expense row.
// The legacy `expenses.documentId` column is kept for backwards compatibility;
// the deploy backfill seeds this table from all non-null `documentId` rows.
// All attachment reads/writes going forward use this table exclusively.
export const expenseAttachments = pgTable("expense_attachments", {
  id: serial("id").primaryKey(),
  expenseId: integer("expense_id").notNull().references(() => expenses.id, { onDelete: "cascade" }),
  documentId: integer("document_id").notNull().references(() => propertyDocuments.id, { onDelete: "cascade" }),
  attachedAt: timestamp("attached_at").defaultNow().notNull(),
}, (table) => ({
  expenseDocumentUniqueIdx: uniqueIndex("expense_attachments_expense_document_idx").on(table.expenseId, table.documentId),
}));

export const insertExpenseAttachmentSchema = createInsertSchema(expenseAttachments).omit({ id: true, attachedAt: true });
export type InsertExpenseAttachment = z.infer<typeof insertExpenseAttachmentSchema>;
export type ExpenseAttachment = typeof expenseAttachments.$inferSelect;

export const expenseCategorySettings = pgTable("expense_category_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  disabledCategories: jsonb("disabled_categories").$type<string[]>().default([]),
  customCategories: jsonb("custom_categories").$type<{label: string, value: string, isBalanceSheet?: boolean}[]>().default([]),
  defaultImportedCategory: text("default_imported_category"),
  // Task #1938 — Per-host threshold (0-100) that controls which Mercury
  // reconciliation candidates are picked by the "Select all high-confidence"
  // checkbox. Defaults to 85 to preserve historical behavior.
  mercuryHighConfidenceThreshold: integer("mercury_high_confidence_threshold").default(85).notNull(),
  // Task #1950 — When ON, the merge-candidate scorer immediately merges any
  // newly-discovered Mercury suggestion whose score >= the host's
  // mercuryHighConfidenceThreshold. Defaults OFF so existing hosts keep the
  // explicit opt-in flow they have today.
  mercuryAutoMergeEnabled: boolean("mercury_auto_merge_enabled").default(false).notNull(),
  // Task #2165 — Per-host confidence threshold (0-100) controlling which AI
  // category suggestions are surfaced as the violet "AI:" pill on
  // uncategorized expenses. Defaults to 85 to match the prior hard-coded
  // constant from Task #2159.
  aiSuggestionConfidenceThreshold: integer("ai_suggestion_confidence_threshold").default(85).notNull(),
  // Task #2166 — When ON, brand-new Mercury imports whose initial category
  // would be "other" are run through the Gemini category-suggestion service
  // at import time. If the returned confidence ≥ aiAutoCategorizeThreshold
  // (percent, 0-100), the suggested PadPulse category is written in place
  // and the row is tagged `category_source = 'ai_auto'` so hosts can audit
  // and bulk-revert. Defaults OFF; threshold defaults to 95 to bias for
  // precision over recall on first launch.
  aiAutoCategorizeEnabled: boolean("ai_auto_categorize_enabled").default(false).notNull(),
  aiAutoCategorizeThreshold: integer("ai_auto_categorize_threshold").default(95).notNull(),
  // Task #2301 — Custom income categories, parallel to customCategories (expense).
  customIncomeCategories: jsonb("custom_income_categories").$type<{label: string, value: string}[]>().default([]),
  disabledIncomeCategories: jsonb("disabled_income_categories").$type<string[]>().default([]),
}, (table) => ({
  userIdUniq: uniqueIndex("expense_category_settings_user_id_idx").on(table.userId),
}));

export const insertExpenseCategorySettingsSchema = createInsertSchema(expenseCategorySettings).omit({ id: true });
export type InsertExpenseCategorySettings = z.infer<typeof insertExpenseCategorySettingsSchema>;
export type ExpenseCategorySettings = typeof expenseCategorySettings.$inferSelect;

export const MERCURY_WEBHOOK_STATUS = ["active", "disconnected"] as const;
export type MercuryWebhookStatus = typeof MERCURY_WEBHOOK_STATUS[number];

export const MERCURY_BACKFILL_STATUSES = ["idle", "pending", "running", "done", "failed"] as const;
export type MercuryBackfillStatus = typeof MERCURY_BACKFILL_STATUSES[number];

export const mercuryWebhookConnections = pgTable("mercury_webhook_connections", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }).unique(),
  token: text("token").notNull().unique(),
  secretKey: text("secret_key"),
  // Task #1904 — Mercury API token (separate from the webhook signing
  // secret). Required for card-management endpoints used by the
  // `mercury_card_provisioning` beta. NULL until the host pastes it.
  apiKey: text("api_key"),
  // Task #1906 — Per-host Mercury API token used to pull monthly statements
  // (Mercury's API auth is per-user; the platform does not own a global
  // token). Optional: hosts can still use the inbound webhook for
  // transaction sync without enabling the statements archive.
  apiToken: text("api_token"),
  status: text("status").$type<MercuryWebhookStatus>().notNull().default("active"),
  eventsReceived: integer("events_received").notNull().default(0),
  signatureFailures: integer("signature_failures").notNull().default(0),
  lastEventAt: timestamp("last_event_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Task #1903 — API-key driven connect flow. The API key (and the signing
  // secret we get back from Mercury's createWebhook call) live encrypted at
  // rest in `secretKey` / `apiKeyEncrypted` and are never returned to the
  // client (only the last-4 preview). `mercuryWebhookId` is the id returned
  // by Mercury so disconnect can call deleteWebhook.
  apiKeyEncrypted: text("api_key_encrypted"),
  apiKeyLast4: text("api_key_last4"),
  mercuryWebhookId: text("mercury_webhook_id"),
  backfillStatus: text("backfill_status").$type<MercuryBackfillStatus>().notNull().default("idle"),
  backfillTotal: integer("backfill_total").notNull().default(0),
  backfillImported: integer("backfill_imported").notNull().default(0),
  backfillSkipped: integer("backfill_skipped").notNull().default(0),
  backfillStartDate: text("backfill_start_date"),
  backfillStartedAt: timestamp("backfill_started_at"),
  backfillFinishedAt: timestamp("backfill_finished_at"),
  backfillLastError: text("backfill_last_error"),
  // Task #2334 — Host-visible warning when the saved Mercury API key passed
  // the connect check but didn't return any IO Credit Card accounts. Probed
  // at connect time and on every accounts refresh so the host doesn't have
  // to wait days to discover a missing scope. Cleared when a corrected key
  // (or a refresh) returns at least one credit-card account.
  apiKeyScopeWarning: text("api_key_scope_warning"),
});

export const insertMercuryWebhookConnectionSchema = createInsertSchema(mercuryWebhookConnections).omit({ id: true, createdAt: true });
export type InsertMercuryWebhookConnection = z.infer<typeof insertMercuryWebhookConnectionSchema>;
export type MercuryWebhookConnection = typeof mercuryWebhookConnections.$inferSelect;

// Mercury webhook event ledger — every received event is recorded here for
// audit + dedup. The Mercury event `id` is unique per connection; duplicate
// deliveries are no-ops (the route returns 200 without reprocessing).
export const mercuryWebhookEvents = pgTable("mercury_webhook_events", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  eventId: text("event_id").notNull(),
  eventType: text("event_type").notNull(),
  outcome: text("outcome").notNull(),
  resourceId: text("resource_id"),
  payload: jsonb("payload"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqUserEvent: uniqueIndex("mercury_webhook_events_user_event_unique").on(table.userId, table.eventId),
}));

export const insertMercuryWebhookEventSchema = createInsertSchema(mercuryWebhookEvents).omit({ id: true, createdAt: true });
export type InsertMercuryWebhookEvent = z.infer<typeof insertMercuryWebhookEventSchema>;
export type MercuryWebhookEvent = typeof mercuryWebhookEvents.$inferSelect;

// ============================================================
// Quo (OpenPhone) messaging integration — per-host connection + event ledger.
// One row per host account (owner org). The API key and the webhook signing
// secret live encrypted at rest (`encryptField`, applied in the storage layer)
// and are never returned to the client — only `apiKeyLast4`. `webhookToken`
// is the opaque path segment of our callback URL
// (`/api/webhooks/quo/:token`) and is how an inbound delivery resolves its
// tenant; `quoWebhookId` is Quo's id so disconnect can delete the webhook.
// ============================================================
export const QUO_CONNECTION_STATUSES = ["active", "disconnected", "error"] as const;
export type QuoConnectionStatus = typeof QUO_CONNECTION_STATUSES[number];

export const QUO_BACKFILL_STATUSES = ["idle", "pending", "running", "done", "failed"] as const;
export type QuoBackfillStatus = typeof QUO_BACKFILL_STATUSES[number];

export const quoConnections = pgTable("quo_connections", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }).unique(),
  apiKeyEncrypted: text("api_key_encrypted"),
  apiKeyLast4: text("api_key_last4"),
  // The single Quo inbox (phone number) this host relays through.
  phoneNumberId: text("phone_number_id"),
  phoneNumber: text("phone_number"),
  phoneNumberName: text("phone_number_name"),
  webhookToken: text("webhook_token").notNull().unique(),
  quoWebhookId: text("quo_webhook_id"),
  webhookSecretEncrypted: text("webhook_secret_encrypted"),
  status: text("status").$type<QuoConnectionStatus>().notNull().default("active"),
  lastError: text("last_error"),
  eventsReceived: integer("events_received").notNull().default(0),
  signatureFailures: integer("signature_failures").notNull().default(0),
  lastEventAt: timestamp("last_event_at"),
  lastPolledAt: timestamp("last_polled_at"),
  backfillStatus: text("backfill_status").$type<QuoBackfillStatus>().notNull().default("idle"),
  backfillConversationsTotal: integer("backfill_conversations_total").notNull().default(0),
  backfillConversationsMatched: integer("backfill_conversations_matched").notNull().default(0),
  backfillImported: integer("backfill_imported").notNull().default(0),
  backfillSkipped: integer("backfill_skipped").notNull().default(0),
  backfillStartedAt: timestamp("backfill_started_at"),
  backfillFinishedAt: timestamp("backfill_finished_at"),
  backfillLastError: text("backfill_last_error"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertQuoConnectionSchema = createInsertSchema(quoConnections).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertQuoConnection = z.infer<typeof insertQuoConnectionSchema>;
export type QuoConnection = typeof quoConnections.$inferSelect;

// Quo webhook delivery ledger. `deliveryId` is Quo's `webhook-id` header —
// stable across the provider's 8 retries — so (user_id, delivery_id) is the
// dedupe key. A row that is still `received` means a prior attempt crashed
// mid-processing and the retry is allowed to reprocess; any other outcome is
// terminal and the duplicate is acknowledged with 200 and skipped.
export const QUO_WEBHOOK_OUTCOMES = [
  "received",
  "processed",
  "ignored_unmatched",
  "ignored_group",
  "ignored_type",
] as const;
export type QuoWebhookOutcome = typeof QUO_WEBHOOK_OUTCOMES[number];

export const quoWebhookEvents = pgTable("quo_webhook_events", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  deliveryId: text("delivery_id").notNull(),
  eventId: text("event_id"),
  eventType: text("event_type").notNull(),
  outcome: text("outcome").$type<QuoWebhookOutcome>().notNull(),
  resourceId: text("resource_id"),
  payload: jsonb("payload"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqUserDelivery: uniqueIndex("quo_webhook_events_user_delivery_unique").on(table.userId, table.deliveryId),
}));

export const insertQuoWebhookEventSchema = createInsertSchema(quoWebhookEvents).omit({ id: true, createdAt: true });
export type InsertQuoWebhookEvent = z.infer<typeof insertQuoWebhookEventSchema>;
export type QuoWebhookEvent = typeof quoWebhookEvents.$inferSelect;

export const SLACK_CONNECTION_STATUSES = ["active", "disconnected"] as const;
export const mercuryCategoryMemory = pgTable("mercury_category_memory", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  merchantNormalized: text("merchant_normalized").notNull(),
  category: text("category").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uniqMemory: uniqueIndex("mercury_category_memory_unique").on(table.userId, table.propertyId, table.merchantNormalized),
}));

export const insertMercuryCategoryMemorySchema = createInsertSchema(mercuryCategoryMemory).omit({ id: true, updatedAt: true });
export type InsertMercuryCategoryMemory = z.infer<typeof insertMercuryCategoryMemorySchema>;
export type MercuryCategoryMemory = typeof mercuryCategoryMemory.$inferSelect;

// Task #2159 — Global cache of Gemini-suggested PadPulse categories for
// brand-new vendors that have no prior categorization history. Keyed by
// the normalized vendor name + Mercury's own category string so we only
// spend tokens the first time any host sees a given (vendor,
// mercuryCategory) pair. Confidence is the model's self-reported 0–1
// score; the frontend only surfaces suggestions above a threshold.
export const expenseAiCategorySuggestions = pgTable("expense_ai_category_suggestions", {
  id: serial("id").primaryKey(),
  vendorNormalized: text("vendor_normalized").notNull(),
  mercuryCategoryNormalized: text("mercury_category_normalized").notNull(),
  suggestedCategory: text("suggested_category").notNull(),
  confidence: doublePrecision("confidence").notNull(),
  model: text("model").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uniq: uniqueIndex("expense_ai_category_suggestions_unique").on(
    table.vendorNormalized,
    table.mercuryCategoryNormalized,
  ),
}));

export type ExpenseAiCategorySuggestion = typeof expenseAiCategorySuggestions.$inferSelect;

// Task #2167 — Telemetry on whether hosts actually trust the AI category
// suggestions surfaced from `expenseAiCategorySuggestions`. Every time a host
// applies the violet "Apply" pill we write an `accepted` event; every time a
// host picks a different category on an expense that had a high-confidence
// AI suggestion visible we write an `overridden` event with the chosen
// category. The admin dashboard aggregates accept rate per suggested
// category and the top overridden vendors so we can tune the prompt /
// threshold over time.
export const expenseAiCategorySuggestionEvents = pgTable("expense_ai_category_suggestion_events", {
  id: serial("id").primaryKey(),
  // FK to expense_ai_category_suggestions(id) declared via foreignKey() with a
  // short name because the drizzle-auto-generated name
  // (expense_ai_category_suggestion_events_suggestion_id_expense_ai_category_suggestions_id_fk)
  // exceeds Postgres's 63-char identifier limit.
  suggestionId: integer("suggestion_id").notNull(),
  expenseId: integer("expense_id").notNull().references(() => expenses.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(), // 'accepted' | 'overridden'
  vendorNormalized: text("vendor_normalized").notNull(),
  mercuryCategoryNormalized: text("mercury_category_normalized").notNull(),
  suggestedCategory: text("suggested_category").notNull(),
  chosenCategory: text("chosen_category"), // null for accepted (= suggestedCategory), set for overridden
  confidence: doublePrecision("confidence").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  byExpense: index("expense_ai_category_suggestion_events_expense_idx").on(table.expenseId),
  bySuggestion: index("expense_ai_category_suggestion_events_suggestion_idx").on(table.suggestionId),
  suggestionFk: foreignKey({
    name: "expense_ai_cat_suggestion_events_suggestion_fk",
    columns: [table.suggestionId],
    foreignColumns: [expenseAiCategorySuggestions.id],
  }).onDelete("cascade"),
}));

export type ExpenseAiCategorySuggestionEvent = typeof expenseAiCategorySuggestionEvents.$inferSelect;

// Task #1905 — Mercury accounts & balances dashboard.
// Materializes each Mercury bank account owned by a host so the Financials
// home tab can render live balances. Seeded from `listAccounts` on connect
// and kept current by `account.*.balance.updated` webhook events.
export const mercuryAccounts = pgTable("mercury_accounts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  mercuryAccountId: text("mercury_account_id").notNull(),
  nickname: text("nickname"),
  kind: text("kind"),
  accountNumberLast4: text("account_number_last4"),
  currentBalance: numeric("current_balance", { precision: 14, scale: 2 }),
  availableBalance: numeric("available_balance", { precision: 14, scale: 2 }),
  currency: text("currency").notNull().default("USD"),
  lastSyncedAt: timestamp("last_synced_at"),
  // Task #2131 — last time we successfully pulled transactions for this
  // account via Mercury's `/account/{id}/transactions` REST endpoint. Drives
  // the incremental sync of credit-card transactions (and any other account
  // kinds we pull on a polling cadence). Null = never synced yet, so the
  // sync routine performs an initial backfill window.
  lastTransactionSyncAt: timestamp("last_transaction_sync_at"),
  // Task #2315 — per-account sync toggle. When false, webhook events and
  // backfill jobs skip importing transactions for this account. Enabled by
  // default so existing accounts continue syncing without host action.
  syncEnabled: boolean("sync_enabled").notNull().default(true),
  // Task #2369 — per-account "Include in P&L" toggle, separate from
  // `syncEnabled`. When false, this account is fully hidden from the P&L
  // tab (account card, balance tiles, totals, monthly columns, Mercury
  // credits income row, excluded-Mercury count), its imported expenses
  // and credits are excluded from all P&L / ledger queries and the
  // reconciliation candidate finder, and the Mercury webhook / card sync
  // pipeline skips it. Reversible — no data is deleted. Default = true.
  includedInPnl: boolean("included_in_pnl").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqUserAccount: uniqueIndex("mercury_accounts_user_account_unique").on(table.userId, table.mercuryAccountId),
}));

export const insertMercuryAccountSchema = createInsertSchema(mercuryAccounts).omit({ id: true, createdAt: true });
export type InsertMercuryAccount = z.infer<typeof insertMercuryAccountSchema>;
export type MercuryAccount = typeof mercuryAccounts.$inferSelect;

// Task #1906 — Mercury monthly statements archive. One row per
// (userId, mercuryAccountId, statementMonth). `statementMonth` is the
// "YYYY-MM" the statement covers.
//
// Task #1918 — PDFs are now stored in Replit Object Storage under
// `${PRIVATE_OBJECT_DIR}/mercury-statements/<uuid>.pdf` and the row only
// keeps `fileKey` (the path relative to PRIVATE_OBJECT_DIR). `fileUrl`
// is kept nullable for backward compatibility with rows created before
// the migration (data URLs); a one-time backfill drains those rows and
// new rows always set `fileKey`. When a host has a single active
// property we also materialize the statement as a property_documents
// row (category "bank_statement") and store the id here; otherwise it
// stays null and the host can attach it manually.
export const mercuryStatements = pgTable("mercury_statements", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  mercuryAccountId: text("mercury_account_id").notNull(),
  mercuryAccountName: text("mercury_account_name"),
  mercuryStatementId: text("mercury_statement_id"),
  statementMonth: text("statement_month").notNull(),
  startDate: text("start_date"),
  endDate: text("end_date"),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url"),
  fileKey: text("file_key"),
  totalDeposits: numeric("total_deposits"),
  totalWithdrawals: numeric("total_withdrawals"),
  endingBalance: numeric("ending_balance"),
  propertyDocumentId: integer("property_document_id"),
  archivedAt: timestamp("archived_at"),
  downloadedAt: timestamp("downloaded_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqMonth: uniqueIndex("mercury_statements_user_account_month_unique").on(
    table.userId, table.mercuryAccountId, table.statementMonth,
  ),
  userIdx: index("mercury_statements_user_idx").on(table.userId, table.statementMonth),
}));

export const insertMercuryStatementSchema = createInsertSchema(mercuryStatements).omit({ id: true, downloadedAt: true, createdAt: true });
export type InsertMercuryStatement = z.infer<typeof insertMercuryStatementSchema>;
export type MercuryStatement = typeof mercuryStatements.$inferSelect;

export const SUPPLY_CATEGORIES = ["Cleaning", "Bathroom", "HVAC", "Kitchen", "Laundry", "Exterior", "General", "Other"] as const;
export type SupplyCategoryName = typeof SUPPLY_CATEGORIES[number];

export const SUPPLY_ORDER_STATUSES = ["requested", "ordered", "delivered"] as const;
export type SupplyOrderStatus = typeof SUPPLY_ORDER_STATUSES[number];

export const supplyCategories = pgTable("supply_categories", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  isDefault: boolean("is_default").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertSupplyCategorySchema = createInsertSchema(supplyCategories).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  name: z.string().min(1, "Category name is required"),
  isDefault: z.boolean().optional().default(false),
});
export type InsertSupplyCategory = z.infer<typeof insertSupplyCategorySchema>;
export type SupplyCategory = typeof supplyCategories.$inferSelect;

export const supplyItems = pgTable("supply_items", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  category: text("category").notNull(),
  purchaseUrl: text("purchase_url"),
  preferredVendor: text("preferred_vendor"),
  defaultQuantity: integer("default_quantity").default(1),
  isFrequent: boolean("is_frequent").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertSupplyItemSchema = createInsertSchema(supplyItems).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  name: z.string().min(1, "Name is required"),
  category: z.string().min(1, "Category is required"),
  purchaseUrl: z.string().optional().nullable(),
  preferredVendor: z.string().optional().nullable(),
  defaultQuantity: z.number().optional().default(1),
  isFrequent: z.boolean().optional().default(false),
});
export type InsertSupplyItem = z.infer<typeof insertSupplyItemSchema>;
export type SupplyItem = typeof supplyItems.$inferSelect;

export const supplyOrders = pgTable("supply_orders", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  supplyItemId: integer("supply_item_id").references(() => supplyItems.id, { onDelete: "set null" }),
  itemName: text("item_name").notNull(),
  category: text("category"),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  requestedBy: text("requested_by"),
  assigneeId: integer("assignee_id").references(() => appUsers.id, { onDelete: "set null" }),
  // Task #2430 — vendor-team-member assignment for supply requests (parallels
  // `tasks.assignedTeamMemberId`). When set, Pulse boosts the supply request
  // for the vendor team member viewing the dashboard. FK is declared at the
  // table level (see `t => ({...})` block below) so the generated constraint
  // name stays under Postgres's 63-char identifier limit.
  assignedTeamMemberId: integer("assigned_team_member_id"),
  status: text("status").default("requested").notNull(),
  quantity: integer("quantity").default(1),
  purchaseUrl: text("purchase_url"),
  orderReference: text("order_reference"),
  trackingUrl: text("tracking_url"),
  notes: text("notes"),
  dateRequested: timestamp("date_requested").defaultNow().notNull(),
  dateOrdered: timestamp("date_ordered"),
  dateDelivered: timestamp("date_delivered"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  assignedTeamMemberFk: foreignKey({
    name: "supply_orders_assigned_team_member_fk",
    columns: [t.assignedTeamMemberId],
    foreignColumns: [vendorTeamMembers.id],
  }).onDelete("set null"),
}));

export const insertSupplyOrderSchema = createInsertSchema(supplyOrders).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  itemName: z.string().min(1, "Item name is required"),
  supplyItemId: z.number().optional().nullable(),
  category: z.string().optional().nullable(),
  propertyId: z.number({ required_error: "Property is required" }).min(1, "Property is required"),
  requestedBy: z.string().optional().nullable(),
  assigneeId: z.number().optional().nullable(),
  assignedTeamMemberId: z.number().optional().nullable(),
  status: z.enum(SUPPLY_ORDER_STATUSES).default("requested"),
  quantity: z.number().optional().default(1),
  purchaseUrl: z.string().optional().nullable(),
  orderReference: z.string().optional().nullable(),
  trackingUrl: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  dateRequested: z.coerce.date().optional(),
  dateOrdered: z.coerce.date().optional().nullable(),
  dateDelivered: z.coerce.date().optional().nullable(),
});
export type InsertSupplyOrder = z.infer<typeof insertSupplyOrderSchema>;
export type SupplyOrder = typeof supplyOrders.$inferSelect;

export const supplyRecurringSchedules = pgTable("supply_recurring_schedules", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  supplyItemId: integer("supply_item_id").notNull().references(() => supplyItems.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  intervalDays: integer("interval_days").notNull(),
  lastOrderDate: timestamp("last_order_date"),
  nextDueDate: timestamp("next_due_date"),
  isActive: boolean("is_active").default(true),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertSupplyRecurringScheduleSchema = createInsertSchema(supplyRecurringSchedules).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  supplyItemId: z.number(),
  propertyId: z.number({ required_error: "Property is required" }).min(1, "Property is required"),
  intervalDays: z.number({ required_error: "Interval is required", invalid_type_error: "Interval is required" }).min(1, "Interval must be at least 1 day"),
  lastOrderDate: z.coerce.date().optional().nullable(),
  nextDueDate: z.coerce.date({ required_error: "Next due date is required", invalid_type_error: "Next due date is required" }),
  isActive: z.boolean().optional().default(true),
  archivedAt: z.coerce.date().optional().nullable(),
});
export type InsertSupplyRecurringSchedule = z.infer<typeof insertSupplyRecurringScheduleSchema>;
export type SupplyRecurringSchedule = typeof supplyRecurringSchedules.$inferSelect;

export const supplyPropertySettings = pgTable("supply_property_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  defaultAssigneeId: integer("default_assignee_id").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertSupplyPropertySettingsSchema = createInsertSchema(supplyPropertySettings).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  propertyId: z.number(),
  defaultAssigneeId: z.number().optional().nullable(),
});
export type InsertSupplyPropertySettings = z.infer<typeof insertSupplyPropertySettingsSchema>;
export type SupplyPropertySettings = typeof supplyPropertySettings.$inferSelect;

export const updateProfileSchema = z.object({
  firstName: z.string().trim().max(100, "First name is too long").optional(),
  lastName: z.string().trim().max(100, "Last name is too long").optional(),
  email: z.string().email("Please enter a valid email address").optional(),
  phone: z
    .string()
    .trim()
    .refine(
      (val) => val === "" || /^\+[1-9]\d{6,14}$/.test(val),
      "Phone number must be in international format (e.g. +14155551234)",
    )
    .optional(),
  profilePictureUrl: z.string().optional().nullable(),
  journeyStage: z.enum(JOURNEY_STAGES).optional(),
  teamEmailNotificationsEnabled: z.boolean().optional(),
  unmatchedSmsEmailEnabled: z.boolean().optional(),
  padsplitPushFailureEmailEnabled: z.boolean().optional(),
  mercuryStatementsEmailEnabled: z.boolean().optional(),
  mercuryCardNearLimitEmailEnabled: z.boolean().optional(),
  preferredTheme: z.enum(["light", "dark", "system"]).optional(),
  sopLink: z.string().url("Must be a valid URL").or(z.literal("")).optional().nullable(),
  excludeInactiveRoomsFromOccupancy: z.boolean().optional(),
  showInactiveMessages: z.boolean().optional(),
  // Task #2909 — Account-level IANA timezone. Reject non-IANA strings so a bad
  // value can't reach any scheduled-feature cron and throw inside
  // Intl.DateTimeFormat for that host.
  timezone: z
    .string()
    .min(1)
    .refine((tz) => {
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    }, "Must be a valid IANA timezone")
    .optional(),
  // Task #3972 — Unified assignment-notification channel prefs. Accepts the
  // PARTIAL shape (any subset of types/channels): the route deep-merges the
  // payload over the user's resolved stored prefs (see
  // mergeAssignmentNotificationPrefs) before writing, so omitted types keep
  // their stored values. This is what lets a previously cached bundle that
  // predates a newer type (e.g. member_message_received, OneSignal Phase 2)
  // keep saving during a rollout — a full-shape schema here 400'd its 4-type
  // payload, and a wholesale replace would have silently reset the new type.
  assignmentNotificationPrefs: assignmentNotificationPrefsPatchSchema.optional(),
});

// Task #2122 — Per-user trigger character for the inline canned-response menu
// in the messages reply box. Stored server-side so it follows the user across
// browsers and devices. NULL/undefined means "unset → client falls back to
// `/`". An empty string disables the shortcut entirely. Anything else must be
// a single character.
export const updateCannedResponseTriggerSchema = z.object({
  trigger: z
    .string()
    .max(1, "Trigger must be a single character"),
});
export type UpdateCannedResponseTriggerInput = z.infer<typeof updateCannedResponseTriggerSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().max(200, "Password is too long").optional().default(""),
  newPassword: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(200, "Password is too long"),
});

export const SERVICE_FORM_TYPES = ["cleaning", "landscaping", "handyman", "swat", "pool", "other"] as const;
export type ServiceFormType = typeof SERVICE_FORM_TYPES[number];

export const serviceFormTemplates = pgTable("service_form_templates", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdByUserId: integer("created_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  formType: text("form_type").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  sections: jsonb("sections").$type<Array<{
    id: string;
    title: string;
    checklistItems: Array<{ id: string; label: string; photoRequired: boolean }>;
    photoSlots: Array<{ id: string; label: string; required: boolean }>;
    hasNotes: boolean;
  }>>().default([]).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  // Default for tasks.time_tracking_enabled on form-tasks spawned from this
  // template — cleaning-style templates turn the portal timer on, simple
  // acknowledgment forms leave it off.
  timeTrackingDefault: boolean("time_tracking_default").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdIdx: index("idx_service_form_templates_user_id").on(table.userId),
}));

export const insertServiceFormTemplateSchema = createInsertSchema(serviceFormTemplates).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  formType: z.enum(SERVICE_FORM_TYPES),
  name: z.string().min(1, "Name is required"),
  createdByUserId: z.number().optional().nullable(),
  timeTrackingDefault: z.boolean().optional(),
});
export type InsertServiceFormTemplate = z.infer<typeof insertServiceFormTemplateSchema>;
export type ServiceFormTemplate = typeof serviceFormTemplates.$inferSelect;

export const serviceFormLinks = pgTable("service_form_links", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  templateId: integer("template_id").notNull().references(() => serviceFormTemplates.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  vendorId: integer("vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  token: text("token").notNull().unique(),
  recipientPhone: text("recipient_phone"),
  recipientName: text("recipient_name"),
  sentAt: timestamp("sent_at"),
  expiresAt: timestamp("expires_at"),
  archivedAt: timestamp("archived_at"),
  autoArchivedAt: timestamp("auto_archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertServiceFormLinkSchema = createInsertSchema(serviceFormLinks).omit({
  id: true,
  createdAt: true,
  archivedAt: true,
  autoArchivedAt: true,
}).extend({
  userId: z.number(),
  templateId: z.number(),
  propertyId: z.number().optional().nullable(),
  vendorId: z.number().optional().nullable(),
  memberId: z.number().optional().nullable(),
  token: z.string(),
  recipientPhone: z.string().optional().nullable(),
  recipientName: z.string().optional().nullable(),
});
export type InsertServiceFormLink = z.infer<typeof insertServiceFormLinkSchema>;
export type ServiceFormLink = typeof serviceFormLinks.$inferSelect;

export const serviceFormSubmissions = pgTable("service_form_submissions", {
  id: serial("id").primaryKey(),
  // Nullable since the forms-on-tasks convergence: vendor-path submissions
  // attach to a form-task via task_id and never mint a link; member/phone
  // magic-link submissions keep link_id. At least one of the two is set.
  linkId: integer("link_id").references(() => serviceFormLinks.id, { onDelete: "cascade" }),
  taskId: integer("task_id").references((): AnyPgColumn => tasks.id, { onDelete: "cascade" }),
  templateId: integer("template_id").notNull(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  vendorId: integer("vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  submitterName: text("submitter_name"),
  notes: text("notes"),
  checklistResponses: jsonb("checklist_responses").$type<Record<string, boolean>>().default({}).notNull(),
  photoUrls: jsonb("photo_urls").$type<Record<string, string[]>>().default({}).notNull(),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  locationFlagged: boolean("location_flagged").default(false).notNull(),
  locationDistanceMiles: doublePrecision("location_distance_miles"),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  dateOfService: text("date_of_service"),
  nextServiceDate: text("next_service_date"),
}, (table) => [
  foreignKey({
    name: "service_form_submissions_template_fk",
    columns: [table.templateId],
    foreignColumns: [serviceFormTemplates.id],
  }).onDelete("cascade"),
  index("idx_service_form_submissions_user_id").on(table.userId),
  index("idx_service_form_submissions_link_id").on(table.linkId),
  index("idx_service_form_submissions_task_id").on(table.taskId),
]);

export const insertServiceFormSubmissionSchema = createInsertSchema(serviceFormSubmissions).omit({
  id: true,
  submittedAt: true,
}).extend({
  linkId: z.number().optional().nullable(),
  taskId: z.number().optional().nullable(),
  templateId: z.number(),
  userId: z.number(),
  propertyId: z.number().optional().nullable(),
  vendorId: z.number().optional().nullable(),
  memberId: z.number().optional().nullable(),
  submitterName: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  checklistResponses: z.record(z.boolean()).optional(),
  photoUrls: z.record(z.array(z.string())).optional(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  locationFlagged: z.boolean().optional().default(false),
  locationDistanceMiles: z.number().optional().nullable(),
  dateOfService: z.string().optional().nullable(),
  nextServiceDate: z.string().optional().nullable(),
});
export type InsertServiceFormSubmission = z.infer<typeof insertServiceFormSubmissionSchema>;
export type ServiceFormSubmission = typeof serviceFormSubmissions.$inferSelect;

// ─── Service Form Submission Messages ────────────────────────────────────────
export const serviceFormSubmissionMessages = pgTable("service_form_submission_messages", {
  id: serial("id").primaryKey(),
  // Both FKs declared at the table level so we can override the
  // auto-generated names — the defaults
  // (`service_form_submission_messages_submission_id_service_form_submissions_id_fk` 77 chars,
  //  `service_form_submission_messages_vendor_team_member_id_vendor_team_members_id_fk` 80 chars)
  // exceed Postgres's 63-char identifier limit and would otherwise cause a
  // perpetual `db:push` diff against the silently-truncated catalog form.
  // Task #2102 — `submission_id` is nullable so a message can be attached
  // to a service-form *link* before a submission exists (host ↔ vendor chat
  // on a pending form). `link_id` is always set going forward and is the
  // stable thread key across the form's pending → submitted lifecycle.
  submissionId: integer("submission_id"),
  linkId: integer("link_id"),
  senderType: text("sender_type").notNull(),
  senderId: integer("sender_id").notNull(),
  vendorTeamMemberId: integer("vendor_team_member_id"),
  body: text("body").notNull(),
  attachments: jsonb("attachments").default([]).notNull(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  submissionFk: foreignKey({
    name: "sfs_messages_submission_fk",
    columns: [t.submissionId],
    foreignColumns: [serviceFormSubmissions.id],
  }).onDelete("cascade"),
  linkFk: foreignKey({
    name: "sfs_messages_link_fk",
    columns: [t.linkId],
    foreignColumns: [serviceFormLinks.id],
  }).onDelete("cascade"),
  vendorTeamMemberFk: foreignKey({
    name: "sfs_messages_vendor_team_member_fk",
    columns: [t.vendorTeamMemberId],
    foreignColumns: [vendorTeamMembers.id],
  }).onDelete("set null"),
}));

export const insertServiceFormSubmissionMessageSchema = createInsertSchema(serviceFormSubmissionMessages).omit({
  id: true,
  createdAt: true,
  readAt: true,
}).extend({
  submissionId: z.number().int().optional().nullable(),
  linkId: z.number().int().optional().nullable(),
  senderType: z.enum(["host", "vendor_owner", "vendor_member"]),
  senderId: z.number().int(),
  vendorTeamMemberId: z.number().int().optional().nullable(),
  body: z.string(),
});
export type InsertServiceFormSubmissionMessage = z.infer<typeof insertServiceFormSubmissionMessageSchema>;
export type ServiceFormSubmissionMessage = typeof serviceFormSubmissionMessages.$inferSelect;

// Forms-on-tasks Phase 5 — `service_form_schedules` (the forms-specific
// recurrence table) was retired; recurring form sends are `recurring`
// task_rules rows processed by processTaskRules() in schedule-cron.ts. The
// deploy script drops the table only when it is empty in the target
// environment (a guard against deleting unseen production rows).

// ─── Member Cleaning System (Build 2) ────────────────────────────────────────
// Tenant self-cleaning programs: a per-property config picks a model
// (7-day Daily Duty Roster or Weekly Whole-House Rotation), the generation
// engine materializes assignment rows ahead of time from live occupancy, and
// proof-of-completion reuses the Service Forms subsystem (member magic links →
// service_form_submissions). Assignments deliberately live OUTSIDE the vendor
// `tasks` spine: members are not task assignees, the roster status model is
// different, and the 30-day horizon regenerates rows at a churn the tasks
// table's attached machinery (PadSplit sync journal, SLA, batch pool) should
// never see. Third-party cleaner visits DO ride the tasks spine.

export const CLEANING_PROGRAM_MODELS = ["daily_roster", "weekly_rotation"] as const;
export type CleaningProgramModel = typeof CLEANING_PROGRAM_MODELS[number];

export const CLEANING_ASSIGNMENT_STATUSES = ["scheduled", "notified", "submitted", "completed", "skipped", "uncovered"] as const;
export type CleaningAssignmentStatus = typeof CLEANING_ASSIGNMENT_STATUSES[number];

export const cleaningPrograms = pgTable("cleaning_programs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  model: text("model").default("daily_roster").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  // Simple on/off add-on entitlement per property (no billing wiring).
  addOnEnabled: boolean("add_on_enabled").default(false).notNull(),
  // Member-facing opt-out confirmation amount. This is configuration only;
  // the cleaning module does not change rent or create a charge.
  optOutWeeklyPriceIncreaseCents: integer("opt_out_weekly_price_increase_cents").default(0).notNull(),
  // Daily model: which weekdays get coverage (0=Sunday … 6=Saturday).
  daysOfWeek: jsonb("days_of_week").$type<number[]>().default([0, 1, 2, 3, 4, 5, 6]).notNull(),
  leadTimeDays: integer("lead_time_days").default(7).notNull(),
  horizonDays: integer("horizon_days").default(30).notNull(),
  // Proof-of-completion Service Form template (checklist + photo). FK declared
  // at the table level so we can name it — the auto-generated name is 72 chars,
  // over Postgres's 63-char identifier limit.
  cleaningFormTemplateId: integer("cleaning_form_template_id"),
  // Module-scoped reminder cadence / message templates (Phase 3).
  rules: jsonb("rules").default({}).notNull(),
  scheduleStartPending: boolean("schedule_start_pending").default(false).notNull(),
  scheduleStartSentAt: timestamp("schedule_start_sent_at"),
  introRosterInitializedAt: timestamp("intro_roster_initialized_at"),
  // Weekly model: index into the property's room sequence; advances weekly and
  // wraps at the room count. Persisted so the rotation survives regeneration.
  rotationPointer: integer("rotation_pointer").default(0).notNull(),
  // Daily model: standing member→weekday bindings (memberId → 0-6), preserved
  // across regenerations so a member's cleaning day stays stable.
  standingDays: jsonb("standing_days").$type<Record<string, number>>().default({}).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  propertyUnique: uniqueIndex("cleaning_programs_property_unique").on(t.propertyId),
  userIdx: index("cleaning_programs_user_id_idx").on(t.userId),
  optOutPriceNonnegative: check(
    "cleaning_programs_opt_out_price_nonnegative",
    sql`${t.optOutWeeklyPriceIncreaseCents} >= 0`,
  ),
  formTemplateFk: foreignKey({
    name: "cleaning_programs_form_template_fk",
    columns: [t.cleaningFormTemplateId],
    foreignColumns: [serviceFormTemplates.id],
  }).onDelete("set null"),
}));

export const insertCleaningProgramSchema = createInsertSchema(cleaningPrograms).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  rotationPointer: true,
  standingDays: true,
}).extend({
  userId: z.number(),
  propertyId: z.number(),
  model: z.enum(CLEANING_PROGRAM_MODELS).default("daily_roster"),
  isActive: z.boolean().optional(),
  addOnEnabled: z.boolean().optional(),
  optOutWeeklyPriceIncreaseCents: z.number().int().min(0).optional(),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  leadTimeDays: z.number().int().min(0).max(60).optional(),
  horizonDays: z.number().int().min(7).max(90).optional(),
  cleaningFormTemplateId: z.number().optional().nullable(),
  rules: z.record(z.any()).optional(),
});
export type InsertCleaningProgram = z.infer<typeof insertCleaningProgramSchema>;
export type CleaningProgram = typeof cleaningPrograms.$inferSelect;

export const cleaningAssignments = pgTable("cleaning_assignments", {
  id: serial("id").primaryKey(),
  programId: integer("program_id").notNull().references(() => cleaningPrograms.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  // Null = an `uncovered` slot (a departed/vacant slot the operator must see).
  assigneeMemberId: integer("assignee_member_id").references((): AnyPgColumn => members.id, { onDelete: "set null" }),
  // Weekly model: the room-sequence slot that owns this week.
  roomId: integer("room_id").references(() => rooms.id, { onDelete: "set null" }),
  // Daily: the day. Weekly: the week's Monday.
  scheduledDate: date("scheduled_date").notNull(),
  period: text("period").default("day").notNull(),
  status: text("status").default("scheduled").notNull(),
  notificationRevision: integer("notification_revision").default(0).notNull(),
  // Delivery channel for reminders/proof: padsplit outbound vs portal-native.
  channel: text("channel").default("padsplit").notNull(),
  // Proof-of-completion linkage (Phase 2): the magic link minted for this
  // assignment and the member's submission once it lands.
  formLinkId: integer("form_link_id").references(() => serviceFormLinks.id, { onDelete: "set null" }),
  // FK declared at the table level — the auto-generated name is 65 chars,
  // over Postgres's 63-char identifier limit.
  submissionId: integer("submission_id"),
  notifiedAt: timestamp("notified_at"),
  // One-shot advance and follow-up delivery stamps.  Kept on the assignment
  // rather than the cron state so retries and regenerated schedules cannot nag.
  advanceNotifiedAt: timestamp("advance_notified_at"),
  followUpSentAt: timestamp("follow_up_sent_at"),
  submittedAt: timestamp("submitted_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  lookupIdx: index("idx_cleaning_assign_lookup").on(t.propertyId, t.scheduledDate, t.status),
  programDateIdx: index("idx_cleaning_assign_program_date").on(t.programId, t.scheduledDate),
  userIdx: index("idx_cleaning_assign_user").on(t.userId),
  submissionFk: foreignKey({
    name: "cleaning_assignments_submission_fk",
    columns: [t.submissionId],
    foreignColumns: [serviceFormSubmissions.id],
  }).onDelete("set null"),
}));

export const insertCleaningAssignmentSchema = createInsertSchema(cleaningAssignments).omit({
  id: true,
  createdAt: true,
}).extend({
  programId: z.number(),
  userId: z.number(),
  propertyId: z.number(),
  assigneeMemberId: z.number().optional().nullable(),
  roomId: z.number().optional().nullable(),
  scheduledDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  period: z.enum(["day", "week"]).default("day"),
  status: z.enum(CLEANING_ASSIGNMENT_STATUSES).default("scheduled"),
  channel: z.enum(["padsplit", "portal"]).default("padsplit"),
  formLinkId: z.number().optional().nullable(),
  submissionId: z.number().optional().nullable(),
  notifiedAt: z.coerce.date().optional().nullable(),
  submittedAt: z.coerce.date().optional().nullable(),
  completedAt: z.coerce.date().optional().nullable(),
});
export type InsertCleaningAssignment = z.infer<typeof insertCleaningAssignmentSchema>;
export type CleaningAssignment = typeof cleaningAssignments.$inferSelect;

export const cleaningNotificationDeliveries = pgTable("cleaning_notification_deliveries", {
  id: serial("id").primaryKey(),
  programId: integer("program_id").notNull(),
  memberId: integer("member_id").notNull(),
  assignmentKey: integer("assignment_key").default(0).notNull(),
  kind: text("kind").notNull(),
  status: text("status").default("claimed").notNull(),
  claimedAt: timestamp("claimed_at").defaultNow().notNull(),
  deliveredAt: timestamp("delivered_at"),
}, (t) => ({
  oneShotUnique: uniqueIndex("cleaning_notification_delivery_unique").on(t.programId, t.memberId, t.assignmentKey, t.kind),
  programFk: foreignKey({
    name: "cleaning_notification_delivery_program_fk",
    columns: [t.programId],
    foreignColumns: [cleaningPrograms.id],
  }).onDelete("cascade"),
  memberFk: foreignKey({
    name: "cleaning_notification_delivery_member_fk",
    columns: [t.memberId],
    foreignColumns: [members.id],
  }).onDelete("cascade"),
}));
// Immutable review snapshots and independent audit survive assignment claim retirement.
export const cleaningMessagePreviews = pgTable("cleaning_message_previews", {
  id: text("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  actorId: integer("actor_id").notNull(),
  action: text("action").notNull(),
  snapshot: jsonb("snapshot").notNull(),
  input: jsonb("input").notNull(),
  fingerprint: text("fingerprint").notNull(),
  status: text("status").default("preview").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  confirmedAt: timestamp("confirmed_at"),
}, (t) => ({ propertyIdx: index("cleaning_message_preview_property_idx").on(t.propertyId) }));

export const cleaningMessageAttempts = pgTable("cleaning_message_attempts", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  previewId: text("preview_id"),
  actorId: integer("actor_id"),
  cycleKey: text("cycle_key").notNull(),
  snapshot: jsonb("snapshot").notNull(),
  status: text("status").default("uncertain").notNull(),
  result: jsonb("result"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  finishedAt: timestamp("finished_at"),
}, (t) => ({ cycleUnique: uniqueIndex("cleaning_message_attempt_cycle_unique").on(t.cycleKey),
  propertyIdx: index("cleaning_message_attempt_property_idx").on(t.propertyId) }));

export const cleaningOptOuts = pgTable("cleaning_opt_outs", {
  id: serial("id").primaryKey(),
  programId: integer("program_id").notNull().references(() => cleaningPrograms.id, { onDelete: "cascade" }),
  memberId: integer("member_id").notNull().references((): AnyPgColumn => members.id, { onDelete: "cascade" }),
  note: text("note"),
  padsplitNotified: boolean("padsplit_notified").default(false).notNull(),
  // Host-created rows are active immediately. Member-created rows remain
  // pending until a host explicitly approves them.
  status: text("status").default("active").notNull(),
  origin: text("origin").default("host").notNull(),
  reviewedBy: integer("reviewed_by").references(() => appUsers.id, { onDelete: "set null" }),
  reviewedAt: timestamp("reviewed_at"),
  padsplitMessagePendingId: integer("padsplit_message_pending_id"),
  effectiveFrom: date("effective_from").default(sql`CURRENT_DATE`).notNull(),
  effectiveTo: date("effective_to"),
  createdBy: integer("created_by").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  programMemberIdx: index("idx_cleaning_opt_outs_program_member").on(t.programId, t.memberId),
  liveProgramMemberUnique: uniqueIndex("cleaning_opt_outs_live_program_member_uidx")
    .on(t.programId, t.memberId)
    .where(sql`${t.status} IN ('pending', 'active') AND ${t.effectiveTo} IS NULL`),
}));

export const insertCleaningOptOutSchema = createInsertSchema(cleaningOptOuts).omit({
  id: true,
  createdAt: true,
}).extend({
  programId: z.number(),
  memberId: z.number(),
  note: z.string().optional().nullable(),
  padsplitNotified: z.boolean().optional(),
  status: z.enum(["pending", "active", "rejected"]).optional(),
  origin: z.enum(["host", "member"]).optional(),
  reviewedBy: z.number().optional().nullable(),
  reviewedAt: z.coerce.date().optional().nullable(),
  padsplitMessagePendingId: z.number().optional().nullable(),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  effectiveTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  createdBy: z.number().optional().nullable(),
});
export type InsertCleaningOptOut = z.infer<typeof insertCleaningOptOutSchema>;
export type CleaningOptOut = typeof cleaningOptOuts.$inferSelect;

// Lightweight projection of a member's form submission, surfaced as an inline
// card in that member's Messages timeline (same interleave pattern as
// ReportedTaskSummary). kind "cleaning" = a cleaning-assignment proof (carries
// the assignment linkage); kind "form" = any other service form the member
// submitted. Carries only what the card renders/links with.
export type CleaningSubmissionSummary = {
  kind: "cleaning" | "form";
  assignmentId: number | null;
  submissionId: number;
  propertyId: number | null;
  scheduledDate: string | null;
  period: string | null;
  status: string | null;
  templateName: string | null;
  photoCount: number;
  createdAt: Date | string; // submission time — the interleave key
};

export const BOOKING_REQUEST_FIELD_TYPES = [
  "text", "longtext", "email", "phone", "number", "date",
  "yesno", "radio", "select", "multiselect",
  "photo", "video_record", "video_upload", "signature", "agreement", "file",
] as const;
export type BookingRequestFieldType = typeof BOOKING_REQUEST_FIELD_TYPES[number];

export const fieldConditionSchema = z.object({
  sourceFieldId: z.string(),
  operator: z.enum(["equals", "not-equals", "is-any-of"]),
  values: z.array(z.union([z.string(), z.boolean(), z.number()])),
});
export type FieldCondition = z.infer<typeof fieldConditionSchema>;

export const bookingRequestFieldSchema = z.object({
  id: z.string(),
  type: z.enum(BOOKING_REQUEST_FIELD_TYPES),
  label: z.string().min(1),
  required: z.boolean().default(false),
  helpText: z.string().optional().nullable(),
  placeholder: z.string().optional().nullable(),
  options: z.array(z.string()).optional().nullable(),
  allowOther: z.boolean().optional().nullable(),
  agreementText: z.string().optional().nullable(),
  maxFiles: z.number().int().min(1).max(10).optional().nullable(),
  maxDurationSeconds: z.number().int().min(1).max(600).optional().nullable(),
  conditions: z.array(fieldConditionSchema).optional().nullable(),
  conditionCombinator: z.enum(["all", "any"]).optional().nullable(),
  requiredWhenVisible: z.boolean().optional().nullable(),
  page: z.number().int().min(1).optional().nullable(),
});
export type BookingRequestField = z.infer<typeof bookingRequestFieldSchema>;

export const bookingRequestBrandingSchema = z.object({
  brandName: z.string().optional().nullable(),
  brandLogoUrl: z.string().optional().nullable(),
  accentColor: z.string().optional().nullable(),
  headline: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  ctaButtonText: z.string().optional().nullable(),
  footerText: z.string().optional().nullable(),
});
export type BookingRequestBranding = z.infer<typeof bookingRequestBrandingSchema>;

// "archived" — the applicant withdrew/cancelled before a decision. Archiving
// deliberately records NO member decision and fires NO booking_approved /
// booking_rejected messaging events (the external warm browser mirrors those
// into PadSplit, which errors for a cancelled application).
export const BOOKING_REQUEST_STATUSES = ["pending", "approved", "rejected", "archived"] as const;
export type BookingRequestStatus = typeof BOOKING_REQUEST_STATUSES[number];

export const bookingRequestSettings = pgTable("booking_request_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  brandName: text("brand_name"),
  brandLogoUrl: text("brand_logo_url"),
  accentColor: text("accent_color").default("#0052CC"),
  headline: text("headline").default("Tell us about you"),
  description: text("description"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  ctaButtonText: text("cta_button_text").default("Submit Request"),
  footerText: text("footer_text"),
  notifyEmails: jsonb("notify_emails").$type<string[]>().default([]),
  emailNotificationsEnabled: boolean("email_notifications_enabled").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertBookingRequestSettingsSchema = createInsertSchema(bookingRequestSettings).omit({
  id: true, createdAt: true, updatedAt: true,
}).extend({
  userId: z.number(),
  brandName: z.string().optional().nullable(),
  brandLogoUrl: z.string().optional().nullable(),
  accentColor: z.string().optional().nullable(),
  headline: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  ctaButtonText: z.string().optional().nullable(),
  footerText: z.string().optional().nullable(),
  notifyEmails: z.array(z.string().email()).optional().default([]),
  emailNotificationsEnabled: z.boolean().optional().default(true),
});
export type InsertBookingRequestSettings = z.infer<typeof insertBookingRequestSettingsSchema>;
export type BookingRequestSettingsRow = typeof bookingRequestSettings.$inferSelect;

export const PADPULSE_DEFAULT_TEMPLATE_NAME = "PadPulse Default Prospect Application";
export const PADPULSE_DEFAULT_TEMPLATE_DESCRIPTION = "Comprehensive PadPulse default prospect intake — contact info, employment, income, lifestyle questions, ID upload, 60-second intro video, background-check consent, and signature.";

export function makePadPulseDefaultFields(makeId: () => string): BookingRequestField[] {
  return [
    { id: makeId(), type: "text", label: "Full legal name", required: true, helpText: "As shown on your government ID", placeholder: "Jane Smith", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "email", label: "Email address", required: true, helpText: "We'll send updates to this address", placeholder: "you@example.com", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "phone", label: "Mobile phone", required: true, helpText: "Best number to reach you", placeholder: "(555) 555-5555", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "date", label: "Preferred move-in date", required: true, helpText: "", placeholder: "", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "select", label: "Employment status", required: true, helpText: "", placeholder: "", options: ["Employed full-time", "Employed part-time", "Self-employed", "Student", "Retired", "Other"], agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "number", label: "Approximate monthly income (USD)", required: true, helpText: "Pre-tax monthly income", placeholder: "3500", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "longtext", label: "Why are you relocating?", required: false, helpText: "A few sentences is plenty", placeholder: "New job, school, family…", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "yesno", label: "Do you have any pets?", required: true, helpText: "If yes, please describe in the next field", placeholder: "", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "yesno", label: "Do you smoke?", required: true, helpText: "Including vaping", placeholder: "", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "video_record", label: "Record a 60-second introduction", required: true, helpText: "Tell us a little about yourself, your work, and why this home is a good fit. This helps us match the right people to the right home.", placeholder: "", options: null, agreementText: null, maxFiles: 1, maxDurationSeconds: 60 },
    { id: makeId(), type: "file", label: "Upload a government-issued ID", required: true, helpText: "Driver's license, passport, or state ID. Photo or PDF.", placeholder: "", options: null, agreementText: null, maxFiles: 2, maxDurationSeconds: null },
    { id: makeId(), type: "agreement", label: "Background & credit check consent", required: true, helpText: "", placeholder: "", options: null, agreementText: "I authorize the property manager to obtain a background check, credit report, and rental history report for the purpose of evaluating my application. I understand that information collected may include criminal history, eviction history, and credit information.", maxFiles: null, maxDurationSeconds: null },
    { id: makeId(), type: "signature", label: "Signature", required: true, helpText: "Sign with your finger or mouse to confirm everything above is true and accurate.", placeholder: "", options: null, agreementText: null, maxFiles: null, maxDurationSeconds: null },
  ];
}

export const bookingRequestTemplates = pgTable("booking_request_templates", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdByUserId: integer("created_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  description: text("description"),
  fields: jsonb("fields").$type<BookingRequestField[]>().default([]),
  brandingOverride: jsonb("branding_override").$type<BookingRequestBranding | null>(),
  propertyIds: jsonb("property_ids").$type<number[]>().default([]),
  isActive: boolean("is_active").default(true).notNull(),
  isStarterTemplate: boolean("is_starter_template").default(false).notNull(),
  // Soft delete: submissions FK-cascade off templates, so a hard delete would
  // erase submission history. Deleted templates are hidden from lists but kept
  // so historical stats retain their rows and labels.
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  // The unique constraint on (user_id) WHERE is_starter_template = true is
  // intentionally omitted here. Declaring it as a Drizzle uniqueIndex() causes
  // the Replit deploy platform (which diffs the dev DB schema before the build
  // step) to generate a bare CREATE UNIQUE INDEX without the dedup step. The
  // real index is created idempotently in scripts/deploy-migrate.sql after
  // deduplicating starter templates (re-pointing share links / submissions).
]);

export const insertBookingRequestTemplateSchema = createInsertSchema(bookingRequestTemplates).omit({
  // deletedAt omitted so no insert/PATCH body can set OR clear the soft-delete
  // stamp — deletion state changes only through deleteBookingRequestTemplate.
  id: true, createdAt: true, updatedAt: true, deletedAt: true,
}).extend({
  userId: z.number(),
  createdByUserId: z.number().optional().nullable(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  fields: z.array(bookingRequestFieldSchema).default([]),
  brandingOverride: bookingRequestBrandingSchema.optional().nullable(),
  propertyIds: z.array(z.number()).optional().default([]),
  isActive: z.boolean().optional().default(true),
  isStarterTemplate: z.boolean().optional().default(false),
});
export type InsertBookingRequestTemplate = z.infer<typeof insertBookingRequestTemplateSchema>;
export type BookingRequestTemplate = typeof bookingRequestTemplates.$inferSelect;

export const bookingRequestLinks = pgTable("booking_request_links", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  templateId: integer("template_id").notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  token: text("token").notNull().unique(),
  label: text("label"),
  isActive: boolean("is_active").default(true).notNull(),
  // Soft delete: submissions FK-cascade off links, same rationale as templates.
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "booking_request_links_template_fk",
    columns: [table.templateId],
    foreignColumns: [bookingRequestTemplates.id],
  }).onDelete("cascade"),
]);

export const insertBookingRequestLinkSchema = createInsertSchema(bookingRequestLinks).omit({
  // deletedAt omitted — same rationale as the template schema.
  id: true, createdAt: true, deletedAt: true,
}).extend({
  userId: z.number(),
  templateId: z.number(),
  propertyId: z.number().optional().nullable(),
  token: z.string(),
  label: z.string().optional().nullable(),
  isActive: z.boolean().optional().default(true),
});
export type InsertBookingRequestLink = z.infer<typeof insertBookingRequestLinkSchema>;
export type BookingRequestLink = typeof bookingRequestLinks.$inferSelect;

export const bookingRequestSubmissions = pgTable("booking_request_submissions", {
  id: serial("id").primaryKey(),
  linkId: integer("link_id").notNull().references(() => bookingRequestLinks.id, { onDelete: "cascade" }),
  templateId: integer("template_id").notNull(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  // Task #4594 — the member this submission was resolved to (matched or
  // created) on submit. Nullable: historical rows created before this column
  // have none, and a match may fail to resolve on rare malformed input.
  memberId: integer("member_id"),
  submitterName: text("submitter_name"),
  submitterEmail: text("submitter_email"),
  submitterPhone: text("submitter_phone"),
  responses: jsonb("responses").$type<Record<string, any>>().default({}),
  mediaUrls: jsonb("media_urls").$type<Record<string, string[]>>().default({}),
  status: text("status").default("pending").notNull(),
  reviewedBy: integer("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  // Required alongside reviewNotes when rejecting; one of
  // BOOKING_REJECTION_CATEGORIES keys (shared/booking-rejection-categories.ts).
  rejectionCategory: text("rejection_category"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "booking_request_submissions_template_fk",
    columns: [table.templateId],
    foreignColumns: [bookingRequestTemplates.id],
  }).onDelete("cascade"),
  foreignKey({
    name: "booking_request_submissions_member_fk",
    columns: [table.memberId],
    foreignColumns: [members.id],
  }).onDelete("set null"),
]);

export const insertBookingRequestSubmissionSchema = createInsertSchema(bookingRequestSubmissions).omit({
  id: true, submittedAt: true, reviewedAt: true, reviewedBy: true,
}).extend({
  linkId: z.number(),
  templateId: z.number(),
  userId: z.number(),
  propertyId: z.number().optional().nullable(),
  submitterName: z.string().optional().nullable(),
  submitterEmail: z.string().optional().nullable(),
  submitterPhone: z.string().optional().nullable(),
  responses: z.record(z.any()).optional().default({}),
  mediaUrls: z.record(z.array(z.string())).optional().default({}),
  status: z.enum(BOOKING_REQUEST_STATUSES).optional().default("pending"),
  reviewNotes: z.string().optional().nullable(),
  ipAddress: z.string().optional().nullable(),
});
export type InsertBookingRequestSubmission = z.infer<typeof insertBookingRequestSubmissionSchema>;
export type BookingRequestSubmission = typeof bookingRequestSubmissions.$inferSelect;

// padsplit_credentials (legacy scraper login table) was dropped in Task #4748
// along with the in-app scraper stack.

export const SYNC_TYPES = ["members", "properties", "maintenance", "financials", "reviews"] as const;
export type SyncType = typeof SYNC_TYPES[number];

export const SYNC_STATUSES = ["idle", "running", "success", "error"] as const;
export type SyncStatus = typeof SYNC_STATUSES[number];

export const padsplitSyncStates = pgTable("padsplit_sync_states", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  syncType: text("sync_type").notNull(),
  status: text("status").default("idle").notNull(),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  lastError: text("last_error"),
  recordsAffected: integer("records_affected"),
  lastSyncedAt: timestamp("last_synced_at", { withTimezone: true }),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertPadsplitSyncStateSchema = createInsertSchema(padsplitSyncStates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPadsplitSyncState = z.infer<typeof insertPadsplitSyncStateSchema>;
export type PadsplitSyncState = typeof padsplitSyncStates.$inferSelect;

export const padsplitSyncLogs = pgTable("padsplit_sync_logs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  syncType: text("sync_type").notNull(),
  status: text("status").notNull(),
  startedAt: timestamp("started_at").notNull(),
  completedAt: timestamp("completed_at"),
  recordsAffected: integer("records_affected"),
  errorMessage: text("error_message"),
  details: jsonb("details"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("padsplit_sync_logs_user_idx").on(table.userId),
  typeIdx: index("padsplit_sync_logs_type_idx").on(table.userId, table.syncType),
}));

export const insertPadsplitSyncLogSchema = createInsertSchema(padsplitSyncLogs).omit({
  id: true,
  createdAt: true,
});
export type InsertPadsplitSyncLog = z.infer<typeof insertPadsplitSyncLogSchema>;
export type PadsplitSyncLog = typeof padsplitSyncLogs.$inferSelect;

export const padsplitFinancials = pgTable("padsplit_financials", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  padsplitPropertyId: text("padsplit_property_id"),
  periodStart: text("period_start"),
  periodEnd: text("period_end"),
  totalEarnings: numeric("total_earnings"),
  managementFees: numeric("management_fees"),
  netPayout: numeric("net_payout"),
  otherFees: numeric("other_fees"),
  rawData: jsonb("raw_data"),
  syncedAt: timestamp("synced_at").defaultNow().notNull(),
  month: text("month"),
  grossRevenue: integer("gross_revenue").default(0),
  netRevenue: integer("net_revenue").default(0),
  managementFee: integer("management_fee").default(0),
  utilities: integer("utilities").default(0),
  otherDeductions: integer("other_deductions").default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPadsplitFinancialSchema = createInsertSchema(padsplitFinancials).omit({
  id: true,
  createdAt: true,
});
export type InsertPadsplitFinancial = z.infer<typeof insertPadsplitFinancialSchema>;
export type PadsplitFinancial = typeof padsplitFinancials.$inferSelect;

// ─── PadSplit Reviews (Task #1475) ───────────────────────────────────────────
// Stay reviews come from /host/csat; move-in reviews from /host/member-feedback/public-reviews.
// Both tables are keyed by the PadSplit review ID to allow idempotent upserts.

export const PADSPLIT_REVIEW_SENTIMENTS = ["positive", "neutral", "negative"] as const;
export type PadsplitReviewSentiment = typeof PADSPLIT_REVIEW_SENTIMENTS[number];

export const PADSPLIT_REVIEW_TYPES = ["stay", "move_in"] as const;
export type PadsplitReviewType = typeof PADSPLIT_REVIEW_TYPES[number];

/** Biweekly CSAT / stay reviews scraped from /host/csat */
export const padsplitStayReviews = pgTable("padsplit_stay_reviews", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  padsplitReviewId: text("padsplit_review_id").notNull(),
  padsplitPropertyId: text("padsplit_property_id"),
  padsplitMemberId: text("padsplit_member_id"),
  memberName: text("member_name"),
  rating: integer("rating"),
  reviewText: text("review_text"),
  reviewDate: timestamp("review_date"),
  sentiment: text("sentiment").$type<PadsplitReviewSentiment>(),
  rawData: jsonb("raw_data"),
  notifiedAt: timestamp("notified_at"),
  syncedAt: timestamp("synced_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniquePadsplitReviewId: uniqueIndex("padsplit_stay_reviews_padsplit_review_id_user_idx")
    .on(table.padsplitReviewId, table.userId),
}));

export const insertPadsplitStayReviewSchema = createInsertSchema(padsplitStayReviews).omit({ id: true, createdAt: true });
export type InsertPadsplitStayReview = z.infer<typeof insertPadsplitStayReviewSchema>;
export type PadsplitStayReview = typeof padsplitStayReviews.$inferSelect;

/** Move-in reviews scraped from /host/member-feedback/public-reviews */
export const padsplitMoveInReviews = pgTable("padsplit_move_in_reviews", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  padsplitReviewId: text("padsplit_review_id").notNull(),
  padsplitPropertyId: text("padsplit_property_id"),
  padsplitMemberId: text("padsplit_member_id"),
  memberName: text("member_name"),
  roomLabel: text("room_label"),
  rating: integer("rating"),
  reviewText: text("review_text"),
  reviewDate: timestamp("review_date"),
  sentiment: text("sentiment").$type<PadsplitReviewSentiment>(),
  rawData: jsonb("raw_data"),
  notifiedAt: timestamp("notified_at"),
  syncedAt: timestamp("synced_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniquePadsplitReviewId: uniqueIndex("padsplit_move_in_reviews_padsplit_review_id_user_idx")
    .on(table.padsplitReviewId, table.userId),
}));

export const insertPadsplitMoveInReviewSchema = createInsertSchema(padsplitMoveInReviews).omit({ id: true, createdAt: true });
export type InsertPadsplitMoveInReview = z.infer<typeof insertPadsplitMoveInReviewSchema>;
export type PadsplitMoveInReview = typeof padsplitMoveInReviews.$inferSelect;

/** Cached Gemini 30-day summary per property */
export const padsplitReviewSummaries = pgTable("padsplit_review_summaries", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  summaryText: text("summary_text"),
  keyThemes: text("key_themes").array(),
  averageRating: numeric("average_rating"),
  reviewCount: integer("review_count").default(0).notNull(),
  windowDays: integer("window_days").default(30).notNull(),
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uniqueUserProperty: uniqueIndex("padsplit_review_summaries_user_property_idx")
    .on(table.userId, table.propertyId),
}));

export const insertPadsplitReviewSummarySchema = createInsertSchema(padsplitReviewSummaries).omit({ id: true });
export type InsertPadsplitReviewSummary = z.infer<typeof insertPadsplitReviewSummarySchema>;
export type PadsplitReviewSummary = typeof padsplitReviewSummaries.$inferSelect;

// ── PadSplit Review Requests (Task #1475) ────────────────────────────────────
// Records host-initiated review requests for members. PadSplit controls when
// actual CSAT emails are sent; this table is for host tracking only.

export const padsplitReviewRequests = pgTable("padsplit_review_requests", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  memberId: integer("member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  memberName: text("member_name"),
  status: text("status").notNull().default("noted"),
  notes: text("notes"),
  requestedAt: timestamp("requested_at", { withTimezone: true }).defaultNow().notNull(),
});

export const insertPadsplitReviewRequestSchema = createInsertSchema(padsplitReviewRequests).omit({ id: true, requestedAt: true });
export type InsertPadsplitReviewRequest = z.infer<typeof insertPadsplitReviewRequestSchema>;
export type PadsplitReviewRequest = typeof padsplitReviewRequests.$inferSelect;

// ── PadSplit Review Suppression Requests (Task #1477) ────────────────────────
// Hosts use this to track formal suppression requests for low-rated/negative
// PadSplit reviews. PadSplit support ultimately approves suppression; this
// table is the host-side record of the request.

export const PADSPLIT_SUPPRESSION_REASONS = [
  "inaccurate_facts",
  "retaliation",
  "policy_violation",
  "other",
] as const;
export type PadsplitSuppressionReason = typeof PADSPLIT_SUPPRESSION_REASONS[number];

export const PADSPLIT_SUPPRESSION_STATUSES = ["draft", "sent", "approved", "denied"] as const;
export type PadsplitSuppressionStatus = typeof PADSPLIT_SUPPRESSION_STATUSES[number];

export const padsplitReviewSuppressionRequests = pgTable("padsplit_review_suppression_requests", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  requestedByUserId: integer("requested_by_user_id").notNull(),
  reviewType: text("review_type").notNull(),
  reviewId: integer("review_id").notNull(),
  padsplitReviewId: text("padsplit_review_id"),
  memberName: text("member_name"),
  reason: text("reason").notNull(),
  notes: text("notes"),
  status: text("status").default("draft").notNull(),
  emailDraftSent: boolean("email_draft_sent").default(false).notNull(),
  emailSentAt: timestamp("email_sent_at", { withTimezone: true }),
  // Task #1496 — Outcome tracking. Hosts mark a request approved/denied
  // after PadSplit support responds, optionally adding response notes and
  // hiding the underlying review from the dashboard when approved.
  responseNotes: text("response_notes"),
  respondedAt: timestamp("responded_at", { withTimezone: true }),
  respondedByUserId: integer("responded_by_user_id"),
  hideReview: boolean("hide_review").default(false).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  requestedByFk: foreignKey({
    columns: [t.requestedByUserId],
    foreignColumns: [appUsers.id],
    name: "psrsr_requested_by_app_users_fk",
  }).onDelete("cascade"),
  respondedByFk: foreignKey({
    columns: [t.respondedByUserId],
    foreignColumns: [appUsers.id],
    name: "psrsr_responded_by_app_users_fk",
  }).onDelete("set null"),
}));

export const insertPadsplitReviewSuppressionRequestSchema = createInsertSchema(padsplitReviewSuppressionRequests).omit({
  id: true,
  createdAt: true,
  emailDraftSent: true,
  emailSentAt: true,
  responseNotes: true,
  respondedAt: true,
  respondedByUserId: true,
  hideReview: true,
});
export type InsertPadsplitReviewSuppressionRequest = z.infer<typeof insertPadsplitReviewSuppressionRequestSchema>;
export type PadsplitReviewSuppressionRequest = typeof padsplitReviewSuppressionRequests.$inferSelect;

export const updatePadsplitReviewSuppressionRequestSchema = z.object({
  status: z.enum(PADSPLIT_SUPPRESSION_STATUSES),
  responseNotes: z.string().max(2000).optional().nullable(),
  hideReview: z.boolean().optional(),
});
export type UpdatePadsplitReviewSuppressionRequest = z.infer<typeof updatePadsplitReviewSuppressionRequestSchema>;

// ── PadSplit Pending Move-in Reviews (Review Request Automation) ─────────────
// Rows scraped by the warm-browser fleet from the host dashboard's "Pending
// Move-in Reviews" tab (/host/member-feedback/moveins/reviews): move-ins the
// host can still ask for a review on. The fleet converts the tab's relative
// "Time left" countdown into an absolute request_deadline_at AT SCRAPE TIME
// (relative strings go stale in the DB); the raw string is kept in time_left
// for display/debugging. requested_at is the app-owned once-only sentinel —
// stamped (COALESCE-guarded) when a request push is enqueued, so re-harvested
// rows can never re-trigger a send.

export const padsplitPendingMoveInReviews = pgTable("padsplit_pending_move_in_reviews", {
  id: serial("id").primaryKey(),
  /**
   * Fleet-facing identity — the host's PadPulse login email, exactly as passed
   * in the harvest request body. This is the ONLY owner key the scraper writes;
   * it never resolves PadPulse-internal ids. Same contract as padsplit_locks.
   */
  hostEmail: text("host_email"),
  /**
   * App-resolved owner. NULL on a freshly scraped row — resolve-pending-review-links
   * fills it from host_email. The eligibility poller ignores unresolved rows.
   */
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  /** The fleet's stable id for the tab row (falls back to a member+move-in composite upstream). */
  padsplitPendingReviewId: text("padsplit_pending_review_id").notNull(),
  padsplitPropertyId: text("padsplit_property_id"),
  padsplitMemberId: text("padsplit_member_id"),
  memberName: text("member_name"),
  roomLabel: text("room_label"),
  /** Raw scraped countdown text, e.g. "5 days left". Display only — never parse this app-side. */
  timeLeft: text("time_left"),
  /** Absolute deadline computed by the scraper at scrape time. */
  requestDeadlineAt: timestamp("request_deadline_at"),
  /** App-owned sentinel: when a review-request push was enqueued for this row. */
  requestedAt: timestamp("requested_at"),
  rawData: jsonb("raw_data"),
  syncedAt: timestamp("synced_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // Fleet upsert key. Must be the columns the SCRAPER owns — user_id is NULL on
  // a fresh scrape, and Postgres does not dedupe NULLs, so keying on it would
  // let every re-harvest insert a duplicate. Mirrors padsplit_locks' host_email
  // natural key.
  uniquePendingReviewId: uniqueIndex("padsplit_pending_move_in_reviews_host_pending_idx")
    .on(t.hostEmail, t.padsplitPendingReviewId),
  // Eligibility-poller scan: unrequested rows per resolved org.
  unrequestedIdx: index("padsplit_pending_move_in_reviews_unrequested_idx")
    .on(t.userId)
    .where(sql`requested_at IS NULL`),
  // Link resolver scan: rows the fleet wrote that still need user/property/member.
  unresolvedIdx: index("padsplit_pending_move_in_reviews_unresolved_idx")
    .on(t.hostEmail)
    .where(sql`user_id IS NULL`),
}));

export const insertPadsplitPendingMoveInReviewSchema = createInsertSchema(padsplitPendingMoveInReviews).omit({ id: true, createdAt: true });
export type InsertPadsplitPendingMoveInReview = z.infer<typeof insertPadsplitPendingMoveInReviewSchema>;
export type PadsplitPendingMoveInReview = typeof padsplitPendingMoveInReviews.$inferSelect;

// ── PadSplit Review Automation Settings ──────────────────────────────────────
// One row per org. OFF by default (collections-rollout pattern): nothing sends
// until the org flips enabled. delay_days (1-7) counts from the member's
// move-in date; send_time snaps delivery to one of the fixed wall-clock slots
// in the org timezone.

export const REVIEW_REQUEST_SEND_TIMES = ["08:00", "17:00", "19:00"] as const;
export type ReviewRequestSendTime = (typeof REVIEW_REQUEST_SEND_TIMES)[number];

// App-level default (no DB DEFAULT — settings rows are always app-created,
// and the writer supplies this when the org hasn't customized a template).
export const DEFAULT_REVIEW_REQUEST_TEMPLATE =
  "Hi! We hope your move-in went smoothly. If you have a moment, we'd really " +
  "appreciate a quick review of your experience so far — it helps us a lot. Thank you!";

export const padsplitReviewAutomationSettings = pgTable("padsplit_review_automation_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").default(false).notNull(),
  delayDays: integer("delay_days").default(3).notNull(),
  sendTime: text("send_time").$type<ReviewRequestSendTime>().default("08:00").notNull(),
  timezone: text("timezone").default("America/New_York").notNull(),
  messageTemplate: text("message_template").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  uniqueUser: uniqueIndex("padsplit_review_automation_settings_user_idx").on(t.userId),
}));

export const insertPadsplitReviewAutomationSettingsSchema = createInsertSchema(padsplitReviewAutomationSettings)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    delayDays: z.number().int().min(1).max(7),
    sendTime: z.enum(REVIEW_REQUEST_SEND_TIMES),
  });
export type InsertPadsplitReviewAutomationSettings = z.infer<typeof insertPadsplitReviewAutomationSettingsSchema>;
export type PadsplitReviewAutomationSettings = typeof padsplitReviewAutomationSettings.$inferSelect;

// ── PadSplit Review Request Pushes ───────────────────────────────────────────
// Push queue consumed by the warm-browser fleet (POST /api/browser-users/
// {host_user_id}/request-review with { review_request_push_id }). The fleet
// loads the row by id, finds the pending move-in row on the reviews tab by
// PadSplit identity (never list position), re-verifies the "Request Review"
// button still exists, types message_template into the modal, sends, then
// stamps sent_at on success or writes send_error on terminal failure. A 409
// from the fleet means the button is already gone (host requested manually /
// window expired) and is treated as terminal success app-side. All target
// fields are denormalized so the fleet never joins our tables. Derived status
// (messages_pending semantics): queued (sent_at, send_error, cancelled_at all
// NULL) → sent | failed | cancelled.

export const padsplitReviewRequestPushes = pgTable("padsplit_review_request_pushes", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Warm-browser routing key — same precedence as message sends (acting owner
  // → properties.padsplitSenderUserId → member owner). The fleet egresses
  // through THIS host's PadSplit session.
  hostUserId: integer("host_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  sentByUserId: integer("sent_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  padsplitPropertyId: text("padsplit_property_id"),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  padsplitMemberId: text("padsplit_member_id"),
  memberName: text("member_name"),
  roomLabel: text("room_label"),
  pendingReviewId: integer("pending_review_id"),
  padsplitPendingReviewId: text("padsplit_pending_review_id").notNull(),
  messageTemplate: text("message_template").notNull(),
  sendAt: timestamp("send_at").notNull(),
  requestDeadlineAt: timestamp("request_deadline_at"),
  attempts: integer("attempts").default(0).notNull(),
  sendError: text("send_error"),
  sentAt: timestamp("sent_at"),
  cancelledAt: timestamp("cancelled_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // Short explicit name — the drizzle-generated one exceeds Postgres's
  // 63-char identifier limit.
  pendingReviewFk: foreignKey({
    columns: [t.pendingReviewId],
    foreignColumns: [padsplitPendingMoveInReviews.id],
    name: "prrp_pending_review_fk",
  }).onDelete("set null"),
  // Dupe guard: one live (unsent, uncancelled) request per pending tab row.
  uniqueLivePush: uniqueIndex("padsplit_review_request_pushes_live_unique")
    .on(t.userId, t.padsplitPendingReviewId)
    .where(sql`sent_at IS NULL AND cancelled_at IS NULL`),
  dueIdx: index("padsplit_review_request_pushes_due_idx")
    .on(t.sendAt)
    .where(sql`sent_at IS NULL AND cancelled_at IS NULL AND send_error IS NULL`),
  failedIdx: index("padsplit_review_request_pushes_failed_idx")
    .on(t.userId)
    .where(sql`send_error IS NOT NULL AND sent_at IS NULL`),
}));

export const insertPadsplitReviewRequestPushSchema = createInsertSchema(padsplitReviewRequestPushes).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPadsplitReviewRequestPush = z.infer<typeof insertPadsplitReviewRequestPushSchema>;
export type PadsplitReviewRequestPush = typeof padsplitReviewRequestPushes.$inferSelect;

// ── PadSplit Review Response Pushes ──────────────────────────────────────────
// Push queue for replying to a published review (auto-respond to positive
// public reviews). Same envelope/status contract as request pushes; the fleet
// endpoint is /api/browser-users/{host_user_id}/respond-review with
// { review_response_push_id }. Schema-only for now — enqueue/dispatch wiring
// lands with the auto-respond feature.

export const padsplitReviewResponsePushes = pgTable("padsplit_review_response_pushes", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  hostUserId: integer("host_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  sentByUserId: integer("sent_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  /** "stay" | "move_in" — which review table review_id points into. */
  reviewType: text("review_type").$type<PadsplitReviewType>().notNull(),
  reviewId: integer("review_id").notNull(),
  padsplitReviewId: text("padsplit_review_id").notNull(),
  memberName: text("member_name"),
  responseText: text("response_text").notNull(),
  sendAt: timestamp("send_at"),
  attempts: integer("attempts").default(0).notNull(),
  sendError: text("send_error"),
  sentAt: timestamp("sent_at"),
  cancelledAt: timestamp("cancelled_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // One live response per review.
  uniqueLivePush: uniqueIndex("padsplit_review_response_pushes_live_unique")
    .on(t.userId, t.reviewType, t.padsplitReviewId)
    .where(sql`sent_at IS NULL AND cancelled_at IS NULL`),
  failedIdx: index("padsplit_review_response_pushes_failed_idx")
    .on(t.userId)
    .where(sql`send_error IS NOT NULL AND sent_at IS NULL`),
}));

export const insertPadsplitReviewResponsePushSchema = createInsertSchema(padsplitReviewResponsePushes).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPadsplitReviewResponsePush = z.infer<typeof insertPadsplitReviewResponsePushSchema>;
export type PadsplitReviewResponsePush = typeof padsplitReviewResponsePushes.$inferSelect;

// ─── End PadSplit Reviews ─────────────────────────────────────────────────────

export const FURNISHING_ROOM_TYPES = ["bedroom", "bathroom", "common_space", "kitchen", "appliances", "exterior", "other"] as const;
export type FurnishingRoomType = typeof FURNISHING_ROOM_TYPES[number];

export const FURNISHING_ITEM_CATEGORIES = [
  "furniture",
  "appliance",
  "decor",
  "electronics_smart_home",
  "safety_security",
  "cleaning_maintenance",
  "tools_supplies",
  "storage_organization",
] as const;
export type FurnishingItemCategory = typeof FURNISHING_ITEM_CATEGORIES[number];

export const FURNISHING_ORDER_STATUSES = ["pending", "ordered", "shipped", "delivered", "installed"] as const;
export type FurnishingOrderStatus = typeof FURNISHING_ORDER_STATUSES[number];

export const furnishingCatalogItems = pgTable("furnishing_catalog_items", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdByUserId: integer("created_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  description: text("description"),
  roomType: text("room_type").notNull(),
  category: text("category").default("furniture").notNull(),
  purchaseUrl: text("purchase_url"),
  affiliateLink: text("affiliate_link"),
  defaultPrice: text("default_price"),
  isSeeded: boolean("is_seeded").default(false).notNull(),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, () => [
  // The partial unique index furnishing_catalog_items_seeded_user_name_room_uniq
  // ON (user_id, lower(btrim(name)), room_type) WHERE is_seeded = true is
  // intentionally omitted here (same precedent as bookingRequestTemplates).
  // Declaring it as a Drizzle uniqueIndex() would make the Replit deploy
  // platform (which diffs the dev DB schema before the build step) emit a bare
  // CREATE UNIQUE INDEX with no dedupe, failing on the existing production
  // duplicate rows. The real index is created idempotently in
  // scripts/deploy-migrate.sql AFTER collapsing seeded duplicates (re-pointing
  // furnishing_orders / furnishing_package_items to the keeper first).
  // storage.seedFurnishingCatalog relies on a per-user advisory lock +
  // existence check for idempotency; ON CONFLICT DO NOTHING is the prod backstop.
]);

export const insertFurnishingCatalogItemSchema = createInsertSchema(furnishingCatalogItems).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  createdByUserId: z.number().optional().nullable(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  roomType: z.enum(FURNISHING_ROOM_TYPES),
  category: z.enum(FURNISHING_ITEM_CATEGORIES).default("furniture"),
  purchaseUrl: z.string().optional().nullable(),
  affiliateLink: z.string().optional().nullable(),
  defaultPrice: z.string().optional().nullable(),
  isSeeded: z.boolean().optional().default(false),
  archivedAt: z.date().optional().nullable(),
});
export type InsertFurnishingCatalogItem = z.infer<typeof insertFurnishingCatalogItemSchema>;
export type FurnishingCatalogItem = typeof furnishingCatalogItems.$inferSelect;

export const furnishingRoomLabels = pgTable("furnishing_room_labels", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  roomType: text("room_type").notNull(),
  label: text("label").notNull(),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertFurnishingRoomLabelSchema = createInsertSchema(furnishingRoomLabels).omit({ id: true, createdAt: true }).extend({
  propertyId: z.number(),
  userId: z.number(),
  roomType: z.string().min(1),
  label: z.string().min(1, "Label is required"),
  sortOrder: z.number().optional().default(0),
});
export type InsertFurnishingRoomLabel = z.infer<typeof insertFurnishingRoomLabelSchema>;
export type FurnishingRoomLabel = typeof furnishingRoomLabels.$inferSelect;

export const furnishingOrders = pgTable("furnishing_orders", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdByUserId: integer("created_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  roomLabelId: integer("room_label_id").references(() => furnishingRoomLabels.id, { onDelete: "set null" }),
  catalogItemId: integer("catalog_item_id"),
  itemName: text("item_name").notNull(),
  description: text("description"),
  quantity: integer("quantity").default(1).notNull(),
  price: text("price"),
  purchaseUrl: text("purchase_url"),
  orderUrl: text("order_url"),
  orderDate: text("order_date"),
  status: text("status").default("pending").notNull(),
  shippingAddressId: integer("shipping_address_id"),
  notes: text("notes"),
  installedDate: timestamp("installed_date"),
  installationExpense: text("installation_expense"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  foreignKey({
    name: "furnishing_orders_catalog_item_fk",
    columns: [table.catalogItemId],
    foreignColumns: [furnishingCatalogItems.id],
  }).onDelete("set null"),
]);

export const insertFurnishingOrderSchema = createInsertSchema(furnishingOrders).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  userId: z.number(),
  createdByUserId: z.number().optional().nullable(),
  propertyId: z.number(),
  roomLabelId: z.number().optional().nullable(),
  catalogItemId: z.number().optional().nullable(),
  itemName: z.string().min(1, "Item name is required"),
  description: z.string().optional().nullable(),
  quantity: z.number().min(1).default(1),
  price: z.string().optional().nullable(),
  purchaseUrl: z.union([z.string().url("Must be a valid URL"), z.literal(""), z.null()]).optional(),
  orderUrl: z.union([z.string().url("Must be a valid URL"), z.literal(""), z.null()]).optional(),
  orderDate: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "orderDate must be YYYY-MM-DD"), z.literal(""), z.null()]).optional(),
  status: z.enum(FURNISHING_ORDER_STATUSES).default("pending"),
  shippingAddressId: z.number().optional().nullable(),
  notes: z.string().optional().nullable(),
  installedDate: optionalCoercedDate,
  installationExpense: z.string().optional().nullable(),
});
export type InsertFurnishingOrder = z.infer<typeof insertFurnishingOrderSchema>;
export type FurnishingOrder = typeof furnishingOrders.$inferSelect;

export const furnishingPackages = pgTable("furnishing_packages", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdByUserId: integer("created_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  roomType: text("room_type").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdIdx: index("furnishing_packages_user_id_idx").on(table.userId),
}));

export const insertFurnishingPackageSchema = createInsertSchema(furnishingPackages).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  createdByUserId: z.number().optional().nullable(),
  name: z.string().min(1, "Package name is required"),
  roomType: z.enum(FURNISHING_ROOM_TYPES),
});
export type InsertFurnishingPackage = z.infer<typeof insertFurnishingPackageSchema>;
export type FurnishingPackage = typeof furnishingPackages.$inferSelect;

export const furnishingPackageItems = pgTable("furnishing_package_items", {
  id: serial("id").primaryKey(),
  packageId: integer("package_id").notNull().references(() => furnishingPackages.id, { onDelete: "cascade" }),
  catalogItemId: integer("catalog_item_id").notNull(),
  quantity: integer("quantity").default(1).notNull(),
}, (table) => [
  foreignKey({
    name: "furnishing_package_items_catalog_item_fk",
    columns: [table.catalogItemId],
    foreignColumns: [furnishingCatalogItems.id],
  }).onDelete("cascade"),
  index("furnishing_package_items_package_id_idx").on(table.packageId),
  index("furnishing_package_items_catalog_item_id_idx").on(table.catalogItemId),
]);

export const insertFurnishingPackageItemSchema = createInsertSchema(furnishingPackageItems).omit({ id: true }).extend({
  packageId: z.number(),
  catalogItemId: z.number(),
  quantity: z.number().min(1).default(1),
});
export type InsertFurnishingPackageItem = z.infer<typeof insertFurnishingPackageItemSchema>;
export type FurnishingPackageItem = typeof furnishingPackageItems.$inferSelect;

export const furnishingShippingAddresses = pgTable("furnishing_shipping_addresses", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  label: text("label").notNull(),
  addressLine1: text("address_line1").notNull(),
  addressLine2: text("address_line2"),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zip: text("zip").notNull(),
  country: text("country").notNull().default("US"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertFurnishingShippingAddressSchema = createInsertSchema(furnishingShippingAddresses).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  propertyId: z.number(),
  label: z.string().min(1, "Recipient name / label is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional().nullable(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zip: z.string().min(1, "ZIP is required"),
  country: z.string().default("US"),
});
export type InsertFurnishingShippingAddress = z.infer<typeof insertFurnishingShippingAddressSchema>;
export type FurnishingShippingAddress = typeof furnishingShippingAddresses.$inferSelect;

export const furnishingAddressBook = pgTable("furnishing_address_book", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  nickname: text("nickname").notNull(),
  label: text("label").notNull(),
  addressLine1: text("address_line1").notNull(),
  addressLine2: text("address_line2"),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zip: text("zip").notNull(),
  country: text("country").notNull().default("US"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("furnishing_address_book_user_nickname_idx").on(table.userId, table.nickname),
]);

export const insertFurnishingAddressBookSchema = createInsertSchema(furnishingAddressBook).omit({ id: true, createdAt: true }).extend({
  userId: z.number(),
  nickname: z.string().min(1, "Nickname is required"),
  label: z.string().min(1, "Recipient name / label is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional().nullable(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zip: z.string().min(1, "ZIP is required"),
  country: z.string().default("US"),
});
export type InsertFurnishingAddressBookEntry = z.infer<typeof insertFurnishingAddressBookSchema>;
export type FurnishingAddressBookEntry = typeof furnishingAddressBook.$inferSelect;

export const memberFinancialSettings = pgTable("member_financial_settings", {
  ownerId: integer("owner_id").notNull(),
  // 0 = account default; positive ids are authorized property overrides.
  propertyId: integer("property_id").notNull().default(0),
  thresholds: jsonb("thresholds").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  primaryKey({ columns: [table.ownerId, table.propertyId] }),
  foreignKey({ name: "member_financial_settings_owner_fk", columns: [table.ownerId], foreignColumns: [appUsers.id] }).onDelete("cascade"),
]);
export const collectionSettings = pgTable("collection_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  // Task #4830 — account-level master switch. Defaults FALSE: before this the
  // daily cron looped every approved app user and messaged anyone with a
  // negative balance, even accounts that had never opened the Collections page
  // (DEFAULT_SETTINGS fallback). Collections is now strictly opt-in at two
  // levels — this switch AND a per-property enrollment row.
  enabled: boolean("enabled").notNull().default(false),
  negativeBalanceThreshold: numeric("negative_balance_threshold").notNull().default("-299"),
  paymentPercentage: numeric("payment_percentage").notNull().default("10"),
  paymentFrequency: text("payment_frequency").notNull().default("weekly"),
  paymentDeadlineDays: integer("payment_deadline_days").notNull().default(7),
  offerLimit: integer("offer_limit").notNull().default(3),
  // Minimum days between notices to the SAME member. Nothing enforced this
  // before, so a member could be messaged on consecutive days until they hit
  // the (all-time) offer limit.
  cooldownDays: integer("cooldown_days").notNull().default(7),
  // Window for counting prior offers against `offerLimit`. NULL = count
  // all-time, which is exactly the pre-#4830 behaviour and stays the default.
  offerResetDays: integer("offer_reset_days"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// Raw last-good evidence: never a projection into operational financial fields.
export const memberFinancialObservationCache = pgTable("member_financial_observation_cache", {
  ownerId: integer("owner_id").notNull(),
  memberId: integer("member_id").notNull(),
  personId: text("person_id").notNull(),
  occupantId: text("occupant_id").notNull(),
  field: text("field").notNull(),
  observation: jsonb("observation").notNull(),
  observedAt: timestamp("observed_at", { withTimezone: true }).notNull(),
}, table => [
  primaryKey({ name: "member_fin_obs_cache_pk", columns: [table.ownerId, table.memberId, table.personId, table.occupantId, table.field] }),
  foreignKey({ name: "member_fin_obs_cache_owner_fk", columns: [table.ownerId], foreignColumns: [appUsers.id] }).onDelete("cascade"),
  foreignKey({ name: "member_fin_obs_cache_member_fk", columns: [table.memberId], foreignColumns: [members.id] }).onDelete("cascade"),
]);

export const insertCollectionSettingsSchema = createInsertSchema(collectionSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  enabled: z.boolean().default(false),
  negativeBalanceThreshold: z.string().refine((v) => {
    const n = parseFloat(v);
    return !isNaN(n) && n >= -299 && n <= 100;
  }, {
    message: "Threshold must be between -$299 and $100",
  }).default("-299"),
  paymentPercentage: z.string().default("10"),
  paymentFrequency: z.string().default("weekly"),
  paymentDeadlineDays: z.number().min(1).default(7),
  offerLimit: z.number().min(1).default(3),
  cooldownDays: z.number().min(0).default(7),
  offerResetDays: z.number().min(1).optional().nullable(),
});
export type InsertCollectionSettings = z.infer<typeof insertCollectionSettingsSchema>;
export type CollectionSettings = typeof collectionSettings.$inferSelect;

// ─── Per-property collections enrollment + rule overrides (Task #4830) ───────
// Collections runs per PROPERTY, not per account. A property is processed only
// when it has a row here with `enabled = true` AND the account master switch is
// on. Every rule column is nullable: NULL means "inherit the account default",
// which is what `resolveCollectionSettings()` in shared/collections-settings.ts
// implements. A dedicated table (rather than a nullable-propertyId row on
// collection_settings) because every column there is NOT NULL with a default
// and user_id is UNIQUE — "NULL = inherit" is inexpressible without dismantling
// the account row's invariants. Precedents: supply_property_settings,
// trash_enrollments.
export const collectionPropertySettings = pgTable("collection_property_settings", {
  id: serial("id").primaryKey(),
  ownerUserId: integer("owner_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  // Who turned collections on for this property, and when — this switch starts
  // automated debt messaging, so it is worth an explicit audit trail.
  enabledAt: timestamp("enabled_at"),
  enabledByUserId: integer("enabled_by_user_id"),
  // Rule overrides — NULL = inherit from collection_settings.
  negativeBalanceThreshold: numeric("negative_balance_threshold"),
  paymentPercentage: numeric("payment_percentage"),
  paymentFrequency: text("payment_frequency"),
  paymentDeadlineDays: integer("payment_deadline_days"),
  offerLimit: integer("offer_limit"),
  offerResetDays: integer("offer_reset_days"),
  cooldownDays: integer("cooldown_days"),
  // Set after each completed run; the cron uses it to skip a property already
  // processed today and the UI surfaces it as "Last run".
  lastRunAt: timestamp("last_run_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // Makes double-enrollment impossible at the DB level rather than by
  // application convention.
  ownerPropertyUnique: uniqueIndex("collection_property_settings_owner_property_unique").on(table.ownerUserId, table.propertyId),
  ownerIdx: index("collection_property_settings_owner_idx").on(table.ownerUserId),
  propertyIdx: index("collection_property_settings_property_idx").on(table.propertyId),
  // Explicit short name: the auto-generated
  // `collection_property_settings_enabled_by_user_id_app_users_id_fk` is
  // exactly 63 chars, right at the Postgres identifier limit.
  enabledByFk: foreignKey({
    name: "coll_prop_settings_enabled_by_fk",
    columns: [table.enabledByUserId],
    foreignColumns: [appUsers.id],
  }).onDelete("set null"),
}));

export const insertCollectionPropertySettingsSchema = createInsertSchema(collectionPropertySettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  ownerUserId: z.number(),
  propertyId: z.number(),
  enabled: z.boolean().default(false),
  enabledAt: z.coerce.date().optional().nullable(),
  enabledByUserId: z.number().optional().nullable(),
  negativeBalanceThreshold: z.string().optional().nullable(),
  paymentPercentage: z.string().optional().nullable(),
  paymentFrequency: z.string().optional().nullable(),
  paymentDeadlineDays: z.number().min(1).optional().nullable(),
  offerLimit: z.number().min(1).optional().nullable(),
  offerResetDays: z.number().min(1).optional().nullable(),
  cooldownDays: z.number().min(0).optional().nullable(),
  lastRunAt: z.coerce.date().optional().nullable(),
});
export type InsertCollectionPropertySettings = z.infer<typeof insertCollectionPropertySettingsSchema>;
export type CollectionPropertySettings = typeof collectionPropertySettings.$inferSelect;

// ─── Collections activity log (Task #4830) ──────────────────────────────────
// One row per member EVALUATED by a run — sends and skips alike. The old
// Activity Log was a raw dump of collection_notices, so a member who was
// skipped (below threshold, offer limit reached, no host…) left no trace and
// operators had no way to answer "why wasn't this person contacted?".
//
// Deliberately NOT reusing `audit_logs`: AUDIT_ACTIONS is a closed union of
// team-member actions, whereas these are system decisions carrying skip
// reasons, a resolved-settings snapshot, and a link to the queued message.
export const COLLECTION_ACTIVITY_OUTCOMES = ["sent", "skipped", "failed"] as const;
export type CollectionActivityOutcome = (typeof COLLECTION_ACTIVITY_OUTCOMES)[number];

export const COLLECTION_SKIP_REASONS = [
  // No balance to act on: `total_due` is NULL/unparseable (we hold no figure
  // for this member) or exactly zero (settled). Previously these members were
  // dropped with a bare `continue`, so the Activity Log could not answer "why
  // wasn't this person contacted?" for precisely the settled/unknown cases.
  // Logging them changes auditability only — never eligibility.
  "no_balance",
  "below_threshold",
  "offer_limit_reached",
  "cooldown_active",
  "no_administrator",
  "no_host",
  "duplicate_claim",
  "enqueue_failed",
  "publish_failed",
] as const;
export type CollectionSkipReason = (typeof COLLECTION_SKIP_REASONS)[number];

export const collectionActivity = pgTable("collection_activity", {
  id: serial("id").primaryKey(),
  ownerUserId: integer("owner_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Groups every decision made by a single engine run.
  runId: text("run_id").notNull(),
  runType: text("run_type").notNull(), // "cron" | "manual" | "legacy"
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  // Snapshots so the log stays readable after a property or member is deleted.
  propertyName: text("property_name"),
  memberId: integer("member_id").references(() => members.id, { onDelete: "set null" }),
  memberName: text("member_name").notNull(),
  amountDue: numeric("amount_due"),
  outcome: text("outcome").notNull(),
  skipReason: text("skip_reason"),
  messagesPendingId: integer("messages_pending_id"),
  // The EFFECTIVE settings used for this decision, so a later "why did it use
  // that threshold?" is answerable even after the rules change.
  resolvedSettings: jsonb("resolved_settings"),
  messageContent: text("message_content"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  ownerCreatedIdx: index("collection_activity_owner_created_idx").on(table.ownerUserId, desc(table.createdAt)),
  ownerPropertyIdx: index("collection_activity_owner_property_idx").on(table.ownerUserId, table.propertyId),
  ownerMemberIdx: index("collection_activity_owner_member_idx").on(table.ownerUserId, table.memberId),
  runIdx: index("collection_activity_run_idx").on(table.runId),
  // Explicit short name — the auto-generated
  // `collection_activity_messages_pending_id_messages_pending_id_fk` is 62
  // chars, uncomfortably close to the 63-char limit.
  pendingFk: foreignKey({
    name: "coll_activity_pending_fk",
    columns: [table.messagesPendingId],
    foreignColumns: [messagesPending.id],
  }).onDelete("set null"),
}));

export const insertCollectionActivitySchema = createInsertSchema(collectionActivity).omit({
  id: true,
  createdAt: true,
}).extend({
  ownerUserId: z.number(),
  runId: z.string(),
  runType: z.string(),
  propertyId: z.number().optional().nullable(),
  propertyName: z.string().optional().nullable(),
  memberId: z.number().optional().nullable(),
  memberName: z.string(),
  amountDue: z.string().optional().nullable(),
  outcome: z.enum(COLLECTION_ACTIVITY_OUTCOMES),
  skipReason: z.enum(COLLECTION_SKIP_REASONS).optional().nullable(),
  messagesPendingId: z.number().optional().nullable(),
  resolvedSettings: z.unknown().optional().nullable(),
  messageContent: z.string().optional().nullable(),
});
export type InsertCollectionActivity = z.infer<typeof insertCollectionActivitySchema>;
export type CollectionActivity = typeof collectionActivity.$inferSelect;

export const collectionNotices = pgTable("collection_notices", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  memberId: integer("member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  memberName: text("member_name").notNull(),
  amountDue: numeric("amount_due").notNull(),
  messageContent: text("message_content").notNull(),
  // Task #1029 / #1123 / #1133: the legacy `conversation_id` column (from
  // when collection notices were tied to a `conversations` row) has been
  // dropped. The conversations table was retired in #1123; the column was
  // kept briefly as a plain nullable integer to preserve historical values
  // and is now removed in #1133 (no live readers / no audit consumers).
  sentMessageId: integer("sent_message_id"),
  // Task #4830 — which property the member was assigned to when the notice was
  // written. Nullable because historical rows predate per-property collections
  // (the backfill fills what it can; the rest render as "—").
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  // Ties the notice to the engine run that produced it.
  runId: text("run_id"),
  // The messages_pending row this notice enqueued. Supersedes `sentMessageId`,
  // which the old cron always wrote as null. Lets the Activity Log deep-link to
  // the delivered chat via messages_pending.reconciled_chat_id.
  messagesPendingId: integer("messages_pending_id"),
  // Property-local calendar date of the send, set on new rows only. This plus
  // the partial unique index below is the double-send rail: the runner INSERTS
  // this row (status "claimed") BEFORE enqueueing, so an overlapping cron and
  // manual run collide on the index instead of both messaging the member.
  sentOnDate: date("sent_on_date"),
  status: text("status").notNull().default("sent"),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  memberIdIdx: index("collection_notices_member_id_idx").on(table.memberId),
  userIdIdx: index("collection_notices_user_id_idx").on(table.userId),
  propertyIdx: index("collection_notices_property_idx").on(table.userId, table.propertyId),
  sentAtIdx: index("collection_notices_user_sent_at_idx").on(table.userId, desc(table.sentAt)),
  // PARTIAL unique index — at most one claim per (account, member, day).
  // Partial is load-bearing: historical rows have a NULL sent_on_date and may
  // already contain same-day duplicates from past manual clicks, so a total
  // unique index could not be created on existing data.
  claimUnique: uniqueIndex("collection_notices_daily_claim_unique")
    .on(table.userId, table.memberId, table.sentOnDate)
    .where(sql`sent_on_date IS NOT NULL`),
}));

export const insertCollectionNoticeSchema = createInsertSchema(collectionNotices).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  memberId: z.number(),
  memberName: z.string(),
  amountDue: z.string(),
  messageContent: z.string(),
  sentMessageId: z.number().optional().nullable(),
  propertyId: z.number().optional().nullable(),
  runId: z.string().optional().nullable(),
  messagesPendingId: z.number().optional().nullable(),
  sentOnDate: z.string().optional().nullable(),
  status: z.string().default("sent"),
  sentAt: z.coerce.date().optional(),
});
export type InsertCollectionNotice = z.infer<typeof insertCollectionNoticeSchema>;
export type CollectionNotice = typeof collectionNotices.$inferSelect;

export const RENOVATION_ITEM_TYPES = [
  "building",
  "bedrooms",
  "kitchen_remodel",
  "roof_replacement",
  "hvac",
  "plumbing",
  "electrical",
  "flooring",
  "painting",
  "windows_doors",
  "bathroom_remodel",
  "landscaping",
  "driveway_parking",
  "foundation",
  "insulation",
  "siding_exterior",
  "appliances",
  "fire_safety_code",
  "pest_treatment",
  "general_demolition",
] as const;

export type RenovationItemType = typeof RENOVATION_ITEM_TYPES[number];

export const RENOVATION_ITEM_LABELS: Record<RenovationItemType, string> = {
  building: "Building",
  bedrooms: "Bedrooms",
  kitchen_remodel: "Kitchen Remodel",
  roof_replacement: "Roof Replacement",
  hvac: "HVAC",
  plumbing: "Plumbing",
  electrical: "Electrical",
  flooring: "Flooring",
  painting: "Painting",
  windows_doors: "Windows / Doors",
  bathroom_remodel: "Bathroom Remodel",
  landscaping: "Landscaping",
  driveway_parking: "Driveway / Parking",
  foundation: "Foundation",
  insulation: "Insulation",
  siding_exterior: "Siding / Exterior",
  appliances: "Appliances",
  fire_safety_code: "Fire Safety / Code Compliance",
  pest_treatment: "Pest Treatment",
  general_demolition: "General Demolition",
};

export const renovationItems = pgTable("renovation_items", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  itemType: text("item_type").notNull(),
  itemName: text("item_name").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  estimatedDays: integer("estimated_days").notNull().default(14),
  budget: text("budget"),
  // Task #869: declared as `text` (not `numeric`) so `npm run db:push --force`
  // can run cleanly on legacy dev DBs. Production has this as numeric;
  // divergence allowlisted in scripts/check-schema-deploy-parity-allowlist.json.
  bufferPercent: text("buffer_percent"),
  completed: boolean("completed").default(false).notNull(),
  completedDate: text("completed_date"),
  documentId: integer("document_id"),
  bidVendorId: integer("bid_vendor_id"),
  bidAmount: text("bid_amount"),
  bidDocumentId: integer("bid_document_id"),
  voidedAt: timestamp("voided_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertRenovationItemSchema = createInsertSchema(renovationItems).omit({ id: true, createdAt: true });
export type InsertRenovationItem = z.infer<typeof insertRenovationItemSchema>;
export type RenovationItem = typeof renovationItems.$inferSelect;

export const renovationDefaults = pgTable("renovation_defaults", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  defaultDays: jsonb("default_days").$type<Record<string, number>>().default({}),
  defaultPermitDays: integer("default_permit_days").default(45).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRenovationDefaultsSchema = createInsertSchema(renovationDefaults).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertRenovationDefaults = z.infer<typeof insertRenovationDefaultsSchema>;
export type RenovationDefaults = typeof renovationDefaults.$inferSelect;

export const renovationSettings = pgTable("renovation_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  unlockRoles: jsonb("unlock_roles").$type<string[]>().default(["owner", "admin"]),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRenovationSettingsSchema = createInsertSchema(renovationSettings).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertRenovationSettings = z.infer<typeof insertRenovationSettingsSchema>;
export type RenovationSettings = typeof renovationSettings.$inferSelect;

export const directInquiryLeads = pgTable("direct_inquiry_leads", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  referralSource: text("referral_source"),
  landingPageSlug: text("landing_page_slug"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertDirectInquiryLeadSchema = createInsertSchema(directInquiryLeads).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number().optional().nullable(),
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(1, "Phone is required"),
  propertyId: z.number().optional().nullable(),
  referralSource: z.string().optional().nullable(),
});
export type InsertDirectInquiryLead = z.infer<typeof insertDirectInquiryLeadSchema>;
export type DirectInquiryLead = typeof directInquiryLeads.$inferSelect;

export const thirdPartyListings = pgTable("third_party_listings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  platform: text("platform").notNull(),
  url: text("url").notNull(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  status: text("status").default("active").notNull(),
  notes: text("notes"),
  datePublished: timestamp("date_published"),
  reminderDate: timestamp("reminder_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertThirdPartyListingSchema = createInsertSchema(thirdPartyListings).omit({
  id: true,
  createdAt: true,
}).extend({
  userId: z.number(),
  platform: z.string().min(1, "Platform is required"),
  url: z.string().url("Valid URL is required"),
  propertyId: z.number().optional().nullable(),
  status: z.enum(["active", "inactive"]).default("active"),
  notes: z.string().optional().nullable(),
  datePublished: optionalCoercedDate,
  reminderDate: optionalCoercedDate,
});
export type InsertThirdPartyListing = z.infer<typeof insertThirdPartyListingSchema>;
export type ThirdPartyListing = typeof thirdPartyListings.$inferSelect;

export const marketingSettings = pgTable("marketing_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  headline: text("headline").default("Rooms for Rent").notNull(),
  description: text("description"),
  tagline: text("tagline"),
  ctaButtonText: text("cta_button_text").default("Start Free Application"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  accentColor: text("accent_color").default("#0052CC"),
  footerText: text("footer_text"),
  brandName: text("brand_name"),
  brandLogoUrl: text("brand_logo_url"),
  enabledPropertyIds: jsonb("enabled_property_ids"),
  enabledPlatforms: jsonb("enabled_platforms"),
  customListingSites: jsonb("custom_listing_sites"),
  defaultReminderDays: integer("default_reminder_days"),
  strategyNotes: text("strategy_notes"),
  landingPageSlug: text("landing_page_slug"),
  dallasReferralLink: text("dallas_referral_link"),
  tampaReferralLink: text("tampa_referral_link"),
  referralLinks: jsonb("referral_links").default([]),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const referralLinkSchema = z.object({
  marketName: z.string().min(1, "Market name is required"),
  url: z.string().url("Valid URL is required"),
  isDefault: z.boolean().default(false),
});
export type ReferralLink = z.infer<typeof referralLinkSchema>;

export const landingPages = pgTable("landing_pages", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  slug: text("slug").notNull().unique(),
  propertyGroupId: integer("property_group_id").notNull().references(() => propertyGroups.id, { onDelete: "cascade" }),
  headline: text("headline"),
  description: text("description"),
  tagline: text("tagline"),
  ctaButtonText: text("cta_button_text"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  accentColor: text("accent_color"),
  brandName: text("brand_name"),
  brandLogoUrl: text("brand_logo_url"),
  footerText: text("footer_text"),
  enabledPropertyIds: jsonb("enabled_property_ids"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertLandingPageSchema = createInsertSchema(landingPages).omit({ id: true, createdAt: true }).extend({
  slug: z.string()
    .min(1, "Slug is required")
    .max(80, "Slug is too long")
    .regex(/^[a-z0-9-]+$/, "Slug may only contain lowercase letters, numbers, and hyphens"),
  propertyGroupId: z.number().int().positive("A property group is required"),
});
export type InsertLandingPage = z.infer<typeof insertLandingPageSchema>;
export type LandingPage = typeof landingPages.$inferSelect;

export const customListingSiteSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  color: z.string().default("bg-gray-500"),
});
export type CustomListingSite = z.infer<typeof customListingSiteSchema>;

export const buyDeals = pgTable("buy_deals", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  address: text("address").notNull(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zipCode: text("zip_code"),
  market: text("market").notNull(),
  imageUrl: text("image_url"),
  photos: text("photos").array(),
  purchasePrice: numeric("purchase_price").notNull(),
  cashOnCash: numeric("cash_on_cash").notNull(),
  monthlyCashFlow: numeric("monthly_cash_flow").notNull(),
  capRate: numeric("cap_rate"),
  currentBedrooms: integer("current_bedrooms").notNull(),
  currentBathrooms: integer("current_bathrooms").notNull(),
  futureBedrooms: integer("future_bedrooms"),
  futureBathrooms: integer("future_bathrooms"),
  sqft: integer("sqft"),
  garageSpaces: integer("garage_spaces"),
  lotSize: numeric("lot_size"),
  yearBuilt: integer("year_built"),
  propertyType: text("property_type"),
  arv: numeric("arv"),
  rehabCost: numeric("rehab_cost"),
  monthlyRent: numeric("monthly_rent"),
  monthlyExpenses: numeric("monthly_expenses"),
  noi: numeric("noi"),
  downPayment: numeric("down_payment"),
  loanAmount: numeric("loan_amount"),
  interestRate: numeric("interest_rate"),
  loanTermYears: integer("loan_term_years"),
  monthlyMortgage: numeric("monthly_mortgage"),
  insuranceCost: numeric("insurance_cost"),
  taxesAnnual: numeric("taxes_annual"),
  hoaMonthly: numeric("hoa_monthly"),
  vacancyRate: numeric("vacancy_rate"),
  managementFee: numeric("management_fee"),
  provider: text("provider"),
  status: text("status").default("preview").notNull(),
  notes: text("notes"),
  listingUrl: text("listing_url"),
  mlsNumber: text("mls_number"),
  isSample: boolean("is_sample").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertBuyDealSchema = createInsertSchema(buyDeals).omit({
  id: true,
  createdAt: true,
});
export type InsertBuyDeal = z.infer<typeof insertBuyDealSchema>;
export type BuyDeal = typeof buyDeals.$inferSelect;

export const marketResearch = pgTable("market_research", {
  id: serial("id").primaryKey(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zipCode: text("zip_code"),
  level: text("level").notNull().default("city"),
  marketType: text("market_type"),
  occupancyRate: numeric("occupancy_rate"),
  avgPriceShared: numeric("avg_price_shared"),
  avgPricePrivate: numeric("avg_price_private"),
  totalSearchVolume: integer("total_search_volume"),
  availableUnits: integer("available_units"),
  totalUnits: integer("total_units"),
  upcomingUnits: integer("upcoming_units"),
  searchToRoomRatio: numeric("search_to_room_ratio"),
  daysToFirstBooking: integer("days_to_first_booking"),
  daysTo80Occupancy: integer("days_to_80_occupancy"),
  dataMonth: text("data_month"),
  sourceUrl: text("source_url"),
  isSample: boolean("is_sample").default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertMarketResearchSchema = createInsertSchema(marketResearch).omit({
  id: true,
  createdAt: true,
});
export type InsertMarketResearch = z.infer<typeof insertMarketResearchSchema>;
export type MarketResearch = typeof marketResearch.$inferSelect;

export const marketResearchHistory = pgTable("market_research_history", {
  id: serial("id").primaryKey(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  month: text("month").notNull(),
  searches: integer("searches"),
  activeUnits: integer("active_units"),
  upcomingUnits: integer("upcoming_units"),
  occupancy: integer("occupancy"),
  avgPriceShared: numeric("avg_price_shared"),
  avgPricePrivate: numeric("avg_price_private"),
  daysToFirstBooking: integer("days_to_first_booking"),
  daysTo80Occupancy: integer("days_to_80_occupancy"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  cityStateMonthUniq: uniqueIndex("market_research_history_city_state_month_idx").on(table.city, table.state, table.month),
}));

export type MarketResearchHistory = typeof marketResearchHistory.$inferSelect;

// Task #1139 — One pre-computed AI market analysis per city/state. The
// research-tier user-facing path always reads from this table so a click
// returns instantly without burning Gemini quota. Refreshed monthly by
// the `city-ai-analysis-refresh` cron and on first deploy by the
// `city-ai-analyses` deploy backfill. Growth/Pro custom-prompt runs do
// NOT write here — they're per-session only.
export const cityAiAnalyses = pgTable("city_ai_analyses", {
  id: serial("id").primaryKey(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  analysis: text("analysis").notNull(),
  model: text("model").notNull(),
  // Task #1148 — Persist the Gemini token spend of the most recent
  // refresh of this city so the admin dashboard can surface per-row
  // cost without joining `gemini_call_log`. The same call is also
  // recorded in `gemini_call_log` for the global Gemini Usage tab;
  // these columns just denormalize the *latest* refresh's totals so
  // operators can spot a runaway prompt at a glance. Nullable because
  // rows backfilled before Task #1148 didn't capture token counts.
  inputTokens: integer("input_tokens"),
  outputTokens: integer("output_tokens"),
  costUsd: doublePrecision("cost_usd"),
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
}, (table) => ({
  cityStateUnique: uniqueIndex("city_ai_analyses_city_state_idx").on(table.city, table.state),
}));

export const insertCityAiAnalysisSchema = createInsertSchema(cityAiAnalyses).omit({
  id: true,
  generatedAt: true,
});
export type InsertCityAiAnalysis = z.infer<typeof insertCityAiAnalysisSchema>;
export type CityAiAnalysis = typeof cityAiAnalyses.$inferSelect;

export const insertMarketingSettingsSchema = createInsertSchema(marketingSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  headline: z.string().default("Rooms for Rent"),
  description: z.string().optional().nullable(),
  tagline: z.string().optional().nullable(),
  ctaButtonText: z.string().optional().nullable(),
  contactPhone: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  accentColor: z.string().optional().nullable(),
  footerText: z.string().optional().nullable(),
  brandName: z.string().optional().nullable(),
  brandLogoUrl: z.string().optional().nullable(),
  enabledPropertyIds: z.any().optional().nullable(),
  enabledPlatforms: z.array(z.string()).optional().nullable(),
  customListingSites: z.array(customListingSiteSchema).optional().nullable(),
  defaultReminderDays: z.number().int().min(1).max(365).optional().nullable(),
  strategyNotes: z.string().optional().nullable(),
  landingPageSlug: z.string().optional().nullable(),
  dallasReferralLink: z.string().url().optional().nullable(),
  tampaReferralLink: z.string().url().optional().nullable(),
  referralLinks: z.array(referralLinkSchema).optional().nullable(),
});
export type InsertMarketingSettings = z.infer<typeof insertMarketingSettingsSchema>;
export type MarketingSettings = typeof marketingSettings.$inferSelect;

export const lenders = pgTable("lenders", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  company: text("company").notNull(),
  specialty: text("specialty").notNull(),
  description: text("description").notNull(),
  logoUrl: text("logo_url"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  website: text("website"),
  active: boolean("active").default(true).notNull(),
  displayOrder: integer("display_order").default(0).notNull(),
  // Task #3068 — Optional link to a partner account (partner_type = 'lender').
  // When set, host "Connect" clicks on this lender card route the resulting
  // partner_thread to this partner so it lands in their portal inbox.
  partnerId: integer("partner_id").references((): AnyPgColumn => partners.id, { onDelete: "set null" }),
  // Task #3366 — The finance directory now publishes BOTH registered lenders and
  // registered brokers. `kind` discriminates the card type; loanTypes/statesServiced
  // feed the card tags; the source* columns link a published card back to the
  // admin "Registered Lenders/Brokers" record it was published from (plain ints,
  // mirroring partners.sourceLenderId/sourceBrokerId — no FK to avoid a circular
  // forward-reference into the later-declared registered_* tables).
  kind: text("kind").default("lender").notNull(),
  loanTypes: text("loan_types").array(),
  statesServiced: text("states_serviced").array(),
  sourceRegisteredLenderId: integer("source_registered_lender_id"),
  sourceRegisteredBrokerId: integer("source_registered_broker_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertLenderSchema = createInsertSchema(lenders).omit({
  id: true,
  createdAt: true,
}).extend({
  name: z.string().min(1, "Name is required"),
  company: z.string().min(1, "Company is required"),
  specialty: z.string().min(1, "Specialty is required"),
  description: z.string().min(1, "Description is required"),
  active: z.boolean().optional(),
  displayOrder: z.number().int().optional(),
  partnerId: z.number().int().nullable().optional(),
  kind: z.enum(["lender", "broker"]).optional(),
  loanTypes: z.array(z.string()).optional().nullable(),
  statesServiced: z.array(z.string()).optional().nullable(),
  sourceRegisteredLenderId: z.number().int().nullable().optional(),
  sourceRegisteredBrokerId: z.number().int().nullable().optional(),
});
export type InsertLender = z.infer<typeof insertLenderSchema>;
export type Lender = typeof lenders.$inferSelect;

export const lenderClicks = pgTable("lender_clicks", {
  id: serial("id").primaryKey(),
  appUserId: integer("app_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  lenderId: integer("lender_id").notNull().references(() => lenders.id, { onDelete: "cascade" }),
  userName: text("user_name").notNull(),
  userEmail: text("user_email").notNull(),
  userPhone: text("user_phone"),
  message: text("message"),
  clickedAt: timestamp("clicked_at").defaultNow().notNull(),
});

export const insertLenderClickSchema = createInsertSchema(lenderClicks).omit({
  id: true,
  clickedAt: true,
}).extend({
  appUserId: z.number().int(),
  lenderId: z.number().int(),
  userName: z.string().min(1, "Name is required"),
  userEmail: z.string().email("Valid email required"),
  userPhone: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
});
export type InsertLenderClick = z.infer<typeof insertLenderClickSchema>;
export type LenderClick = typeof lenderClicks.$inferSelect;

export const ACQUISITION_STATUSES = [
  "prospect",
  "offer_submitted",
  "offer_accepted",
  "under_contract",
  "due_diligence",
  "appraisal",
  "clear_to_close",
  "closing",
  "closed",
] as const;

export type AcquisitionStatus = typeof ACQUISITION_STATUSES[number];

export const acquisitions = pgTable("acquisitions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  address: text("address").notNull(),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  status: text("status").default("prospect").notNull(),
  offerPrice: text("offer_price"),
  earnestMoney: text("earnest_money"),
  closingCostsEstimate: text("closing_costs_estimate"),
  offerDate: text("offer_date"),
  offerExpirationDate: text("offer_expiration_date"),
  contractDate: text("contract_date"),
  inspectionDeadline: text("inspection_deadline"),
  appraisalDeadline: text("appraisal_deadline"),
  financingContingencyDeadline: text("financing_contingency_deadline"),
  closingDateScheduled: text("closing_date_scheduled"),
  closingDateActual: text("closing_date_actual"),
  agentName: text("agent_name"),
  agentPhone: text("agent_phone"),
  agentEmail: text("agent_email"),
  lenderName: text("lender_name"),
  lenderPhone: text("lender_phone"),
  lenderEmail: text("lender_email"),
  titleCompanyName: text("title_company_name"),
  titleCompanyPhone: text("title_company_phone"),
  titleCompanyEmail: text("title_company_email"),
  sellerName: text("seller_name"),
  sellerPhone: text("seller_phone"),
  sellerEmail: text("seller_email"),
  purchasingEntity: text("purchasing_entity"),
  expectedInterestRate: text("expected_interest_rate"),
  loanType: text("loan_type"),
  optionPeriodExpiration: text("option_period_expiration"),
  optionFee: text("option_fee"),
  notes: text("notes"),
  statusChangedAt: timestamp("status_changed_at").defaultNow(),
  noRenovationNeeded: boolean("no_renovation_needed").default(false),
  closingStatementReminderAt: timestamp("closing_statement_reminder_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertAcquisitionSchema = createInsertSchema(acquisitions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  address: z.string().min(1, "Address is required"),
  status: z.enum(ACQUISITION_STATUSES).default("prospect"),
  propertyId: z.number().optional().nullable(),
  offerPrice: z.string().optional().nullable(),
  earnestMoney: z.string().optional().nullable(),
  closingCostsEstimate: z.string().optional().nullable(),
  offerDate: z.string().optional().nullable(),
  offerExpirationDate: z.string().optional().nullable(),
  contractDate: z.string().optional().nullable(),
  inspectionDeadline: z.string().optional().nullable(),
  appraisalDeadline: z.string().optional().nullable(),
  financingContingencyDeadline: z.string().optional().nullable(),
  closingDateScheduled: z.string().optional().nullable(),
  closingDateActual: z.string().optional().nullable(),
  agentName: z.string().optional().nullable(),
  agentPhone: z.string().optional().nullable(),
  agentEmail: z.string().optional().nullable(),
  lenderName: z.string().optional().nullable(),
  lenderPhone: z.string().optional().nullable(),
  lenderEmail: z.string().optional().nullable(),
  titleCompanyName: z.string().optional().nullable(),
  titleCompanyPhone: z.string().optional().nullable(),
  titleCompanyEmail: z.string().optional().nullable(),
  sellerName: z.string().optional().nullable(),
  sellerPhone: z.string().optional().nullable(),
  sellerEmail: z.string().optional().nullable(),
  purchasingEntity: z.string().optional().nullable(),
  expectedInterestRate: z.string().optional().nullable(),
  loanType: z.string().optional().nullable(),
  optionPeriodExpiration: z.string().optional().nullable(),
  optionFee: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  noRenovationNeeded: z.boolean().optional().nullable(),
  statusChangedAt: z.coerce.date().optional().nullable(),
});

export type InsertAcquisition = z.infer<typeof insertAcquisitionSchema>;
export type Acquisition = typeof acquisitions.$inferSelect;

export const LOAN_TYPES = [
  "conventional",
  "fha",
  "va",
  "usda",
  "hard_money",
  "private",
  "portfolio",
  "dscr",
  "bridge",
  "seller_financing",
  "cash",
  "other",
] as const;

export type LoanType = typeof LOAN_TYPES[number];

export const LOAN_TYPE_LABELS: Record<LoanType, string> = {
  conventional: "Conventional",
  fha: "FHA",
  va: "VA",
  usda: "USDA",
  hard_money: "Hard Money",
  private: "Private",
  portfolio: "Portfolio",
  dscr: "DSCR",
  bridge: "Bridge",
  seller_financing: "Seller Financing",
  cash: "Cash",
  other: "Other",
};

export const DOCUMENT_TYPES = [
  "contract",
  "amendment",
  "title",
  "survey",
  "inspection",
  "appraisal",
  "insurance",
  "closing_disclosure",
  "closing_statement",
  "loan_estimate",
  "proof_of_funds",
  "earnest_money_receipt",
  "option_fee_receipt",
  "hoa_docs",
  "other",
] as const;

export type DocumentType = typeof DOCUMENT_TYPES[number];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  contract: "Contract",
  amendment: "Amendment",
  title: "Title",
  survey: "Survey",
  inspection: "Inspection Report",
  appraisal: "Appraisal",
  insurance: "Insurance",
  closing_disclosure: "Closing Disclosure",
  closing_statement: "Closing Statement (HUD/ALTA)",
  loan_estimate: "Loan Estimate",
  proof_of_funds: "Proof of Funds",
  earnest_money_receipt: "Earnest Money Receipt",
  option_fee_receipt: "Option Fee Receipt",
  hoa_docs: "HOA Docs",
  other: "Other",
};

export const acquisitionDocuments = pgTable("acquisition_documents", {
  id: serial("id").primaryKey(),
  acquisitionId: integer("acquisition_id").notNull().references(() => acquisitions.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  fileUrl: text("file_url").notNull(),
  documentType: text("document_type").notNull(),
  notes: text("notes"),
  uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
});

export const insertAcquisitionDocumentSchema = createInsertSchema(acquisitionDocuments).omit({
  id: true,
  uploadedAt: true,
}).extend({
  acquisitionId: z.number(),
  userId: z.number(),
  fileName: z.string().min(1, "File name is required"),
  fileUrl: z.string().min(1, "File URL is required"),
  documentType: z.enum(DOCUMENT_TYPES),
  notes: z.string().optional().nullable(),
});

export type InsertAcquisitionDocument = z.infer<typeof insertAcquisitionDocumentSchema>;
export type AcquisitionDocument = typeof acquisitionDocuments.$inferSelect;

export const PANEL_SIZES = ["default", "wide", "tall", "half", "half-tall"] as const;
export type PanelSize = typeof PANEL_SIZES[number];

export const dashboardPreferences = pgTable("dashboard_preferences", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  panelOrder: jsonb("panel_order").$type<string[]>().notNull(),
  panelSizes: jsonb("panel_sizes").$type<Record<string, PanelSize>>(),
  hiddenPanels: jsonb("hidden_panels").$type<string[]>(),
  dismissedJourneyStages: jsonb("dismissed_journey_stages").$type<string[]>(),
  sidebarFavorites: jsonb("sidebar_favorites").$type<string[]>(),
  taskBoardHiddenColumns: jsonb("task_board_hidden_columns").$type<string[]>(),
});

export const insertDashboardPreferencesSchema = createInsertSchema(dashboardPreferences).omit({
  id: true,
}).extend({
  userId: z.number(),
  panelOrder: z.array(z.string()),
  panelSizes: z.record(z.string(), z.enum(PANEL_SIZES)).optional().nullable(),
  hiddenPanels: z.array(z.string()).optional().nullable(),
  dismissedJourneyStages: z.array(z.string()).optional().nullable(),
  sidebarFavorites: z.array(z.string()).optional().nullable(),
  taskBoardHiddenColumns: z.array(z.string()).optional().nullable(),
});

export type InsertDashboardPreferences = z.infer<typeof insertDashboardPreferencesSchema>;
export type DashboardPreferences = typeof dashboardPreferences.$inferSelect;

export const DASHBOARD_TIER_DEFAULT_TIERS = [
  "Research", "Host", "Growth", "Professional", "Enterprise", "PMPro",
  "Founder25", "Founder50", "Founder100", "Interest",
] as const;
export type DashboardTierDefaultTier = typeof DASHBOARD_TIER_DEFAULT_TIERS[number];

export const dashboardTierDefaults = pgTable("dashboard_tier_defaults", {
  id: serial("id").primaryKey(),
  tier: text("tier").notNull().unique(),
  panelOrder: jsonb("panel_order").$type<string[]>().notNull(),
  panelSizes: jsonb("panel_sizes").$type<Record<string, PanelSize>>(),
  hiddenPanels: jsonb("hidden_panels").$type<string[]>(),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type DashboardTierDefault = typeof dashboardTierDefaults.$inferSelect;

export const explainerVideos = pgTable("explainer_videos", {
  id: serial("id").primaryKey(),
  pageKey: text("page_key").notNull().unique(),
  title: text("title").notNull(),
  embedUrl: text("embed_url"),
  youtubeUrl: text("youtube_url"),
  status: text("status").notNull().default("draft"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertExplainerVideoSchema = createInsertSchema(explainerVideos).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  pageKey: z.string().min(1, "Page key is required"),
  title: z.string().min(1, "Title is required"),
  embedUrl: z.string().url("Must be a valid URL").or(z.literal("")).nullish().transform((v) => v || null),
  youtubeUrl: z.string().url("Must be a valid URL").or(z.literal("")).nullish().transform((v) => v || null),
  status: z.enum(["draft", "pending", "final"]).default("draft"),
});

export type InsertExplainerVideo = z.infer<typeof insertExplainerVideoSchema>;
export type ExplainerVideo = typeof explainerVideos.$inferSelect;

export const explainerVideoHistory = pgTable("explainer_video_history", {
  id: serial("id").primaryKey(),
  videoId: integer("video_id").notNull().references(() => explainerVideos.id, { onDelete: "cascade" }),
  pageKey: text("page_key").notNull(),
  title: text("title").notNull(),
  embedUrl: text("embed_url"),
  youtubeUrl: text("youtube_url"),
  status: text("status").notNull(),
  changedBy: text("changed_by"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const insertExplainerVideoHistorySchema = createInsertSchema(explainerVideoHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertExplainerVideoHistory = z.infer<typeof insertExplainerVideoHistorySchema>;
export type ExplainerVideoHistory = typeof explainerVideoHistory.$inferSelect;

export const testimonials = pgTable("testimonials", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  city: text("city").notNull(),
  quote: text("quote").notNull(),
  videoUrl: text("video_url"),
  featured: boolean("featured").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertTestimonialSchema = createInsertSchema(testimonials).omit({
  id: true,
  createdAt: true,
}).extend({
  name: z.string().min(1, "Name is required").max(120, "Name is too long"),
  city: z.string().min(1, "City is required").max(120, "City is too long"),
  quote: z.string().min(1, "Quote is required").max(1000, "Quote is too long"),
  videoUrl: z.string().url("Must be a valid URL").max(500).optional().nullable().or(z.literal("")),
  featured: z.boolean().optional(),
});

export type InsertTestimonial = z.infer<typeof insertTestimonialSchema>;
export type Testimonial = typeof testimonials.$inferSelect;

export const buyBoxCriteria = pgTable("buy_box_criteria", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  minCocReturn: numeric("min_coc_return", { precision: 10, scale: 4 }),
  minMonthlyCashFlow: numeric("min_monthly_cash_flow", { precision: 12, scale: 2 }),
  maxPurchasePrice: numeric("max_purchase_price", { precision: 14, scale: 2 }),
  minBedrooms: integer("min_bedrooms"),
  maxBedrooms: integer("max_bedrooms"),
  minSqft: integer("min_sqft"),
  maxSqft: integer("max_sqft"),
  minLotSize: integer("min_lot_size"),
  noHoa: boolean("no_hoa").default(false),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const insertBuyBoxCriteriaSchema = createInsertSchema(buyBoxCriteria).omit({
  id: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  minCocReturn: z.coerce.number().nullable().optional(),
  minMonthlyCashFlow: z.coerce.number().nullable().optional(),
  maxPurchasePrice: z.coerce.number().nullable().optional(),
  minBedrooms: z.coerce.number().int().nullable().optional(),
  maxBedrooms: z.coerce.number().int().nullable().optional(),
  minSqft: z.coerce.number().int().nullable().optional(),
  maxSqft: z.coerce.number().int().nullable().optional(),
  minLotSize: z.coerce.number().int().nullable().optional(),
  noHoa: z.boolean().nullable().optional(),
});

export type InsertBuyBoxCriteria = z.infer<typeof insertBuyBoxCriteriaSchema>;
export type BuyBoxCriteria = typeof buyBoxCriteria.$inferSelect;

export const BUY_BOX_REPORT_STATUSES = ["queued", "running", "complete", "partially_complete", "failed"] as const;
export type BuyBoxReportStatus = typeof BUY_BOX_REPORT_STATUSES[number];

export const buyBoxReports = pgTable("buy_box_reports", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  city: text("city").notNull(),
  state: text("state").notNull(),
  status: text("status").default("running").notNull(),
  statusNote: text("status_note"),
  criteriaSnapshot: jsonb("criteria_snapshot").$type<Record<string, unknown>>(),
  defaultsSnapshot: jsonb("defaults_snapshot").$type<Record<string, unknown>>(),
  weeklyRate: numeric("weekly_rate"),
  totalFound: integer("total_found").default(0).notNull(),
  totalPassed: integer("total_passed").default(0).notNull(),
  totalFailed: integer("total_failed").default(0).notNull(),
  startedAt: timestamp("started_at", { withTimezone: true }).defaultNow().notNull(),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  // Buy Box Agents — set only on daily agent runs. Recorded at run time (not
  // joined later) so a report keeps its original Agent / Version / City /
  // Date name after the agent or version is renamed or deleted.
  // `lookbackDays` marks a "new listings only" scan: such a report never
  // stands in for a full-city scan in the city cache.
  agentId: integer("agent_id"),
  agentNumber: integer("agent_number"),
  versionId: integer("version_id"),
  versionNumber: integer("version_number"),
  versionLabel: text("version_label"),
  agentRunDate: text("agent_run_date"),
  lookbackDays: integer("lookback_days"),
  runName: text("run_name"),
}, (table) => ({
  userIdIdx: index("idx_buy_box_reports_user_id").on(table.userId),
}));

export const insertBuyBoxReportSchema = createInsertSchema(buyBoxReports).omit({
  id: true,
  startedAt: true,
}).extend({
  userId: z.number(),
  city: z.string().min(1),
  state: z.string().min(1),
  status: z.enum(BUY_BOX_REPORT_STATUSES).default("running").optional(),
  statusNote: z.string().nullable().optional(),
  criteriaSnapshot: z.record(z.unknown()).nullable().optional(),
  defaultsSnapshot: z.record(z.unknown()).nullable().optional(),
  weeklyRate: z.union([z.string(), z.number()]).nullable().optional(),
  totalFound: z.number().int().default(0).optional(),
  totalPassed: z.number().int().default(0).optional(),
  totalFailed: z.number().int().default(0).optional(),
  completedAt: z.coerce.date().nullable().optional(),
  agentId: z.number().int().nullable().optional(),
  agentNumber: z.number().int().nullable().optional(),
  versionId: z.number().int().nullable().optional(),
  versionNumber: z.number().int().nullable().optional(),
  versionLabel: z.string().nullable().optional(),
  agentRunDate: z.string().nullable().optional(),
  lookbackDays: z.number().int().nullable().optional(),
  runName: z.string().nullable().optional(),
});

export type InsertBuyBoxReport = z.infer<typeof insertBuyBoxReportSchema>;
export type BuyBoxReport = typeof buyBoxReports.$inferSelect;

export const buyBoxResults = pgTable("buy_box_results", {
  id: serial("id").primaryKey(),
  reportId: integer("report_id").notNull().references(() => buyBoxReports.id, { onDelete: "cascade" }),
  // Source-agnostic listing identifiers. Originally `zpid` / `zillow_url`
  // (Zillow via APIllow); after Task #1101 the data source switched to
  // the `epctex/homes-com-scraper` Apify actor and Task #1132 renamed
  // the columns to neutral names so the schema matches its real meaning.
  listingId: text("listing_id"),
  listingUrl: text("listing_url"),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  price: numeric("price", { precision: 14, scale: 2 }),
  sqft: integer("sqft"),
  originalBedrooms: integer("original_bedrooms"),
  originalBathrooms: numeric("original_bathrooms", { precision: 5, scale: 1 }),
  convertedBedrooms: integer("converted_bedrooms"),
  convertedBathrooms: integer("converted_bathrooms"),
  imageUrl: text("image_url"),
  propertyType: text("property_type"),
  monthlyCashFlow: numeric("monthly_cash_flow", { precision: 12, scale: 2 }),
  cocReturn: numeric("coc_return", { precision: 10, scale: 4 }),
  totalCashInvested: numeric("total_cash_invested", { precision: 14, scale: 2 }),
  totalMonthlyIncome: numeric("total_monthly_income", { precision: 12, scale: 2 }),
  totalMonthlyExpenses: numeric("total_monthly_expenses", { precision: 12, scale: 2 }),
  mortgagePI: numeric("mortgage_p_i", { precision: 12, scale: 2 }),
  // Carrying-cost details scraped from the Homes.com listing so investors
  // can see the underlying property tax and HOA assumptions (vs. the
  // generic defaults the underwriter would otherwise use).
  annualTax: numeric("annual_tax", { precision: 12, scale: 2 }),
  monthlyHoa: numeric("monthly_hoa", { precision: 12, scale: 2 }),
  isCornerLot: boolean("is_corner_lot"),
  // Number of garage spaces from the Zillow listing. Surfaced on the
  // result card and fed into `calcBedroomsFromSqft` (each space ≈ 200
  // sqft) so users can see how the converted bed count was derived.
  garageSpaces: integer("garage_spaces"),
  // Lot size in square feet as returned by Zillow. Nullable — not all
  // listings surface a lot size. Surfaced on the result card alongside
  // sqft so hosts can quickly gauge deal size without opening the listing.
  lotSize: integer("lot_size"),
  // Free-text listing description from Zillow. Stored so the UI can
  // surface keyword badges (cul-de-sac, end unit, etc.) without
  // re-fetching the listing. Nullable — older rows won't have it.
  description: text("description"),
  // Task #1690 follow-up — when `annual_tax` was not present on the MLS
  // listing, fall back to a Gemini-derived city-average effective tax
  // rate × price. This flag tells the UI to label the value "(est.)"
  // so users know it's not the actual MLS-reported tax.
  annualTaxIsEstimate: boolean("annual_tax_is_estimate"),
  passesCocReturn: boolean("passes_coc_return"),
  passesCashFlow: boolean("passes_cash_flow"),
  passesPurchasePrice: boolean("passes_purchase_price"),
  passesBedrooms: boolean("passes_bedrooms"),
  passesSqft: boolean("passes_sqft"),
  passesLotSize: boolean("passes_lot_size"),
  passesAll: boolean("passes_all"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  reportIdIdx: index("idx_buy_box_results_report_id").on(table.reportId),
}));

export const insertBuyBoxResultSchema = createInsertSchema(buyBoxResults).omit({
  id: true,
  createdAt: true,
}).extend({
  reportId: z.number(),
  listingId: z.string().nullable().optional(),
  listingUrl: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  price: z.coerce.number().nullable().optional(),
  sqft: z.coerce.number().int().nullable().optional(),
  originalBedrooms: z.coerce.number().int().nullable().optional(),
  originalBathrooms: z.coerce.number().nullable().optional(),
  convertedBedrooms: z.coerce.number().int().nullable().optional(),
  convertedBathrooms: z.coerce.number().int().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  propertyType: z.string().nullable().optional(),
  monthlyCashFlow: z.coerce.number().nullable().optional(),
  cocReturn: z.coerce.number().nullable().optional(),
  totalCashInvested: z.coerce.number().nullable().optional(),
  totalMonthlyIncome: z.coerce.number().nullable().optional(),
  totalMonthlyExpenses: z.coerce.number().nullable().optional(),
  mortgagePI: z.coerce.number().nullable().optional(),
  annualTax: z.coerce.number().nullable().optional(),
  monthlyHoa: z.coerce.number().nullable().optional(),
  isCornerLot: z.boolean().nullable().optional(),
  garageSpaces: z.coerce.number().int().nullable().optional(),
  lotSize: z.coerce.number().int().nullable().optional(),
  annualTaxIsEstimate: z.boolean().nullable().optional(),
  passesCocReturn: z.boolean().nullable().optional(),
  passesCashFlow: z.boolean().nullable().optional(),
  passesPurchasePrice: z.boolean().nullable().optional(),
  passesBedrooms: z.boolean().nullable().optional(),
  passesSqft: z.boolean().nullable().optional(),
  passesLotSize: z.boolean().nullable().optional(),
  passesAll: z.boolean().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
});

export type InsertBuyBoxResult = z.infer<typeof insertBuyBoxResultSchema>;
export type BuyBoxResult = typeof buyBoxResults.$inferSelect;

// Task #1690 follow-up — Gemini-derived city-level effective property
// tax rate cache. One row per (city, state). Used by the Buy Box scan
// to (a) display "Annual Tax (est.)" on result cards when the MLS row
// has no tax figure and (b) override the underwriting default tax
// percent with a market-localized rate during scan-time underwriting.
export const cityTaxRates = pgTable("city_tax_rates", {
  id: serial("id").primaryKey(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  effectiveTaxPercent: numeric("effective_tax_percent", { precision: 6, scale: 4 }),
  source: text("source").notNull().default("gemini"),
  rationale: text("rationale"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  cityStateUnique: uniqueIndex("city_tax_rates_city_state_unique").on(t.city, t.state),
}));

export type CityTaxRate = typeof cityTaxRates.$inferSelect;

// Task #1690 follow-up — Buy Box favorites. A host can star any
// property card across any Buy Box report and pull them up later on a
// dedicated "Saved" page. We snapshot the display-relevant fields so
// the saved view survives report deletion / re-scan.
export const buyBoxFavorites = pgTable("buy_box_favorites", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  listingId: text("listing_id").notNull(),
  sourceReportId: integer("source_report_id"),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  price: numeric("price", { precision: 14, scale: 2 }),
  sqft: integer("sqft"),
  lotSize: integer("lot_size"),
  garageSpaces: integer("garage_spaces"),
  originalBedrooms: integer("original_bedrooms"),
  originalBathrooms: numeric("original_bathrooms", { precision: 5, scale: 1 }),
  convertedBedrooms: integer("converted_bedrooms"),
  convertedBathrooms: integer("converted_bathrooms"),
  imageUrl: text("image_url"),
  propertyType: text("property_type"),
  listingUrl: text("listing_url"),
  monthlyCashFlow: numeric("monthly_cash_flow", { precision: 12, scale: 2 }),
  cocReturn: numeric("coc_return", { precision: 10, scale: 4 }),
  totalCashInvested: numeric("total_cash_invested", { precision: 14, scale: 2 }),
  annualTax: numeric("annual_tax", { precision: 12, scale: 2 }),
  annualTaxIsEstimate: boolean("annual_tax_is_estimate"),
  monthlyHoa: numeric("monthly_hoa", { precision: 12, scale: 2 }),
  // Buy Box Agents — which agent/version/city found this house (set by the
  // server when the starred house came from a daily agent run).
  agentTags: jsonb("agent_tags").$type<BuyBoxAgentTag[]>(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  userListingUnique: uniqueIndex("buy_box_favorites_user_listing_unique").on(t.userId, t.listingId),
}));

export const insertBuyBoxFavoriteSchema = createInsertSchema(buyBoxFavorites).omit({
  id: true,
  createdAt: true,
  // Derived server-side from the account's agent matches, never client-set.
  agentTags: true,
});

export type InsertBuyBoxFavorite = z.infer<typeof insertBuyBoxFavoriteSchema>;
export type BuyBoxFavorite = typeof buyBoxFavorites.$inferSelect;

export const SALE_LISTING_STATUSES = ["active", "under_contract", "sold"] as const;
export type SaleListingStatus = typeof SALE_LISTING_STATUSES[number];

export const saleListings = pgTable("sale_listings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  askingPrice: numeric("asking_price").notNull(),
  description: text("description"),
  photos: text("photos").array().notNull(),
  status: text("status").default("active").notNull(),
  sellerName: text("seller_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  hidden: boolean("hidden").default(false).notNull(),
  hiddenReason: text("hidden_reason"),
  hiddenAt: timestamp("hidden_at"),
  hiddenByUserId: integer("hidden_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdIdx: index("idx_sale_listings_user_id").on(table.userId),
  propertyIdIdx: index("idx_sale_listings_property_id").on(table.propertyId),
  statusIdx: index("idx_sale_listings_status").on(table.status),
}));

export const insertSaleListingSchema = createInsertSchema(saleListings).omit({
  id: true,
  userId: true,
  hidden: true,
  hiddenReason: true,
  hiddenAt: true,
  hiddenByUserId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  propertyId: z.coerce.number().int().positive("Select a property"),
  askingPrice: z.coerce.number().positive("Asking price must be greater than 0").transform((n) => n.toString()),
  description: z.string().optional().nullable(),
  photos: z.array(z.string()).default([]),
  status: z.enum(SALE_LISTING_STATUSES).default("active"),
  sellerName: z.string().max(120, "Seller name is too long").optional().or(z.literal("")).nullable(),
  contactEmail: z.string().email("Invalid email").optional().or(z.literal("")).nullable(),
  contactPhone: z.string().optional().or(z.literal("")).nullable(),
});

export type InsertSaleListing = z.infer<typeof insertSaleListingSchema>;
export type SaleListing = typeof saleListings.$inferSelect;

export const saleInquiries = pgTable("sale_inquiries", {
  id: serial("id").primaryKey(),
  listingId: integer("listing_id").notNull().references(() => saleListings.id, { onDelete: "cascade" }),
  sellerId: integer("seller_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  buyerId: integer("buyer_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  lastMessagePreview: text("last_message_preview"),
  lastMessageAt: timestamp("last_message_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  listingIdIdx: index("sale_inquiries_listing_id_idx").on(table.listingId),
}));

export type SaleInquiry = typeof saleInquiries.$inferSelect;

export const saleMessages = pgTable("sale_messages", {
  id: serial("id").primaryKey(),
  inquiryId: integer("inquiry_id").notNull().references(() => saleInquiries.id, { onDelete: "cascade" }),
  senderId: integer("sender_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  inquiryIdIdx: index("sale_messages_inquiry_id_idx").on(table.inquiryId),
}));

export const insertSaleMessageSchema = createInsertSchema(saleMessages).omit({
  id: true,
  senderId: true,
  readAt: true,
  createdAt: true,
}).extend({
  content: z.string().trim().min(1, "Message cannot be empty").max(4000, "Message is too long"),
});

export type InsertSaleMessage = z.infer<typeof insertSaleMessageSchema>;
export type SaleMessage = typeof saleMessages.$inferSelect;

export const TESTIMONIAL_VOUCHER_STATUSES = ["pending", "redeemed", "rejected"] as const;
export type TestimonialVoucherStatus = typeof TESTIMONIAL_VOUCHER_STATUSES[number];

export const testimonialSubmissions = pgTable("testimonial_submissions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  voucherStatus: text("voucher_status").default("pending").notNull(),
  notes: text("notes"),
}, (table) => ({
  userIdUniq: uniqueIndex("testimonial_submissions_user_id_unique").on(table.userId),
}));

export const insertTestimonialSubmissionSchema = createInsertSchema(testimonialSubmissions).omit({
  id: true,
  submittedAt: true,
  voucherStatus: true,
  notes: true,
});

export type InsertTestimonialSubmission = z.infer<typeof insertTestimonialSubmissionSchema>;
export type TestimonialSubmission = typeof testimonialSubmissions.$inferSelect;

export const updateTestimonialVoucherStatusSchema = z.object({
  voucherStatus: z.enum(TESTIMONIAL_VOUCHER_STATUSES),
  notes: z.string().max(2000).optional().nullable(),
});
export type UpdateTestimonialVoucherStatusInput = z.infer<typeof updateTestimonialVoucherStatusSchema>;
export const DOCUMENT_CATEGORIES = [
  "contractor_invoice",
  "contractor_bid",
  "receipt",
  "bank_statement",
  "other",
] as const;
export type DocumentCategory = typeof DOCUMENT_CATEGORIES[number];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  contractor_invoice: "Contractor Invoice",
  contractor_bid: "Contractor Bid",
  receipt: "Receipt",
  bank_statement: "Bank Statement",
  other: "Other",
};

export const DOCUMENT_PIPELINE_STAGES = [
  "underwriting",
  "acquisition",
  "renovate",
  "launch",
  "active",
  "archive",
] as const;
export type DocumentPipelineStage = typeof DOCUMENT_PIPELINE_STAGES[number];

export const propertyDocuments = pgTable("property_documents", {
  id: serial("id").primaryKey(),
  // Permanent deletion detaches expense-linked receipts before the cascade.
  // Unlinked documents still follow the property's lifetime.
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  mimeType: text("mime_type").notNull(),
  // Task #1927 — Document bytes now live in Object Storage under
  // `${PRIVATE_OBJECT_DIR}/property-documents/<uuid>`. `fileKey` stores
  // the path relative to PRIVATE_OBJECT_DIR. Legacy rows that pre-date
  // the migration still carry the original base64 `data:...` URL on
  // `fileUrl`; the download route prefers `fileKey` and falls back to
  // `fileUrl` so existing rows keep working until the one-time backfill
  // drains them. New uploads always populate `fileKey` and leave
  // `fileUrl` null.
  fileUrl: text("file_url"),
  fileKey: text("file_key"),
  category: text("category").notNull(),
  pipelineStage: text("pipeline_stage"),
  fileHash: text("file_hash"),
  extractedNumber: text("extracted_number"),
  vendorId: integer("vendor_id").references(() => vendors.id, { onDelete: "set null" }),
  extractedData: jsonb("extracted_data"),
  voidedAt: timestamp("voided_at"),
  uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
}, (table) => ({
  propertyUploadedAtIdx: index("property_documents_property_uploaded_at_idx").on(table.propertyId, table.uploadedAt.desc()),
  propertyCategoryIdx: index("property_documents_property_category_idx").on(table.propertyId, table.category),
}));

// New uploads still require a live property; null is reserved for retention.
export const insertPropertyDocumentSchema = createInsertSchema(propertyDocuments).omit({ id: true, uploadedAt: true }).extend({ propertyId: z.number().int() });
export type InsertPropertyDocument = z.infer<typeof insertPropertyDocumentSchema>;
export type PropertyDocument = typeof propertyDocuments.$inferSelect;

export const renovationHistory = pgTable("renovation_history", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  action: text("action").notNull(),
  detail: text("detail"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  propertyIdIdx: index("renovation_history_property_id_idx").on(table.propertyId),
}));

export const insertRenovationHistorySchema = createInsertSchema(renovationHistory).omit({ id: true, createdAt: true });
export type InsertRenovationHistory = z.infer<typeof insertRenovationHistorySchema>;
export type RenovationHistory = typeof renovationHistory.$inferSelect;

export const apifyRunLog = pgTable("apify_run_log", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  actor: text("actor").notNull(),
  runId: text("run_id"),
  trigger: text("trigger").notNull(),
  triggerDetail: text("trigger_detail"),
  status: text("status").notNull().default("running"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  finishedAt: timestamp("finished_at"),
  computeUnits: numeric("compute_units"),
  costUsd: numeric("cost_usd"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  createdAtIdx: index("apify_run_log_created_at_idx").on(table.createdAt.desc()),
  actorIdx: index("apify_run_log_actor_idx").on(table.actor),
  // Task #2415: Apify run IDs are globally unique. A partial unique index
  // guards against duplicate rows for the same actor run (e.g. retry races)
  // while still allowing many rows in the pre-finalize "running" state where
  // run_id is null.
  runIdUniq: uniqueIndex("apify_run_log_run_id_uniq")
    .on(table.runId)
    .where(sql`run_id IS NOT NULL`),
}));

export type ApifyRunLog = typeof apifyRunLog.$inferSelect;
export type InsertApifyRunLog = typeof apifyRunLog.$inferInsert;

/**
 * Centralized team activity / audit log (Task #3233).
 *
 * One row per state-changing action a team member takes within a tenant.
 * Keyed to `ownerUserId` (the effective account owner) so every read/write
 * is tenant-scoped. `actorUserId` references the acting app user (set null
 * if that user is later deleted) and `actorName` snapshots the display name
 * at record time so the log stays readable even after the actor leaves.
 * Read-only by construction from the app's perspective — there is no
 * update path and the only delete path is the retention pruner. Rows are
 * retained for `AUDIT_LOG_RETENTION_MONTHS` (default 12 months), enforced
 * by the daily `audit-log-prune` cron (see `server/schedule-cron.ts` →
 * `storage.pruneOldAuditLogs`). Trimming is purely age-based and never
 * touches the read API / CSV export (they just return fewer rows).
 */
export const auditLogs = pgTable("audit_logs", {
  id: serial("id").primaryKey(),
  ownerUserId: integer("owner_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  actorUserId: integer("actor_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  actorName: text("actor_name").notNull(),
  action: text("action").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: integer("entity_id"),
  entityLabel: text("entity_label"),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  ownerCreatedAtIdx: index("audit_logs_owner_created_at_idx").on(table.ownerUserId, table.createdAt.desc()),
  actorIdx: index("audit_logs_actor_idx").on(table.actorUserId),
  // Filtered reads always scope by owner first, so the action/property
  // filters are served best by owner-prefixed composite indexes rather than
  // single-column ones (which Postgres would have to AND with the owner
  // index). Keeps the audit-log table view fast as volume grows.
  ownerActionIdx: index("audit_logs_owner_action_idx").on(table.ownerUserId, table.action),
  ownerPropertyIdx: index("audit_logs_owner_property_idx").on(table.ownerUserId, table.propertyId),
}));

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  createdAt: true,
});
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

// Task #3293 — Singleton row recording the outcome of the daily audit-log
// prune cron (Task #3252) so admins can see whether the nightly trim actually
// ran. Persisted (rather than in-process) so "last successful prune time"
// survives restarts/deploys. Always id = 1.
export const auditLogPruneState = pgTable("audit_log_prune_state", {
  id: integer("id").primaryKey().default(1),
  lastRunAt: timestamp("last_run_at").defaultNow().notNull(),
  lastDeletedCount: integer("last_deleted_count").default(0).notNull(),
  lastRetentionMonths: integer("last_retention_months").default(12).notNull(),
});

export type AuditLogPruneState = typeof auditLogPruneState.$inferSelect;
export type InsertAuditLogPruneState = typeof auditLogPruneState.$inferInsert;

export const geminiCallLog = pgTable("gemini_call_log", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  feature: text("feature").notNull(),
  model: text("model").notNull(),
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  totalTokens: integer("total_tokens").notNull().default(0),
  latencyMs: integer("latency_ms").notNull().default(0),
  success: boolean("success").notNull().default(true),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  createdAtIdx: index("gemini_call_log_created_at_idx").on(table.createdAt.desc()),
  featureIdx: index("gemini_call_log_feature_idx").on(table.feature),
}));

export type GeminiCallLog = typeof geminiCallLog.$inferSelect;
export type InsertGeminiCallLog = typeof geminiCallLog.$inferInsert;

/**
 * ElevenLabs TTS usage log (Task #2437).
 *
 * Mirrors the lightweight per-call audit used for Gemini so the admin
 * dashboard can surface call counts, total characters synthesized
 * (cost proxy), and per-user breakdowns for the Pulse voice briefing.
 * `characters` is the length of the script we sent to ElevenLabs —
 * ElevenLabs bills per character, so this directly proxies cost.
 */
export const elevenlabsCallLog = pgTable("elevenlabs_call_log", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  feature: text("feature").notNull(),
  voiceId: text("voice_id").notNull(),
  modelId: text("model_id").notNull(),
  characters: integer("characters").notNull().default(0),
  latencyMs: integer("latency_ms").notNull().default(0),
  success: boolean("success").notNull().default(true),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  createdAtIdx: index("elevenlabs_call_log_created_at_idx").on(table.createdAt.desc()),
  featureIdx: index("elevenlabs_call_log_feature_idx").on(table.feature),
}));

export type ElevenlabsCallLog = typeof elevenlabsCallLog.$inferSelect;
export type InsertElevenlabsCallLog = typeof elevenlabsCallLog.$inferInsert;

export const geminiCostAlerts = pgTable("gemini_cost_alerts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  alertDate: text("alert_date").notNull(),
  dailyCostUsd: doublePrecision("daily_cost_usd").notNull(),
  baselineCostUsd: doublePrecision("baseline_cost_usd").notNull(),
  growthRatio: doublePrecision("growth_ratio").notNull(),
  reason: text("reason").notNull(),
  recipients: text("recipients").array().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  acknowledgedAt: timestamp("acknowledged_at"),
  acknowledgedBy: text("acknowledged_by"),
}, (table) => ({
  userDateIdx: uniqueIndex("gemini_cost_alerts_user_date_idx").on(table.userId, table.alertDate),
  createdAtIdx: index("gemini_cost_alerts_created_at_idx").on(table.createdAt.desc()),
  acknowledgedAtIdx: index("gemini_cost_alerts_acknowledged_at_idx").on(table.acknowledgedAt),
}));

export type GeminiCostAlert = typeof geminiCostAlerts.$inferSelect;
export type InsertGeminiCostAlert = typeof geminiCostAlerts.$inferInsert;

export const geminiAlertSettings = pgTable("gemini_alert_settings", {
  id: integer("id").primaryKey().default(1),
  thresholdUsd: doublePrecision("threshold_usd").notNull(),
  growthRatio: doublePrecision("growth_ratio").notNull(),
  minBaselineUsd: doublePrecision("min_baseline_usd").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: integer("updated_by").references(() => appUsers.id, { onDelete: "set null" }),
});

export type GeminiAlertSettings = typeof geminiAlertSettings.$inferSelect;
export type InsertGeminiAlertSettings = typeof geminiAlertSettings.$inferInsert;

export const updateGeminiAlertSettingsSchema = z.object({
  thresholdUsd: z.number().positive().max(100000),
  growthRatio: z.number().min(1).max(1000),
  minBaselineUsd: z.number().min(0).max(100000),
});
export type UpdateGeminiAlertSettings = z.infer<typeof updateGeminiAlertSettingsSchema>;

// Task #2443 — Apify spend spike alerts. Mirrors the gemini_cost_alerts /
// gemini_alert_settings shape but keys by Apify actor (per-actor + aggregate)
// instead of by user, since Apify cost is system-wide not per-user.
export const apifyCostAlerts = pgTable("apify_cost_alerts", {
  id: serial("id").primaryKey(),
  // NULL actor = aggregate-across-all-actors row for the day.
  actor: text("actor"),
  alertDate: text("alert_date").notNull(),
  dailyCostUsd: doublePrecision("daily_cost_usd").notNull(),
  baselineCostUsd: doublePrecision("baseline_cost_usd").notNull(),
  growthRatio: doublePrecision("growth_ratio").notNull(),
  reason: text("reason").notNull(),
  recipients: text("recipients").array().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  acknowledgedAt: timestamp("acknowledged_at"),
  acknowledgedBy: text("acknowledged_by"),
}, (table) => ({
  actorDateIdx: uniqueIndex("apify_cost_alerts_actor_date_idx").on(table.actor, table.alertDate),
  createdAtIdx: index("apify_cost_alerts_created_at_idx").on(table.createdAt.desc()),
  acknowledgedAtIdx: index("apify_cost_alerts_acknowledged_at_idx").on(table.acknowledgedAt),
}));

export type ApifyCostAlert = typeof apifyCostAlerts.$inferSelect;
export type InsertApifyCostAlert = typeof apifyCostAlerts.$inferInsert;

export const apifyAlertSettings = pgTable("apify_alert_settings", {
  id: integer("id").primaryKey().default(1),
  thresholdUsd: doublePrecision("threshold_usd").notNull(),
  growthRatio: doublePrecision("growth_ratio").notNull(),
  minBaselineUsd: doublePrecision("min_baseline_usd").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: integer("updated_by").references(() => appUsers.id, { onDelete: "set null" }),
});

export type ApifyAlertSettings = typeof apifyAlertSettings.$inferSelect;
export type InsertApifyAlertSettings = typeof apifyAlertSettings.$inferInsert;

export const updateApifyAlertSettingsSchema = z.object({
  thresholdUsd: z.number().positive().max(100000),
  growthRatio: z.number().min(1).max(1000),
  minBaselineUsd: z.number().min(0).max(100000),
});
export type UpdateApifyAlertSettings = z.infer<typeof updateApifyAlertSettingsSchema>;

// ─── Relations ───────────────────────────────────────────────────────────────

export const appUsersRelations = relations(appUsers, ({ many, one }) => ({
  invitedByUser: one(appUsers, { fields: [appUsers.invitedBy], references: [appUsers.id], relationName: "invitedBy" }),
  invitees: many(appUsers, { relationName: "invitedBy" }),
  teamMembers: many(teamMembers),
  properties: many(appUsersProperties),
  rooms: many(rooms),
  members: many(members),
  notifications: many(notifications),
  tasks: many(tasks),
  vendors: many(vendors),
  serviceFormLinks: many(serviceFormLinks),
  bookingRequestLinks: many(bookingRequestLinks),
}));

export const propertyGroupsRelations = relations(propertyGroups, ({ one, many }) => ({
  owner: one(appUsers, { fields: [propertyGroups.userId], references: [appUsers.id] }),
  properties: many(properties),
}));

export const propertiesRelations = relations(properties, ({ one, many }) => ({
  parentProperty: one(properties, { fields: [properties.parentPropertyId], references: [properties.id], relationName: "parentChild" }),
  subProperties: many(properties, { relationName: "parentChild" }),
  appUsersProperties: many(appUsersProperties),
  rooms: many(rooms),
  images: many(propertyImages),
  tasks: many(tasks),
  members: many(members),
  documents: many(propertyDocuments),
  renovationItems: many(renovationItems),
}));

export const roomsRelations = relations(rooms, ({ one, many }) => ({
  property: one(properties, { fields: [rooms.propertyId], references: [properties.id] }),
  shadowOf: one(rooms, { fields: [rooms.shadowOfRoomId], references: [rooms.id], relationName: "roomShadow" }),
  images: many(propertyImages),
  members: many(members),
  tasks: many(tasks),
  pricingHistory: many(roomPricingHistory),
}));

export const roomPricingHistoryRelations = relations(roomPricingHistory, ({ one }) => ({
  room: one(rooms, { fields: [roomPricingHistory.roomId], references: [rooms.id] }),
}));

export const appUsersPropertiesRelations = relations(appUsersProperties, ({ one }) => ({
  user: one(appUsers, { fields: [appUsersProperties.userId], references: [appUsers.id] }),
  property: one(properties, { fields: [appUsersProperties.propertyId], references: [properties.id] }),
}));

export const teamMembersRelations = relations(teamMembers, ({ one }) => ({
  owner: one(appUsers, { fields: [teamMembers.userId], references: [appUsers.id] }),
  invitee: one(appUsers, { fields: [teamMembers.inviteeUserId], references: [appUsers.id] }),
}));

export const membersRelations = relations(members, ({ one, many }) => ({
  // (the legacy `owner` relation over members.user_id was removed — host↔member
  // mapping lives in members_users and the relation had no query call sites)
  room: one(rooms, { fields: [members.roomId], references: [rooms.id] }),
  // Property assignment lives in properties_members; reads go through
  // storage.getCurrentPropertyIdForMember(...).
  propertiesMembers: many(propertiesMembers),
}));

export const propertiesMembersRelations = relations(propertiesMembers, ({ one }) => ({
  member: one(members, { fields: [propertiesMembers.memberId], references: [members.id] }),
  property: one(properties, { fields: [propertiesMembers.propertyId], references: [properties.id] }),
}));

// Task #1029 — `internalThreadsRelations` was dropped along with the
// underlying table. The storage layer queries `memberChats` /
// `memberChatWorkflow` / `memberChatThreads` directly without going through
// drizzle relations, which matches the Paul-table loose-FK posture (no FKs
// cross the app/harvest boundary anyway).
//
// `conversationsRelations` / `messagesRelations` are intentionally NOT
// re-added even though their tables ARE re-declared above for prod-sync
// purposes — the storage layer queries them via `from(conversations)` /
// `eq(messages.conversationId, …)` directly, never via `query.conversations`,
// so adding relations would just be dead code that would have to be deleted
// again when the legacy stack is physically dropped (see follow-up).

export const tasksRelations = relations(tasks, ({ one, many }) => ({
  owner: one(appUsers, { fields: [tasks.userId], references: [appUsers.id] }),
  property: one(properties, { fields: [tasks.propertyId], references: [properties.id] }),
  room: one(rooms, { fields: [tasks.roomId], references: [rooms.id] }),
  member: one(members, { fields: [tasks.memberId], references: [members.id] }),
  vendor: one(vendors, { fields: [tasks.vendorId], references: [vendors.id] }),
  assignee: one(appUsers, { fields: [tasks.assigneeId], references: [appUsers.id], relationName: "taskAssignee" }),
  assignedTeamMember: one(vendorTeamMembers, { fields: [tasks.assignedTeamMemberId], references: [vendorTeamMembers.id] }),
  createdByUser: one(appUsers, { fields: [tasks.createdBy], references: [appUsers.id], relationName: "taskCreatedBy" }),
  approvedByUser: one(appUsers, { fields: [tasks.approvedBy], references: [appUsers.id], relationName: "taskApprovedBy" }),
  checklistItems: many(taskChecklistItems),
  photos: many(taskPhotos),
  messages: many(taskMessages),
  comments: many(taskComments),
}));

export const taskCommentsRelations = relations(taskComments, ({ one }) => ({
  task: one(tasks, { fields: [taskComments.taskId], references: [tasks.id] }),
  author: one(appUsers, { fields: [taskComments.authorId], references: [appUsers.id] }),
}));

export const vendorsRelations = relations(vendors, ({ one, many }) => ({
  owner: one(appUsers, { fields: [vendors.userId], references: [appUsers.id] }),
  documents: many(vendorDocuments),
  rateOverrides: many(vendorRateOverrides),
  hostRelationships: many(vendorHostRelationships),
  propertyAssignments: many(propertyVendorAssignments),
  reviews: many(vendorReviews),
  payoutAccount: one(vendorPayoutAccounts),
  teamMembers: many(vendorTeamMembers),
  propertyTeamAssignments: many(vendorPropertyTeamAssignments),
}));

export const vendorTeamMembersRelations = relations(vendorTeamMembers, ({ one, many }) => ({
  vendor: one(vendors, { fields: [vendorTeamMembers.vendorId], references: [vendors.id] }),
  propertyAssignments: many(vendorPropertyTeamAssignments),
}));

export const vendorPropertyTeamAssignmentsRelations = relations(vendorPropertyTeamAssignments, ({ one }) => ({
  vendor: one(vendors, { fields: [vendorPropertyTeamAssignments.vendorId], references: [vendors.id] }),
  property: one(properties, { fields: [vendorPropertyTeamAssignments.propertyId], references: [properties.id] }),
  teamMember: one(vendorTeamMembers, { fields: [vendorPropertyTeamAssignments.teamMemberId], references: [vendorTeamMembers.id] }),
}));

export const vendorPortalTourProgressRelations = relations(vendorPortalTourProgress, ({ one }) => ({
  vendor: one(vendors, { fields: [vendorPortalTourProgress.vendorId], references: [vendors.id] }),
  teamMember: one(vendorTeamMembers, { fields: [vendorPortalTourProgress.teamMemberId], references: [vendorTeamMembers.id] }),
}));

export const vendorDocumentsRelations = relations(vendorDocuments, ({ one }) => ({
  vendor: one(vendors, { fields: [vendorDocuments.vendorId], references: [vendors.id] }),
}));

export const taskChecklistItemsRelations = relations(taskChecklistItems, ({ one }) => ({
  task: one(tasks, { fields: [taskChecklistItems.taskId], references: [tasks.id] }),
}));

export const taskPhotosRelations = relations(taskPhotos, ({ one }) => ({
  task: one(tasks, { fields: [taskPhotos.taskId], references: [tasks.id] }),
  checklistItem: one(taskChecklistItems, { fields: [taskPhotos.checklistItemId], references: [taskChecklistItems.id] }),
  uploader: one(appUsers, { fields: [taskPhotos.uploadedBy], references: [appUsers.id] }),
}));

export const taskMessagesRelations = relations(taskMessages, ({ one }) => ({
  task: one(tasks, { fields: [taskMessages.taskId], references: [tasks.id] }),
  vendorTeamMember: one(vendorTeamMembers, { fields: [taskMessages.vendorTeamMemberId], references: [vendorTeamMembers.id] }),
}));

export const serviceFormSubmissionMessagesRelations = relations(serviceFormSubmissionMessages, ({ one }) => ({
  submission: one(serviceFormSubmissions, { fields: [serviceFormSubmissionMessages.submissionId], references: [serviceFormSubmissions.id] }),
  vendorTeamMember: one(vendorTeamMembers, { fields: [serviceFormSubmissionMessages.vendorTeamMemberId], references: [vendorTeamMembers.id] }),
}));

export const propertyImagesRelations = relations(propertyImages, ({ one }) => ({
  property: one(properties, { fields: [propertyImages.propertyId], references: [properties.id] }),
  room: one(rooms, { fields: [propertyImages.roomId], references: [rooms.id] }),
}));

export const propertyDocumentsRelations = relations(propertyDocuments, ({ one }) => ({
  property: one(properties, { fields: [propertyDocuments.propertyId], references: [properties.id] }),
  owner: one(appUsers, { fields: [propertyDocuments.userId], references: [appUsers.id] }),
  vendor: one(vendors, { fields: [propertyDocuments.vendorId], references: [vendors.id] }),
}));

export const serviceFormLinksRelations = relations(serviceFormLinks, ({ one, many }) => ({
  owner: one(appUsers, { fields: [serviceFormLinks.userId], references: [appUsers.id] }),
  template: one(serviceFormTemplates, { fields: [serviceFormLinks.templateId], references: [serviceFormTemplates.id] }),
  property: one(properties, { fields: [serviceFormLinks.propertyId], references: [properties.id] }),
  vendor: one(vendors, { fields: [serviceFormLinks.vendorId], references: [vendors.id] }),
  submissions: many(serviceFormSubmissions),
}));

export const bookingRequestLinksRelations = relations(bookingRequestLinks, ({ one, many }) => ({
  owner: one(appUsers, { fields: [bookingRequestLinks.userId], references: [appUsers.id] }),
  template: one(bookingRequestTemplates, { fields: [bookingRequestLinks.templateId], references: [bookingRequestTemplates.id] }),
  property: one(properties, { fields: [bookingRequestLinks.propertyId], references: [properties.id] }),
  submissions: many(bookingRequestSubmissions),
}));

export const renovationItemsRelations = relations(renovationItems, ({ one }) => ({
  property: one(properties, { fields: [renovationItems.propertyId], references: [properties.id] }),
  owner: one(appUsers, { fields: [renovationItems.userId], references: [appUsers.id] }),
}));

export const saleListingsRelations = relations(saleListings, ({ one, many }) => ({
  owner: one(appUsers, { fields: [saleListings.userId], references: [appUsers.id] }),
  property: one(properties, { fields: [saleListings.propertyId], references: [properties.id] }),
  inquiries: many(saleInquiries),
}));

export const saleInquiriesRelations = relations(saleInquiries, ({ one, many }) => ({
  listing: one(saleListings, { fields: [saleInquiries.listingId], references: [saleListings.id] }),
  seller: one(appUsers, { fields: [saleInquiries.sellerId], references: [appUsers.id] }),
  buyer: one(appUsers, { fields: [saleInquiries.buyerId], references: [appUsers.id] }),
  saleMessages: many(saleMessages),
}));

export const paymentTransactionsRelations = relations(paymentTransactions, ({ one }) => ({
  task: one(tasks, { fields: [paymentTransactions.taskId], references: [tasks.id] }),
  host: one(appUsers, { fields: [paymentTransactions.hostId], references: [appUsers.id] }),
  vendor: one(vendors, { fields: [paymentTransactions.vendorId], references: [vendors.id] }),
  property: one(properties, { fields: [paymentTransactions.propertyId], references: [properties.id] }),
}));

export const acquisitionsRelations = relations(acquisitions, ({ one, many }) => ({
  owner: one(appUsers, { fields: [acquisitions.userId], references: [appUsers.id] }),
  property: one(properties, { fields: [acquisitions.propertyId], references: [properties.id] }),
  documents: many(acquisitionDocuments),
}));

export const placeholderRoomRetirementRuns = pgTable("placeholder_room_retirement_runs", {
  id: text("id").primaryKey(),
  startedAt: timestamp("started_at", { withTimezone: true }).defaultNow().notNull(),
  finishedAt: timestamp("finished_at", { withTimezone: true }),
  triggeredBy: text("triggered_by").notNull(),
  status: text("status").notNull(),
  propertyId: integer("property_id"),
  report: jsonb("report"),
  error: text("error"),
}, (table) => ({
  sourceStartedIdx: index("placeholder_retirement_source_started_idx").on(table.triggeredBy, table.startedAt.desc()),
  statusStartedIdx: index("placeholder_retirement_status_started_idx").on(table.status, table.startedAt.desc()),
}));
export const clientBundleScanRuns = pgTable("client_bundle_scan_runs", {
  id: serial("id").primaryKey(),
  scannedAt: timestamp("scanned_at").defaultNow().notNull(),
  triggeredBy: text("triggered_by").default("cron").notNull(),
  filesScanned: integer("files_scanned").notNull().default(0),
  findingsCount: integer("findings_count").notNull().default(0),
  findings: jsonb("findings").default([]).notNull(),
  status: text("status").notNull().default("ok"),
  durationMs: integer("duration_ms"),
  buildHash: text("build_hash"),
  alertSignature: text("alert_signature"),
  alertSentAt: timestamp("alert_sent_at"),
}, (table) => ({
  alertSigSentAtIdx: index("client_bundle_scan_runs_alert_sig_sent_at_idx").on(table.alertSignature, table.alertSentAt.desc()),
}));

export const bundleScanFindingSchema = z.object({
  file: z.string(),
  patternName: z.string(),
  snippet: z.string(),
  masked: z.string(),
});
export type BundleScanFinding = z.infer<typeof bundleScanFindingSchema>;

export function computeBundleScanFindingSignature(input: { patternName: string; file: string; masked: string }): string {
  return `${input.patternName}::${input.file}::${input.masked}`;
}

export const BUNDLE_SCAN_TRIAGE_STATUSES = ["open", "acknowledged", "false_positive"] as const;
export type BundleScanTriageStatus = (typeof BUNDLE_SCAN_TRIAGE_STATUSES)[number];

export const bundleScanFindingTriages = pgTable("bundle_scan_finding_triages", {
  signature: text("signature").primaryKey(),
  patternName: text("pattern_name").notNull(),
  file: text("file").notNull(),
  masked: text("masked").notNull(),
  status: text("status").notNull().default("open"),
  note: text("note"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  // Free-text admin email (not an FK to app_users.id) so we can keep the audit
  // trail even after the admin's account is deleted. Triage actions are taken
  // exclusively from the internal admin dashboard, so the value is captured
  // from the authenticated admin's email and is not user-supplied.
  updatedBy: text("updated_by"),
});

export const upsertBundleScanFindingTriageSchema = z.object({
  signature: z.string().min(1),
  patternName: z.string().min(1),
  file: z.string().min(1),
  masked: z.string().min(1),
  status: z.enum(BUNDLE_SCAN_TRIAGE_STATUSES),
  note: z.string().max(2000).nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type UpsertBundleScanFindingTriage = z.infer<typeof upsertBundleScanFindingTriageSchema>;
export type BundleScanFindingTriage = typeof bundleScanFindingTriages.$inferSelect;

export const bulkUpsertBundleScanFindingTriageSchema = z.object({
  status: z.enum(BUNDLE_SCAN_TRIAGE_STATUSES),
  note: z.string().max(2000).nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  findings: z.array(z.object({
    signature: z.string().min(1),
    patternName: z.string().min(1),
    file: z.string().min(1),
    masked: z.string().min(1),
  })).min(1).max(500),
});
export type BulkUpsertBundleScanFindingTriage = z.infer<typeof bulkUpsertBundleScanFindingTriageSchema>;

export const bundleScanFindingTriageHistory = pgTable("bundle_scan_finding_triage_history", {
  id: serial("id").primaryKey(),
  signature: text("signature").notNull(),
  status: text("status").notNull(),
  note: text("note"),
  changedBy: text("changed_by"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
}, (table) => ({
  signatureIdx: index("bundle_scan_triage_history_signature_idx").on(table.signature),
}));

export type BundleScanFindingTriageHistoryEntry = typeof bundleScanFindingTriageHistory.$inferSelect;

export const insertClientBundleScanRunSchema = createInsertSchema(clientBundleScanRuns).omit({
  id: true,
  scannedAt: true,
}).extend({
  triggeredBy: z.string().default("cron"),
  filesScanned: z.number().int().default(0),
  findingsCount: z.number().int().default(0),
  findings: z.array(bundleScanFindingSchema).default([]),
  status: z.string().default("ok"),
  durationMs: z.number().int().optional().nullable(),
  buildHash: z.string().optional().nullable(),
  alertSignature: z.string().optional().nullable(),
  alertSentAt: optionalCoercedDate,
});

export type InsertClientBundleScanRun = z.infer<typeof insertClientBundleScanRunSchema>;
export type ClientBundleScanRun = typeof clientBundleScanRuns.$inferSelect;

// ─── Encryption Drift Audit ──────────────────────────────────────────────────
//
// Daily check that counts plaintext rows still sitting in columns that are
// supposed to be encrypted with the `enc:v1:` prefix. Drift can be introduced
// by raw SQL imports, restored backups, or new code paths that bypass the
// storage helpers.
export const encryptionAuditRuns = pgTable("encryption_audit_runs", {
  id: serial("id").primaryKey(),
  ranAt: timestamp("ran_at").defaultNow().notNull(),
  triggeredBy: text("triggered_by").default("cron").notNull(),
  seamApiKeyPlaintext: integer("seam_api_key_plaintext").notNull().default(0),
  propertyWifiPasswordPlaintext: integer("property_wifi_password_plaintext").notNull().default(0),
  paymentAccountNumberPlaintext: integer("payment_account_number_plaintext").notNull().default(0),
  paymentRoutingNumberPlaintext: integer("payment_routing_number_plaintext").notNull().default(0),
  quoApiKeyPlaintext: integer("quo_api_key_plaintext").notNull().default(0),
  quoWebhookSecretPlaintext: integer("quo_webhook_secret_plaintext").notNull().default(0),
  totalPlaintext: integer("total_plaintext").notNull().default(0),
  status: text("status").notNull().default("ok"),
  durationMs: integer("duration_ms"),
  alertSignature: text("alert_signature"),
  alertSentAt: timestamp("alert_sent_at"),
  errorMessage: text("error_message"),
}, (table) => ({
  ranAtIdx: index("encryption_audit_runs_ran_at_idx").on(table.ranAt.desc()),
}));

export const encryptionAuditCountsSchema = z.object({
  seamApiKey: z.number().int().nonnegative(),
  propertyWifiPassword: z.number().int().nonnegative(),
  paymentAccountNumber: z.number().int().nonnegative(),
  paymentRoutingNumber: z.number().int().nonnegative(),
  quoApiKey: z.number().int().nonnegative(),
  quoWebhookSecret: z.number().int().nonnegative(),
});
export type EncryptionAuditCounts = z.infer<typeof encryptionAuditCountsSchema>;

export const insertEncryptionAuditRunSchema = createInsertSchema(encryptionAuditRuns).omit({
  id: true,
  ranAt: true,
});
export type InsertEncryptionAuditRun = z.infer<typeof insertEncryptionAuditRunSchema>;
export type EncryptionAuditRun = typeof encryptionAuditRuns.$inferSelect;

// ─── Referral Tracking: Registered Lenders ───────────────────────────────────

export const registeredLenders = pgTable("registered_lenders", {
  id: serial("id").primaryKey(),
  companyName: text("company_name").notNull(),
  contactName: text("contact_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  statesLicensed: text("states_licensed").array(),
  loanTypes: text("loan_types").array(),
  logoUrl: text("logo_url"),
  active: boolean("active").default(true).notNull(),
  commissionAmount: numeric("commission_amount", { precision: 10, scale: 2 }).default("1000").notNull(),
  notes: text("notes"),
  // Task #3366 — Finance-page publishing. When showOnFinancePage is true the
  // record is mirrored into a `lenders` (finance directory) card and a partner
  // account. blurb/website are the public-facing copy/link shown on that card.
  showOnFinancePage: boolean("show_on_finance_page").default(false).notNull(),
  blurb: text("blurb"),
  website: text("website"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRegisteredLenderSchema = createInsertSchema(registeredLenders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  companyName: z.string().min(1, "Company name is required"),
  contactName: z.string().min(1, "Contact name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().optional().nullable(),
  statesLicensed: z.array(z.string()).optional().nullable(),
  loanTypes: z.array(z.string()).optional().nullable(),
  logoUrl: z.string().optional().nullable(),
  active: z.boolean().default(true).optional(),
  commissionAmount: z.string().default("1000").optional(),
  notes: z.string().optional().nullable(),
  showOnFinancePage: z.boolean().default(false).optional(),
  blurb: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
});

export type InsertRegisteredLender = z.infer<typeof insertRegisteredLenderSchema>;
export type RegisteredLender = typeof registeredLenders.$inferSelect;

// ─── Referral Tracking: Registered Brokers ───────────────────────────────────

export const registeredBrokers = pgTable("registered_brokers", {
  id: serial("id").primaryKey(),
  companyName: text("company_name").notNull(),
  contactName: text("contact_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  statesLicensed: text("states_licensed").array(),
  logoUrl: text("logo_url"),
  active: boolean("active").default(true).notNull(),
  commissionAmount: numeric("commission_amount", { precision: 10, scale: 2 }).default("1000").notNull(),
  notes: text("notes"),
  // Task #3366 — Finance-page publishing (mirrors registered_lenders).
  showOnFinancePage: boolean("show_on_finance_page").default(false).notNull(),
  blurb: text("blurb"),
  website: text("website"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRegisteredBrokerSchema = createInsertSchema(registeredBrokers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  companyName: z.string().min(1, "Company name is required"),
  contactName: z.string().min(1, "Contact name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().optional().nullable(),
  statesLicensed: z.array(z.string()).optional().nullable(),
  logoUrl: z.string().optional().nullable(),
  active: z.boolean().default(true).optional(),
  commissionAmount: z.string().default("1000").optional(),
  notes: z.string().optional().nullable(),
  showOnFinancePage: z.boolean().default(false).optional(),
  blurb: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
});

export type InsertRegisteredBroker = z.infer<typeof insertRegisteredBrokerSchema>;
export type RegisteredBroker = typeof registeredBrokers.$inferSelect;

// ─── Referral Tracking: Property Closing Team ────────────────────────────────

export const propertyClosingTeams = pgTable("property_closing_teams", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "cascade" }),
  acquisitionId: integer("acquisition_id").references(() => acquisitions.id, { onDelete: "set null" }),
  capturedAtStage: text("captured_at_stage").notNull(),
  capturedByUserId: integer("captured_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  capturedAt: timestamp("captured_at").defaultNow().notNull(),
  lenderId: integer("lender_id").references(() => registeredLenders.id, { onDelete: "set null" }),
  lenderContactName: text("lender_contact_name"),
  lenderEmail: text("lender_email"),
  lenderPhone: text("lender_phone"),
  lenderCompanyName: text("lender_company_name"),
  lenderIsRegistered: boolean("lender_is_registered").default(false).notNull(),
  lenderPartnerId: integer("lender_partner_id").references(() => partners.id, { onDelete: "set null" }),
  brokerId: integer("broker_id").references(() => registeredBrokers.id, { onDelete: "set null" }),
  brokerContactName: text("broker_contact_name"),
  brokerEmail: text("broker_email"),
  brokerPhone: text("broker_phone"),
  brokerCompanyName: text("broker_company_name"),
  brokerIsRegistered: boolean("broker_is_registered").default(false).notNull(),
  brokerPartnerId: integer("broker_partner_id").references(() => partners.id, { onDelete: "set null" }),
  // Task #3370 — realtor on the closing team. Realtors are `partners` rows
  // (partnerType='realtor') with no separate registered-* table, so the only
  // FK is realtorPartnerId. realtorCommissionAmount is the real-estate
  // commission captured from the HUD/closing statement; the PadPulse→realtor
  // referral invoice bills 25% of it.
  realtorPartnerId: integer("realtor_partner_id").references(() => partners.id, { onDelete: "set null" }),
  realtorContactName: text("realtor_contact_name"),
  realtorEmail: text("realtor_email"),
  realtorPhone: text("realtor_phone"),
  realtorCompanyName: text("realtor_company_name"),
  realtorIsRegistered: boolean("realtor_is_registered").default(false).notNull(),
  realtorCommissionAmount: numeric("realtor_commission_amount", { precision: 12, scale: 2 }),
  loanTypeSought: text("loan_type_sought"),
  estimatedValue: numeric("estimated_value", { precision: 14, scale: 2 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const LOAN_TYPES_SOUGHT = [
  "conventional",
  "fha",
  "va",
  "usda",
  "jumbo",
  "dscr",
  "hard_money",
  "cash_out_refinance",
  "heloc",
  "other",
] as const;
export type LoanTypeSought = typeof LOAN_TYPES_SOUGHT[number];

export const insertPropertyClosingTeamSchema = createInsertSchema(propertyClosingTeams).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  capturedAtStage: z.string().min(1),
  lenderContactName: z.string().min(1, "Lender contact name is required"),
  lenderEmail: z.string().email("Valid lender email required"),
  lenderPhone: z.string().optional().nullable(),
  lenderCompanyName: z.string().optional().nullable(),
  lenderIsRegistered: z.boolean().default(false).optional(),
  brokerContactName: z.string().min(1, "Broker contact name is required"),
  brokerEmail: z.string().email("Valid broker email required"),
  brokerPhone: z.string().optional().nullable(),
  brokerCompanyName: z.string().optional().nullable(),
  brokerIsRegistered: z.boolean().default(false).optional(),
  realtorPartnerId: z.number().int().positive().optional().nullable(),
  realtorContactName: z.string().optional().nullable(),
  realtorEmail: z.string().email("Valid realtor email required").optional().nullable(),
  realtorPhone: z.string().optional().nullable(),
  realtorCompanyName: z.string().optional().nullable(),
  realtorIsRegistered: z.boolean().default(false).optional(),
  realtorCommissionAmount: z.union([z.string(), z.number()]).optional().nullable().transform((v, ctx) => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(v);
    if (Number.isNaN(n) || n < 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Realtor commission must be a non-negative number" });
      return z.NEVER;
    }
    return n.toFixed(2);
  }),
  loanTypeSought: z.enum(LOAN_TYPES_SOUGHT).optional().nullable(),
  estimatedValue: z.union([z.string(), z.number()]).optional().nullable().transform((v, ctx) => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(v);
    if (Number.isNaN(n) || n < 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Estimated value must be a non-negative number" });
      return z.NEVER;
    }
    return n.toFixed(2);
  }),
});

export type InsertPropertyClosingTeam = z.infer<typeof insertPropertyClosingTeamSchema>;
export type PropertyClosingTeam = typeof propertyClosingTeams.$inferSelect;

// ─── Referral Tracking: Closing Team Audit Log ───────────────────────────────

export const closingTeamAuditLog = pgTable("closing_team_audit_log", {
  id: serial("id").primaryKey(),
  closingTeamId: integer("closing_team_id").notNull(),
  changedByUserId: integer("changed_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
  fieldChanged: text("field_changed").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
}, (table) => ({
  closingTeamFk: foreignKey({
    name: "closing_team_audit_log_closing_team_id_fk",
    columns: [table.closingTeamId],
    foreignColumns: [propertyClosingTeams.id],
  }).onDelete("cascade"),
}));

export type ClosingTeamAuditLog = typeof closingTeamAuditLog.$inferSelect;

// ─── Referral Tracking: Unregistered Leads ───────────────────────────────────

export const UNREGISTERED_LEAD_STATUSES = ["new", "contacted", "added_to_platform", "not_interested"] as const;
export type UnregisteredLeadStatus = typeof UNREGISTERED_LEAD_STATUSES[number];

export const unregisteredLenderLeads = pgTable("unregistered_lender_leads", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  contactName: text("contact_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  companyName: text("company_name"),
  capturedAtStage: text("captured_at_stage"),
  capturedByUserId: integer("captured_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  status: text("status").default("new").notNull(),
  adminNotes: text("admin_notes"),
  partnerId: integer("partner_id").references(() => partners.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type UnregisteredLenderLead = typeof unregisteredLenderLeads.$inferSelect;
export type UnregisteredLenderLeadWithSource = UnregisteredLenderLead & {
  propertyAddress: string | null;
  capturedByName: string | null;
  capturedByEmail: string | null;
};

export const unregisteredBrokerLeads = pgTable("unregistered_broker_leads", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  contactName: text("contact_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  companyName: text("company_name"),
  capturedAtStage: text("captured_at_stage"),
  capturedByUserId: integer("captured_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  status: text("status").default("new").notNull(),
  adminNotes: text("admin_notes"),
  partnerId: integer("partner_id").references(() => partners.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type UnregisteredBrokerLead = typeof unregisteredBrokerLeads.$inferSelect;
export type UnregisteredBrokerLeadWithSource = UnregisteredBrokerLead & {
  propertyAddress: string | null;
  capturedByName: string | null;
  capturedByEmail: string | null;
};

// ─── Referral Tracking: Referral Invoices ────────────────────────────────────

export const REFERRAL_INVOICE_STATUSES = [
  "pending_payment_instructions",
  "sent",
  "paid",
  "overdue",
  "disputed",
  "voided",
] as const;
export type ReferralInvoiceStatus = typeof REFERRAL_INVOICE_STATUSES[number];

// Task #3370 — referral invoice billing model. 'lender' covers the existing
// flat-amount lender/broker invoices; 'realtor' is the percentage model
// (25% of the realtor's captured commission).
export const REFERRAL_INVOICE_TYPES = ["lender", "realtor"] as const;
export type ReferralInvoiceType = typeof REFERRAL_INVOICE_TYPES[number];

export const referralInvoices = pgTable("referral_invoices", {
  id: serial("id").primaryKey(),
  invoiceNumber: text("invoice_number").notNull().unique(),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  closingTeamId: integer("closing_team_id").references(() => propertyClosingTeams.id, { onDelete: "set null" }),
  lenderId: integer("lender_id").references(() => registeredLenders.id, { onDelete: "set null" }),
  // Task #3366 — Broker closing-team billing parity. A closing with a registered
  // broker now generates its own PadPulse→broker invoice. partyType discriminates
  // lender vs broker invoices; brokerId links to the registered broker. The
  // lender* contact columns below double as the generic recipient fields (they
  // hold the broker's contact info when partyType = 'broker').
  brokerId: integer("broker_id").references(() => registeredBrokers.id, { onDelete: "set null" }),
  partyType: text("party_type").default("lender").notNull(),
  partnerId: integer("partner_id").references(() => partners.id, { onDelete: "set null" }),
  lenderEmail: text("lender_email").notNull(),
  lenderContactName: text("lender_contact_name").notNull(),
  lenderCompanyName: text("lender_company_name"),
  propertyAddress: text("property_address").notNull(),
  userFullName: text("user_full_name").notNull(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull().default("1000"),
  // Task #3370 — invoice-type discriminator + percentage model. Lender/broker
  // invoices keep the flat `amount` model (invoiceType='lender', basis/percent
  // null). Realtor referral invoices use the percentage model: amount =
  // commissionBasisAmount * commissionPercent/100 (PadPulse bills 25% of the
  // realtor's captured commission). The lender_* text columns above are reused
  // generically as the billed-partner contact for realtor invoices.
  invoiceType: text("invoice_type").default("lender").notNull(),
  commissionBasisAmount: numeric("commission_basis_amount", { precision: 12, scale: 2 }),
  commissionPercent: numeric("commission_percent", { precision: 5, scale: 2 }),
  status: text("status").default("pending_payment_instructions").notNull(),
  invoiceDate: text("invoice_date").notNull(),
  dueDate: text("due_date").notNull(),
  paymentInstructions: text("payment_instructions"),
  pdfDataUrl: text("pdf_data_url"),
  sentAt: timestamp("sent_at"),
  paidAt: timestamp("paid_at"),
  paymentDate: date("payment_date"),
  paidMethod: text("paid_method"),
  paidAmount: numeric("paid_amount", { precision: 10, scale: 2 }),
  disputedAt: timestamp("disputed_at"),
  disputeNotes: text("dispute_notes"),
  voidedAt: timestamp("voided_at"),
  voidReason: text("void_reason"),
  sendHistory: jsonb("send_history").default([]).notNull(),
  adminNotes: text("admin_notes"),
  lastReminderSentAt: timestamp("last_reminder_sent_at"),
  reminderCount: integer("reminder_count").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertReferralInvoiceSchema = createInsertSchema(referralInvoices).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  invoiceNumber: z.string().min(1),
  lenderEmail: z.string().email(),
  lenderContactName: z.string().min(1),
  propertyAddress: z.string().min(1),
  userFullName: z.string().min(1),
  amount: z.string().default("1000"),
  invoiceType: z.enum(REFERRAL_INVOICE_TYPES).default("lender").optional(),
  commissionBasisAmount: z.string().optional().nullable(),
  commissionPercent: z.string().optional().nullable(),
  status: z.enum(REFERRAL_INVOICE_STATUSES).default("pending_payment_instructions"),
  invoiceDate: z.string().min(1),
  dueDate: z.string().min(1),
  sendHistory: z.array(z.any()).default([]),
});

export type InsertReferralInvoice = z.infer<typeof insertReferralInvoiceSchema>;
export type ReferralInvoice = typeof referralInvoices.$inferSelect;

// Human-readable label for the party a referral invoice is billed to. The
// `lender*` contact columns are reused generically across all party types, so
// the label must be derived from `partyType` (lender vs broker) and
// `invoiceType` (lender vs realtor) rather than from the column names. Used by
// both the admin UI and the server-side PDF/email so the wording stays in sync.
export function referralInvoicePartyLabel(inv: {
  partyType?: string | null;
  invoiceType?: string | null;
}): "Lender" | "Broker" | "Realtor" {
  if (inv.partyType === "broker") return "Broker";
  if (inv.invoiceType === "realtor") return "Realtor";
  return "Lender";
}

// ─── Referral Tracking: Admin Notifications ──────────────────────────────────

export const referralInvoiceNotifications = pgTable("referral_invoice_notifications", {
  id: serial("id").primaryKey(),
  invoiceId: integer("invoice_id").notNull(),
  message: text("message").notNull(),
  type: text("type").default("overdue").notNull(),
  read: boolean("read").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  invoiceFk: foreignKey({
    name: "referral_invoice_notif_invoice_id_fk",
    columns: [table.invoiceId],
    foreignColumns: [referralInvoices.id],
  }).onDelete("cascade"),
}));

export type ReferralInvoiceNotification = typeof referralInvoiceNotifications.$inferSelect;

// ─── Refer-a-Host: Tiered Payouts ────────────────────────────────────────────

export const REFERRAL_HOST_STATEMENT_STATUSES = ["draft", "sent", "paid", "overdue", "voided"] as const;
export type ReferralHostStatementStatus = typeof REFERRAL_HOST_STATEMENT_STATUSES[number];

export const referralHostAccruals = pgTable("referral_host_accruals", {
  id: serial("id").primaryKey(),
  referralId: integer("referral_id").notNull().references(() => referrals.id, { onDelete: "cascade" }),
  referrerUserId: integer("referrer_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  referrerPartnerId: integer("referrer_partner_id"),
  referredUserId: integer("referred_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  periodMonth: text("period_month").notNull(),
  invoiceAmountCents: integer("invoice_amount_cents").notNull(),
  commissionCents: integer("commission_cents").notNull(),
  stripeInvoiceId: text("stripe_invoice_id"),
  statementId: integer("statement_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // Partial unique index — prevents duplicate accruals when Stripe webhook
  // retries race the application-level dedupe in handleReferralHostInvoicePaid.
  // NULL stripe_invoice_id is allowed (manual/CSV-driven accruals don't carry
  // one). Mirrors the existing CREATE UNIQUE INDEX in scripts/deploy-migrate.sql
  // so check-schema-deploy-parity.ts no longer flags it as a stale-index.
  stripeInvoiceIdUniq: uniqueIndex("referral_host_accruals_stripe_invoice_id_uniq")
    .on(t.stripeInvoiceId)
    .where(sql`${t.stripeInvoiceId} IS NOT NULL`),
}));

export type ReferralHostAccrual = typeof referralHostAccruals.$inferSelect;

export const REFERRAL_HOST_CREDIT_TYPES = ["welcome_referral", "host_referral"] as const;
export type ReferralHostCreditType = typeof REFERRAL_HOST_CREDIT_TYPES[number];

export const referralHostCredits = pgTable("referral_host_credits", {
  id: serial("id").primaryKey(),
  referralId: integer("referral_id").notNull().references(() => referrals.id, { onDelete: "cascade" }),
  recipientUserId: integer("recipient_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  creditType: text("credit_type").notNull(),
  amountCents: integer("amount_cents").notNull().default(2500),
  expiresAt: timestamp("expires_at").notNull(),
  redeemedAt: timestamp("redeemed_at"),
  stripeInvoiceId: text("stripe_invoice_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type ReferralHostCredit = typeof referralHostCredits.$inferSelect;

// Refer-a-Host statement lifecycle:
//   draft   — generated by quarterly job; PDF is rendered + uploaded to object
//             storage so admins can preview before approving. NO outbound email
//             or payout has been initiated yet.
//   sent    — admin clicked "Approve & Send" on the admin Quarterly Payouts
//             tab: PDF email was emailed to the referrer AND a payout was
//             initiated against Mercury Bank ACH (currently stubbed —
//             `payment_method='mercury_ach_stub'` and `payment_reference` is a
//             synthetic id; the real Mercury API integration ships in a follow-
//             up). approvedAt + approvedByUserId record who pressed Approve.
//   paid    — admin manually marked the statement paid once Mercury settled
//             (or is settled by future Mercury webhook).
//   overdue — derived flag set by admin (or future cron) when a sent statement
//             has not been paid within its window.
//   voided  — admin voided the statement; no payout will be sent.
export const referralHostStatements = pgTable("referral_host_statements", {
  id: serial("id").primaryKey(),
  referrerUserId: integer("referrer_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  referrerPartnerId: integer("referrer_partner_id"),
  periodQuarter: text("period_quarter").notNull(),
  totalCents: integer("total_cents").notNull(),
  status: text("status").default("draft").notNull(),
  pdfUrl: text("pdf_url"),
  sentAt: timestamp("sent_at"),
  paidAt: timestamp("paid_at"),
  voidedAt: timestamp("voided_at"),
  // Approval audit (populated when admin clicks "Approve & Send" on a draft).
  approvedAt: timestamp("approved_at"),
  approvedByUserId: integer("approved_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  // Payout method + external reference. `paymentMethod` is currently always
  // 'mercury_ach_stub' — the real Mercury API ships in a follow-up; the stub
  // logs the payout intent and returns a synthetic reference so this column is
  // still queryable for audit. Future values: 'mercury_ach', 'wire', 'manual'.
  paymentMethod: text("payment_method"),
  paymentReference: text("payment_reference"),
  adminNotes: text("admin_notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type ReferralHostStatement = typeof referralHostStatements.$inferSelect;

// ─── Partner Portal: Unified Partners ────────────────────────────────────────

export const PARTNER_TYPES = ["lender", "wholesaler", "realtor", "broker", "affiliate", "founder_circle", "property_manager"] as const;
export type PartnerType = typeof PARTNER_TYPES[number];

export const PARTNER_AGREEMENT_TYPES = ["lender", "realtor", "wholesaler", "broker", "affiliate", "founder_circle", "property_manager"] as const;
export type PartnerAgreementType = typeof PARTNER_AGREEMENT_TYPES[number];

// Per-type detail schemas — used for type-safe validation of application details
export const lenderDetailsSchema = z.object({
  nmlsNumber: z.string().min(1, "NMLS number is required"),
  statesLicensed: z.array(z.string()).min(1, "At least one licensed state is required"),
  loanTypes: z.array(z.string()).optional(),
  yearsInBusiness: z.number().optional().nullable(),
});

export const brokerDetailsSchema = z.object({
  licenseNumber: z.string().optional().nullable(),
  statesLicensed: z.array(z.string()).optional(),
  yearsInBusiness: z.number().optional().nullable(),
}).passthrough();

export const LISTING_FIELD_KEYS = [
  "address", "city", "state", "zipCode",
  "askingPrice", "arv",
  "bedrooms", "bathrooms", "sqft", "lotSize", "garageSqft", "yearBuilt",
  "description", "dealNotes",
  "sellerFinancing", "interestRate", "termYears", "downPaymentPercent",
  "estimatedDaysAvailable", "status",
  "photos",
] as const;
export type ListingFieldKey = typeof LISTING_FIELD_KEYS[number];

export const airtableFieldMapSchema = z.record(z.string(), z.string());
export type AirtableFieldMap = z.infer<typeof airtableFieldMapSchema>;

export const wholesalerDetailsSchema = z.object({
  markets: z.array(z.string()).min(1, "At least one market is required"),
  dealVolume: z.string().optional().nullable(),
  airtableKey: z.string().optional().nullable(),
  airtableBaseId: z.string().optional().nullable(),
  airtableTableName: z.string().optional().nullable(),
  airtableFieldMap: airtableFieldMapSchema.optional().nullable(),
  airtableLastSyncAt: z.string().optional().nullable(),
  airtableLastSyncStatus: z.string().optional().nullable(),
  airtableScheduleFailCount: z.number().optional().nullable(),
  airtableScheduleLastAlertAt: z.string().optional().nullable(),
});

export const realtorDetailsSchema = z.object({
  licenseNumber: z.string().min(1, "License number is required"),
  licenseState: z.string().min(2, "License state is required"),
  colivingCertified: z.boolean().optional().default(false),
  certifications: z.array(z.string()).optional(),
  colivingDealsCount: z.number().int().nonnegative().optional(),
});

export type LenderDetails = z.infer<typeof lenderDetailsSchema>;
export type WholesalerDetails = z.infer<typeof wholesalerDetailsSchema>;
export type RealtorDetails = z.infer<typeof realtorDetailsSchema>;

export const PARTNER_STATUSES = ["active", "suspended"] as const;
export type PartnerStatus = typeof PARTNER_STATUSES[number];

export const partners = pgTable("partners", {
  id: serial("id").primaryKey(),
  partnerType: text("partner_type").notNull(),
  companyName: text("company_name").notNull(),
  contactName: text("contact_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  logoUrl: text("logo_url"),
  active: boolean("active").default(true).notNull(),
  suspended: boolean("suspended").default(false).notNull(),
  suspensionReason: text("suspension_reason"),
  // Task #3495 — screen-before-sign onboarding. A conditionally-approved partner
  // is created with active=true + awaitingAgreement=true: they can log in with
  // their emailed one-time password but are routed straight to the agreement and
  // blocked from portal APIs until they e-sign (which clears this flag).
  awaitingAgreement: boolean("awaiting_agreement").default(false).notNull(),
  commissionAmount: numeric("commission_amount", { precision: 10, scale: 2 }).default("1000").notNull(),
  commissionType: text("commission_type").default("flat").notNull(),
  premiumTier: boolean("premium_tier").default(false).notNull(),
  // Task #3370 — gates whether a realtor partner appears in the public
  // /app/find-realtor directory. Defaults true so existing active realtors stay
  // visible on rollout; admin can toggle off without suspending the account.
  marketingVisible: boolean("marketing_visible").default(true).notNull(),
  details: jsonb("details").$type<Record<string, unknown>>().default(sql`'{}'::jsonb`).notNull(),
  applicationId: integer("application_id"),
  sourceLenderId: integer("source_lender_id"),
  sourceBrokerId: integer("source_broker_id"),
  needsReclassification: boolean("needs_reclassification").default(false).notNull(),
  loginHistory: jsonb("login_history").default([]).notNull(),
  notes: text("notes"),
  passwordHash: text("password_hash"),
  notificationPrefs: jsonb("notification_prefs").default({}).notNull(),
  lastLoginAt: timestamp("last_login_at"),
  previousLoginAt: timestamp("previous_login_at"),
  lastViewedReferralsAt: timestamp("last_viewed_referrals_at"),
  referralCode: text("referral_code").unique(),
  // Task #1501 — TOTP (two-factor authentication) for partner portal logins.
  totpSecret: text("totp_secret"),
  totpEnabled: boolean("totp_enabled").default(false).notNull(),
  totpBackupCodes: text("totp_backup_codes").array(),
  totpEnrolledAt: timestamp("totp_enrolled_at", { withTimezone: true }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertPartnerSchema = createInsertSchema(partners).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerType: z.enum(PARTNER_TYPES),
  companyName: z.string().min(1, "Company name is required"),
  contactName: z.string().min(1, "Contact name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().optional().nullable(),
  active: z.boolean().default(true).optional(),
  suspended: z.boolean().default(false).optional(),
  suspensionReason: z.string().optional().nullable(),
  commissionAmount: z.string().default("1000").optional(),
  commissionType: z.enum(["flat", "percent"]).default("flat").optional(),
  premiumTier: z.boolean().default(false).optional(),
  marketingVisible: z.boolean().default(true).optional(),
  awaitingAgreement: z.boolean().default(false).optional(),
  details: z.record(z.unknown()).default({}).optional(),
  needsReclassification: z.boolean().default(false).optional(),
  notes: z.string().optional().nullable(),
});

export type InsertPartner = z.infer<typeof insertPartnerSchema>;
export type Partner = typeof partners.$inferSelect;

// ─── Partner Portal: Applications ────────────────────────────────────────────

export const PARTNER_APPLICATION_STATUSES = ["pending", "conditionally_approved", "approved", "rejected", "needs_more_info"] as const;
export type PartnerApplicationStatus = typeof PARTNER_APPLICATION_STATUSES[number];

export const partnerApplications = pgTable("partner_applications", {
  id: serial("id").primaryKey(),
  partnerType: text("partner_type").notNull(),
  status: text("status").default("pending").notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  companyName: text("company_name").notNull(),
  website: text("website"),
  // Task #3495 — required free-text "tell us about your business" captured by the
  // screening intake (the public Apply form). Nullable in DB for legacy rows.
  description: text("description"),
  details: jsonb("details").default({}).notNull(),
  adminNotes: text("admin_notes"),
  reviewedAt: timestamp("reviewed_at"),
  reviewedByUserId: integer("reviewed_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  setupToken: text("setup_token"),
  setupTokenExpiresAt: timestamp("setup_token_expires_at"),
  partnerId: integer("partner_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertPartnerApplicationSchema = createInsertSchema(partnerApplications).omit({
  id: true,
  status: true,
  adminNotes: true,
  reviewedAt: true,
  reviewedByUserId: true,
  setupToken: true,
  setupTokenExpiresAt: true,
  partnerId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerType: z.enum(PARTNER_TYPES),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().optional().nullable(),
  companyName: z.string().min(1, "Company name is required"),
  website: z.string().url("Valid URL required").optional().nullable(),
  details: z.record(z.unknown()).default({}).optional(),
}).superRefine((data, ctx) => {
  if (!data.details) return;
  const detailsMap: Record<string, typeof lenderDetailsSchema | typeof wholesalerDetailsSchema | typeof realtorDetailsSchema | typeof brokerDetailsSchema | undefined> = {
    lender: lenderDetailsSchema,
    wholesaler: wholesalerDetailsSchema,
    realtor: realtorDetailsSchema,
    broker: brokerDetailsSchema,
  };
  const detailSchema = detailsMap[data.partnerType];
  if (!detailSchema) return;
  const result = detailSchema.safeParse(data.details);
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue({ ...issue, path: ["details", ...issue.path] });
    }
  }
});

export type InsertPartnerApplication = z.infer<typeof insertPartnerApplicationSchema>;

// Task #3495 / #3496 — screen-before-sign intake. The public Apply form collects
// basic business info plus a REQUIRED free-text description, the states the
// partner is registered in (required), and an NMLS/license number (required for
// the licensed types — lender/realtor/broker). No per-type strict `details`
// requirements (the agreement is what they sign later, after a soft approve), so
// this is still looser than `insertPartnerApplicationSchema`.
//
// `SCREENING_LICENSE_REQUIRED_TYPES` mirrors the `requiresLicense` gate on the
// later agreement page (PartnerAgreementApply.tsx) so "if applicable" stays
// consistent across the two steps.
export const SCREENING_LICENSE_REQUIRED_TYPES: PartnerType[] = ["lender", "realtor", "broker"];

export const partnerScreeningSchema = z.object({
  partnerType: z.enum(PARTNER_TYPES),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().min(7, "A valid phone number is required"),
  companyName: z.string().min(1, "Company name is required"),
  website: z.string().url("Enter a valid URL (including https://)").optional().or(z.literal("")),
  description: z.string().min(10, "Please tell us a bit about your business"),
  statesRegistered: z.array(z.string().min(1)).min(1, "List at least one state you're registered in"),
  licenseNumber: z.string().optional().or(z.literal("")),
  roomsManaged: z.string().optional().or(z.literal("")),
}).superRefine((data, ctx) => {
  if (SCREENING_LICENSE_REQUIRED_TYPES.includes(data.partnerType) && !data.licenseNumber?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["licenseNumber"],
      message: "An NMLS / license number is required",
    });
  }
  if (data.partnerType === "property_manager" && !data.roomsManaged?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["roomsManaged"],
      message: "Please enter the number of rooms you currently manage on PadSplit",
    });
  }
});
export type PartnerScreeningInput = z.infer<typeof partnerScreeningSchema>;
export type PartnerApplication = typeof partnerApplications.$inferSelect;

// ─── Partner Portal: Payment Instructions Settings ───────────────────────────

export const paymentInstructionsSettings = pgTable("payment_instructions_settings", {
  id: serial("id").primaryKey(),
  preferredMethod: text("preferred_method"),
  bankName: text("bank_name"),
  accountName: text("account_name"),
  accountNumber: text("account_number"),
  routingNumber: text("routing_number"),
  wireInstructions: text("wire_instructions"),
  achMemoFormat: text("ach_memo_format"),
  zelleInfo: text("zelle_info"),
  notes: text("notes"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type PaymentInstructionsSettings = typeof paymentInstructionsSettings.$inferSelect;

export const updatePaymentInstructionsSchema = z.object({
  preferredMethod: z.string().optional().nullable(),
  bankName: z.string().optional().nullable(),
  accountName: z.string().optional().nullable(),
  accountNumber: z.string().optional().nullable(),
  routingNumber: z.string().optional().nullable(),
  wireInstructions: z.string().optional().nullable(),
  achMemoFormat: z.string().optional().nullable(),
  zelleInfo: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

// ─── Co-Living Conference Promo Banner Settings ─────────────────────────────

export const coLivingConferencePromoSettings = pgTable("co_living_conference_promo_settings", {
  id: serial("id").primaryKey(),
  active: boolean("active").default(false).notNull(),
  text: text("text"),
  ctaLabel: text("cta_label"),
  startAt: timestamp("start_at"),
  endAt: timestamp("end_at"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type CoLivingConferencePromoSettings =
  typeof coLivingConferencePromoSettings.$inferSelect;

export const updateCoLivingConferencePromoSchema = z
  .object({
    active: z.boolean().optional(),
    text: z.string().max(500).optional().nullable(),
    ctaLabel: z.string().max(100).optional().nullable(),
    startAt: z.coerce.date().optional().nullable(),
    endAt: z.coerce.date().optional().nullable(),
  })
  .refine(
    (data) =>
      !data.startAt ||
      !data.endAt ||
      data.endAt.getTime() > data.startAt.getTime(),
    { message: "End time must be after the start time.", path: ["endAt"] },
  );
export type UpdateCoLivingConferencePromo = z.infer<typeof updateCoLivingConferencePromoSchema>;

// ─── Founder Circle Launch Date Settings ────────────────────────────────────
// Singleton row that lets admins change the Founder Circle launch / countdown
// date without redeploying. The public `/api/founding-access-date` endpoint
// reads this row first and falls back to the `VITE_FOUNDING_ACCESS_DATE` env
// var (or the compiled default) when the row is absent or `launch_date` is
// null. See `client/src/config/founding-access.ts`.

export const foundingAccessSettings = pgTable("founding_access_settings", {
  id: serial("id").primaryKey(),
  launchDate: timestamp("launch_date", { withTimezone: true }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type FoundingAccessSettings = typeof foundingAccessSettings.$inferSelect;

export const updateFoundingAccessSchema = z.object({
  launchDate: z.coerce.date().nullable(),
});
export type UpdateFoundingAccess = z.infer<typeof updateFoundingAccessSchema>;

// ─── Messaging Outage Settings ──────────────────────────────────────────────
// Singleton row controlling the global PadSplit message-send outage banner.
// When `enabled` is true, every outbound compose box across all hosts is
// replaced with a temporary-outage banner directing the host to send directly
// through PadSplit, and the manual send routes refuse to enqueue new outbound
// messages. Mirrors the founding-access singleton pattern. A missing row is
// treated as `enabled = false` (no outage).
export const messagingOutageSettings = pgTable("messaging_outage_settings", {
  id: serial("id").primaryKey(),
  enabled: boolean("enabled").default(false).notNull(),
  // Admin email captured from `getAdminActorLabel(req)` on the last save — may
  // be NULL if the session lacks an attributable identity.
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type MessagingOutageSettings = typeof messagingOutageSettings.$inferSelect;

export const updateMessagingOutageSchema = z.object({
  enabled: z.boolean(),
});
export type UpdateMessagingOutage = z.infer<typeof updateMessagingOutageSchema>;

// ─── Global AI Settings (house voice) ────────────────────────────────────────
// Singleton row holding the platform-global, super-admin-editable "AI Reply
// Voice & Service Standard" (house voice) for the AI Messaging Suggest/Improve
// feature. Exactly one tone wrapper applies across every customer org — only
// super-admins may read/edit it. The non-editable locked safety contract lives
// in code (server/services/kb-retrieval.ts) and is always applied first; this
// editable house voice sits between it and the per-call task instruction. The
// company KB, property KB, and approved memory stay per-org — only this tone
// wrapper is global. `housePrompt = NULL` means "use the seeded default voice"
// (DEFAULT_HOUSE_VOICE in kb-retrieval.ts); Reset restores NULL. Mirrors the
// founding-access / messaging-outage singleton pattern.
export const globalAiSettings = pgTable("global_ai_settings", {
  id: serial("id").primaryKey(),
  // NULL => fall back to the seeded default house voice. A non-null value is
  // the super-admin's override, applied immediately across all orgs.
  housePrompt: text("house_prompt"),
  // Super-admin email captured on the last save — may be NULL if the session
  // lacks an attributable identity.
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type GlobalAiSettings = typeof globalAiSettings.$inferSelect;

export const updateGlobalAiSettingsSchema = z.object({
  // Empty / whitespace-only resolves to NULL (= seeded default) server-side.
  // Capped to keep the wrapper bounded; the locked contract is separate.
  prompt: z.string().max(10000),
});
export type UpdateGlobalAiSettings = z.infer<typeof updateGlobalAiSettingsSchema>;

// Singleton row tuning the daily churn-risk digest (server/services/churn-risk-digest.ts).
// A missing row preserves the original hardcoded behavior: enabled, sent at 08:00
// UTC to the admin email set, alerting only on "At risk" / "Dormant" crossings.
// `recipients` is an optional override — when empty/NULL the digest falls back to
// storage.getAdminEmails(). The three `alert*` flags pick which worsening
// transitions trigger an alert.
export const churnRiskAlertSettings = pgTable("churn_risk_alert_settings", {
  id: serial("id").primaryKey(),
  // Master switch — when false the daily cron skips the digest entirely.
  enabled: boolean("enabled").default(true).notNull(),
  // Hour of day (0-23, UTC) at which the cron fires the digest.
  sendHourUtc: integer("send_hour_utc").default(8).notNull(),
  // Optional recipient override. Empty array => fall back to getAdminEmails().
  recipients: text("recipients").array().default(sql`ARRAY[]::text[]`).notNull(),
  // Which worsening transitions trigger an alert.
  alertCoolingOff: boolean("alert_cooling_off").default(false).notNull(),
  alertAtRisk: boolean("alert_at_risk").default(true).notNull(),
  alertDormant: boolean("alert_dormant").default(true).notNull(),
  // Admin email captured from `getAdminActorLabel(req)` on the last save.
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type ChurnRiskAlertSettings = typeof churnRiskAlertSettings.$inferSelect;

export const updateChurnRiskAlertSchema = z.object({
  enabled: z.boolean(),
  sendHourUtc: z.number().int().min(0).max(23),
  recipients: z.array(z.string().trim().email()).max(50),
  alertCoolingOff: z.boolean(),
  alertAtRisk: z.boolean(),
  alertDormant: z.boolean(),
});
export type UpdateChurnRiskAlert = z.infer<typeof updateChurnRiskAlertSchema>;

// Task #2036 — Audit log of every admin save to the Founder Circle launch
// date. `previousLaunchDate` is NULL when there was no prior row (or the
// prior row had a NULL launch_date, i.e. the public surface was using the
// env-var / compiled fallback). `newLaunchDate` is NULL when the admin
// clears the override to fall back to the env var. `changedBy` is the
// admin's email captured from `getAdminActorLabel(req)` — may be NULL if
// the session somehow lacks an attributable identity.
export const foundingAccessSettingsHistory = pgTable("founding_access_settings_history", {
  id: serial("id").primaryKey(),
  previousLaunchDate: timestamp("previous_launch_date", { withTimezone: true }),
  newLaunchDate: timestamp("new_launch_date", { withTimezone: true }),
  changedBy: text("changed_by"),
  changedAt: timestamp("changed_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  // DESC modifier caused Drizzle to re-detect the index as changed on every
  // push (Drizzle introspection quirk). Switching to ASC (default) is safe —
  // the history query applies its own ORDER BY desc(changedAt) at query time.
  changedAtIdx: index("founding_access_settings_history_changed_at_idx").on(t.changedAt),
}));

export type FoundingAccessSettingsHistoryEntry = typeof foundingAccessSettingsHistory.$inferSelect;

// ─── Auth/Session Storage (managed by connect-pg-simple) ────────────────────

export const sessionTable = pgTable("session", {
  sid: varchar("sid").primaryKey(),
  sess: json("sess").notNull(),
  expire: timestamp("expire", { mode: "date", precision: 6 }).notNull(),
}, (table) => ({
  expireIdx: index("IDX_session_expire").on(table.expire),
}));

// ─── Partner Portal: Auth Codes (magic-link 6-digit codes) ──────────────────

export const partnerAuthCodes = pgTable("partner_auth_codes", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  code: text("code").notNull(),
  expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
  usedAt: timestamp("used_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (t) => ({
  // Task #825: The daily `auth-codes-cleanup` cron deletes rows where
  // `expires_at < cutoff OR (used_at IS NOT NULL AND used_at < cutoff)`. Without
  // these indexes the DELETE seq-scans the whole table; with them Postgres can
  // BitmapOr two cheap range scans even as the table grows.
  expiresAtIdx: index("partner_auth_codes_expires_at_idx").on(t.expiresAt),
  // Partial index — `used_at` is NULL for the vast majority of rows (every
  // sent-but-not-verified code), so the partial form keeps the index small
  // while still covering the `used_at < cutoff` branch of the prune.
  usedAtIdx: index("partner_auth_codes_used_at_idx")
    .on(t.usedAt)
    .where(sql`${t.usedAt} IS NOT NULL`),
}));

export type PartnerAuthCode = typeof partnerAuthCodes.$inferSelect;

// ─── Vendor Portal: Auth Codes (SMS 6-digit OTP) ─────────────────────────────
// Persisted in Postgres so the issued OTP is visible to every Node instance —
// otherwise a vendor could request the code on Server A and have verify-otp
// land on Server B with no record of it (Task #764). Mirrors the
// partnerAuthCodes shape; one row per send, marked usedAt on successful verify.
export const vendorAuthCodes = pgTable("vendor_auth_codes", {
  id: serial("id").primaryKey(),
  vendorId: integer("vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  // FK declared at the table level to override the auto-generated name —
  // `vendor_auth_codes_vendor_team_member_id_vendor_team_members_id_fk` is
  // 65 chars and exceeds Postgres's 63-char identifier limit.
  vendorTeamMemberId: integer("vendor_team_member_id"),
  normalizedPhone: text("normalized_phone").notNull(),
  code: text("code").notNull(),
  expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
  usedAt: timestamp("used_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (t) => ({
  phoneIdx: index("vendor_auth_codes_phone_idx").on(t.normalizedPhone),
  // Task #825: Mirror of the partner_auth_codes indexes — supports the daily
  // `auth-codes-cleanup` prune (expires_at OR used_at < cutoff) so the DELETE
  // stays index-driven once the table grows past trivial sizes.
  expiresAtIdx: index("vendor_auth_codes_expires_at_idx").on(t.expiresAt),
  usedAtIdx: index("vendor_auth_codes_used_at_idx")
    .on(t.usedAt)
    .where(sql`${t.usedAt} IS NOT NULL`),
  vendorTeamMemberFk: foreignKey({
    name: "vendor_auth_codes_team_member_fk",
    columns: [t.vendorTeamMemberId],
    foreignColumns: [vendorTeamMembers.id],
  }).onDelete("cascade"),
}));

export type VendorAuthCode = typeof vendorAuthCodes.$inferSelect;

// ─── Member Portal (members.padpulse.io) ─────────────────────────────────────
// Third external portal (after partner/owner/vendor). Direct (off-PadSplit)
// members only — gated on members.type = 'direct' at the app layer. All tables
// here are purely additive; no existing column/table is modified. The "org"
// axis in this app is the host appUsers.id (members.userId), so portal rows
// scope through the member → host relationship, never a separate org_id.

// Portal access flag. One row per invited direct member.
export const memberPortalAccounts = pgTable("member_portal_accounts", {
  id: serial("id").primaryKey(),
  memberId: integer("member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  status: text("status").default("invited").notNull(), // 'invited' | 'active' | 'disabled'
  lastLoginAt: timestamp("last_login_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
}, (t) => ({
  memberIdUnique: uniqueIndex("member_portal_accounts_member_id_unique").on(t.memberId),
}));
export type MemberPortalAccount = typeof memberPortalAccounts.$inferSelect;

// Passwordless login codes (email magic-link code OR SMS OTP). Mirrors the
// partnerAuthCodes / vendorAuthCodes shape; one row per send, marked usedAt on
// successful verify. Code is stored hashed (never raw).
export const memberAuthCodes = pgTable("member_auth_codes", {
  id: serial("id").primaryKey(),
  memberId: integer("member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  channel: varchar("channel", { length: 10 }).notNull(), // 'email' | 'sms'
  codeHash: text("code_hash").notNull(),
  expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
  usedAt: timestamp("used_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (t) => ({
  memberIdIdx: index("member_auth_codes_member_id_idx").on(t.memberId),
  expiresAtIdx: index("member_auth_codes_expires_at_idx").on(t.expiresAt),
  usedAtIdx: index("member_auth_codes_used_at_idx")
    .on(t.usedAt)
    .where(sql`${t.usedAt} IS NOT NULL`),
}));
export type MemberAuthCode = typeof memberAuthCodes.$inferSelect;

// One house (group) chat per property. Participants = the property's direct
// members + the host. Native PadPulse only (off-PadSplit properties).
export const houseConversations = pgTable("house_conversations", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // NULL means a permanently deleted property's read-only, account-owned archive.
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  archivedPropertyName: text("archived_property_name"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  // When the host last opened this house chat. Member messages newer than this
  // (or all of them when null) count as unread for the host's Messages inbox.
  hostLastReadAt: timestamp("host_last_read_at", { withTimezone: true }),
}, (t) => ({
  propertyIdUnique: uniqueIndex("house_conversations_property_id_unique").on(t.propertyId),
}));
export type HouseConversation = typeof houseConversations.$inferSelect;

// House-chat messages. Kept in a dedicated table (not member_chats) so the
// rigid 1:1 member timeline — and the MemberChat type that flows through hot
// operational query paths — is left completely untouched.
export const houseChatMessages = pgTable("house_chat_messages", {
  id: bigserial("id", { mode: "number" }).primaryKey(),
  houseConversationId: integer("house_conversation_id").notNull(),
  propertyId: integer("property_id").notNull(),
  senderType: varchar("sender_type", { length: 20 }).notNull(), // 'member' | 'host'
  senderMemberId: integer("sender_member_id"), // set when a member authored
  senderUserId: integer("sender_user_id"),     // set when the host authored
  senderName: varchar("sender_name", { length: 255 }).notNull(),
  messageText: text("message_text").notNull(),
  // 'text' (default) or 'maintenance' — a maintenance post is a live card that
  // reflects linkedTaskId's current status, not a plain message bubble.
  kind: varchar("kind", { length: 20 }).default("text").notNull(),
  // The tasks.id this message is about (maintenance kind only); nullable.
  linkedTaskId: integer("linked_task_id"),
  // Message-level workflow (host-only, inbox-parity): assign a teammate to
  // follow up on this message + flag it as awaiting the member's reply. These
  // are independent of any linkedTaskId. assigneeId is an app_users.id.
  assigneeId: integer("assignee_id"),
  assigneeName: varchar("assignee_name", { length: 255 }),
  waitingReplyAt: timestamp("waiting_reply_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  convIdx: index("house_chat_messages_conversation_idx").on(t.houseConversationId, t.createdAt),
  // Explicit short FK name — the auto-generated
  // `house_chat_messages_house_conversation_id_house_conversations_id_fk` is
  // 67 chars and exceeds Postgres's 63-char identifier limit.
  houseConversationFk: foreignKey({
    name: "house_chat_messages_conversation_fk",
    columns: [t.houseConversationId],
    foreignColumns: [houseConversations.id],
  }).onDelete("cascade"),
}));
export type HouseChatMessage = typeof houseChatMessages.$inferSelect;

// Host-only internal notes attached to a specific house-chat message — the
// house-chat analog of the inbox "thread notes" (member_chat_threads). NEVER
// surfaced to members: only the host org (userId) can read/write them, so the
// host can annotate a housemate's message ("called plumber", "waiting on part")
// without the members seeing it. Cascades away with the message it hangs off.
export const houseChatMessageNotes = pgTable("house_chat_message_notes", {
  id: serial("id").primaryKey(),
  houseChatMessageId: bigint("house_chat_message_id", { mode: "number" }).notNull(),
  // Owning host org (the property owner) — the read/write scope for the note.
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Which team member actually wrote it (app-user id) + a denormalized name so
  // the note renders an author without a join even after roster changes.
  authorUserId: integer("author_user_id").notNull(),
  authorName: varchar("author_name", { length: 255 }).notNull(),
  body: text("body").notNull(),
  // @-mention target app-user ids (owner + accepted teammates), same as the
  // inbox thread notes (member_chat_threads.mentions). Drives purple-token
  // highlighting + bell/email tagging of the mentioned teammate.
  mentions: jsonb("mentions").$type<number[]>().default([]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  messageIdx: index("house_chat_message_notes_message_idx").on(t.houseChatMessageId, t.createdAt),
  // Explicit short FK name — the auto-generated
  // `house_chat_message_notes_house_chat_message_id_house_chat_messages_id_fk`
  // is 72 chars and exceeds Postgres's 63-char identifier limit.
  messageFk: foreignKey({
    name: "house_chat_message_notes_message_fk",
    columns: [t.houseChatMessageId],
    foreignColumns: [houseChatMessages.id],
  }).onDelete("cascade"),
}));
export type HouseChatMessageNote = typeof houseChatMessageNotes.$inferSelect;

// Member peer reviews — housemate-to-housemate sentiment. One (updatable) row
// per (reviewer, subject) pair. Members see only aggregates about others (never
// who rated them); the host sees per-resident averages + comments as a sentiment
// signal. Scoped to the owning host org + property so a member can only rate
// current housemates.
export const memberPeerReviews = pgTable("member_peer_reviews", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  reviewerMemberId: integer("reviewer_member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  subjectMemberId: integer("subject_member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(), // 1..5
  comment: text("comment"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  pairUnique: uniqueIndex("member_peer_reviews_pair_unique").on(t.reviewerMemberId, t.subjectMemberId),
  subjectIdx: index("member_peer_reviews_subject_idx").on(t.subjectMemberId),
}));
export type MemberPeerReview = typeof memberPeerReviews.$inferSelect;

// Per-member notification channel prefs + mute.
export const memberNotificationPrefs = pgTable("member_notification_prefs", {
  memberId: integer("member_id").primaryKey().references(() => members.id, { onDelete: "cascade" }),
  emailOn: boolean("email_on").default(true).notNull(),
  smsOn: boolean("sms_on").default(true).notNull(),
  mutedUntil: timestamp("muted_until", { mode: "date" }),
});
export type MemberNotificationPref = typeof memberNotificationPrefs.$inferSelect;

// Coalescing ledger — last time we notified a member about a conversation, and
// when they last opened it. Powers the COALESCE_WINDOW debounce.
export const memberMessageNotifyState = pgTable("member_message_notify_state", {
  memberId: integer("member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  conversationKey: text("conversation_key").notNull(), // 'house:{id}' | 'host:{memberId}'
  lastNotifiedAt: timestamp("last_notified_at", { mode: "date" }).defaultNow().notNull(),
  lastOpenedAt: timestamp("last_opened_at", { mode: "date" }),
}, (t) => ({
  pk: primaryKey({ columns: [t.memberId, t.conversationKey] }),
}));
export type MemberMessageNotifyState = typeof memberMessageNotifyState.$inferSelect;

// Member-side presence — "is this member actively viewing conversation X right
// now" — used to suppress redundant notifications. Heartbeat-based, like
// member_chat_presence on the host side.
export const memberPortalPresence = pgTable("member_portal_presence", {
  memberId: integer("member_id").notNull().references(() => members.id, { onDelete: "cascade" }),
  conversationKey: text("conversation_key").notNull(),
  state: varchar("state", { length: 20 }).default("viewing").notNull(),
  heartbeatAt: timestamp("heartbeat_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  pk: primaryKey({ columns: [t.memberId, t.conversationKey] }),
}));
export type MemberPortalPresence = typeof memberPortalPresence.$inferSelect;

// ─── Partner Portal: Threads (initiated by main-app users or portal owners) ──
// The counterparty is EITHER a main-app user (appUserId) OR an owner-portal
// owner (ownerId) — exactly one is set. Owner threads reuse the denormalized
// appUserName/Email/Phone columns for the owner's contact info so the partner
// portal UI renders them without changes. Every PM/app-side query filters on
// appUserId, so owner threads are invisible to PMs by construction.

export const partnerThreads = pgTable("partner_threads", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  appUserId: integer("app_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  ownerId: integer("owner_id").references(() => owners.id, { onDelete: "cascade" }),
  appUserName: text("app_user_name"),
  appUserEmail: text("app_user_email"),
  appUserPhone: text("app_user_phone"),
  subject: text("subject"),
  threadType: text("thread_type").notNull().default("general"),
  propertyId: integer("property_id"),
  propertyAddress: text("property_address"),
  unreadByPartner: integer("unread_by_partner").default(0).notNull(),
  unreadByUser: integer("unread_by_user").default(0).notNull(),
  lastMessageAt: timestamp("last_message_at", { mode: "date" }).defaultNow().notNull(),
  lastMessagePreview: text("last_message_preview"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  ownerIdx: index("idx_partner_threads_owner").on(table.ownerId),
}));

export const insertPartnerThreadSchema = createInsertSchema(partnerThreads).omit({
  id: true,
  unreadByPartner: true,
  unreadByUser: true,
  lastMessageAt: true,
  lastMessagePreview: true,
  createdAt: true,
}).extend({
  partnerId: z.number().int(),
  appUserId: z.number().int().optional().nullable(),
  ownerId: z.number().int().optional().nullable(),
  threadType: z.string().default("general").optional(),
});

export type InsertPartnerThread = z.infer<typeof insertPartnerThreadSchema>;
export type PartnerThread = typeof partnerThreads.$inferSelect;

// ─── Partner Portal: Messages ─────────────────────────────────────────────────

export const partnerMessages = pgTable("partner_messages", {
  id: serial("id").primaryKey(),
  threadId: integer("thread_id").notNull().references(() => partnerThreads.id, { onDelete: "cascade" }),
  senderType: text("sender_type").notNull(),
  senderName: text("sender_name"),
  content: text("content").notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export const insertPartnerMessageSchema = createInsertSchema(partnerMessages).omit({
  id: true,
  createdAt: true,
}).extend({
  threadId: z.number().int(),
  senderType: z.enum(["user", "partner"]),
  content: z.string().min(1).max(1000),
});

export type InsertPartnerMessage = z.infer<typeof insertPartnerMessageSchema>;
export type PartnerMessage = typeof partnerMessages.$inferSelect;

// ─── Partner Portal: Notifications ───────────────────────────────────────────

export const partnerNotifications = pgTable("partner_notifications", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  link: text("link"),
  read: boolean("read").default(false).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export type PartnerNotification = typeof partnerNotifications.$inferSelect;

// ─── Lender Portal: Lead Pipeline ────────────────────────────────────────────

export const LENDER_LEAD_STATUSES = [
  "new",
  "in_contact",
  "application_submitted",
  "under_review",
  "approved",
  "closed",
  "dead",
] as const;
export type LenderLeadStatus = typeof LENDER_LEAD_STATUSES[number];

export const LENDER_LEAD_DEAD_REASONS = [
  "chose_another_lender",
  "property_fell_through",
  "unresponsive",
  "other",
] as const;
export type LenderLeadDeadReason = typeof LENDER_LEAD_DEAD_REASONS[number];

export const lenderLeadPipeline = pgTable("lender_lead_pipeline", {
  id: serial("id").primaryKey(),
  closingTeamId: integer("closing_team_id").notNull(),
  lenderPartnerId: integer("lender_partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  status: text("status").default("new").notNull(),
  deadReason: text("dead_reason"),
  privateNotes: text("private_notes"),
  statusChangedAt: timestamp("status_changed_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  closingTeamFk: foreignKey({
    name: "lender_lead_pipeline_closing_team_id_fk",
    columns: [table.closingTeamId],
    foreignColumns: [propertyClosingTeams.id],
  }).onDelete("cascade"),
}));

// ─── Wholesaler Listings ──────────────────────────────────────────────────────

export const wholesalerListings = pgTable("wholesaler_listings", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  address: text("address").notNull(),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  askingPrice: numeric("asking_price", { precision: 12, scale: 2 }).notNull(),
  arv: numeric("arv", { precision: 12, scale: 2 }),
  bedrooms: integer("bedrooms"),
  bathrooms: numeric("bathrooms", { precision: 3, scale: 1 }),
  sqft: integer("sqft"),
  lotSize: numeric("lot_size", { precision: 10, scale: 2 }),
  garageSqft: numeric("garage_sqft", { precision: 10, scale: 2 }),
  yearBuilt: integer("year_built"),
  description: text("description"),
  photos: text("photos").array().notNull(),
  dealNotes: text("deal_notes"),
  sellerFinancing: boolean("seller_financing").default(false).notNull(),
  interestRate: numeric("interest_rate", { precision: 5, scale: 2 }),
  termYears: integer("term_years"),
  downPaymentPercent: numeric("down_payment_percent", { precision: 5, scale: 2 }),
  estimatedDaysAvailable: integer("estimated_days_available"),
  status: text("status").default("active").notNull(),
  views: integer("views").default(0).notNull(),
  uniqueViewers: integer("unique_viewers").array().notNull(),
  airtableRecordId: text("airtable_record_id"),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  airtableRecordIdx: uniqueIndex("wholesaler_listings_partner_airtable_record_idx")
    .on(table.partnerId, table.airtableRecordId),
}));

export const insertLenderLeadPipelineSchema = createInsertSchema(lenderLeadPipeline).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  statusChangedAt: true,
});

export type InsertLenderLeadPipeline = z.infer<typeof insertLenderLeadPipelineSchema>;
export type LenderLeadPipeline = typeof lenderLeadPipeline.$inferSelect;

// ─── Broker Portal: Lead Pipeline ────────────────────────────────────────────

export const BROKER_LEAD_STATUSES = LENDER_LEAD_STATUSES;
export type BrokerLeadStatus = typeof BROKER_LEAD_STATUSES[number];

export const BROKER_LEAD_DEAD_REASONS = [
  "chose_another_broker",
  "property_fell_through",
  "unresponsive",
  "other",
] as const;
export type BrokerLeadDeadReason = typeof BROKER_LEAD_DEAD_REASONS[number];

export const brokerLeadPipeline = pgTable("broker_lead_pipeline", {
  id: serial("id").primaryKey(),
  closingTeamId: integer("closing_team_id").notNull(),
  brokerPartnerId: integer("broker_partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  status: text("status").default("new").notNull(),
  deadReason: text("dead_reason"),
  privateNotes: text("private_notes"),
  statusChangedAt: timestamp("status_changed_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  closingTeamFk: foreignKey({
    name: "broker_lead_pipeline_closing_team_id_fk",
    columns: [table.closingTeamId],
    foreignColumns: [propertyClosingTeams.id],
  }).onDelete("cascade"),
}));

export const insertBrokerLeadPipelineSchema = createInsertSchema(brokerLeadPipeline).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  statusChangedAt: true,
});

export type InsertBrokerLeadPipeline = z.infer<typeof insertBrokerLeadPipelineSchema>;
export type BrokerLeadPipeline = typeof brokerLeadPipeline.$inferSelect;

export const insertWholesalerListingSchema = createInsertSchema(wholesalerListings).omit({
  id: true,
  views: true,
  uniqueViewers: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerId: z.number().int(),
  address: z.string().min(1, "Address is required"),
  askingPrice: z.union([z.string(), z.number()]),
  status: z.enum(["active", "under_contract", "sold"]).default("active"),
  photos: z.array(z.string()).max(20).default([]),
  sellerFinancing: z.boolean().default(false),
});

export type InsertWholesalerListing = z.infer<typeof insertWholesalerListingSchema>;
export type WholesalerListing = typeof wholesalerListings.$inferSelect;

// ─── Wholesaler Sync Runs ─────────────────────────────────────────────────────

export const WHOLESALER_SYNC_RUN_TRIGGERS = ["manual", "scheduled"] as const;
export type WholesalerSyncRunTrigger = typeof WHOLESALER_SYNC_RUN_TRIGGERS[number];

export const WHOLESALER_SYNC_RUN_STATUSES = ["ok", "config_error", "fetch_error"] as const;
export type WholesalerSyncRunStatus = typeof WHOLESALER_SYNC_RUN_STATUSES[number];

export const wholesalerSyncRuns = pgTable("wholesaler_sync_runs", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  success: boolean("success").notNull(),
  status: text("status").notNull(),
  trigger: text("trigger").default("manual").notNull(),
  total: integer("total").default(0).notNull(),
  created: integer("created").default(0).notNull(),
  updated: integer("updated").default(0).notNull(),
  skipped: integer("skipped").default(0).notNull(),
  skippedInactive: integer("skipped_inactive").default(0).notNull(),
  errorCount: integer("error_count").default(0).notNull(),
  summary: text("summary").notNull(),
});

export const insertWholesalerSyncRunSchema = createInsertSchema(wholesalerSyncRuns).omit({
  id: true,
  startedAt: true,
});

export type InsertWholesalerSyncRun = z.infer<typeof insertWholesalerSyncRunSchema>;
export type WholesalerSyncRun = typeof wholesalerSyncRuns.$inferSelect;

// ─── Wholesaler Leads ─────────────────────────────────────────────────────────

export const wholesalerLeads = pgTable("wholesaler_leads", {
  id: serial("id").primaryKey(),
  listingId: integer("listing_id").notNull().references(() => wholesalerListings.id, { onDelete: "cascade" }),
  threadId: integer("thread_id").references(() => partnerThreads.id, { onDelete: "set null" }),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  appUserId: integer("app_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  appUserName: text("app_user_name"),
  appUserEmail: text("app_user_email"),
  appUserPhone: text("app_user_phone"),
  referralTrigger: text("referral_trigger").default("direct").notNull(),
  status: text("status").default("new").notNull(),
  notes: text("notes"),
  closedAt: timestamp("closed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertWholesalerLeadSchema = createInsertSchema(wholesalerLeads).omit({
  id: true,
  closedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  listingId: z.number().int(),
  partnerId: z.number().int(),
  referralTrigger: z.enum(["deal_view", "direct"]).default("direct").optional(),
  status: z.enum(["new", "in_contact", "under_contract", "closed", "dead"]).default("new"),
});

export type InsertWholesalerLead = z.infer<typeof insertWholesalerLeadSchema>;
export type WholesalerLead = typeof wholesalerLeads.$inferSelect;

// ─── Partner Portal: Listings (Realtor + Wholesaler) ─────────────────────────

export const PARTNER_LISTING_TYPES = ["mls", "off_market", "pocket"] as const;
export type PartnerListingType = typeof PARTNER_LISTING_TYPES[number];

export const PARTNER_LISTING_STATUSES = ["active", "under_contract", "sold", "expired", "withdrawn"] as const;
export type PartnerListingStatus = typeof PARTNER_LISTING_STATUSES[number];

export const COLIVING_SUITABILITY_OPTIONS = [
  "yes_already_operating",
  "yes_suitable_not_converted",
  "needs_conversion",
  "unsure",
] as const;
export type ColivingSuitability = typeof COLIVING_SUITABILITY_OPTIONS[number];

export const partnerListings = pgTable("partner_listings", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  sourceType: text("source_type").notNull().default("realtor"),
  address: text("address").notNull(),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  latitude: numeric("latitude", { precision: 10, scale: 7 }),
  longitude: numeric("longitude", { precision: 10, scale: 7 }),
  price: numeric("price", { precision: 12, scale: 2 }).notNull(),
  arv: numeric("arv", { precision: 12, scale: 2 }),
  bedrooms: integer("bedrooms"),
  bathrooms: numeric("bathrooms", { precision: 4, scale: 1 }),
  sqft: integer("sqft"),
  lotSize: numeric("lot_size", { precision: 10, scale: 4 }),
  garagesqft: integer("garage_sqft"),
  yearBuilt: integer("year_built"),
  description: text("description"),
  photos: jsonb("photos").default([]).notNull(),
  dealNotes: text("deal_notes"),
  sellerFinancing: boolean("seller_financing").default(false).notNull(),
  estimatedDaysAvailable: integer("estimated_days_available"),
  status: text("status").default("active").notNull(),
  mlsNumber: text("mls_number"),
  listingType: text("listing_type"),
  buyerAgentCommission: numeric("buyer_agent_commission", { precision: 5, scale: 2 }),
  colivingSuitability: text("coliving_suitability"),
  // Task #3441 — New financial + conversion fields from Matt Solis CSV import
  coverPhotoUrl: text("cover_photo_url"),
  propertyReportUrl: text("property_report_url"),
  bedroomsAfterConversion: integer("bedrooms_after_conversion"),
  bathroomsAfterConversion: numeric("bathrooms_after_conversion", { precision: 4, scale: 1 }),
  irr10Year: numeric("irr_10_year", { precision: 6, scale: 4 }),
  cashOnCashReturn: numeric("cash_on_cash_return", { precision: 6, scale: 4 }),
  cashOutlay: numeric("cash_outlay", { precision: 12, scale: 2 }),
  monthlyCashFlow: numeric("monthly_cash_flow", { precision: 12, scale: 2 }),
  dealType: text("deal_type"),
  dateListed: date("date_listed"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});

export const insertPartnerListingSchema = createInsertSchema(partnerListings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerId: z.number().int(),
  address: z.string().min(1, "Address is required"),
  price: z.union([z.string(), z.number()]),
  arv: z.union([z.string(), z.number()]).optional().nullable(),
  bedrooms: z.number().int().optional().nullable(),
  bathrooms: z.union([z.string(), z.number()]).optional().nullable(),
  sqft: z.number().int().optional().nullable(),
  lotSize: z.union([z.string(), z.number()]).optional().nullable(),
  garagesqft: z.number().int().optional().nullable(),
  yearBuilt: z.number().int().optional().nullable(),
  estimatedDaysAvailable: z.number().int().optional().nullable(),
  sellerFinancing: z.boolean().default(false).optional(),
  photos: z.array(z.string()).default([]).optional(),
  buyerAgentCommission: z.union([z.string(), z.number()]).optional().nullable(),
  listingType: z.enum(["mls", "off_market", "pocket"]).optional().nullable(),
  colivingSuitability: z.enum(COLIVING_SUITABILITY_OPTIONS).optional().nullable(),
  status: z.enum(PARTNER_LISTING_STATUSES).default("active").optional(),
  // Task #3441 new fields
  coverPhotoUrl: z.string().url().optional().nullable(),
  propertyReportUrl: z.string().url().optional().nullable(),
  bedroomsAfterConversion: z.number().int().optional().nullable(),
  bathroomsAfterConversion: z.union([z.string(), z.number()]).optional().nullable(),
  irr10Year: z.union([z.string(), z.number()]).optional().nullable(),
  cashOnCashReturn: z.union([z.string(), z.number()]).optional().nullable(),
  cashOutlay: z.union([z.string(), z.number()]).optional().nullable(),
  monthlyCashFlow: z.union([z.string(), z.number()]).optional().nullable(),
  dealType: z.string().optional().nullable(),
  dateListed: z.string().optional().nullable(),
});

export type InsertPartnerListing = z.infer<typeof insertPartnerListingSchema>;
export type PartnerListing = typeof partnerListings.$inferSelect;

// ─── Partner Portal: Leads (Buyer Connection / Referral Pipeline) ─────────────

export const PARTNER_LEAD_STATUSES = [
  "new",
  "in_contact",
  "showing_scheduled",
  "offer_submitted",
  "under_contract",
  "closed",
  "dead",
] as const;
export type PartnerLeadStatus = typeof PARTNER_LEAD_STATUSES[number];

export const PARTNER_LEAD_TRIGGERS = ["work_with_realtor", "request_showing", "owner_inquiry"] as const;
export type PartnerLeadTrigger = typeof PARTNER_LEAD_TRIGGERS[number];

export const partnerLeads = pgTable("partner_leads", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  appUserId: integer("app_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  appUserName: text("app_user_name"),
  appUserEmail: text("app_user_email"),
  listingId: integer("listing_id").references(() => partnerListings.id, { onDelete: "set null" }),
  listingAddress: text("listing_address"),
  threadId: integer("thread_id").references(() => partnerThreads.id, { onDelete: "set null" }),
  referralTrigger: text("referral_trigger").notNull().default("work_with_realtor"),
  status: text("status").notNull().default("new"),
  privateNotes: text("private_notes"),
  closedAt: timestamp("closed_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});

export const insertPartnerLeadSchema = createInsertSchema(partnerLeads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerId: z.number().int(),
  status: z.enum(PARTNER_LEAD_STATUSES).default("new").optional(),
  referralTrigger: z.enum(PARTNER_LEAD_TRIGGERS).default("work_with_realtor").optional(),
});

export type InsertPartnerLead = z.infer<typeof insertPartnerLeadSchema>;
export type PartnerLead = typeof partnerLeads.$inferSelect;

// ─── Partner Certification Events (audit trail of grant/revoke) ─────────────

export const PARTNER_CERTIFICATION_ACTIONS = ["granted", "revoked"] as const;
export type PartnerCertificationAction = typeof PARTNER_CERTIFICATION_ACTIONS[number];

export const partnerCertificationEvents = pgTable("partner_certification_events", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  action: text("action").notNull(),
  reason: text("reason"),
  adminUserId: integer("admin_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  adminLabel: text("admin_label"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export const insertPartnerCertificationEventSchema = createInsertSchema(partnerCertificationEvents).omit({
  id: true,
  createdAt: true,
}).extend({
  action: z.enum(PARTNER_CERTIFICATION_ACTIONS),
  reason: z.string().optional().nullable(),
  adminUserId: z.number().int().optional().nullable(),
  adminLabel: z.string().optional().nullable(),
});

export type InsertPartnerCertificationEvent = z.infer<typeof insertPartnerCertificationEventSchema>;
export type PartnerCertificationEvent = typeof partnerCertificationEvents.$inferSelect;

// ─── Partner Type Change Events (audit trail of manual type reclassifications) ─

export const partnerTypeChangeEvents = pgTable("partner_type_change_events", {
  id: serial("id").primaryKey(),
  partnerId: integer("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
  fromType: text("from_type").notNull(),
  toType: text("to_type").notNull(),
  adminUserId: integer("admin_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  adminLabel: text("admin_label"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  partnerIdIdx: index("partner_type_change_events_partner_id_idx").on(table.partnerId),
}));

export const insertPartnerTypeChangeEventSchema = createInsertSchema(partnerTypeChangeEvents).omit({
  id: true,
  createdAt: true,
}).extend({
  fromType: z.enum(PARTNER_TYPES),
  toType: z.enum(PARTNER_TYPES),
  adminUserId: z.number().int().optional().nullable(),
  adminLabel: z.string().optional().nullable(),
});

export type InsertPartnerTypeChangeEvent = z.infer<typeof insertPartnerTypeChangeEventSchema>;
export type PartnerTypeChangeEvent = typeof partnerTypeChangeEvents.$inferSelect;

// ─── Partner Listing Views (de-duped per user per day) ────────────────────────

export const partnerListingViews = pgTable(
  "partner_listing_views",
  {
    id: serial("id").primaryKey(),
    listingId: integer("listing_id").notNull().references(() => partnerListings.id, { onDelete: "cascade" }),
    appUserId: integer("app_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
    viewDate: date("view_date").notNull(),
    createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  },
  (t) => ({
    uniquePerDay: uniqueIndex("partner_listing_views_unique_per_day").on(t.listingId, t.appUserId, t.viewDate),
    listingIdx: index("partner_listing_views_listing_idx").on(t.listingId),
  })
);

export type PartnerListingView = typeof partnerListingViews.$inferSelect;

// ─── TTLock Connections ───────────────────────────────────────────────────────

export const TTLOCK_CONNECTION_STATUSES = ["connected", "token_expired", "revoked", "error"] as const;
export type TtlockConnectionStatus = typeof TTLOCK_CONNECTION_STATUSES[number];

// Smart-lock providers that share the TTLock Open Platform API. Sifely hardware
// runs on the TTLock platform, so the same OAuth2/MD5/endpoint stack serves both
// — only the API host + developer app credentials differ per provider.
export const LOCK_PROVIDERS = ["ttlock", "sifely"] as const;
export type LockProvider = typeof LOCK_PROVIDERS[number];

export const ttlockConnections = pgTable("ttlock_connections", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  ttlockUsername: text("ttlock_username").notNull(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  status: text("status").default("connected").notNull(),
  // Discriminates which TTLock-platform provider this account belongs to.
  // Defaults to 'ttlock' so all existing rows keep their behavior unchanged.
  provider: text("provider").default("ttlock").notNull(),
  ttlockUid: integer("ttlock_uid"),
  lastRefreshedAt: timestamp("last_refreshed_at"),
  lastError: text("last_error"),
  // ─── Sifely per-host credentials ────────────────────────────────────────────
  // Sifely uses the real login API (POST /system/smart/login) with per-host
  // developer-app credentials + the host's Sifely account login:
  //   sifelyClientId     — developer-app Client ID (from the host's Sifely app)
  //   sifelyClientSecret — developer-app Client Secret (required to mint tokens)
  //   sifelyUsername     — host's Sifely account email or phone (no country code)
  //   sifelyMd5Password  — MD5-hashed Sifely account password (for re-login)
  // Null on all TTLock rows and on legacy Sifely rows predating this fix.
  sifelyClientId: text("sifely_client_id"),
  sifelyUsername: text("sifely_username"),
  sifelyClientSecret: text("sifely_client_secret"),
  sifelyMd5Password: text("sifely_md5_password"),
  // Unique random token for the per-host Sifely webhook URL. Each host's Sifely
  // developer-app registration points to:
  //   POST /api/webhooks/sifely/records?token=<sifelyWebhookToken>
  // The token is generated before the connection exists (so the host can paste
  // the URL into Sifely during registration) and stored on connect.
  sifelyWebhookToken: text("sifely_webhook_token"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // Partial unique index mirroring scripts/deploy-migrate.sql exactly (only
  // enforced for non-NULL tokens, so TTLock rows and legacy Sifely rows with
  // NULL tokens are unaffected). Declared as a uniqueIndex with a .where()
  // clause rather than a column-level .unique() because the latter generates a
  // non-partial constraint that diverges from prod and fails the deploy-build
  // parity check.
  sifelyWebhookTokenUnique: uniqueIndex("ttlock_connections_sifely_webhook_token_unique")
    .on(table.sifelyWebhookToken)
    .where(sql`sifely_webhook_token IS NOT NULL`),
}));

export const insertTtlockConnectionSchema = createInsertSchema(ttlockConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.number(),
  ttlockUsername: z.string().min(1),
  accessToken: z.string().min(1),
  refreshToken: z.string(),
  expiresAt: z.date(),
  status: z.enum(TTLOCK_CONNECTION_STATUSES).default("connected"),
  provider: z.enum(LOCK_PROVIDERS).default("ttlock"),
  sifelyClientId: z.string().optional(),
  sifelyUsername: z.string().optional(),
  sifelyClientSecret: z.string().optional(),
  sifelyMd5Password: z.string().optional(),
  sifelyWebhookToken: z.string().optional(),
});
export type InsertTtlockConnection = z.infer<typeof insertTtlockConnectionSchema>;
export type TtlockConnection = typeof ttlockConnections.$inferSelect;

// ─── TTLock Lock Cache ────────────────────────────────────────────────────────

export const ttlockLocks = pgTable("ttlock_locks", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  connectionId: integer("connection_id").notNull().references(() => ttlockConnections.id, { onDelete: "cascade" }),
  ttlockLockId: integer("ttlock_lock_id").notNull(),
  lockAlias: text("lock_alias"),
  lockName: text("lock_name"),
  lockMac: text("lock_mac"),
  electricQuantity: integer("electric_quantity"),
  hasGateway: boolean("has_gateway").default(false).notNull(),
  groupId: integer("group_id"),
  groupName: text("group_name"),
  propertyId: integer("property_id"),
  roomId: integer("room_id"),
  accessScope: text("access_scope").default("common").notNull(),
  // Task #1729 — ignored=true means "not a PadSplit lock; skip from all
  // property lists, battery checks, and member auto-access". Preserved
  // across syncs (not overwritten by the TTLock API resync).
  ignored: boolean("ignored").default(false).notNull(),
  noKeyPwd: text("no_key_pwd"),
  featureValue: text("feature_value"),
  rawData: jsonb("raw_data"),
  syncedAt: timestamp("synced_at"),
  recordsSyncedAt: timestamp("records_synced_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  uniqueLock: uniqueIndex("ttlock_locks_conn_lock_unique").on(t.connectionId, t.ttlockLockId),
  userIdx: index("ttlock_locks_user_idx").on(t.userId),
}));

export const insertTtlockLockSchema = createInsertSchema(ttlockLocks).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTtlockLock = z.infer<typeof insertTtlockLockSchema>;
export type TtlockLock = typeof ttlockLocks.$inferSelect;

// ─── TTLock Passcode Cache ────────────────────────────────────────────────────

export const ttlockPasscodes = pgTable("ttlock_passcodes", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  connectionId: integer("connection_id").notNull().references(() => ttlockConnections.id, { onDelete: "cascade" }),
  ttlockLockId: integer("ttlock_lock_id").notNull(),
  keyboardPwdId: integer("keyboard_pwd_id").notNull(),
  keyboardPwd: text("keyboard_pwd"),
  keyboardPwdName: text("keyboard_pwd_name"),
  keyboardPwdType: integer("keyboard_pwd_type"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  isCustom: integer("is_custom"),
  syncedAt: timestamp("synced_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  uniquePasscode: uniqueIndex("ttlock_passcodes_conn_pwd_unique").on(t.connectionId, t.keyboardPwdId),
  lockIdx: index("ttlock_passcodes_lock_idx").on(t.connectionId, t.ttlockLockId),
}));

export const insertTtlockPasscodeSchema = createInsertSchema(ttlockPasscodes).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTtlockPasscode = z.infer<typeof insertTtlockPasscodeSchema>;
export type TtlockPasscode = typeof ttlockPasscodes.$inferSelect;

// ─── TTLock Unlock Records ────────────────────────────────────────────────────

export const ttlockUnlockRecords = pgTable("ttlock_unlock_records", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  connectionId: integer("connection_id").notNull().references(() => ttlockConnections.id, { onDelete: "cascade" }),
  ttlockLockId: integer("ttlock_lock_id").notNull(),
  ttlockRecordId: text("ttlock_record_id").notNull(),
  recordType: integer("record_type"),
  username: text("username"),
  keyboardPwd: text("keyboard_pwd"),
  success: integer("success"),
  recordTime: text("record_time"),
  lockDate: text("lock_date"),
  serverDate: text("server_date"),
  rawPayload: jsonb("raw_payload"),
  // Task #1805 — provenance: "poll" (15-min API sync) vs "webhook" (real-time
  // TTLock callback push). Nullable for backfill compatibility with rows
  // inserted before this column existed.
  source: text("source"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // Task #1804 — 3-field dedupe key so poll + webhook push collapse cleanly
  // even if TTLock ever reuses recordIds across different connections.
  uniqueRecord: uniqueIndex("ttlock_unlock_records_conn_lock_rec_unique").on(t.connectionId, t.ttlockLockId, t.ttlockRecordId),
  lockIdx: index("ttlock_unlock_records_lock_idx").on(t.connectionId, t.ttlockLockId),
  timeIdx: index("ttlock_unlock_records_time_idx").on(t.userId, t.recordTime),
}));

export const insertTtlockUnlockRecordSchema = createInsertSchema(ttlockUnlockRecords).omit({ id: true, createdAt: true });
export type InsertTtlockUnlockRecord = z.infer<typeof insertTtlockUnlockRecordSchema>;
export type TtlockUnlockRecord = typeof ttlockUnlockRecords.$inferSelect;

// ─── TTLock per-lock refresh cooldown (Task #742) ────────────────────────────
// Persists the last on-demand records-sync time per lock so the 30-second
// throttle survives restarts and is enforced across multiple Node processes.
// Keyed by (connectionId, ttlockLockId); a single row per lock — no history.
export const ttlockRefreshCooldowns = pgTable("ttlock_refresh_cooldowns", {
  connectionId: integer("connection_id").notNull().references(() => ttlockConnections.id, { onDelete: "cascade" }),
  ttlockLockId: integer("ttlock_lock_id").notNull(),
  lastRunAt: timestamp("last_run_at").defaultNow().notNull(),
}, (t) => ({
  pk: primaryKey({ columns: [t.connectionId, t.ttlockLockId] }),
}));

export type TtlockRefreshCooldown = typeof ttlockRefreshCooldowns.$inferSelect;

// ─── Seam Lock Usage Snapshots (metering) ────────────────────────────────────

export const seamLockUsageSnapshots = pgTable("seam_lock_usage_snapshots", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  snapshotMonth: text("snapshot_month").notNull(),
  seamLockCount: integer("seam_lock_count").notNull().default(0),
  unitPriceCents: integer("unit_price_cents").notNull().default(250),
  totalCents: integer("total_cents").notNull().default(0),
  stripeLineItemId: text("stripe_line_item_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  userMonthUnique: uniqueIndex("seam_lock_usage_snapshots_user_month_idx").on(t.userId, t.snapshotMonth),
}));

export type SeamLockUsageSnapshot = typeof seamLockUsageSnapshots.$inferSelect;

// ─── Seam Lock Count Cache (per-connection) ──────────────────────────────────
//
// Task #2192 — Cached count of Seam locks per `seam_connections.id` so the
// Devices overview tab can render the smart-lock total without making a live
// `seam.locks.list()` API call per connection on every page load. Refreshed by
// a 10-minute cron + on connection create. Deleted via FK cascade when the
// underlying Seam connection is removed.
export const seamLockCountCache = pgTable("seam_lock_count_cache", {
  connectionId: integer("connection_id").primaryKey().references(() => seamConnections.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  lockCount: integer("lock_count").notNull().default(0),
  refreshedAt: timestamp("refreshed_at").defaultNow().notNull(),
}, (t) => ({
  userIdx: index("seam_lock_count_cache_user_idx").on(t.userId),
}));

export type SeamLockCountCache = typeof seamLockCountCache.$inferSelect;

// ─── PadSplit Lock-Code Sync ─────────────────────────────────────────────────
//
// PadSplit's host UI tracks smart locks + per-room access codes per listing
// (house-manual → Devices → Smart Locks; each lock has a CODES lightbox).
// These tables mirror that registry so we can flag drift between PadSplit's
// codes and what's actually programmed on the physical TTLock/Sifely/Seam
// locks, and queue code pushes back to PadSplit via the warm-browser fleet.
//
// padsplit_locks + padsplit_lock_codes are HARVEST DESTINATIONS written by the
// external browsers-team Lambda (events/locks harvest type) — no FKs to our
// tables so the Lambda is never blocked by our referential state; the app
// joins on properties.padsplitPropertyId at read time. The Lambda upserts
// locks on (host_email, padsplit_property_id, padsplit_lock_id) and REPLACES
// each lock's child code rows per harvest, then stamps synced_at.
// Contract: docs/padsplit-lock-sync-contract.md

export const PADSPLIT_LOCK_SCOPES = ["room", "common", "unknown"] as const;
export type PadsplitLockScope = (typeof PADSPLIT_LOCK_SCOPES)[number];

export const padsplitLocks = pgTable("padsplit_locks", {
  id: serial("id").primaryKey(),
  hostEmail: text("host_email").notNull(),
  padsplitPropertyId: text("padsplit_property_id").notNull(),
  // Smart lock ID from PadSplit's "Edit Smart Lock Devices" dialog (e.g.
  // "95345") — the primary identity. lock_name is fallback only if absent.
  padsplitLockId: text("padsplit_lock_id"),
  lockName: text("lock_name"),
  scope: text("scope").default("unknown"),
  padsplitRoomLabel: text("padsplit_room_label"),
  padsplitRoomId: text("padsplit_room_id"),
  moveInInstructions: text("move_in_instructions"),
  internalNotes: text("internal_notes"),
  rawData: jsonb("raw_data"),
  syncedAt: timestamp("synced_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  propertyIdx: index("padsplit_locks_property_idx").on(t.padsplitPropertyId),
  hostIdx: index("padsplit_locks_host_idx").on(t.hostEmail),
  uniqueLock: uniqueIndex("padsplit_locks_host_property_lock_unique").on(t.hostEmail, t.padsplitPropertyId, t.padsplitLockId),
}));

export const insertPadsplitLockSchema = createInsertSchema(padsplitLocks).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPadsplitLock = z.infer<typeof insertPadsplitLockSchema>;
export type PadsplitLock = typeof padsplitLocks.$inferSelect;

// One row per room-row in the lock's CODES lightbox table. A common lock (e.g.
// Back Door) carries one row PER ROOM; a room lock carries exactly one row.
// Codes are stored raw as PadSplit shows them and may include a "#" suffix —
// always String()-coerce and digit-normalize before comparing.
export const padsplitLockCodes = pgTable("padsplit_lock_codes", {
  id: serial("id").primaryKey(),
  padsplitLockRowId: integer("padsplit_lock_row_id").notNull().references(() => padsplitLocks.id, { onDelete: "cascade" }),
  roomLabel: text("room_label"),
  assignedTo: text("assigned_to"),
  occupantProfileUrl: text("occupant_profile_url"),
  code: text("code"),
  rawData: jsonb("raw_data"),
  syncedAt: timestamp("synced_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  lockIdx: index("padsplit_lock_codes_lock_idx").on(t.padsplitLockRowId),
}));

export const insertPadsplitLockCodeSchema = createInsertSchema(padsplitLockCodes).omit({ id: true, createdAt: true });
export type InsertPadsplitLockCode = z.infer<typeof insertPadsplitLockCodeSchema>;
export type PadsplitLockCode = typeof padsplitLockCodes.$inferSelect;

// Persisted PadSplit-lock ↔ physical-lock link, confirmed by staff (server
// suggests by normalized name + room pin; suggestions are never auto-saved).
// device_id uses the unified format: "ttlock-<connectionId>-<ttlockLockId>"
// for TTLock/Sifely, raw Seam device_id for Seam.
export const padsplitLockMappings = pgTable("padsplit_lock_mappings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  padsplitLockKey: text("padsplit_lock_key").notNull(),
  deviceId: text("device_id").notNull(),
  createdByUserId: integer("created_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  uniqueMapping: uniqueIndex("padsplit_lock_mappings_user_property_lock_unique").on(t.userId, t.propertyId, t.padsplitLockKey),
  propertyIdx: index("padsplit_lock_mappings_property_idx").on(t.propertyId),
}));

export const insertPadsplitLockMappingSchema = createInsertSchema(padsplitLockMappings).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPadsplitLockMapping = z.infer<typeof insertPadsplitLockMappingSchema>;
export type PadsplitLockMapping = typeof padsplitLockMappings.$inferSelect;

// Push queue consumed by the warm-browser fleet (POST /api/browser-users/
// {host_user_id}/push-lock-code with { lock_code_push_id }). The fleet loads
// the row by id, opens the target lock's CODES lightbox, sets the access-code
// input on the row matching room_label, clicks Save, then stamps sent_at on
// success or writes a human-readable send_error on terminal failure.
// padsplit_property_id / lock name are denormalized so the fleet never joins
// our tables. Derived status (messages_pending semantics): queued (sent_at,
// send_error, cancelled_at all NULL) → sent | failed | cancelled.
export const PADSPLIT_LOCK_PUSH_ACTIONS = ["set_room_code"] as const;
export type PadsplitLockPushAction = (typeof PADSPLIT_LOCK_PUSH_ACTIONS)[number];

export const padsplitLockCodePushes = pgTable("padsplit_lock_code_pushes", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Warm-browser routing key — resolved at enqueue time via the same
  // precedence as message sends (acting owner → properties.padsplitSenderUserId
  // → legacy). The fleet egresses through THIS host's PadSplit session.
  hostUserId: integer("host_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  sentByUserId: integer("sent_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  padsplitPropertyId: text("padsplit_property_id").notNull(),
  roomId: integer("room_id").references(() => rooms.id, { onDelete: "set null" }),
  padsplitRoomId: text("padsplit_room_id"),
  padsplitLockKey: text("padsplit_lock_key").notNull(),
  padsplitLockName: text("padsplit_lock_name"),
  roomLabel: text("room_label").notNull(),
  deviceId: text("device_id"),
  code: text("code").notNull(),
  codeLabel: text("code_label"),
  action: text("action").default("set_room_code").notNull(),
  payload: jsonb("payload"),
  attempts: integer("attempts").default(0).notNull(),
  sendError: text("send_error"),
  sentAt: timestamp("sent_at"),
  cancelledAt: timestamp("cancelled_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  propertyIdx: index("padsplit_lock_code_pushes_property_idx").on(t.propertyId),
  // Dupe guard: one live (unsent, uncancelled) push per lightbox row.
  uniqueLivePush: uniqueIndex("padsplit_lock_code_pushes_live_unique")
    .on(t.padsplitLockKey, t.roomLabel)
    .where(sql`sent_at IS NULL AND cancelled_at IS NULL`),
  failedIdx: index("padsplit_lock_code_pushes_failed_idx")
    .on(t.userId)
    .where(sql`send_error IS NOT NULL AND sent_at IS NULL`),
}));

export const insertPadsplitLockCodePushSchema = createInsertSchema(padsplitLockCodePushes).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPadsplitLockCodePush = z.infer<typeof insertPadsplitLockCodePushSchema>;
export type PadsplitLockCodePush = typeof padsplitLockCodePushes.$inferSelect;

// ─── Billing Settlement Errors ───────────────────────────────────────────────
//
// Persists per-user failures from the nightly billing settlement crons (proration
// + Seam lock charges). Surfaces in the admin panel and feeds the daily admin
// alert email so silent Stripe rejections / snapshot-update failures stop slipping
// past unnoticed.
export const BILLING_SETTLEMENT_ERROR_KINDS = [
  "seam_lock",
  "proration_credit",
  "proration_charge",
] as const;
export type BillingSettlementErrorKind = (typeof BILLING_SETTLEMENT_ERROR_KINDS)[number];

export const billingSettlementErrors = pgTable("billing_settlement_errors", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  stage: text("stage").notNull(),
  snapshotMonth: text("snapshot_month"),
  amountCents: integer("amount_cents"),
  errorMessage: text("error_message").notNull(),
  triggeredBy: text("triggered_by").notNull().default("cron"),
  attemptCount: integer("attempt_count").notNull().default(1),
  lastAttemptAt: timestamp("last_attempt_at"),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: text("resolved_by"),
  resolutionNote: text("resolution_note"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  unresolvedIdx: index("billing_settlement_errors_unresolved_idx").on(t.resolvedAt, t.createdAt),
  userIdx: index("billing_settlement_errors_user_idx").on(t.userId),
}));

export const insertBillingSettlementErrorSchema = createInsertSchema(billingSettlementErrors).omit({
  id: true,
  createdAt: true,
  resolvedAt: true,
  resolvedBy: true,
  resolutionNote: true,
  lastAttemptAt: true,
}).extend({
  userId: z.number().int(),
  kind: z.enum(BILLING_SETTLEMENT_ERROR_KINDS),
  stage: z.string().min(1),
  snapshotMonth: z.string().nullable().optional(),
  amountCents: z.number().int().nullable().optional(),
  errorMessage: z.string().min(1),
  triggeredBy: z.string().default("cron"),
  attemptCount: z.number().int().min(1).default(1),
});
export type InsertBillingSettlementError = z.infer<typeof insertBillingSettlementErrorSchema>;
export type BillingSettlementError = typeof billingSettlementErrors.$inferSelect;

// Task #855 — Admin Change Log. Permanent record of every published release,
// generated either by the historical backfill (one-shot) or by the
// post-deploy hook (`scripts/changelog/record-deploy-release.ts` invoked
// from `scripts/deploy-build.sh`). Each row mirrors a markdown file under
// `docs/changelog/releases/` and is surfaced in the admin "Change Log" tab
// under Operations. `deploy_commit_sha` is unique so re-runs of the hook /
// backfill are idempotent.
export const RELEASE_NOTES_SOURCES = ["backfill", "deploy-hook", "manual"] as const;
export type ReleaseNotesSource = (typeof RELEASE_NOTES_SOURCES)[number];

// Task #861 — structured per-commit + contributor data so the admin Change
// Log list can show the deploy author + task badges + areas without parsing
// the markdown body. New columns are nullable / default-empty so existing
// rows (written before the schema change) still load; re-running the
// historical backfill repopulates them.
export interface ReleaseNotesContributor {
  name: string;
  email: string;
  count: number;
}

export interface ReleaseNotesCommit {
  sha: string;
  shortSha: string;
  subject: string;
  body: string;
  authorName: string;
  authorEmail: string;
  authoredAt: string; // ISO string — jsonb can't store Date directly
  filesChanged: string[];
  taskIds: number[];
  areas: string[];
}

export const releaseNotes = pgTable("release_notes", {
  id: serial("id").primaryKey(),
  deployCommitSha: varchar("deploy_commit_sha", { length: 40 }).notNull().unique(),
  prevDeployCommitSha: varchar("prev_deploy_commit_sha", { length: 40 }),
  publishedAt: timestamp("published_at").notNull(),
  commitCount: integer("commit_count").notNull().default(0),
  taskCount: integer("task_count").notNull().default(0),
  fileCount: integer("file_count").notNull().default(0),
  filePath: varchar("file_path", { length: 256 }).notNull(),
  title: varchar("title", { length: 256 }).notNull(),
  summary: text("summary"),
  bodyMarkdown: text("body_markdown").notNull(),
  source: varchar("source", { length: 32 }).notNull(),
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  // Task #861 additions:
  deployAuthorName: text("deploy_author_name"),
  deployAuthorEmail: text("deploy_author_email"),
  taskIds: integer("task_ids").array().notNull().default(sql`'{}'::integer[]`),
  contributors: jsonb("contributors").$type<ReleaseNotesContributor[]>().notNull().default([]),
  commits: jsonb("commits").$type<ReleaseNotesCommit[]>().notNull().default([]),
  taskTitles: jsonb("task_titles").$type<Record<string, string>>().notNull().default({}),
  areas: text("areas").array().notNull().default(sql`'{}'::text[]`),
  // Task #1602 — set atomically by the first runtime instance that successfully
  // posts the "Published your App" Slack announcement for this release. The
  // build phase no longer posts (which fired before autoscale promote finished
  // and produced inaccurate "Published" notices when the run phase failed);
  // instead it drops a sentinel JSON into `dist/.pending-release-announcement.json`
  // and the runtime claims this column to dedupe across multiple autoscale
  // instances cold-starting at once. Null = not yet announced.
  announcedAt: timestamp("announced_at"),
  // Task #2021 — runtime-claimed dedupe marker for the PadPulse Task History
  // Google Doc auto-seal. Same pattern as `announcedAt` above: the build hook
  // used to call `sealCurrentDoc` directly (Task #2020), which sealed the
  // open window doc DURING the build phase — before the new revision had
  // actually started serving traffic. When the run phase then failed on the
  // port-5000 deadline (the same Task #1602 scenario the announcement path
  // already solved), the doc folder ended up with a sealed window for a
  // release that never shipped. The build phase now drops a sentinel JSON
  // at `dist/.pending-task-history-doc-seal.json` and the first runtime
  // instance to UPDATE this column from NULL → now() wins the seal race
  // and calls `sealCurrentDoc` from the runtime.
  taskHistoryDocSealedAt: timestamp("task_history_doc_sealed_at"),
}, (t) => ({
  publishedAtIdx: index("release_notes_published_at_idx").on(t.publishedAt),
}));

// Task #890 — Snapshot of project-task plan files (`.local/tasks/task-{id}.md`)
// captured at deploy build time. The agent's `.local/` tree is system-
// gitignored and never reaches the production filesystem, so the
// runtime admin Change Log endpoint cannot read those files directly.
// The deploy-build hook (`scripts/changelog/snapshot-task-plans.ts`)
// upserts each plan into this table, and
// `GET /api/admin/changelog/task-plan/:id` reads from here so admins
// see the actual title + body when they click a task badge. Indexed
// by `task_id` (one row per project task, latest snapshot wins).
export const taskPlans = pgTable("task_plans", {
  taskId: integer("task_id").primaryKey(),
  title: text("title"),
  content: text("content").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  // Task #2013 — immutable "first time this plan was snapshotted" so the
  // task-history-doc-sync cron has a stable merge-time proxy that doesn't
  // get bumped on every deploy. `upsertTaskPlan` only writes this on
  // INSERT (preserved on ON CONFLICT DO UPDATE).
  firstSeenAt: timestamp("first_seen_at").defaultNow().notNull(),
});

export const insertTaskPlanSchema = createInsertSchema(taskPlans).omit({
  updatedAt: true,
  firstSeenAt: true,
}).extend({
  taskId: z.number().int().positive(),
  title: z.string().nullable().optional(),
  content: z.string().min(1),
});
export type InsertTaskPlan = z.infer<typeof insertTaskPlanSchema>;
export type TaskPlan = typeof taskPlans.$inferSelect;

// Task #1026 — slow-burn changelog heal regression detection. The
// per-deploy detector (Task #968) pages on-call when a single deploy
// reports `needingHeal > 1` on a non-cold-start. A `needingHeal === 1`
// value across many consecutive deploys is the same class of bug (the
// recorder hook silently fails to populate `release_notes.deploy_author_name`,
// or something is nulling it out, and the same commit is re-healed every
// deploy) but slips under the per-deploy threshold. We persist a tiny
// per-commit history so the script can detect "this same SHA needed
// healing on the last K deploys" and fire the existing
// `deploy-build-changelog-backfill-heal-regression` alert.
//
// One row per commit SHA. `consecutive_heal_count` is incremented every
// time the SHA appears in the unhealed-set on a backfill run; if a
// backfill run does NOT include the SHA, the row is deleted (the chain
// is broken — only consecutive deploys count). `first_healed_at` /
// `last_healed_at` give an on-call engineer an immediate read on how
// long the regression has been silently re-healing.
export const changelogHealHistory = pgTable("changelog_heal_history", {
  deployCommitSha: varchar("deploy_commit_sha", { length: 40 }).primaryKey(),
  consecutiveHealCount: integer("consecutive_heal_count").notNull().default(0),
  firstHealedAt: timestamp("first_healed_at").notNull(),
  lastHealedAt: timestamp("last_healed_at").notNull(),
});

export const insertChangelogHealHistorySchema = createInsertSchema(changelogHealHistory).extend({
  deployCommitSha: z.string().regex(/^[0-9a-f]{40}$/i),
  consecutiveHealCount: z.number().int().min(0),
  firstHealedAt: z.coerce.date(),
  lastHealedAt: z.coerce.date(),
});
export type InsertChangelogHealHistory = z.infer<typeof insertChangelogHealHistorySchema>;
export type ChangelogHealHistoryEntry = typeof changelogHealHistory.$inferSelect;

export const insertReleaseNotesSchema = createInsertSchema(releaseNotes).omit({
  id: true,
  generatedAt: true,
}).extend({
  deployCommitSha: z.string().regex(/^[0-9a-f]{40}$/i),
  prevDeployCommitSha: z.string().regex(/^[0-9a-f]{40}$/i).nullable().optional(),
  publishedAt: z.coerce.date(),
  commitCount: z.number().int().min(0).default(0),
  taskCount: z.number().int().min(0).default(0),
  fileCount: z.number().int().min(0).default(0),
  filePath: z.string().min(1).max(256),
  title: z.string().min(1).max(256),
  summary: z.string().nullable().optional(),
  bodyMarkdown: z.string().min(1),
  source: z.enum(RELEASE_NOTES_SOURCES),
  deployAuthorName: z.string().nullable().optional(),
  deployAuthorEmail: z.string().nullable().optional(),
  taskIds: z.array(z.number().int().positive()).default([]),
  contributors: z.array(z.object({
    name: z.string(),
    email: z.string(),
    count: z.number().int().min(0),
  })).default([]),
  commits: z.array(z.object({
    sha: z.string(),
    shortSha: z.string(),
    subject: z.string(),
    body: z.string(),
    authorName: z.string(),
    authorEmail: z.string(),
    authoredAt: z.string(),
    filesChanged: z.array(z.string()),
    taskIds: z.array(z.number().int().positive()),
    areas: z.array(z.string()),
  })).default([]),
  taskTitles: z.record(z.string()).default({}),
  areas: z.array(z.string()).default([]),
});
export type InsertReleaseNote = z.infer<typeof insertReleaseNotesSchema>;
export type ReleaseNote = typeof releaseNotes.$inferSelect;

// Task #1227 — Stripe price catalog. One row per (plan, cycle) recurring
// price plus one row per founder one-time price. Bootstrap script populates
// it from MONTHLY_PRICES_PER_ROOM / ANNUAL_PRICES_PER_ROOM_PER_MONTH /
// FOUNDER_TIER_PRICE_USD so the rest of the app looks up Stripe price IDs
// by (plan, cycle) without hard-coding env vars or duplicating product setup.
export const billingPriceCatalog = pgTable("billing_price_catalog", {
  id: serial("id").primaryKey(),
  plan: text("plan").notNull(),
  cycle: text("cycle").notNull(),
  // Stripe Checkout mode the price is used with: "subscription" | "payment".
  mode: text("mode").notNull(),
  // Which Stripe environment the price ids belong to: "test" | "live".
  // Test and Live price ids for the same (plan, cycle) coexist as separate
  // rows; getBillingPrice filters by the active STRIPE_MODE so a test-mode
  // bootstrap can never overwrite (or be served in place of) live prices.
  stripeMode: text("stripe_mode", { enum: ["test", "live"] }).notNull(),
  stripeProductId: text("stripe_product_id").notNull(),
  stripePriceId: text("stripe_price_id").notNull(),
  unitAmountCents: integer("unit_amount_cents").notNull(),
  currency: text("currency").notNull().default("usd"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  planCycleModeUniq: uniqueIndex("billing_price_catalog_plan_cycle_mode_uniq").on(t.plan, t.cycle, t.stripeMode),
}));

export const insertBillingPriceCatalogSchema = createInsertSchema(billingPriceCatalog).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertBillingPriceCatalog = z.infer<typeof insertBillingPriceCatalogSchema>;
export type BillingPriceCatalog = typeof billingPriceCatalog.$inferSelect;

// Task #1227 — Idempotency table for the Stripe webhook. Stripe occasionally
// re-delivers the same event id (network blip, restart between ack and 200);
// we record the event id the first time we successfully process it so the
// second delivery is a no-op. Tiny table — event id + type + processed_at.
export const processedStripeEvents = pgTable("processed_stripe_events", {
  eventId: text("event_id").primaryKey(),
  eventType: text("event_type").notNull(),
  processedAt: timestamp("processed_at").defaultNow().notNull(),
});

export const insertProcessedStripeEventSchema = createInsertSchema(processedStripeEvents).omit({
  processedAt: true,
});

// ── v3 billing build (docs/build/padpulse-build-backend.md) ──

export const BILLING_MODELS = ["v3"] as const;
export type BillingModel = (typeof BILLING_MODELS)[number];
export const SIGNUP_ROLES = ["host", "property_manager", "researcher"] as const;
export type SignupRole = (typeof SIGNUP_ROLES)[number];
export type SignupAttribution = {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  referrer?: string | null;
  landing_path?: string | null;
};

// One row per Research search (market research, buy box, underwriting), for
// every account type. Drives the free-plan monthly cap and admin cost totals.
export const RESEARCH_SEARCH_TYPES = ["market_research", "buy_box", "underwriting", "buy_box_auto"] as const;
export type ResearchSearchType = (typeof RESEARCH_SEARCH_TYPES)[number];
export const researchSearchLog = pgTable("research_search_log", {
  id: serial("id").primaryKey(),
  accountId: integer("account_id").notNull(),
  type: text("type").$type<ResearchSearchType>().notNull(),
  provider: text("provider").notNull(),
  estimatedCostCents: integer("estimated_cost_cents").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  accountCreatedIdx: index("research_search_log_account_created_idx").on(table.accountId, table.createdAt),
}));
export type ResearchSearchLog = typeof researchSearchLog.$inferSelect;

// Account lifecycle events (trial_started, trial_day_10_no_card, …). The
// unique key makes once-only events idempotent; Phase 2 forwards rows to
// Resend/PostHog and stamps forwarded_at.
export const accountLifecycleEvents = pgTable("account_lifecycle_events", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  event: text("event").notNull(),
  dedupeKey: text("dedupe_key").notNull().default(""),
  props: jsonb("props").$type<Record<string, unknown>>(),
  forwardedAt: timestamp("forwarded_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniqUserEventKey: uniqueIndex("account_lifecycle_events_user_event_key_uniq").on(table.userId, table.event, table.dedupeKey),
  createdIdx: index("account_lifecycle_events_created_idx").on(table.createdAt),
}));
export type AccountLifecycleEvent = typeof accountLifecycleEvents.$inferSelect;

// Global (not per-user) on/off switches. Typed keys live in shared/app-flags.ts;
// a missing row means the key's coded default.
export const appFlags = pgTable("app_flags", {
  key: text("key").primaryKey(),
  enabled: boolean("enabled").notNull().default(false),
  updatedBy: integer("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});
export type AppFlagRow = typeof appFlags.$inferSelect;

// B23 — reusable SYRE offer windows. The banner/postcard QR is reused at
// future events, so open/close dates live here (admin-editable), not in code.
// `active = false` closes a window early without deleting its replay.
export const offerWindows = pgTable("offer_windows", {
  id: serial("id").primaryKey(),
  offerId: text("offer_id").notNull(),
  label: text("label").notNull(),
  opensAt: timestamp("opens_at", { withTimezone: true }).notNull(),
  closesAt: timestamp("closes_at", { withTimezone: true }).notNull(),
  active: boolean("active").notNull().default(true),
  webinarTitle: text("webinar_title"),
  webinarReplayUrl: text("webinar_replay_url"),
  presentationUrl: text("presentation_url"),
  qaUrl: text("qa_url"),
  qaClosesAt: timestamp("qa_closes_at", { withTimezone: true }),
  promotionLabel: text("promotion_label"),
  bonusClosesAt: timestamp("bonus_closes_at", { withTimezone: true }),
  refundFloorAt: timestamp("refund_floor_at", { withTimezone: true }),
  eventTerms: text("event_terms"),
  standardLocks: boolean("standard_locks").notNull().default(false),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  offerOpensIdx: index("offer_windows_offer_opens_idx").on(table.offerId, table.opensAt),
}));
export type OfferWindow = typeof offerWindows.$inferSelect;

export const lifetimePurchases = pgTable("lifetime_purchases", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  sessionId: text("session_id").notNull(),
  paymentIntentId: text("payment_intent_id").notNull(),
  eventId: integer("event_id").notNull(),
  termsSnapshot: jsonb("terms_snapshot").$type<Record<string, string>>().notNull(),
  paidAt: timestamp("paid_at", { withTimezone: true }).notNull(),
  refundUntil: timestamp("refund_until", { withTimezone: true }).notNull(),
  creditedAllowance: integer("credited_allowance").notNull(),
  bonusAllowance: integer("bonus_allowance").notNull().default(0),
  refundedAt: timestamp("refunded_at", { withTimezone: true }),
  reviewReason: text("review_reason"),
  slackCompletedAt: timestamp("slack_completed_at", { withTimezone: true }),
  shippedLocks: integer("shipped_locks").notNull().default(0),
  creditedLocks: integer("credited_locks").notNull().default(0),
  buyerNoticeAt: timestamp("buyer_notice_at", { withTimezone: true }),
  adminNoticeAt: timestamp("admin_notice_at", { withTimezone: true }),
}, (table) => ({
  sessionUniq: uniqueIndex("lifetime_purchases_session_uniq").on(table.sessionId),
  paymentUniq: uniqueIndex("lifetime_purchases_payment_uniq").on(table.paymentIntentId),
  userIdx: index("lifetime_purchases_user_idx").on(table.userId),
}));
// B21 — daily buy box agents (max 3 per account). `criteria` overrides are
// merged onto the account's buy_box_criteria at run time. `lastRunOn` is the
// account-local YYYY-MM-DD of the last claimed daily run: the cron claims a
// day with a conditional UPDATE so several app instances never double-run.
export type BuyBoxAgentCriteria = {
  maxPurchasePrice?: number | null;
  minBedrooms?: number | null;
  maxBedrooms?: number | null;
  minCocReturn?: number | null;
  minMonthlyCashFlow?: number | null;
};
export const buyBoxAgents = pgTable("buy_box_agents", {
  id: serial("id").primaryKey(),
  accountId: integer("account_id").notNull(),
  name: text("name").notNull(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  criteria: jsonb("criteria").$type<BuyBoxAgentCriteria>().notNull().default({}),
  agentNumber: integer("agent_number"),
  versionId: integer("version_id"),
  lookbackDays: integer("lookback_days").notNull().default(2),
  active: boolean("active").notNull().default(true),
  lastRunOn: text("last_run_on"),
  lastRunAt: timestamp("last_run_at", { withTimezone: true }),
  lastRunMatches: integer("last_run_matches"),
  lastRunError: text("last_run_error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  accountFk: foreignKey({
    name: "buy_box_agents_account_fk",
    columns: [table.accountId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
  accountIdx: index("buy_box_agents_account_idx").on(table.accountId),
  accountNumberUniq: uniqueIndex("buy_box_agents_account_number_uniq").on(table.accountId, table.agentNumber),
}));
export type BuyBoxAgent = typeof buyBoxAgents.$inferSelect;

export const buyBoxVersions = pgTable("buy_box_versions", {
  id: serial("id").primaryKey(),
  accountId: integer("account_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  label: text("label"),
  minCocReturn: numeric("min_coc_return", { precision: 10, scale: 4 }),
  minMonthlyCashFlow: numeric("min_monthly_cash_flow", { precision: 12, scale: 2 }),
  maxPurchasePrice: numeric("max_purchase_price", { precision: 14, scale: 2 }),
  minBedrooms: integer("min_bedrooms"),
  maxBedrooms: integer("max_bedrooms"),
  minSqft: integer("min_sqft"),
  maxSqft: integer("max_sqft"),
  minLotSize: integer("min_lot_size"),
  noHoa: boolean("no_hoa"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  accountFk: foreignKey({
    name: "buy_box_versions_account_fk",
    columns: [table.accountId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
  accountNumberUniq: uniqueIndex("buy_box_versions_account_number_uniq").on(table.accountId, table.versionNumber),
}));
export const buyBoxMatches = pgTable("buy_box_matches", {
  id: serial("id").primaryKey(),
  accountId: integer("account_id").notNull(),
  buyBoxId: integer("buy_box_id"),
  listingKey: text("listing_key").notNull(),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  priceCents: integer("price_cents"),
  beds: integer("beds"),
  baths: numeric("baths", { precision: 4, scale: 1 }),
  listingUrl: text("listing_url"),
  imageUrl: text("image_url"),
  monthlyCashFlow: numeric("monthly_cash_flow", { precision: 12, scale: 2 }),
  cocReturn: numeric("coc_return", { precision: 10, scale: 4 }),
  reportId: integer("report_id"),
  resultId: integer("result_id"),
  firstSeenAt: timestamp("first_seen_at", { withTimezone: true }).defaultNow().notNull(),
  // Buy Box Agents — the account-local run date the house was first found,
  // and every agent that found it that day (one row per house, many tags).
  runDate: text("run_date"),
  foundBy: jsonb("found_by").$type<BuyBoxAgentTag[]>().notNull().default([]),
  notifiedAt: timestamp("notified_at", { withTimezone: true }),
  dismissedAt: timestamp("dismissed_at", { withTimezone: true }),
  openedAt: timestamp("opened_at", { withTimezone: true }),
}, (table) => ({
  accountFk: foreignKey({
    name: "buy_box_matches_account_fk",
    columns: [table.accountId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
  agentFk: foreignKey({
    name: "buy_box_matches_agent_fk",
    columns: [table.buyBoxId],
    foreignColumns: [buyBoxAgents.id],
  }).onDelete("set null"),
  accountListingUniq: uniqueIndex("buy_box_matches_account_listing_uniq").on(table.accountId, table.listingKey),
  accountSeenIdx: index("buy_box_matches_account_seen_idx").on(table.accountId, table.firstSeenAt),
}));
export type BuyBoxMatch = typeof buyBoxMatches.$inferSelect;

// B21 — per-account kill switch for daily buy box agents (admin-set).
export const buyBoxAutoAccountControls = pgTable("buy_box_auto_account_controls", {
  accountId: integer("account_id").primaryKey(),
  disabled: boolean("disabled").notNull().default(false),
  reason: text("reason"),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  accountFk: foreignKey({
    name: "buy_box_auto_controls_account_fk",
    columns: [table.accountId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
}));

// Small typed admin settings (numbers/text) that are not on/off flags, e.g.
// the daily buy box cost-alert threshold. Keys live in server/lib/admin-settings.ts.
export const adminSettings = pgTable("admin_settings", {
  key: text("key").primaryKey(),
  valueInt: integer("value_int"),
  valueText: text("value_text"),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// B11: one shared suppression list for every lifecycle/marketing email
// (unsubscribes, bounces, complaints). Transactional mail (codes, receipts)
// ignores it.
export const emailSuppressions = pgTable("email_suppressions", {
  email: text("email").primaryKey(),
  reason: text("reason").notNull(),
  source: text("source"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});
export type EmailSuppression = typeof emailSuppressions.$inferSelect;

// B11: each lifecycle email (E1..E7, T1, T2, P1, L1, L2, announcements) is
// sent to a user at most once.
export const lifecycleEmailSends = pgTable("lifecycle_email_sends", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  emailKey: text("email_key").notNull(),
  resendId: text("resend_id"),
  sentAt: timestamp("sent_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniqUserKey: uniqueIndex("lifecycle_email_sends_user_key_uniq").on(table.userId, table.emailKey),
}));
export type LifecycleEmailSend = typeof lifecycleEmailSends.$inferSelect;

// B9 "cash collected": PadPulse's own Stripe money in/out, recorded by the v3
// webhook handlers (paid invoices, lifetime purchases, refunds). amount_cents
// is positive for charges, negative for refunds. One row per Stripe object.
export const billingCashLedger = pgTable("billing_cash_ledger", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  kind: text("kind").notNull(),
  amountCents: integer("amount_cents").notNull(),
  stripeObjectId: text("stripe_object_id").notNull(),
  occurredAt: timestamp("occurred_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniqObject: uniqueIndex("billing_cash_ledger_object_uniq").on(table.stripeObjectId),
  userIdx: index("billing_cash_ledger_user_idx").on(table.userId),
}));
export type InsertProcessedStripeEvent = z.infer<typeof insertProcessedStripeEventSchema>;
export type ProcessedStripeEvent = typeof processedStripeEvents.$inferSelect;

// Task #1282 — Per-table affected-row counts returned from the admin
// "Wipe Stripe Test Artifacts" endpoint. Update fields here update both the
// storage method, the admin route response, and the frontend confirmation
// summary in lockstep.
// Task #1368 — Super-admin impersonation audit log.
// One row per impersonation session; ended_at is NULL while active.
export const impersonationLog = pgTable("impersonation_log", {
  id: serial("id").primaryKey(),
  impersonatorId: integer("impersonator_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  targetUserId: integer("target_user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
  sessionTokenHash: text("session_token_hash").notNull(),
  // `summary` (human-readable description) and `status` (response HTTP status)
  // are optional because entries written before the audit hardening lack them.
  actionsTaken: jsonb("actions_taken").$type<Array<{ method: string; endpoint: string; timestamp: string; summary?: string; status?: number }>>().default([]).notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
});

export const insertImpersonationLogSchema = createInsertSchema(impersonationLog).omit({
  id: true,
  startedAt: true,
});
export type InsertImpersonationLog = z.infer<typeof insertImpersonationLogSchema>;
export type ImpersonationLog = typeof impersonationLog.$inferSelect;

// Task #1368 — Failed impersonation attempt log (wrong password, non-super-admin, etc.).
export const impersonationFailedAttempts = pgTable("impersonation_failed_attempts", {
  id: serial("id").primaryKey(),
  attemptedBy: text("attempted_by").notNull(),
  reason: text("reason").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertImpersonationFailedAttemptSchema = createInsertSchema(impersonationFailedAttempts).omit({
  id: true,
  createdAt: true,
});
export type InsertImpersonationFailedAttempt = z.infer<typeof insertImpersonationFailedAttemptSchema>;
export type ImpersonationFailedAttempt = typeof impersonationFailedAttempts.$inferSelect;

// Admin Access Layer — audit log of every /admin panel login attempt.
// `reason` is server-side only (unknown_email | not_admin | no_admin_password |
// locked | wrong_password | rate_limited | ok | password_set) and is never
// surfaced to the client — the login endpoint always answers a failed attempt
// with the generic "Invalid credentials". user_id is NULL when the email did
// not resolve to an account; ON DELETE SET NULL so audit rows outlive users.
export const adminLoginLog = pgTable("admin_login_log", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => appUsers.id, { onDelete: "set null" }),
  emailTried: text("email_tried").notNull(),
  success: boolean("success").notNull(),
  reason: text("reason"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertAdminLoginLogSchema = createInsertSchema(adminLoginLog).omit({
  id: true,
  createdAt: true,
});
export type InsertAdminLoginLog = z.infer<typeof insertAdminLoginLogSchema>;
export type AdminLoginLog = typeof adminLoginLog.$inferSelect;

// Admin Access Layer — single-use, 24-hour set-password links for lower-tier
// admins (issued from the super-admin Admin Users UI). The emailed token is
// `<rowId>.<secret>`: the row id selects, the 32-byte secret verifies against
// token_hash (scrypt salt:hash — salted hashes can't be looked up directly).
// Issuing a new token marks prior unused tokens for the same user as used.
export const adminPasswordSetupTokens = pgTable("admin_password_setup_tokens", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  tokenHash: text("token_hash").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdBy: text("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertAdminPasswordSetupTokenSchema = createInsertSchema(adminPasswordSetupTokens).omit({
  id: true,
  createdAt: true,
});
export type InsertAdminPasswordSetupToken = z.infer<typeof insertAdminPasswordSetupTokenSchema>;
export type AdminPasswordSetupToken = typeof adminPasswordSetupTokens.$inferSelect;

// Task #2211 — Vendor impersonation audit log.
// One row per vendor impersonation session; ended_at is NULL while active.
export const vendorImpersonationLog = pgTable("vendor_impersonation_log", {
  id: serial("id").primaryKey(),
  impersonatorId: integer("impersonator_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  targetVendorId: integer("target_vendor_id").notNull().references(() => vendors.id, { onDelete: "cascade" }),
  targetVendorTeamMemberId: integer("target_vendor_team_member_id"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  endedAt: timestamp("ended_at"),
  // Task #2217 — Per-request audit trail mirroring impersonation_log.actions_taken.
  // Every non-GET request hitting /api/vendor-portal/* during an active vendor
  // impersonation session is appended here by the middleware in server/index.ts.
  actionsTaken: jsonb("actions_taken").$type<Array<{ method: string; endpoint: string; timestamp: string }>>().default([]).notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
});

export const insertVendorImpersonationLogSchema = createInsertSchema(vendorImpersonationLog).omit({
  id: true,
  startedAt: true,
});
export type InsertVendorImpersonationLog = z.infer<typeof insertVendorImpersonationLogSchema>;
export type VendorImpersonationLog = typeof vendorImpersonationLog.$inferSelect;

export const wipeStripeTestArtifactsResultSchema = z.object({
  // Rows whose Stripe-id columns were nulled (rows themselves are retained).
  appUsersUpdated: z.number().int().nonnegative(),
  paymentAccountsUpdated: z.number().int().nonnegative(),
  vendorPayoutAccountsUpdated: z.number().int().nonnegative(),
  vendorCreditCardsUpdated: z.number().int().nonnegative(),
  paymentTransactionsUpdated: z.number().int().nonnegative(),
  // Rows that were deleted outright.
  vendorPayoutsDeleted: z.number().int().nonnegative(),
  billingProrationEventsDeleted: z.number().int().nonnegative(),
  billingSettlementErrorsDeleted: z.number().int().nonnegative(),
  referralHostAccrualsDeleted: z.number().int().nonnegative(),
  referralHostCreditsDeleted: z.number().int().nonnegative(),
  processedStripeEventsDeleted: z.number().int().nonnegative(),
});
export type WipeStripeTestArtifactsResult = z.infer<typeof wipeStripeTestArtifactsResultSchema>;

// Task #1619 follow-up — Admin-configurable per-item menu visibility by
// account tier. Stored as a sparse table: rows only exist for items the
// admin has explicitly configured. Items without a row are visible to
// every tier (safe default). Founder tiers are normalized to Professional
// for matching, per shared/schema.ts:264 (hasFeatureAccess).
export const MENU_TIER_LOCK_TIERS = ['Research', 'Host', 'Growth', 'Professional', 'Enterprise', 'PMPro'] as const;
export type MenuTierLockTier = typeof MENU_TIER_LOCK_TIERS[number];

export interface MenuTierLockItemDef {
  key: string;
  label: string;
  href: string;
}
export interface MenuTierLockGroupDef {
  groupKey: string;
  groupLabel: string;
  items: MenuTierLockItemDef[];
}

// Single source of truth for what the admin can configure. Sidebar items
// reference the same `key` strings so a row in menu_tier_locks filters
// the matching nav row.
export const MENU_TIER_LOCK_REGISTRY: MenuTierLockGroupDef[] = [
  { groupKey: 'pinned', groupLabel: 'Pinned', items: [
    { key: 'pinned.dashboard', label: 'Dashboard', href: '/app/dashboard' },
    { key: 'pinned.pulse_ai', label: 'Pulse AI', href: '/app/dashboard#pulse' },
    { key: 'pinned.favorites', label: 'Favorites', href: '/app/favorites' },
    { key: 'pinned.messages', label: 'Messages', href: '/app/messages' },
    // Hash href (mirrors pinned.pulse_ai = /app/dashboard#pulse): keeps this an
    // admin-configurable toggle in the Menu Tier Locks panel WITHOUT colliding
    // with pinned.messages in MENU_TIER_LOCK_HREF_TO_KEY. A bare /app/messages
    // href would overwrite pinned.messages in that reverse map, so a denied
    // ai_drafting lock would hide the entire Messages nav row. AI drafting is
    // enforced by its dedicated middleware (requireAiDraftingAccess), not by
    // href-based sidebar visibility, so the Messages/Connections nav stay visible.
    { key: 'messages.ai_drafting', label: 'AI Drafting (Suggest & Improve)', href: '/app/messages#ai-drafting' },
    // Quo messaging — the Messaging provider settings page (Quo / Twilio
    // chooser). Own href so it can be tier-locked independently of Messages.
    { key: 'messages.messaging_provider', label: 'Messaging Provider (Quo / Twilio)', href: '/app/messages/settings' },
    { key: 'pinned.properties', label: 'Properties', href: '/app/properties' },
    { key: 'pinned.connections', label: 'Connections', href: '/app/connections' },
  ]},
  { groupKey: 'acquisitions', groupLabel: 'Acquisitions', items: [
    { key: 'acquisitions.acquire', label: 'Acquire (section)', href: '/app/acquisitions#acquire' },
    { key: 'acquisitions.buy_box_settings', label: 'Buy Box Settings', href: '/app/buy-box/settings' },
    { key: 'acquisitions.buy_box_reports', label: 'Buy Box Reports', href: '/app/buy-box/reports' },
    { key: 'acquisitions.buy_box_agents', label: 'Buy Box Agents', href: '/app/buy-box/agents' },
    { key: 'acquisitions.underwrite', label: 'Underwrite', href: '/app/underwriting' },
    { key: 'acquisitions.pipeline', label: 'Acquisitions Pipeline', href: '/app/acquisitions' },
    { key: 'acquisitions.find_realtor', label: 'Find a Realtor', href: '/app/find-realtor' },
    { key: 'acquisitions.finance', label: 'Finance', href: '/app/finance' },
    { key: 'acquisitions.buy', label: 'Buy', href: '/app/buy' },
    { key: 'acquisitions.renovate', label: 'Renovate', href: '/app/renovations' },
    { key: 'acquisitions.furnishing_catalog', label: 'Furnishing Catalog', href: '/app/furnishing-catalog' },
    { key: 'acquisitions.property_launch', label: 'Property Launch', href: '/app/property-launch' },
  ]},
  { groupKey: 'members', groupLabel: 'Members', items: [
    { key: 'members.members', label: 'Members', href: '/app/members' },
    { key: 'members.vehicles', label: 'Vehicles', href: '/app/members/vehicles' },
    { key: 'members.booking_requests', label: 'Booking Requests', href: '/app/members/booking-requests' },
    { key: 'members.reviews', label: 'Reviews', href: '/app/reviews' },
  ]},
  { groupKey: 'operations', groupLabel: 'Operations', items: [
    { key: 'operations.tasks', label: 'Tasks', href: '/app/tasks' },
    { key: 'operations.forms', label: 'Forms', href: '/app/operations/service-forms' },
    { key: 'operations.supplies', label: 'Supplies', href: '/app/supplies' },
    { key: 'operations.devices_overview', label: 'Devices Overview', href: '/app/devices' },
    { key: 'operations.devices_smart_locks', label: 'Smart Locks', href: '/app/devices/smart-locks' },
    { key: 'operations.devices_thermostats', label: 'Thermostats', href: '/app/devices/thermostats' },
    { key: 'operations.marketing', label: 'Marketing', href: '/app/marketing#section' },
    { key: 'operations.trash', label: 'Trash Service', href: '/app/operations/trash' },
  ]},
  { groupKey: 'vendors', groupLabel: 'Vendors', items: [
    { key: 'vendors.vendor_directory', label: 'Vendor Directory', href: '/app/operations/vendors' },
    { key: 'vendors.vendor_marketplace', label: 'Vendor Marketplace', href: '/app/operations/marketplace' },
    { key: 'vendors.vendor_payments', label: 'Vendor Payments', href: '/app/operations/approvals' },
    { key: 'vendors.vendor_settings', label: 'Vendor Settings', href: '/app/operations/vendors/settings' },
  ]},
  { groupKey: 'revenue', groupLabel: 'Revenue', items: [
    { key: 'revenue.profit_loss', label: 'Profit & Loss', href: '/app/financials' },
    { key: 'revenue.income', label: 'Income', href: '/app/financials/income' },
    { key: 'revenue.expenses', label: 'Expenses', href: '/app/financials/expenses' },
    { key: 'revenue.forwarded_docs', label: 'Forwarded Docs', href: '/app/financials/mail-inbox' },
    { key: 'revenue.collections', label: 'Collections', href: '/app/financials/collections' },
    { key: 'revenue.financials_settings', label: 'Financials Settings', href: '/app/financials/settings' },
    { key: 'revenue.pricing_rooms', label: 'Pricing / Rooms', href: '/app/pricing' },
    { key: 'revenue.image_generator', label: 'Image Generator', href: '/app/image-generator' },
    { key: 'revenue.sell', label: 'Sell', href: '/app/sell' },
  ]},
  { groupKey: 'owners', groupLabel: 'Owners', items: [
    { key: 'owners.owners_directory', label: 'Owners Directory', href: '/app/operations/owners' },
    { key: 'owners.owner_invoices', label: 'Owner Invoices', href: '/app/operations/owner-invoices' },
    { key: 'owners.owner_settings', label: 'Owner Settings', href: '/app/account/pm-billing' },
  ]},
  { groupKey: 'utilities', groupLabel: 'Utilities', items: [
    { key: 'utilities.help_faq', label: 'Help / FAQ', href: '/app/faq' },
  ]},
];

// Reverse lookup: href → tier-lock key. Used by the sidebar to check
// whether the current user is allowed to see a particular nav row.
export const MENU_TIER_LOCK_HREF_TO_KEY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const group of MENU_TIER_LOCK_REGISTRY) {
    for (const item of group.items) {
      map[item.href] = item.key;
    }
  }
  return map;
})();

export const menuTierLocks = pgTable("menu_tier_locks", {
  id: serial("id").primaryKey(),
  itemKey: text("item_key").notNull().unique(),
  // Use '{}'::text[] (not ARRAY[]::text[]) so the stored default matches
  // what Drizzle reads back from pg_get_expr, keeping db:push idempotent.
  allowedTiers: text("allowed_tiers").array().notNull().default(sql`'{}'::text[]`),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedByUserId: integer("updated_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
});

export const insertMenuTierLockSchema = createInsertSchema(menuTierLocks).omit({
  id: true,
  updatedAt: true,
});
export type InsertMenuTierLock = z.infer<typeof insertMenuTierLockSchema>;
export type MenuTierLock = typeof menuTierLocks.$inferSelect;

// Normalize an AccountType to one of MENU_TIER_LOCK_TIERS for matching.
// Founder tiers inherit Professional's settings. Anything outside the
// 5 lock tiers (e.g. Interest) returns null — treated as "no matching
// tier", which under the safe-default model means the item is only
// hidden if the admin has configured a lock that doesn't include its
// tier. Since there's no row for those users, sidebar code should
// treat null as "always allow".
export function normalizeTierForMenuLock(accountType: AccountType): MenuTierLockTier | null {
  if (isFounderTier(accountType)) return 'Professional';
  if ((MENU_TIER_LOCK_TIERS as readonly string[]).includes(accountType)) {
    return accountType as MenuTierLockTier;
  }
  return null;
}

// Task #1722 — Agent Mail document inbox
export const MAIL_INGESTED_DOCUMENT_STATUSES = ['pending_review', 'approved', 'dismissed'] as const;
export type MailIngestedDocumentStatus = typeof MAIL_INGESTED_DOCUMENT_STATUSES[number];

export const MAIL_INGESTED_DOCUMENT_TYPES = ['invoice', 'expense', 'bid', 'contract', 'other'] as const;
export type MailIngestedDocumentType = typeof MAIL_INGESTED_DOCUMENT_TYPES[number];

export const mailIngestedDocuments = pgTable("mail_ingested_documents", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  agentmailMessageId: text("agentmail_message_id").notNull(),
  agentmailAttachmentName: text("agentmail_attachment_name").notNull(),
  senderEmail: text("sender_email"),
  senderName: text("sender_name"),
  receivedAt: timestamp("received_at", { withTimezone: true }),
  mimeType: text("mime_type").notNull(),
  // Task #1941 — Legacy column carried base64 `data:...` URLs of the original
  // AgentMail attachment. New ingests write bytes to Object Storage and only
  // persist the relative `file_key` instead, leaving `file_url` NULL. The
  // one-time backfill drains existing data URLs and nulls this column out.
  fileUrl: text("file_url"),
  fileKey: text("file_key"),
  fileHash: text("file_hash"),
  status: text("status").notNull().default("pending_review"),
  geminiDocumentType: text("gemini_document_type"),
  extractedData: jsonb("extracted_data"),
  suggestedPropertyId: integer("suggested_property_id").references(() => properties.id, { onDelete: "set null" }),
  approvedPropertyId: integer("approved_property_id").references(() => properties.id, { onDelete: "set null" }),
  // approvedDocumentId FK is declared via foreignKey() below — the auto-generated
  // name (mail_ingested_documents_approved_document_id_property_documents_id_fk,
  // 69 chars) exceeds Postgres's 63-char identifier limit.
  approvedDocumentId: integer("approved_document_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniqueMessageAttachment: uniqueIndex("mail_ingested_docs_msg_att_unique").on(table.agentmailMessageId, table.agentmailAttachmentName),
  approvedDocumentFk: foreignKey({
    name: "mail_ingested_docs_approved_document_fk",
    columns: [table.approvedDocumentId],
    foreignColumns: [propertyDocuments.id],
  }).onDelete("set null"),
}));

export const insertMailIngestedDocumentSchema = createInsertSchema(mailIngestedDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMailIngestedDocument = z.infer<typeof insertMailIngestedDocumentSchema>;
export type MailIngestedDocument = typeof mailIngestedDocuments.$inferSelect;

// Task #1773 — Recommended Furnishings public page + admin XLSX import/export.
export const RECOMMENDED_FURNISHING_ROOM_TYPES = [
  "bedroom",
  "bathroom",
  "kitchen",
  "living_room",
  "common_area",
  "exterior",
] as const;
export type RecommendedFurnishingRoomType = typeof RECOMMENDED_FURNISHING_ROOM_TYPES[number];

export const RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS: Record<RecommendedFurnishingRoomType, string> = {
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  kitchen: "Kitchen",
  living_room: "Living Room",
  common_area: "Common Area",
  exterior: "Exterior",
};

export const recommendedFurnishings = pgTable("recommended_furnishings", {
  id: serial("id").primaryKey(),
  roomType: text("room_type").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  priceCents: integer("price_cents").notNull().default(0),
  purchaseUrl: text("purchase_url"),
  affiliateUrl: text("affiliate_url"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertRecommendedFurnishingSchema = createInsertSchema(recommendedFurnishings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  roomType: z.enum(RECOMMENDED_FURNISHING_ROOM_TYPES),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  priceCents: z.coerce.number().int().min(0).default(0),
  purchaseUrl: z.string().url("Must be a valid URL").regex(/^https?:\/\//, "URL must start with http:// or https://").optional().nullable().or(z.literal("")),
  affiliateUrl: z.string().url("Must be a valid URL").regex(/^https?:\/\//, "URL must start with http:// or https://").optional().nullable().or(z.literal("")),
  sortOrder: z.coerce.number().int().default(0),
});

export type InsertRecommendedFurnishing = z.infer<typeof insertRecommendedFurnishingSchema>;
export type RecommendedFurnishing = typeof recommendedFurnishings.$inferSelect;

// Task #1777 — Track affiliate/purchase link clicks on recommended furnishings.
export const recommendedFurnishingClicks = pgTable("recommended_furnishing_clicks", {
  id: serial("id").primaryKey(),
  furnishingId: integer("furnishing_id").notNull(),
  clickedAt: timestamp("clicked_at").defaultNow().notNull(),
  ipHash: text("ip_hash"),
  userAgent: text("user_agent"),
}, (table) => ({
  // Explicit short FK name — the auto-generated one exceeds Postgres's 63-char identifier limit.
  furnishingFk: foreignKey({
    name: "rec_furnishing_clicks_furnishing_fk",
    columns: [table.furnishingId],
    foreignColumns: [recommendedFurnishings.id],
  }).onDelete("cascade"),
  // Mirror the indexes provisioned in scripts/deploy-migrate.sql so the
  // schema↔deploy parity gate stays clean (Task #785).
  furnishingIdIdx: index("idx_recommended_furnishing_clicks_furnishing_id").on(table.furnishingId),
  clickedAtIdx: index("idx_recommended_furnishing_clicks_clicked_at").on(table.clickedAt),
}));

export type RecommendedFurnishingClick = typeof recommendedFurnishingClicks.$inferSelect;
export type RecommendedFurnishingClickStats = {
  furnishingId: number;
  totalClicks: number;
  clicks30d: number;
};

// Task #1788 — Time-bucketed click counts per furnishing item for trend chart.
export type RecommendedFurnishingClickTimeseriesBucket = {
  furnishingId: number;
  weekStart: string; // ISO date string for the start of the week (Mon, UTC)
  clicks: number;
};

// Task #1828 — Promo Code System
export const promoCodes = pgTable("promo_codes", {
  id: serial("id").primaryKey(),
  code: text("code").notNull().unique(),
  stripePromotionCodeId: text("stripe_promotion_code_id"),
  stripeCouponId: text("stripe_coupon_id"),
  discountType: text("discount_type").notNull(), // "percent" or "fixed"
  discountValue: numeric("discount_value", { precision: 10, scale: 2 }).notNull(),
  duration: text("duration").notNull(), // "forever" or "repeating"
  durationMonths: integer("duration_months"), // only when duration="repeating"
  maxRedemptions: integer("max_redemptions"), // null = unlimited
  currentRedemptions: integer("current_redemptions").default(0).notNull(),
  expiresAt: timestamp("expires_at"),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdByAdminId: integer("created_by_admin_id"),
});

export const insertPromoCodeSchema = createInsertSchema(promoCodes).omit({
  id: true,
  currentRedemptions: true,
  createdAt: true,
  stripePromotionCodeId: true,
  stripeCouponId: true,
}).extend({
  code: z.string().min(1).max(50).regex(/^[A-Z0-9_-]+$/, "Code must be uppercase letters, numbers, underscores, or dashes"),
  discountType: z.enum(["percent", "fixed"]),
  discountValue: z.coerce.number().positive("Discount value must be positive"),
  duration: z.enum(["forever", "repeating"]),
  durationMonths: z.coerce.number().int().positive().optional().nullable(),
  maxRedemptions: z.coerce.number().int().positive().optional().nullable(),
  expiresAt: optionalCoercedDate,
  createdByAdminId: z.number().optional().nullable(),
});
export type InsertPromoCode = z.infer<typeof insertPromoCodeSchema>;
export type PromoCode = typeof promoCodes.$inferSelect;

export const userPromoCodes = pgTable("user_promo_codes", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  promoCodeId: integer("promo_code_id").notNull().references(() => promoCodes.id),
  appliedAt: timestamp("applied_at").defaultNow().notNull(),
  stripePromotionCodeId: text("stripe_promotion_code_id"),
});
export type UserPromoCode = typeof userPromoCodes.$inferSelect;

// Task #1782 — Beta Feature Flag System
export const betaFeatures = pgTable("beta_features", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  allowSelfOptIn: boolean("allow_self_opt_in").notNull().default(false),
  // Task #3976 — runtime "Coming Soon" gate. When gateEnabled is true the
  // feature is gated for non-exempt users (admins + user_beta_access grantees
  // bypass). gateMode selects the experience: 'hidden' (nav hidden + route/API
  // blocked, the historical beta behavior) or 'coming_soon' (nav stays visible,
  // opening it shows gateMessage and the API is blocked). These are independent
  // of isActive/allowSelfOptIn (which still govern the self-opt-in beta flow).
  gateEnabled: boolean("gate_enabled").notNull().default(false),
  gateMode: text("gate_mode").notNull().default("hidden"),
  gateMessage: text("gate_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertBetaFeatureSchema = createInsertSchema(betaFeatures).omit({
  id: true,
  createdAt: true,
}).extend({
  key: z.string().min(1).regex(/^[a-z0-9_-]+$/, "Key must be lowercase letters, numbers, underscores, or dashes"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  allowSelfOptIn: z.boolean().default(false),
  gateEnabled: z.boolean().optional(),
  gateMode: z.enum(["hidden", "coming_soon"]).optional(),
  gateMessage: z.string().optional().nullable(),
});

export type InsertBetaFeature = z.infer<typeof insertBetaFeatureSchema>;
export type BetaFeature = typeof betaFeatures.$inferSelect;

export const userBetaAccess = pgTable("user_beta_access", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  betaFeatureKey: text("beta_feature_key").notNull().references(() => betaFeatures.key, { onDelete: "cascade" }),
  grantedBy: integer("granted_by").references(() => appUsers.id, { onDelete: "set null" }),
  grantedAt: timestamp("granted_at").defaultNow().notNull(),
}, (table) => ({
  uniqueUserFeature: uniqueIndex("user_beta_access_unique").on(table.userId, table.betaFeatureKey),
}));

export const insertUserBetaAccessSchema = createInsertSchema(userBetaAccess).omit({
  id: true,
  grantedAt: true,
});
export type InsertUserBetaAccess = z.infer<typeof insertUserBetaAccessSchema>;
export type UserBetaAccess = typeof userBetaAccess.$inferSelect;

// Task #1908 — Mercury reconciliation suggestions.
// One row per (userId, mercuryExpenseId, manualExpenseId) candidate pair the
// nightly scorer identified. Status drives whether the pair surfaces in the
// host UI ("open" / "snoozed" past expiry) or stays hidden ("merged" /
// "dismissed" / "snoozed" until snoozedUntil). The unique index makes the
// scorer idempotent — re-running it never inserts duplicate pairs.
export const EXPENSE_MERGE_CANDIDATE_STATUSES = ["open", "merged", "dismissed", "snoozed"] as const;
export type ExpenseMergeCandidateStatus = typeof EXPENSE_MERGE_CANDIDATE_STATUSES[number];

export const expenseMergeCandidates = pgTable("expense_merge_candidates", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  mercuryExpenseId: integer("mercury_expense_id").notNull().references(() => expenses.id, { onDelete: "cascade" }),
  manualExpenseId: integer("manual_expense_id").notNull().references(() => expenses.id, { onDelete: "cascade" }),
  status: text("status").$type<ExpenseMergeCandidateStatus>().notNull().default("open"),
  matchScore: numeric("match_score", { precision: 5, scale: 2 }).notNull().default("0"),
  amountDiff: numeric("amount_diff", { precision: 10, scale: 2 }),
  daysDiff: integer("days_diff"),
  vendorOverlap: numeric("vendor_overlap", { precision: 5, scale: 2 }),
  // Task #1910 — many-to-one (split-receipt) candidates. When populated,
  // these arrays carry the additional expense ids on either side of the
  // pairing beyond the primary mercury_expense_id / manual_expense_id. A
  // 1-to-1 pair leaves both arrays NULL/empty. A 1 Mercury → N manuals
  // pairing populates extra_manual_expense_ids; an N Mercurys → 1 manual
  // pairing populates extra_mercury_expense_ids.
  extraMercuryExpenseIds: integer("extra_mercury_expense_ids").array(),
  extraManualExpenseIds: integer("extra_manual_expense_ids").array(),
  snoozedUntil: timestamp("snoozed_until"),
  mergedAt: timestamp("merged_at"),
  dismissedAt: timestamp("dismissed_at"),
  // Task #1946 — historical unmerge audit + snapshot.
  // mercuryPreMergeSnapshot stores the Mercury field values that the merge
  // routine overwrote (per Mercury expense id) so /unmerge can restore them
  // even months later. Shape: { [mercuryExpenseId]: { propertyId, category,
  // notes, description, vendor } }. The audit columns answer "who/when".
  mercuryPreMergeSnapshot: jsonb("mercury_pre_merge_snapshot"),
  mergedByUserId: integer("merged_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  unmergedAt: timestamp("unmerged_at"),
  unmergedByUserId: integer("unmerged_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  // Task #1974 — explicit batch tagging for scorer-driven auto-merges. When
  // the nightly scorer auto-merges a candidate it stamps both columns; pure
  // manual merges (from the UI) leave them NULL. The UI batches "Recently
  // merged" rows by `autoMergeBatchId` (falling back to timestamp
  // clustering for legacy rows) so an auto-merge run is always distinct
  // from a teammate's one-off Merge click and the in-app notification can
  // deep-link straight to its batch.
  autoMergeBatchId: text("auto_merge_batch_id"),
  autoMergedAt: timestamp("auto_merged_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniquePair: uniqueIndex("expense_merge_candidates_unique").on(table.userId, table.mercuryExpenseId, table.manualExpenseId),
  userStatusIdx: index("expense_merge_candidates_user_status_idx").on(table.userId, table.status),
}));

export const insertExpenseMergeCandidateSchema = createInsertSchema(expenseMergeCandidates).omit({ id: true, createdAt: true });
export type InsertExpenseMergeCandidate = z.infer<typeof insertExpenseMergeCandidateSchema>;
export type ExpenseMergeCandidate = typeof expenseMergeCandidates.$inferSelect;

// ---------------------------------------------------------------------------
// Task #1921 — Legacy aliases for the unified `tasks` table. The maintenance
// (`maintenance_tasks`) and operations (`operations_tasks`) tables were
// collapsed into the single `tasks` table in task #1920; these re-exports
// keep one release of source-level compatibility with code paths that still
// reference the old names (`MaintenanceTask`, `OperationsTask`, etc.). All
// alias names map 1:1 to the unified types/schemas — they do not introduce
// new behavior.
// ---------------------------------------------------------------------------
export const MAINTENANCE_CATEGORIES = [
  "HVAC", "Plumbing", "Electrical", "Appliances", "Trash", "Room Turn", "Other",
] as const;
export type MaintenanceCategory = typeof MAINTENANCE_CATEGORIES[number];

export type MaintenanceTask = Task;
export type OperationsTask = Task;
export type MaintenanceComment = TaskComment;
export type MaintenanceTaskMessage = TaskMessage;
export type MaintenanceTaskWithLinkedChats = TaskWithLinkedChats;
export type MaintenanceTaskWithUnread = TaskWithUnread;
export type InsertMaintenanceTask = z.infer<typeof insertMaintenanceTaskSchema>;
export type InsertOperationsTask = z.infer<typeof insertMaintenanceTaskSchema>;
export type InsertMaintenanceComment = InsertTaskComment;

// `insertMaintenanceTaskSchema` is intentionally a permissive standalone
// schema (not a re-export of `insertTaskSchema`) so existing host-side forms
// — which were built against the legacy maintenance shape — keep typechecking
// without needing default values for every unified-table field. The host UI
// rewrite at `/app/tasks` (queued as a separate task) will switch to
// `insertTaskSchema` directly.
export const insertMaintenanceTaskSchema = z.object({
  userId: z.number().optional(),
  title: z.string().min(1, "Title is required"),
  description: z.string().max(500, "Description must be 500 characters or fewer").optional().nullable(),
  propertyId: z.number().optional().nullable(),
  roomId: z.number().optional().nullable(),
  memberId: z.number().optional().nullable(),
  reporterMemberId: z.number().optional().nullable(),
  status: z.string().optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  source: z.enum(TASK_SOURCES).optional(),
  category: z.string().optional().nullable(),
  type: z.enum(TASK_TYPES).optional(),
  vendorId: z.number().optional().nullable(),
  assigneeId: z.number().optional().nullable(),
  assignedTeamMemberId: z.number().optional().nullable(),
  paymentRequired: z.boolean().optional(),
  priceCents: z.number().optional().nullable(),
  estimateCents: z.number().optional().nullable(),
  priceType: z.string().optional().nullable(),
  hoursLogged: z.string().optional().nullable(),
  scheduledStart: optionalCoercedDate,
  scheduledEnd: optionalCoercedDate,
  dueDate: optionalCoercedDate,
  padsplitTaskId: z.string().optional().nullable(),
  syncToPadsplit: z.boolean().optional(),
  padsplitPushError: z.string().optional().nullable(),
  attachmentUrls: z.array(z.string()).optional(),
  linkedApplianceId: z.number().int().positive().optional().nullable(),
  isSample: z.boolean().optional(),
  formTemplateId: z.number().optional().nullable(),
  timeTrackingEnabled: z.boolean().optional(),
  archivedAt: optionalCoercedDate,
  createdBy: z.number().optional().nullable(),
  createdById: z.number().optional().nullable(),
});
export const insertOperationsTaskSchema = insertMaintenanceTaskSchema;
export const insertMaintenanceCommentSchema = insertTaskCommentSchema;

// Task #4833 — Appliance Type field
export const APPLIANCE_TYPES = [
  "Refrigerator",
  "Freezer",
  "Washer",
  "Dryer",
  "Dishwasher",
  "Oven/Range",
  "Microwave",
  "HVAC/AC",
  "Furnace",
  "Water Heater",
  "Garbage Disposal",
  "Other",
] as const;
export type ApplianceType = typeof APPLIANCE_TYPES[number];

// Task #2066 — Appliances tab on property page
export const appliances = pgTable("appliances", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  applianceType: text("appliance_type"),
  make: text("make"),
  model: text("model"),
  serialNumber: text("serial_number"),
  dateOfPurchase: date("date_of_purchase"),
  warrantyLengthMonths: integer("warranty_length_months"),
  warrantyClaimLink: text("warranty_claim_link"),
  purchasedFrom: text("purchased_from"),
  orderLink: text("order_link"),
  photos: text("photos").array().notNull().default(sql`'{}'::text[]`),
  extendedWarranty: boolean("extended_warranty").notNull().default(false),
  extendedWarrantyNumber: text("extended_warranty_number"),
  extendedWarrantyExpiration: date("extended_warranty_expiration"),
  extendedWarrantyLink: text("extended_warranty_link"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  propertyIdx: index("idx_appliances_property_id").on(t.propertyId),
}));

export const insertApplianceSchema = createInsertSchema(appliances).omit({ id: true, createdAt: true }).extend({
  propertyId: z.number(),
  applianceType: z.enum(APPLIANCE_TYPES).nullable().optional(),
  make: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  serialNumber: z.string().nullable().optional(),
  dateOfPurchase: z.string().nullable().optional(),
  warrantyLengthMonths: z.number().int().min(0).nullable().optional(),
  warrantyClaimLink: z.string().url("Must be a valid URL").nullable().optional().or(z.literal("").transform(() => null)),
  purchasedFrom: z.string().nullable().optional(),
  orderLink: z.string().url("Must be a valid URL").nullable().optional().or(z.literal("").transform(() => null)),
  photos: z.array(z.string()).optional().default([]),
  extendedWarranty: z.boolean().optional().default(false),
  extendedWarrantyNumber: z.string().nullable().optional(),
  extendedWarrantyExpiration: z.string().nullable().optional(),
  extendedWarrantyLink: z.string().url("Must be a valid URL").nullable().optional().or(z.literal("").transform(() => null)),
  notes: z.string().nullable().optional(),
});
export type InsertAppliance = z.infer<typeof insertApplianceSchema>;
export type Appliance = typeof appliances.$inferSelect;

// ─── PadSplit Income Imports (Task #2154) ────────────────────────────────────
// Readable PadSplit earnings update history. Legacy rows represent manual ZIP
// imports; current rows are written by the external browser harvest and carry
// raw_summary.producer = "browser_harvest", changed-month entries, and notices.

export const padsplitIncomeImports = pgTable("padsplit_income_imports", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  importedAt: timestamp("imported_at").defaultNow().notNull(),
  dateRangeStart: text("date_range_start"),
  dateRangeEnd: text("date_range_end"),
  propertiesMatched: integer("properties_matched").default(0).notNull(),
  propertiesUnmatched: integer("properties_unmatched").default(0).notNull(),
  rowsInserted: integer("rows_inserted").default(0).notNull(),
  rawSummary: jsonb("raw_summary"),
}, (table) => ({
  userIdIdx: index("idx_padsplit_income_imports_user_id").on(table.userId),
  importedAtIdx: index("idx_padsplit_income_imports_imported_at").on(table.importedAt),
}));

export const insertPadsplitIncomeImportSchema = createInsertSchema(padsplitIncomeImports).omit({
  id: true,
  importedAt: true,
});
export type InsertPadsplitIncomeImport = z.infer<typeof insertPadsplitIncomeImportSchema>;
export type PadsplitIncomeImport = typeof padsplitIncomeImports.$inferSelect;

// ─── Income Transactions (Task #2162) ─────────────────────────────────────────
// First-class income ledger. Every income event — Mercury bank credits,
// PadSplit earnings zip rows, and manually-entered income — lands here.
// The expenses table still owns the PadSplit earnings rows for P&L parity
// (dual-write); this table adds reconciliation status + cross-source matching.

export const incomeTransactions = pgTable("income_transactions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  // 'mercury_credit' | 'padsplit_earnings' | 'manual'
  source: text("source").notNull(),
  category: text("category").notNull().default("other_income"),
  amount: numeric("amount", { precision: 12, scale: 2 }).notNull(),
  // YYYY-MM-DD
  date: text("date").notNull(),
  vendor: text("vendor"),
  description: text("description"),
  notes: text("notes"),
  // Dedup key — Mercury resource ID, padsplit_import external ID, etc.
  externalId: text("external_id"),
  externalSource: text("external_source"),
  mercuryAccountId: text("mercury_account_id"),
  cardLast4: text("card_last_4"),
  // 'unmatched' | 'matched' | 'partially_matched'
  reconciliationStatus: text("reconciliation_status").notNull().default("unmatched"),
  // FK to padsplit_income_imports.id for PadSplit earnings rows
  padsplitImportBatchId: integer("padsplit_import_batch_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Task #2305 — Split transactions. When non-null, this row is a split
  // child line of the income transaction whose id is splitParentId.
  // ON DELETE CASCADE removes children when the parent is deleted.
  splitParentId: integer("split_parent_id"),
  // Task #3788 — Durable "Company Operations" marker. When true, the income
  // is intentionally company-wide (not tied to a single property) and is
  // surfaced as its own P&L group. Distinct from a NULL propertyId. Must
  // survive Mercury re-syncs: the upsert path skips re-stamping propertyId
  // when this is true. Setting a property clears it.
  isCompanyLevel: boolean("is_company_level").default(false),
  excludedAt: timestamp("excluded_at"),
  // Revenue triage — human-confirmed marker; same semantics as
  // expenses.reviewedAt (never stamped by imports or auto-match).
  reviewedAt: timestamp("reviewed_at"),
}, (table) => ({
  externalIdUniq: uniqueIndex("income_transactions_user_external_id_idx")
    .on(table.userId, table.externalId)
    .where(sql`external_id IS NOT NULL`),
  userIdIdx: index("income_transactions_user_id_idx").on(table.userId),
  propertyIdIdx: index("income_transactions_property_id_idx").on(table.propertyId),
  dateIdx: index("income_transactions_date_idx").on(table.date),
  // Task #2305 — Speeds up the "find children of parent" lookup used by
  // the income splits API and the parent-row badge derivation. Mirrors
  // the index created by scripts/deploy-migrate.sql on production.
  splitParentIdIdx: index("income_transactions_split_parent_id_idx").on(table.splitParentId),
}));

export const insertIncomeTransactionSchema = createInsertSchema(incomeTransactions).omit({
  id: true,
  createdAt: true,
}).extend({
  amount: z.string().or(z.number()).transform(String),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
  source: z.enum(["mercury_credit", "padsplit_earnings", "manual", "stripe_bank"]),
  category: z.string().default("other_income"),
  reconciliationStatus: z.enum(["unmatched", "matched", "partially_matched"]).default("unmatched"),
});
export type InsertIncomeTransaction = z.infer<typeof insertIncomeTransactionSchema>;
export type IncomeTransaction = typeof incomeTransactions.$inferSelect;

// ─── Income Reconciliation Links (Task #2162) ─────────────────────────────────
// Links one mercury_credit deposit row to one PadSplit earnings row.
// Multiple links from the same deposit cover multi-property payouts.

export const incomeReconciliationLinks = pgTable("income_reconciliation_links", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // The Mercury PadSplit deposit (source='mercury_credit')
  // FK declared via foreignKey() helper below to keep the constraint name under
  // Postgres's 63-char identifier limit.
  depositIncomeId: integer("deposit_income_id").notNull(),
  // The PadSplit earnings row (source='padsplit_earnings')
  earningsIncomeId: integer("earnings_income_id").notNull(),
  matchedAmount: numeric("matched_amount", { precision: 12, scale: 2 }).notNull().default("0"),
  variance: numeric("variance", { precision: 12, scale: 2 }).notNull().default("0"),
  // 'matched' | 'partial'
  status: text("status").notNull().default("matched"),
  matchedByUserId: integer("matched_by_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  matchedAt: timestamp("matched_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  depositEarningsUniq: uniqueIndex("income_recon_links_deposit_earnings_idx")
    .on(table.depositIncomeId, table.earningsIncomeId),
  depositFk: foreignKey({
    name: "income_recon_links_deposit_fk",
    columns: [table.depositIncomeId],
    foreignColumns: [incomeTransactions.id],
  }).onDelete("cascade"),
  earningsFk: foreignKey({
    name: "income_recon_links_earnings_fk",
    columns: [table.earningsIncomeId],
    foreignColumns: [incomeTransactions.id],
  }).onDelete("cascade"),
  depositIdx: index("income_recon_links_deposit_idx").on(table.depositIncomeId),
  earningsIdx: index("income_recon_links_earnings_idx").on(table.earningsIncomeId),
}));

export const insertIncomeReconciliationLinkSchema = createInsertSchema(incomeReconciliationLinks).omit({
  id: true,
  createdAt: true,
  matchedAt: true,
});
export type InsertIncomeReconciliationLink = z.infer<typeof insertIncomeReconciliationLinkSchema>;
export type IncomeReconciliationLink = typeof incomeReconciliationLinks.$inferSelect;

// Task #2363 — Admin-managed recipient list for partner application alert
// emails. Singleton row (id=1) holding a default recipient list plus an
// optional per-partner-type override map (e.g. realtor → [lead@…]).
export const partnerApplicationNotificationSettings = pgTable(
  "partner_application_notification_settings",
  {
    id: serial("id").primaryKey(),
    defaultRecipients: jsonb("default_recipients").$type<string[]>().default([]).notNull(),
    perTypeRecipients: jsonb("per_type_recipients")
      .$type<Record<string, string[]>>()
      .default({})
      .notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    updatedBy: text("updated_by"),
  },
);

export const partnerApplicationNotificationSettingsSchema = z.object({
  defaultRecipients: z.array(z.string().email()).default([]),
  perTypeRecipients: z
    .record(z.enum(["lender", "wholesaler", "realtor", "broker"]), z.array(z.string().email()))
    .default({}),
});
export type PartnerApplicationNotificationSettingsInput = z.infer<
  typeof partnerApplicationNotificationSettingsSchema
>;
export type PartnerApplicationNotificationSettingsRow =
  typeof partnerApplicationNotificationSettings.$inferSelect;

// ─── Pulse: per-user state (Task #2361 follow-ups) ───────────────────────────
// Per-user persistent state for the Pulse AI dashboard briefing. Replaces the
// in-process `lastViewedAt` Map (which reset on every container restart and
// was inconsistent across multi-instance deployments) and stores durable
// dismissals, per-item snoozes, host feedback (👍/👎), and the email-digest
// opt-in preference. Beta-gated end-to-end — only hosts granted
// `pulse_dashboard_briefing` ever read or write here.
export const pulseUserState = pgTable("pulse_user_state", {
  userId: integer("user_id")
    .primaryKey()
    .references(() => appUsers.id, { onDelete: "cascade" }),
  lastViewedAt: timestamp("last_viewed_at"),
  // Map of stable item-key → ISO timestamp the host dismissed it. Durable
  // (cross-session) dismiss. Items reappear after 7 days so an outdated
  // dismissal doesn't silently bury a re-occurring problem.
  dismissedItems: jsonb("dismissed_items").$type<Record<string, string>>().default({}).notNull(),
  // Map of stable item-key → ISO timestamp the snooze expires.
  snoozedItems: jsonb("snoozed_items").$type<Record<string, string>>().default({}).notNull(),
  // Per-item host feedback (👍/👎). Last vote per key wins.
  feedback: jsonb("feedback")
    .$type<Record<string, { vote: "up" | "down"; at: string; note?: string }>>()
    .default({})
    .notNull(),
  emailDigestEnabled: boolean("email_digest_enabled").notNull().default(false),
  // Local "YYYY-MM-DD" we last sent the email digest to this user, so the
  // every-15-min digest cron doesn't double-send.
  lastEmailSentDate: text("last_email_sent_date"),
  // Task #2570 — per-user configurable hour (0-23, local time) at which the
  // daily digest should fire. Defaults to 7 (7:00 AM) to preserve existing
  // behavior for users who opted in before this column existed.
  digestSendHour: integer("digest_send_hour").notNull().default(7),
  // Task #2570 — durable observability: record the last digest attempt
  // outcome so "were digests actually sent?" has a trustworthy answer.
  lastDigestAttemptAt: timestamp("last_digest_attempt_at"),
  lastDigestStatus: text("last_digest_status"), // 'sent' | 'failed' | 'no_resend'
  lastDigestError: text("last_digest_error"),
  // Task #2435 — host's preferred ElevenLabs voice for the "Tell it to me"
  // voice briefing. Nullable → falls back to ELEVENLABS_VOICE_ID. Validated
  // against PULSE_VOICE_OPTIONS in shared/pulse-voices.ts before write.
  preferredVoiceId: text("preferred_voice_id"),
  // Task #2431 — persistent per-user manual-refresh cooldown expiry.
  // Replaces a previous in-process Map so the tier-based refresh cooldown
  // survives both server restarts AND page reloads. Null means no cooldown
  // is currently armed.
  refreshCooldownUntil: timestamp("refresh_cooldown_until"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type PulseUserState = typeof pulseUserState.$inferSelect;

// ─── Pulse KB suggestions (Task #2361 Sprint 4) ──────────────────────────────
// Candidate additions to either the host's Account Knowledge Base or a
// specific property's Knowledge Base, generated by Pulse from recent
// signals (member messages, completed tasks, host feedback notes).
// Surfaced in the briefing as Memory items with explicit Yes/No buttons —
// nothing is ever written to a KB without the host clicking Yes.
export const pulseKbSuggestions = pgTable("pulse_kb_suggestions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => appUsers.id, { onDelete: "cascade" }),
  // "account" → appends to appUsers.aiMemory; "property" → appends to
  // properties.aiMemory for the propertyId below.
  target: text("target").notNull(),
  propertyId: integer("property_id"),
  // Short title shown as the Pulse item headline.
  title: text("title").notNull(),
  // The exact Markdown that will be appended to the AI Memory section if
  // the host clicks Yes. Bounded server-side so a runaway LLM can't
  // dump a wall of text into the KB.
  proposedMarkdown: text("proposed_markdown").notNull(),
  // Why Pulse thinks this is worth remembering — shown in the "Why this?"
  // popover and persisted for auditing.
  rationale: text("rationale"),
  sourceEvidence: jsonb("source_evidence").$type<Record<string, unknown>>(),
  // "pending" | "confirmed" | "dismissed". Last write wins.
  status: text("status").notNull().default("pending"),
  // Content signature so we don't repeatedly suggest the same memory.
  signature: text("signature").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  decidedAt: timestamp("decided_at"),
}, (table) => ({
  userStatusIdx: index("idx_pulse_kb_suggestions_user_status").on(table.userId, table.status),
  userSignatureUniq: uniqueIndex("pulse_kb_suggestions_user_sig_uniq").on(table.userId, table.signature),
}));

export type PulseKbSuggestion = typeof pulseKbSuggestions.$inferSelect;
export type InsertPulseKbSuggestion = typeof pulseKbSuggestions.$inferInsert;

// ─── Mercury account balance snapshots (Task #2374 / Pulse Sprint 3) ─────────
// Daily snapshot of every Mercury account's current/available balance. Lets
// Pulse compute true prior-day deltas instead of approximating from today's
// transactions (which is wrong when transactions land out-of-order). Falls
// back to the tx-sum approach when no snapshot exists.
export const mercuryAccountBalanceSnapshots = pgTable(
  "mercury_account_balance_snapshots",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => appUsers.id, { onDelete: "cascade" }),
    mercuryAccountId: text("mercury_account_id").notNull(),
    snapshotDate: date("snapshot_date").notNull(),
    currentBalance: numeric("current_balance", { precision: 14, scale: 2 }),
    availableBalance: numeric("available_balance", { precision: 14, scale: 2 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => ({
    uniq: uniqueIndex("mercury_balance_snapshots_acct_date_uniq").on(
      table.mercuryAccountId,
      table.snapshotDate,
    ),
    userDateIdx: index("idx_mercury_balance_snapshots_user_date").on(table.userId, table.snapshotDate),
  }),
);

export type MercuryAccountBalanceSnapshot =
  typeof mercuryAccountBalanceSnapshots.$inferSelect;

// ─── Partner Agreement E-Signature Submissions (Task #2464) ──────────────────

export const PARTNER_AGREEMENT_STATUSES = ["pending", "approved", "rejected", "needs_more_info"] as const;
export type PartnerAgreementStatus = typeof PARTNER_AGREEMENT_STATUSES[number];

export const partnerAgreementSubmissions = pgTable("partner_agreement_submissions", {
  id: serial("id").primaryKey(),
  uuid: text("uuid").notNull().unique(),
  partnerType: text("partner_type").notNull(),
  status: text("status").default("pending").notNull(),

  // Applicant info
  fullName: text("full_name").notNull(),
  entityName: text("entity_name"),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  address: text("address").notNull(),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zip: text("zip").notNull(),
  licenseNumber: text("license_number"),
  brokerageName: text("brokerage_name"),
  websiteUrl: text("website_url"),

  // Consent record
  esignConsent: boolean("esign_consent").notNull(),
  agreementAccepted: boolean("agreement_accepted").notNull(),
  agreementReadComplete: boolean("agreement_read_complete").notNull(),
  agreementReadAt: timestamp("agreement_read_at"),

  // Signature metadata (server-captured)
  typedName: text("typed_name").notNull(),
  signedAt: timestamp("signed_at").notNull(),
  ipAddress: text("ip_address").notNull(),
  userAgent: text("user_agent").notNull(),
  timezone: text("timezone").notNull(),
  signatureSequence: integer("signature_sequence").notNull(),

  // Agreement integrity
  agreementVersion: text("agreement_version").notNull(),
  agreementHash: text("agreement_hash").notNull(),
  agreementSnapshot: text("agreement_snapshot").notNull(),

  // PDF storage key (null if generation failed — graceful degradation)
  pdfObjectKey: text("pdf_object_key"),

  // Review fields
  reviewedAt: timestamp("reviewed_at"),
  // FK declared in the table config below with an explicit short name; the
  // auto-generated name would be 65 chars and exceed Postgres's 63-char limit.
  reviewedByUserId: integer("reviewed_by_user_id"),
  reviewNotes: text("review_notes"),

  // Linked partner record created on approval
  partnerId: integer("partner_id"),

  // Append-only audit log (jsonb array)
  auditLog: jsonb("audit_log").default([]).notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  emailTypeIdx: index("idx_partner_agreement_submissions_email_type").on(table.email, table.partnerType),
  statusIdx: index("idx_partner_agreement_submissions_status").on(table.status),
  uuidIdx: uniqueIndex("idx_partner_agreement_submissions_uuid").on(table.uuid),
  // Partial unique index: at most one pending submission per (email, partner_type).
  // Mirrors the matching block in scripts/deploy-migrate.sql.
  onePendingPerEmailTypeIdx: uniqueIndex("idx_partner_agreement_submissions_one_pending_per_email_type")
    .on(sql`lower(${table.email})`, table.partnerType)
    .where(sql`${table.status} = 'pending'`),
  reviewedByFk: foreignKey({
    name: "partner_agreement_submissions_reviewed_by_fk",
    columns: [table.reviewedByUserId],
    foreignColumns: [appUsers.id],
  }).onDelete("set null"),
}));

export type PartnerAgreementSubmission = typeof partnerAgreementSubmissions.$inferSelect;

export const insertPartnerAgreementSubmissionSchema = createInsertSchema(partnerAgreementSubmissions).omit({
  id: true,
  status: true,
  reviewedAt: true,
  reviewedByUserId: true,
  reviewNotes: true,
  partnerId: true,
  auditLog: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  partnerType: z.enum(PARTNER_AGREEMENT_TYPES),
  fullName: z.string().min(1).max(200),
  entityName: z.string().max(200).optional().nullable(),
  email: z.string().email().max(320),
  phone: z.string().min(7).max(30),
  address: z.string().min(1).max(500),
  city: z.string().min(1).max(100),
  state: z.string().min(1).max(100),
  zip: z.string().min(1).max(20),
  licenseNumber: z.string().max(100).optional().nullable(),
  brokerageName: z.string().max(200).optional().nullable(),
  websiteUrl: z.string().url().max(500).optional().nullable().or(z.literal("")),
  esignConsent: z.literal(true),
  agreementAccepted: z.literal(true),
  agreementReadComplete: z.literal(true),
  agreementReadAt: z.coerce.date().nullable().optional(),
  typedName: z.string().min(1).max(200),
  signedAt: z.coerce.date(),
  ipAddress: z.string().max(100),
  userAgent: z.string().max(500),
  timezone: z.string().max(100),
  signatureSequence: z.number().int().nonnegative(),
  agreementVersion: z.string().max(50),
  agreementHash: z.string().length(64),
  agreementSnapshot: z.string().min(1),
  pdfObjectKey: z.string().max(500).optional().nullable(),
  uuid: z.string().uuid(),
});

export type InsertPartnerAgreementSubmission = z.infer<typeof insertPartnerAgreementSubmissionSchema>;

// Monotonic signature sequence counter — single row, id=1
export const signatureSequenceCounters = pgTable("signature_sequence_counters", {
  id: integer("id").primaryKey(),
  counter: integer("counter").default(0).notNull(),
});

// ─── Admin Pulse: per-admin state (Task #2901) ───────────────────────────────
// Persistent snooze/dismiss state for the Admin Pulse briefing. Keyed by the
// admin's `adminIdentity` string (email) rather than an appUsers FK because
// the admin auth system is independent from host accounts.
export const adminPulseState = pgTable("admin_pulse_state", {
  adminIdentity: text("admin_identity").primaryKey(),
  lastViewedAt: timestamp("last_viewed_at"),
  // Map of stable item-key → ISO timestamp when it was dismissed.
  // Items re-appear after 30 days so recurring issues aren't buried forever.
  dismissedItems: jsonb("dismissed_items").$type<Record<string, string>>().default({}).notNull(),
  // Map of stable item-key → ISO timestamp when the snooze expires.
  snoozedItems: jsonb("snoozed_items").$type<Record<string, string>>().default({}).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type AdminPulseState = typeof adminPulseState.$inferSelect;

// ─── Trash Service (Task #2519) ───────────────────────────────────────────────
// White-label residential collection service. The provider's brand, URL, and
// name are never stored in these table names, columns, or comments.

export const TRASH_ENROLLMENT_STATUSES = ["pending", "active", "paused", "cancelled"] as const;
export type TrashEnrollmentStatus = typeof TRASH_ENROLLMENT_STATUSES[number];

export const TRASH_COLLECTION_DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
] as const;
export type TrashCollectionDay = typeof TRASH_COLLECTION_DAYS[number];

export const trashEnrollments = pgTable("trash_enrollments", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  externalId: text("external_id").unique(),
  providerPropertyId: text("provider_property_id"),
  status: text("status").notNull().default("pending"),
  collectionDay: text("collection_day").notNull(),
  canCount: integer("can_count").notNull().default(1),
  recycleEnabled: boolean("recycle_enabled").notNull().default(false),
  recycleDay: text("recycle_day"),
  nextPickup: timestamp("next_pickup"),
  lastPickup: timestamp("last_pickup"),
  lastEvidencePhotoUrl: text("last_evidence_photo_url"),
  lastSyncedAt: timestamp("last_synced_at"),
  // Billing — recurring per-property service fee.
  monthlyServiceFeeCents: integer("monthly_service_fee_cents"),
  billingStartDate: date("billing_start_date"),
  // Vendor Settings card used to bill this property's trash service.
  // FK declared in the table config below with an explicit short name; the
  // auto-generated name would be 65 chars and exceed Postgres's 63-char limit.
  vendorCreditCardId: integer("vendor_credit_card_id"),
  // When the host explicitly accepted the recurring charge (no money moves at acceptance).
  billingAcceptedAt: timestamp("billing_accepted_at"),
  // pending = accepted, not yet started; active = charging; past_due; paused; canceled.
  billingStatus: text("billing_status").notNull().default("pending"),
  // First-of-month date the monthly cron should next attempt a full charge.
  nextBillingDate: date("next_billing_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  orgIdx: index("idx_trash_enrollments_org").on(table.orgId),
  propertyIdx: index("idx_trash_enrollments_property").on(table.propertyId),
  vendorCardFk: foreignKey({
    name: "trash_enrollments_vendor_card_fk",
    columns: [table.vendorCreditCardId],
    foreignColumns: [vendorCreditCards.id],
  }).onDelete("set null"),
}));

export type TrashEnrollment = typeof trashEnrollments.$inferSelect;
export type InsertTrashEnrollment = typeof trashEnrollments.$inferInsert;
export const insertTrashEnrollmentSchema = createInsertSchema(trashEnrollments).omit({ id: true, createdAt: true, updatedAt: true });

export const trashJobs = pgTable("trash_jobs", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  enrollmentId: integer("enrollment_id").references(() => trashEnrollments.id, { onDelete: "cascade" }),
  externalJobId: text("external_job_id").unique(),
  type: text("type").notNull().default("standard"),
  status: text("status").notNull().default("scheduled"),
  scheduledDate: date("scheduled_date"),
  completedAt: timestamp("completed_at"),
  evidencePhotoUrl: text("evidence_photo_url"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  enrollmentIdx: index("idx_trash_jobs_enrollment").on(table.enrollmentId),
  orgIdx: index("idx_trash_jobs_org").on(table.orgId),
}));

export type TrashJob = typeof trashJobs.$inferSelect;
export type InsertTrashJob = typeof trashJobs.$inferInsert;

export const trashRequests = pgTable("trash_requests", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  taskId: integer("task_id").references(() => tasks.id, { onDelete: "set null" }),
  requestedBy: text("requested_by").notNull(),
  requestedDate: text("requested_date"),
  urgency: text("urgency").default("standard"),
  details: text("details"),
  // Richer on-demand pickup details (Task #3061)
  trashLocation: text("trash_location").notNull(),
  itemDescription: text("item_description").notNull(),
  photoAttachmentUrl: text("photo_attachment_url"),
  resendMessageId: text("resend_message_id"),
  emailStatus: text("email_status").default("pending"),
  // On-demand billing — flat fee charged to the card on file at scheduling.
  vendorCreditCardId: integer("vendor_credit_card_id").references(() => vendorCreditCards.id, { onDelete: "set null" }),
  chargeAmountCents: integer("charge_amount_cents"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  paymentStatus: text("payment_status"),
  billingAcceptedAt: timestamp("billing_accepted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  orgIdx: index("idx_trash_requests_org").on(table.orgId),
  taskIdUniq: uniqueIndex("trash_requests_task_id_uniq").on(table.taskId),
}));

export type TrashRequest = typeof trashRequests.$inferSelect;
export type InsertTrashRequest = typeof trashRequests.$inferInsert;

export const trashWebhookEvents = pgTable("trash_webhook_events", {
  id: serial("id").primaryKey(),
  eventHash: text("event_hash").notNull().unique(),
  eventType: text("event_type").notNull(),
  externalEnrollmentId: text("external_enrollment_id"),
  payload: jsonb("payload").$type<Record<string, unknown>>(),
  processedAt: timestamp("processed_at"),
  outcome: text("outcome").default("received"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  eventHashIdx: uniqueIndex("trash_webhook_events_hash_uniq").on(table.eventHash),
}));

export type TrashWebhookEvent = typeof trashWebhookEvents.$inferSelect;
export type InsertTrashWebhookEvent = typeof trashWebhookEvents.$inferInsert;

// Every billable trash event — recurring initial proration, recurring monthly,
// and one-time on-demand. One row per Stripe PaymentIntent attempt. The unique
// idempotencyKey is the dedupe guarantee so a retried webhook / cron / request
// never double-charges the host's card.
export const trashCharges = pgTable("trash_charges", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  enrollmentId: integer("enrollment_id").references(() => trashEnrollments.id, { onDelete: "set null" }),
  requestId: integer("request_id").references(() => trashRequests.id, { onDelete: "set null" }),
  propertyId: integer("property_id").references(() => properties.id, { onDelete: "set null" }),
  vendorCreditCardId: integer("vendor_credit_card_id").references(() => vendorCreditCards.id, { onDelete: "set null" }),
  // 'initial_proration' | 'monthly' | 'on_demand'
  type: text("type").notNull(),
  // First-of-month the charge covers (recurring only); null for on-demand.
  periodMonth: date("period_month"),
  amountCents: integer("amount_cents").notNull(),
  // 'pending' | 'succeeded' | 'failed'
  status: text("status").notNull().default("pending"),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  failureMessage: text("failure_message"),
  idempotencyKey: text("idempotency_key").notNull().unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  orgIdx: index("idx_trash_charges_org").on(table.orgId),
  enrollmentIdx: index("idx_trash_charges_enrollment").on(table.enrollmentId),
}));

export type TrashCharge = typeof trashCharges.$inferSelect;
export type InsertTrashCharge = typeof trashCharges.$inferInsert;

// ─── Demo account tables ──────────────────────────────────────────────────────

/**
 * demo_snapshots — versioned snapshots of the demo org state, created by
 * scripts/freeze-demo.js and loaded by scripts/reset-demo.js or the
 * POST /api/admin/demo/reset route. Each row holds the complete anonymized
 * dataset for the demo org. The highest `version` (unix ms timestamp) is
 * the "latest" snapshot.
 */
export const demoSnapshots = pgTable("demo_snapshots", {
  id: serial("id").primaryKey(),
  version: bigint("version", { mode: "number" }).notNull(),
  snapshotData: jsonb("snapshot_data").notNull(),
  sourceOrgId: integer("source_org_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  // Mirrors the CREATE INDEX in scripts/deploy-migrate.sql exactly so dev
  // (db:push) and prod stay in sync and the deploy-build parity check passes.
  versionIdx: index("idx_demo_snapshots_version").on(table.version.desc()),
}));

export type DemoSnapshot = typeof demoSnapshots.$inferSelect;
export type InsertDemoSnapshot = typeof demoSnapshots.$inferInsert;

/**
 * demo_settings — single-row table holding admin-editable demo configuration.
 * Currently stores the demo login PIN so admins can rotate it from the UI
 * without a redeploy. When `pin` is NULL the server falls back to the
 * DEMO_PIN env var (default "1234"). Not org-scoped data, so it is NOT part
 * of TABLES_TO_FREEZE and survives demo resets/freezes.
 */
export const demoSettings = pgTable("demo_settings", {
  id: serial("id").primaryKey(),
  pin: text("pin"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type DemoSettings = typeof demoSettings.$inferSelect;

export const updateDemoPinSchema = z.object({
  pin: z.string().regex(/^\d{4}$/, "PIN must be exactly 4 digits."),
});
export type UpdateDemoPin = z.infer<typeof updateDemoPinSchema>;

// ─── Portfolio Analytics tables ───────────────────────────────────────────────
// Phase 1: data model and foundation only. All Analytics UI / calculations are
// built in Phases 2 and 3 and are gated behind the 'portfolio_analytics' beta
// feature key. These tables are created here so Phase 2 has a stable target.

/**
 * analytics_settings — per-account analytics configuration.
 * Stores tier dollar thresholds, the dynamic-weekly-rate toggle for the
 * Yellow boundary, the minimum-days-without-payment downgrade trigger, the
 * collection-rate alert threshold, and the inactive-rooms-exclusion toggle
 * (kept in sync with the host's global inactive-rooms preference). Each host
 * gets at most one row (unique on user_id).
 */
export const analyticsSettings = pgTable("analytics_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Four-tier dollar thresholds (weekly net, per property).
  // Tiers: Green (>= greenThresholdMin) / Yellow (>= yellowThresholdMin) /
  //        Orange (>= orangeThresholdMin) / Red (< orangeThresholdMin).
  // Defaults match the spec's recommended starting tier boundaries.
  greenThresholdMin: numeric("green_threshold_min").notNull().default("0"),
  yellowThresholdMin: numeric("yellow_threshold_min").notNull().default("-299"),
  orangeThresholdMin: numeric("orange_threshold_min").notNull().default("-500"),
  // When true, Yellow's lower boundary tracks the host's dynamic weekly rate
  // instead of the fixed yellowThresholdMin dollar value.
  dynamicWeeklyRate: boolean("dynamic_weekly_rate").notNull().default(false),
  // Minimum consecutive days without payment before a member is downgraded to
  // Red in the collection-rate analysis. Default: 14 days.
  minDaysWithoutPayment: integer("min_days_without_payment").notNull().default(14),
  // Collection-rate percentage below which an alert fires (0–100). Default: 80%.
  collectionRateAlertThreshold: numeric("collection_rate_alert_threshold").notNull().default("80"),
  // Whether to exclude inactive rooms from portfolio-level analytics counts.
  // Kept in sync with the host's global inactive-rooms preference.
  excludeInactiveRooms: boolean("exclude_inactive_rooms").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdUniq: uniqueIndex("analytics_settings_user_id_unique").on(table.userId),
}));

export type AnalyticsSettings = typeof analyticsSettings.$inferSelect;
export type InsertAnalyticsSettings = typeof analyticsSettings.$inferInsert;
export const insertAnalyticsSettingsSchema = createInsertSchema(analyticsSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Which metric ranks the Team Stats leaderboard (team_stats_settings.leaderboard_metric).
export const TEAM_STATS_LEADERBOARD_METRICS = [
  "first_response",
  "resolve",
  "volume",
] as const;
export type TeamStatsLeaderboardMetric = typeof TEAM_STATS_LEADERBOARD_METRICS[number];

/**
 * team_stats_settings — per-account Team Stats (team performance dashboard)
 * configuration. Account-level (Tier A): keyed by the account OWNER's
 * app_users.id and shared by every team member — routes must resolve the
 * owner via resolveSettingsScope() before reading/writing. Stores the SLA
 * targets the leaderboard flags against, whether automated sends (messaging
 * rules / AI) count toward the account-wide answered-within-SLA total, and
 * which metric ranks the leaderboard. Each account gets at most one row
 * (unique on user_id).
 */
export const teamStatsSettings = pgTable("team_stats_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // SLA target for time-to-first-response on inbound member messages, in minutes.
  firstResponseTargetMins: integer("first_response_target_mins").notNull().default(30),
  // SLA target for time-to-resolve on messages/inbox items, in minutes. Default 8h.
  resolveTargetMins: integer("resolve_target_mins").notNull().default(480),
  // When true, automated sends (messaging rules, AI, low-battery notices) count
  // toward the ACCOUNT-WIDE answered-within-SLA total. Per-member performance
  // metrics always count human sends only, regardless of this toggle.
  countAutomatedForAccountSla: boolean("count_automated_for_account_sla").notNull().default(true),
  // Which metric ranks the leaderboard (TEAM_STATS_LEADERBOARD_METRICS).
  leaderboardMetric: text("leaderboard_metric").notNull().default("first_response"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdUniq: uniqueIndex("team_stats_settings_user_id_unique").on(table.userId),
}));

export type TeamStatsSettings = typeof teamStatsSettings.$inferSelect;
export type InsertTeamStatsSettings = typeof teamStatsSettings.$inferInsert;
export const insertTeamStatsSettingsSchema = createInsertSchema(teamStatsSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  leaderboardMetric: z.enum(TEAM_STATS_LEADERBOARD_METRICS).default("first_response"),
});

/**
 * analytics_ai_cache — persisted AI-generated analyses keyed by
 * (userId, scope, windowKey, analysisType). scopeId is stored for reference
 * only and is NOT part of the unique key (see the cacheKeyUniq comment below);
 * per-entity isolation is achieved by callers folding the entity id into
 * `scope` (e.g. `property:<id>`) or `windowKey`.
 * Phase 3 writes variance explanations and health summaries here so page
 * loads don't re-invoke Gemini on every visit. A row is invalidated (by
 * setting invalidatedAt) whenever a new income import lands for the same
 * host; Phase 3 regenerates on the next view and clears invalidatedAt.
 */
export const analyticsAiCache = pgTable("analytics_ai_cache", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  // Scope of the analysis: 'portfolio' | 'property' | 'room'
  scope: text("scope").notNull(),
  // ID of the scoped entity (property_id or room_id). NULL for portfolio scope.
  scopeId: integer("scope_id"),
  // Time window this analysis covers, e.g. '2025-Q1', '2025-01', '2025'.
  windowKey: text("window_key").notNull(),
  // Type of analysis: 'variance_explanation' | 'health_summary' | 'pulse_signal'
  analysisType: text("analysis_type").notNull(),
  // The generated analysis text content.
  content: text("content").notNull(),
  // SHA-256 hash of the prompt used to generate this content.
  // Used to detect prompt changes that should invalidate the cache.
  promptHash: text("prompt_hash"),
  generatedAt: timestamp("generated_at").defaultNow().notNull(),
  // Timestamp set when new import data supersedes this cache entry.
  // Phase 3 regenerates any row where invalidatedAt IS NOT NULL on next view.
  invalidatedAt: timestamp("invalidated_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userWindowIdx: index("idx_analytics_ai_cache_user_window").on(table.userId, table.windowKey),
  userScopeIdx: index("idx_analytics_ai_cache_user_scope").on(table.userId, table.scope, table.scopeId),
  // Unique cache key: one row per (user, scope, window, analysisType).
  // NOTE: scopeId is deliberately NOT part of this key — it is stored for
  // reference only. Per-entity (per-property / per-room) isolation is achieved
  // by callers folding the entity id into `scope` (e.g. `property:<id>`) or into
  // `windowKey`, so distinct entities already map to distinct keys without
  // needing scopeId in the index. See server/routes/analytics.ts.
  cacheKeyUniq: uniqueIndex("analytics_ai_cache_cache_key_uniq").on(
    table.userId,
    table.scope,
    table.windowKey,
    table.analysisType,
  ),
}));

export type AnalyticsAiCache = typeof analyticsAiCache.$inferSelect;
export type InsertAnalyticsAiCache = typeof analyticsAiCache.$inferInsert;
// PM-facing Owner CRM: owners are the PM's external clients (distinct from the
// PM's own `app_users` account). Each owner is scoped to a PM's user_id.

export const OWNER_PORTAL_STATUSES = ["not_invited", "invited", "active", "inactive"] as const;
export type OwnerPortalStatus = typeof OWNER_PORTAL_STATUSES[number];

export const OWNER_CONTACT_METHODS = ["email", "phone", "text", "all"] as const;
export type OwnerContactMethod = typeof OWNER_CONTACT_METHODS[number];

export const owners = pgTable("owners", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  entityName: text("entity_name"),
  email: text("email"),
  phone: text("phone"),
  mailingAddress: text("mailing_address"),
  preferredContactMethod: text("preferred_contact_method").default("email"),
  notes: text("notes"),
  preApprovedThresholdCents: integer("pre_approved_threshold_cents").default(0).notNull(),
  portalStatus: text("portal_status").default("not_invited").notNull(),
  invitedAt: timestamp("invited_at"),
  lastLoginAt: timestamp("last_login_at"),
  // Phase 3 owner portal auth fields
  portalPasswordHash: text("portal_password_hash"),
  portalSetupToken: text("portal_setup_token"),
  portalSetupTokenExpiresAt: timestamp("portal_setup_token_expires_at"),
  portalLoginCode: text("portal_login_code"),
  portalLoginCodeExpiresAt: timestamp("portal_login_code_expires_at"),
  portalLoginCodeAttempts: integer("portal_login_code_attempts").default(0),
  // Notification preferences for owner portal alerts
  portalNotifInvoiceEmail: boolean("portal_notif_invoice_email").default(true).notNull(),
  portalNotifInvoiceSms: boolean("portal_notif_invoice_sms").default(false).notNull(),
  portalNotifMessageEmail: boolean("portal_notif_message_email").default(true).notNull(),
  portalNotifMessageSms: boolean("portal_notif_message_sms").default(false).notNull(),
  portalNotifReportEmail: boolean("portal_notif_report_email").default(true).notNull(),
  portalNotifReportSms: boolean("portal_notif_report_sms").default(false).notNull(),
  portalNotifVendorEmail: boolean("portal_notif_vendor_email").default(true).notNull(),
  portalNotifVendorSms: boolean("portal_notif_vendor_sms").default(false).notNull(),
  // When true, the owner can see their own draft invoices in the owner portal.
  // Default false so unfinished drafts stay PM-only until intentionally exposed.
  portalShowDraftInvoices: boolean("portal_show_draft_invoices").default(false).notNull(),
  // When true, the owner-portal dashboard shows booking-request status counts
  // for the owner's properties. Default false: applicant-flow data is a new
  // data class for owners, so each owner is opted in deliberately by the PM.
  portalShowBookingStats: boolean("portal_show_booking_stats").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("idx_owners_user").on(table.userId),
  emailIdx: index("idx_owners_email").on(table.email),
  setupTokenIdx: index("idx_owners_setup_token").on(table.portalSetupToken),
  // Portal-enabled owners must have a unique (case-insensitive) email so the
  // owner-login lookup is unambiguous across PM tenants. Mirrors the partial
  // unique index in scripts/deploy-migrate.sql (enforced by schema parity).
  portalEmailUniq: uniqueIndex("idx_owners_portal_email_unique")
    .on(sql`lower(trim(${table.email}))`)
    .where(sql`${table.portalStatus} != 'not_invited' AND ${table.email} IS NOT NULL`),
}));

export const insertOwnerSchema = createInsertSchema(owners).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  name: z.string().trim().min(1, "Owner name is required").max(120, "Name is too long"),
  entityName: z.string().trim().max(120, "Entity name is too long").optional().nullable(),
  email: z.string().email("Invalid email").optional().nullable().or(z.literal("")),
  phone: z.string().max(30).optional().nullable(),
  mailingAddress: z.string().max(300).optional().nullable(),
  preferredContactMethod: z.enum(OWNER_CONTACT_METHODS).optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
  preApprovedThresholdCents: z.coerce.number().int().min(0).default(0),
  portalStatus: z.enum(OWNER_PORTAL_STATUSES).default("not_invited"),
  invitedAt: optionalCoercedDate,
  lastLoginAt: optionalCoercedDate,
});
export type InsertOwner = z.infer<typeof insertOwnerSchema>;
export type Owner = typeof owners.$inferSelect;

// ─── Owner ↔ Property-Group co-ownership (joint ventures) ───────────────────
// `propertyGroups.ownerId` remains the PRIMARY owner — the one who is billed
// by the invoice engine and whose pre-approved threshold gates approvals.
// Additional co-owners (e.g. a JV partner) link through this table: they get
// their own portal login, share the group's PM conversation and vendor
// approvals (either co-owner may decide), and can view the group's invoices,
// but are never billed separately. Assigning an already-owned group to another
// owner adds a link here instead of erroring.
export const ownerGroupLinks = pgTable("owner_group_links", {
  id: serial("id").primaryKey(),
  groupId: integer("group_id").notNull().references(() => propertyGroups.id, { onDelete: "cascade" }),
  ownerId: integer("owner_id").notNull().references(() => owners.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  groupOwnerUniq: uniqueIndex("idx_ogl_group_owner_unique").on(table.groupId, table.ownerId),
  ownerIdx: index("idx_ogl_owner").on(table.ownerId),
  groupIdx: index("idx_ogl_group").on(table.groupId),
}));

export type OwnerGroupLink = typeof ownerGroupLinks.$inferSelect;

// ─── Owner Portal: In-app Notifications ──────────────────────────────────────
// Owners log into their own portal (client/src/owner-main.tsx) and need their
// own in-app alert feed — distinct from the host (PM) `notifications` table and
// the `partner_notifications` table. Each row is scoped to a single owner; the
// `link` resolves to a route in the owner portal router (not App.tsx). Mirrors
// the `partner_notifications` shape.
export const ownerNotifications = pgTable("owner_notifications", {
  id: serial("id").primaryKey(),
  ownerId: integer("owner_id").notNull().references(() => owners.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  link: text("link"),
  read: boolean("read").default(false).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  ownerIdx: index("idx_owner_notifications_owner").on(table.ownerId),
}));

export type OwnerNotification = typeof ownerNotifications.$inferSelect;

// PM-level management-fee defaults and vendor upcharge configuration.
// One row per PM account (unique on user_id). Phase 2 invoice engine reads this.
export const PM_FEE_TYPES = ["flat", "percent"] as const;
export type PmFeeType = typeof PM_FEE_TYPES[number];

export const PM_FEE_CATEGORIES = ["maintenance", "cleaning", "landscaping", "plumbing", "hvac", "electrical", "other"] as const;
export type PmFeeCategory = typeof PM_FEE_CATEGORIES[number];

export type PmFeeCategoryUpcharges = Partial<Record<PmFeeCategory, number>>;

export const MERCURY_DISPATCH_MODES = ["mercury_email", "link_only"] as const;
export type MercuryDispatchMode = typeof MERCURY_DISPATCH_MODES[number];

// PM invoice identity (Owner Portal fix pass). Rendered as the header/footer of
// every generated owner invoice (PM preview, jsPDF download, owner portal view,
// and the Resend invoice email). Null = not configured → renderers fall back to
// the generic PadPulse header. logoUrl is either an uploaded-image path from
// POST /api/app/upload ("/api/app/images/<id>") or an https URL; owners load it
// through the auth-bridging proxy GET /api/owner-portal/pm-logo.
export type OwnerInvoiceConfig = {
  companyName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  email: string;
  phone?: string;
  logoUrl?: string;
  footerNotes?: string;
  paymentInstructions?: string;
};

export const pmFeeSettings = pgTable("pm_fee_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().unique().references(() => appUsers.id, { onDelete: "cascade" }),
  mgmtFeeType: text("mgmt_fee_type").notNull().default("percent"),
  mgmtFeeAmount: numeric("mgmt_fee_amount").notNull().default("0"),
  vendorUpchargePercent: numeric("vendor_upcharge_percent").notNull().default("0"),
  categoryUpcharges: jsonb("category_upcharges").$type<PmFeeCategoryUpcharges>().default({}),
  // Account-wide kill switch for the daily overdue-invoice reminder emails.
  // Default ON; PMs can disable globally from the Billing & Fees page.
  overdueRemindersEnabled: boolean("overdue_reminders_enabled").notNull().default(true),
  // Host-set scheduling URL (Google Calendar / Calendly / any link). Surfaced to
  // every owner as the "Book a call with your PM" button on their portal Messages page.
  ownerCallCalendarLink: text("owner_call_calendar_link"),
  // Invoice header/footer identity for generated owner invoices (see OwnerInvoiceConfig).
  ownerInvoiceConfig: jsonb("owner_invoice_config").$type<OwnerInvoiceConfig | null>(),
  // Mercury AR controls. `mercuryInvoicingEnabled` is the PM's kill switch —
  // even with the AR token configured they may prefer plain email invoices.
  // `mercuryDispatchMode` decides who delivers the invoice: "mercury_email"
  // lets Mercury email the owner directly; "link_only" creates the hosted
  // invoice silently so the owner pays via the portal's Pay Invoice button.
  mercuryInvoicingEnabled: boolean("mercury_invoicing_enabled").notNull().default(true),
  mercuryDispatchMode: text("mercury_dispatch_mode").notNull().default("mercury_email").$type<MercuryDispatchMode>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertPmFeeSettingsSchema = createInsertSchema(pmFeeSettings).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  mgmtFeeType: z.enum(PM_FEE_TYPES).default("percent"),
  mgmtFeeAmount: z.coerce.number().min(0).default(0),
  vendorUpchargePercent: z.coerce.number().min(0).default(0),
  categoryUpcharges: z.record(z.enum(PM_FEE_CATEGORIES), z.number().min(0)).optional().default({}),
  overdueRemindersEnabled: z.boolean().optional(),
  // Empty string clears the link; otherwise require an http(s) URL so it can't
  // smuggle a javascript:/data: scheme into the owner-portal button href.
  ownerCallCalendarLink: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z
      .string()
      .trim()
      .url("Enter a valid link (https://…)")
      .max(500)
      .refine((u) => /^https?:\/\//i.test(u), "Link must start with http:// or https://")
      .nullable()
      .optional(),
  ),
  // Null clears the config. logoUrl restricted to uploaded-image paths or https
  // so a javascript:/data: scheme can't reach an <img src> or the jsPDF fetch.
  ownerInvoiceConfig: z
    .object({
      companyName: z.string().trim().min(1, "Company name is required").max(120),
      addressLine1: z.string().trim().min(1, "Address is required").max(200),
      addressLine2: z.string().trim().max(200).optional(),
      city: z.string().trim().min(1, "City is required").max(100),
      state: z.string().trim().min(1, "State is required").max(50),
      postalCode: z.string().trim().min(1, "Postal code is required").max(20),
      email: z.string().trim().email("Enter a valid email").max(200),
      phone: z.string().trim().max(30).optional(),
      logoUrl: z
        .string()
        .trim()
        .max(500)
        .refine(
          (u) => u.startsWith("/api/app/images/") || /^https:\/\//i.test(u),
          "Logo must be an uploaded image or an https URL",
        )
        .optional(),
      footerNotes: z.string().trim().max(2000).optional(),
      paymentInstructions: z.string().trim().max(2000).optional(),
    })
    .optional()
    .nullable(),
  mercuryInvoicingEnabled: z.boolean().optional(),
  mercuryDispatchMode: z.enum(MERCURY_DISPATCH_MODES).optional(),
});
export type InsertPmFeeSettings = z.infer<typeof insertPmFeeSettingsSchema>;
export type PmFeeSettings = typeof pmFeeSettings.$inferSelect;

// Per-owner billing overrides. When overrideMgmtFee is true the per-owner
// mgmtFeeType/mgmtFeeAmount takes precedence over the PM's account default.
// Same logic for overrideVendorUpcharge / vendorUpchargePercent.
export const ownerBillingOverrides = pgTable("owner_billing_overrides", {
  id: serial("id").primaryKey(),
  ownerId: integer("owner_id").notNull().unique().references(() => owners.id, { onDelete: "cascade" }),
  userId: integer("user_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  overrideMgmtFee: boolean("override_mgmt_fee").notNull().default(false),
  mgmtFeeType: text("mgmt_fee_type"),
  mgmtFeeAmount: numeric("mgmt_fee_amount"),
  overrideVendorUpcharge: boolean("override_vendor_upcharge").notNull().default(false),
  vendorUpchargePercent: numeric("vendor_upcharge_percent"),
  categoryUpcharges: jsonb("category_upcharges").$type<PmFeeCategoryUpcharges>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  ownerIdx: index("idx_owner_billing_overrides_owner").on(table.ownerId),
  userIdx: index("idx_owner_billing_overrides_user").on(table.userId),
}));

export const insertOwnerBillingOverrideSchema = createInsertSchema(ownerBillingOverrides).omit({ id: true, createdAt: true, updatedAt: true }).extend({
  overrideMgmtFee: z.boolean().default(false),
  mgmtFeeType: z.enum(PM_FEE_TYPES).optional().nullable(),
  mgmtFeeAmount: z.coerce.number().min(0).optional().nullable(),
  overrideVendorUpcharge: z.boolean().default(false),
  vendorUpchargePercent: z.coerce.number().min(0).optional().nullable(),
  categoryUpcharges: z.record(z.enum(PM_FEE_CATEGORIES), z.number().min(0)).optional().nullable(),
});
export type InsertOwnerBillingOverride = z.infer<typeof insertOwnerBillingOverrideSchema>;
export type OwnerBillingOverride = typeof ownerBillingOverrides.$inferSelect;

// ─── Owner Portal Phase 2 (Task #3737) ───────────────────────────────────────
// Monthly invoices generated by the PM invoice engine for each property owner.
// One draft is auto-created on the 1st of every month; PMs review and send via
// Mercury AR (or fall back to Resend PDF email).

export const OWNER_INVOICE_STATUSES = ["draft", "sent", "viewed", "paid", "overdue"] as const;
export type OwnerInvoiceStatus = typeof OWNER_INVOICE_STATUSES[number];

export type OwnerInvoiceLineItem = {
  type: "management_fee" | "vendor_upcharge" | "adjustment" | "other";
  description: string;
  amountCents: number;
  category?: string;
  taskId?: number;
  // Per-property attribution (Owner Portal fix pass). Absent on invoices
  // generated before the per-property engine — renderers must fall back to a
  // flat, ungrouped list when no line carries a propertyId (see
  // shared/owner-invoice-render.ts).
  propertyId?: number;
  propertyName?: string;
  meta?: Record<string, unknown>;
};

export type OwnerInvoicePortfolioSummary = {
  propertyCount: number;
  propertyNames: string[];
  totalRoomsCount: number;
  grossRentCents: number;
  expensesCents: number;
};

export const ownerInvoices = pgTable("owner_invoices", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  ownerId: integer("owner_id").notNull(),
  billingMonth: text("billing_month").notNull(),
  invoiceNumber: text("invoice_number").notNull(),
  status: text("status").notNull().default("draft"),
  totalDueCents: integer("total_due_cents").notNull().default(0),
  lineItems: jsonb("line_items").$type<OwnerInvoiceLineItem[]>().notNull().default([]),
  portfolioSummary: jsonb("portfolio_summary").$type<OwnerInvoicePortfolioSummary>(),
  notes: text("notes"),
  dueDate: text("due_date"),
  mercuryCustomerId: text("mercury_customer_id"),
  mercuryInvoiceId: text("mercury_invoice_id"),
  mercuryInvoiceUrl: text("mercury_invoice_url"),
  sentAt: timestamp("sent_at"),
  viewedAt: timestamp("viewed_at"),
  paidAt: timestamp("paid_at"),
  // When true, the daily overdue-reminder cron skips this specific invoice even
  // if the account-wide setting is on. Lets a PM mute one noisy owner.
  remindersDisabled: boolean("reminders_disabled").notNull().default(false),
  // Throttle marker for the overdue reminder cron (≤ once per 3 days per invoice).
  lastReminderSentAt: timestamp("last_reminder_sent_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("idx_oi_user").on(table.userId),
  ownerIdx: index("idx_oi_owner").on(table.ownerId),
  monthIdx: index("idx_oi_month").on(table.billingMonth),
  userOwnerMonthUniq: uniqueIndex("idx_oi_user_owner_month").on(table.userId, table.ownerId, table.billingMonth),
  invNumUniq: uniqueIndex("idx_oi_inv_num").on(table.invoiceNumber),
  userFk: foreignKey({ columns: [table.userId], foreignColumns: [appUsers.id], name: "fk_oi_user_id" }).onDelete("cascade"),
  ownerFk: foreignKey({ columns: [table.ownerId], foreignColumns: [owners.id], name: "fk_oi_owner_id" }).onDelete("cascade"),
}));

export const insertOwnerInvoiceSchema = createInsertSchema(ownerInvoices).omit({ id: true, createdAt: true, updatedAt: true, lastReminderSentAt: true }).extend({
  billingMonth: z.string().regex(/^\d{4}-\d{2}$/, "Billing month must be YYYY-MM"),
  status: z.enum(OWNER_INVOICE_STATUSES).default("draft"),
  totalDueCents: z.coerce.number().int().min(0).default(0),
  notes: z.string().max(2000).optional().nullable(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  remindersDisabled: z.boolean().optional(),
});
export type InsertOwnerInvoice = z.infer<typeof insertOwnerInvoiceSchema>;
export type OwnerInvoice = typeof ownerInvoices.$inferSelect;

// ─── Owner Portal Phase 4 (Task #3739) ───────────────────────────────────────
// Two-way messaging between owner and their PM. One thread per owner (no
// multi-topic threads needed — the portal is an owner-level inbox).
export const OWNER_MESSAGE_ROLES = ["owner", "pm"] as const;
export type OwnerMessageRole = typeof OWNER_MESSAGE_ROLES[number];

export const ownerMessages = pgTable("owner_messages", {
  id: serial("id").primaryKey(),
  ownerId: integer("owner_id").notNull(),
  userId: integer("user_id").notNull(),
  senderRole: text("sender_role").notNull().$type<OwnerMessageRole>(),
  body: text("body").notNull(),
  ownerReadAt: timestamp("owner_read_at"),
  pmReadAt: timestamp("pm_read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  ownerFk: foreignKey({ columns: [table.ownerId], foreignColumns: [owners.id], name: "fk_omsg_owner_id" }).onDelete("cascade"),
  userFk: foreignKey({ columns: [table.userId], foreignColumns: [appUsers.id], name: "fk_omsg_user_id" }).onDelete("cascade"),
  ownerIdx: index("idx_omsg_owner").on(table.ownerId),
  userOwnerIdx: index("idx_omsg_user_owner").on(table.userId, table.ownerId),
}));

export const insertOwnerMessageSchema = createInsertSchema(ownerMessages).omit({ id: true, createdAt: true, ownerReadAt: true, pmReadAt: true }).extend({
  senderRole: z.enum(OWNER_MESSAGE_ROLES),
  body: z.string().trim().min(1, "Message cannot be empty").max(4000, "Message is too long"),
});
export type InsertOwnerMessage = z.infer<typeof insertOwnerMessageSchema>;
export type OwnerMessage = typeof ownerMessages.$inferSelect;

// Vendor pre-approval workflow. When a task's cost exceeds the owner's
// preApprovedThresholdCents the PM can request explicit owner approval before
// processing payment. One record per task (unique on task_id).
export const OWNER_VENDOR_APPROVAL_STATUSES = ["pending", "approved", "rejected"] as const;
export type OwnerVendorApprovalStatus = typeof OWNER_VENDOR_APPROVAL_STATUSES[number];

export const ownerVendorApprovals = pgTable("owner_vendor_approvals", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull(),
  ownerId: integer("owner_id").notNull(),
  userId: integer("user_id").notNull(),
  status: text("status").notNull().default("pending").$type<OwnerVendorApprovalStatus>(),
  taskTitle: text("task_title").notNull(),
  taskCostCents: integer("task_cost_cents").notNull(),
  propertyAddress: text("property_address"),
  vendorName: text("vendor_name"),
  ownerNote: text("owner_note"),
  requestedAt: timestamp("requested_at").defaultNow().notNull(),
  decidedAt: timestamp("decided_at"),
  // Joint ventures: any co-owner of the task's property group may decide, so
  // the actual decider is stamped here (ownerId stays the group's primary
  // owner the approval was raised for). Name is a display snapshot.
  respondedByOwnerId: integer("responded_by_owner_id"),
  respondedByOwnerName: text("responded_by_owner_name"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  taskFk: foreignKey({ columns: [table.taskId], foreignColumns: [tasks.id], name: "fk_ova_task_id" }).onDelete("cascade"),
  ownerFk: foreignKey({ columns: [table.ownerId], foreignColumns: [owners.id], name: "fk_ova_owner_id" }).onDelete("cascade"),
  userFk: foreignKey({ columns: [table.userId], foreignColumns: [appUsers.id], name: "fk_ova_user_id" }).onDelete("cascade"),
  taskIdx: index("idx_ova_task").on(table.taskId),
  ownerIdx: index("idx_ova_owner").on(table.ownerId),
  taskUniq: uniqueIndex("idx_ova_task_unique").on(table.taskId),
}));

export const insertOwnerVendorApprovalSchema = createInsertSchema(ownerVendorApprovals).omit({ id: true, createdAt: true, updatedAt: true, decidedAt: true, ownerNote: true }).extend({
  status: z.enum(OWNER_VENDOR_APPROVAL_STATUSES).default("pending"),
  taskTitle: z.string().trim().min(1).max(500),
  taskCostCents: z.coerce.number().int().min(0),
  propertyAddress: z.string().max(300).optional().nullable(),
  vendorName: z.string().max(200).optional().nullable(),
});
export type InsertOwnerVendorApproval = z.infer<typeof insertOwnerVendorApprovalSchema>;
export type OwnerVendorApproval = typeof ownerVendorApprovals.$inferSelect;

// ─── Google Trends Cache ──────────────────────────────────────────────────────
// Persists successful Google Trends fetches so the city research page can serve
// the last good data when Google rate-limits the server (HTTP 429 / CAPTCHA).
// Keyed by (geo, keyword, timeframeMonths) — one row per unique combination.
// `resolvedGeo` records the geo that actually produced data (may differ from the
// request geo when a DMA→state fallback was used). `fetchedAt` surfaces the
// staleness indicator in the UI.
export const googleTrendsCache = pgTable("google_trends_cache", {
  id: serial("id").primaryKey(),
  geo: text("geo").notNull(),
  keyword: text("keyword").notNull(),
  timeframeMonths: integer("timeframe_months").notNull().default(12),
  data: jsonb("data").$type<Array<{ date: string; value: number }>>().notNull().default([]),
  resolvedGeo: text("resolved_geo"),
  fetchedAt: timestamp("fetched_at").defaultNow().notNull(),
  // Retention anchor (Task #4244). Bumped to now() every time the read route
  // serves this row to a host (fresh-cache hit, live-fetch upsert, or stale
  // fallback) — NEVER by the background refresh cron. A geo nobody visits
  // anymore (e.g. a city removed from the canonical list) therefore lets its
  // `lastServedAt` go stale, and the refresh cron prunes any row not served
  // within the retention window so the table stays bounded and we stop spending
  // Google fetch budget refreshing geos nobody looks at.
  lastServedAt: timestamp("last_served_at").defaultNow().notNull(),
}, (table) => ({
  geoKeywordUniq: uniqueIndex("idx_google_trends_cache_geo_keyword").on(table.geo, table.keyword, table.timeframeMonths),
  fetchedAtIdx: index("idx_google_trends_cache_fetched_at").on(table.fetchedAt),
  lastServedAtIdx: index("idx_google_trends_cache_last_served_at").on(table.lastServedAt),
}));

export const insertGoogleTrendsCacheSchema = createInsertSchema(googleTrendsCache).omit({ id: true });
export type InsertGoogleTrendsCache = z.infer<typeof insertGoogleTrendsCacheSchema>;
export type GoogleTrendsCache = typeof googleTrendsCache.$inferSelect;

// ─── Member Chat Presence ─────────────────────────────────────────────────────
// Ephemeral co-presence table: one row per (host org owner, acting teammate,
// member conversation). Written on heartbeat (every ~5 s) and deleted on
// leave. Stale rows (heartbeatAt older than ~15 s) are ignored at read time.
// State is either "viewing" (open but not typing) or "typing" (keypress in
// the last ~3 s). Org-scoped by hostUserId so teammates of different orgs
// never see each other's presence even on the same memberId.
export const memberChatPresence = pgTable("member_chat_presence", {
  id: serial("id").primaryKey(),
  hostUserId: integer("host_user_id").notNull(),
  actingUserId: integer("acting_user_id").notNull(),
  memberId: bigint("member_id", { mode: "number" }).notNull(),
  state: varchar("state", { length: 20 }).notNull().default("viewing"),
  heartbeatAt: timestamp("heartbeat_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniq: uniqueIndex("idx_member_chat_presence_unique").on(table.hostUserId, table.actingUserId, table.memberId),
  hostMemberIdx: index("idx_member_chat_presence_host_member").on(table.hostUserId, table.memberId),
  heartbeatIdx: index("idx_member_chat_presence_heartbeat").on(table.heartbeatAt),
}));

export const insertMemberChatPresenceSchema = createInsertSchema(memberChatPresence).omit({ id: true, heartbeatAt: true });
export type InsertMemberChatPresence = z.infer<typeof insertMemberChatPresenceSchema>;
export type MemberChatPresence = typeof memberChatPresence.$inferSelect;

// ─── Member Chat Drafts ───────────────────────────────────────────────────────
// Persists the in-progress reply text per (host org owner, acting teammate,
// member) so navigating away or reloading never wipes a draft. Scoped to the
// acting user so two teammates always have independent drafts for the same
// member conversation. Last-write-wins on the unique (hostUserId, actingUserId,
// memberId) key — no history or versioning needed.
export const memberChatDrafts = pgTable("member_chat_drafts", {
  id: serial("id").primaryKey(),
  hostUserId: integer("host_user_id").notNull(),
  actingUserId: integer("acting_user_id").notNull(),
  memberId: bigint("member_id", { mode: "number" }).notNull(),
  body: text("body").notNull().default(""),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniq: uniqueIndex("idx_member_chat_drafts_unique").on(table.hostUserId, table.actingUserId, table.memberId),
  hostActingIdx: index("idx_member_chat_drafts_host_acting").on(table.hostUserId, table.actingUserId),
}));

export const insertMemberChatDraftSchema = createInsertSchema(memberChatDrafts).omit({ id: true, updatedAt: true });
export type InsertMemberChatDraft = z.infer<typeof insertMemberChatDraftSchema>;
export type MemberChatDraft = typeof memberChatDrafts.$inferSelect;

// ─── Knowledge Chunks (pgvector RAG foundation) ───────────────────────────────
// Stores chunked + embedded text from property KBs and the company (account)
// KB. Each chunk carries its org-owner, source type, optional property scope,
// the raw text, a SHA-256 content hash (for skip-unchanged dedup), the 768-dim
// embedding (text-embedding-004), and the 0-based position within its source.
//
// Retrieval uses Postgres cosine similarity (embedding <=> query_vector) via the
// pgvector extension. No ANN (HNSW/IVFFlat) index is created — Replit's Publish
// diff regenerates a pgvector index without its operator class, which prod rejects
// and which blocks the whole publish; sequential-scan cosine search is correct and
// fine at launch volumes. See .agents/memory/pgvector-hnsw-parity.md.
export const knowledgeChunks = pgTable("knowledge_chunks", {
  id: bigserial("id", { mode: "number" }).primaryKey(),
  orgId: integer("org_id").notNull(),
  sourceType: varchar("source_type", { length: 30 }).notNull(),
  propertyId: integer("property_id"),
  chunkIndex: integer("chunk_index").notNull().default(0),
  chunkText: text("chunk_text").notNull(),
  contentHash: varchar("content_hash", { length: 64 }).notNull(),
  embedding: vector768("embedding").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  orgSourceIdx: index("idx_knowledge_chunks_org_source").on(table.orgId, table.sourceType),
  orgPropertyIdx: index("idx_knowledge_chunks_org_property").on(table.orgId, table.propertyId),
  contentHashIdx: index("idx_knowledge_chunks_content_hash").on(table.contentHash),
}));

export const insertKnowledgeChunkSchema = createInsertSchema(knowledgeChunks).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertKnowledgeChunk = z.infer<typeof insertKnowledgeChunkSchema>;
export type KnowledgeChunk = typeof knowledgeChunks.$inferSelect;

// ─── AI Memory Entries (Composer Phase 5 — human-approved learning loop) ──────
// Discrete per-learning entries generated by the send-triggered distiller.
// Suggested entries are surfaced on the KB pages for human Approve / Edit /
// Dismiss. Approved entries are embedded into knowledge_chunks
// (source_type = 'approved_memory') so they ground future Suggest/Improve
// answers. Dismissed entries are never embedded.
//
// Reconciles with the existing pulse_kb_suggestions flow: the two tables are
// surfaced together in the KB approval UI so there is one approval surface.
// pulse_kb_suggestions remains the channel for task-derived suggestions;
// this table is the channel for send-triggered learnings.
export const aiMemoryEntries = pgTable("ai_memory_entries", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => appUsers.id, { onDelete: "cascade" }),
  // 'account' → entry is company-scoped; 'property' → scoped to propertyId
  scope: text("scope").notNull(),
  propertyId: integer("property_id"),
  title: text("title").notNull(),
  content: text("content").notNull(),
  // 'suggested' | 'approved' | 'dismissed'
  status: text("status").notNull().default("suggested"),
  rationale: text("rationale"),
  // true when the vector similarity to existing approved memory suggests a
  // potential contradiction that the human should resolve.
  isContradiction: boolean("is_contradiction").notNull().default(false),
  // Provenance tracing: which outbound message triggered this learning.
  sourcePendingId: integer("source_pending_id"),
  sourceMemberId: integer("source_member_id"),
  // Team member who sent the message that triggered this learning.
  createdByUserId: integer("created_by_user_id"),
  // SHA-256 dedup key — (userId, signature) is unique.
  signature: text("signature").notNull(),
  // Semantic-dedup embedding of the entry content, stored at suggestion time so
  // near-duplicate suggested entries (semantically equivalent but reworded) can
  // be caught before two of them are both approved. Nullable: legacy rows and
  // entries created when embedding fails have none and fall back to text overlap.
  embedding: vector768("embedding"),
  decidedAt: timestamp("decided_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userScopeStatusIdx: index("idx_ai_memory_entries_user_scope_status").on(
    table.userId, table.scope, table.status,
  ),
  userSignatureUniq: uniqueIndex("ai_memory_entries_user_sig_uniq").on(
    table.userId, table.signature,
  ),
}));

export type AiMemoryEntry = typeof aiMemoryEntries.$inferSelect;
export type InsertAiMemoryEntry = typeof aiMemoryEntries.$inferInsert;
export const insertAiMemoryEntrySchema = createInsertSchema(aiMemoryEntries).omit({
  id: true, createdAt: true, updatedAt: true, decidedAt: true,
});

// ─── AI Suggestion Telemetry (Task #4565) ────────────────────────────────────
// One row per AI Suggest/Improve call from the production member composer
// (POST /api/app/members/:id/{suggest,improve}). Captures the retrieval +
// generation inputs (query string, retrieved chunks with similarity scores,
// image inclusion, the produced draft). When the host later SENDS a reply, the
// most-recent uncorrelated row for the same (org, member, actor) is back-filled
// with the sent text, an `edited` flag, and a Levenshtein `edit_distance` —
// giving an edit-delta signal for how much hosts change the AI draft.
//
// Writes are fire-and-forget and failure-tolerant: a telemetry failure must
// never block or fail Suggest / Improve / Send. Super-admin Playground calls
// (which use cross-org/explicit scope and are not tied to a real conversation)
// deliberately do NOT write here.
export interface AiSuggestionTelemetryChunk {
  // Stable `knowledge_chunks.id` of the retrieved chunk.
  chunkId: number;
  sourceType: string;
  propertyId: number | null;
  score: number;
}

// AI Draft Context Overhaul — compact context-selection stats per suggest
// call (counts only, never message text), so edit-delta analysis can tell
// whether unresolved-first selection / vision / lookback improved drafts.
// Null for improve calls and for rows written before the overhaul shipped.
export interface AiSuggestionTelemetryContext {
  unresolvedCount: number;
  unresolvedTruncated: boolean;
  fallbackUsed: boolean;
  continuityCount: number;
  imagesAnalyzed: number;
  imagesCached: number;
  priorsConsidered: number;
  priorsKept: number;
}

export const aiSuggestionTelemetry = pgTable("ai_suggestion_telemetry", {
  id: serial("id").primaryKey(),
  // Org owner (host who owns the member) = member.userId.
  orgUserId: integer("org_user_id").notNull(),
  memberId: integer("member_id").notNull(),
  propertyId: integer("property_id"),
  // Acting session user (impersonation-aware) who triggered the call/send.
  actorUserId: integer("actor_user_id").notNull(),
  // 'suggest' | 'improve'
  callType: varchar("call_type", { length: 16 }).notNull(),
  queryText: text("query_text").notNull(),
  // Retrieved grounding chunks (chunkId + sourceType + propertyId + similarity
  // score). Defaults to an empty array so a no-retrieval call is still a valid row.
  retrievedChunks: jsonb("retrieved_chunks").$type<AiSuggestionTelemetryChunk[]>().notNull().default([]),
  chunksUsed: integer("chunks_used").notNull().default(0),
  hadImages: boolean("had_images").notNull().default(false),
  imageCount: integer("image_count").notNull().default(0),
  // The AI-produced text (the suggested draft, or the improved rewrite).
  draftText: text("draft_text").notNull(),
  // Context-selection stats (AI Draft Context Overhaul) — counts only.
  contextBreakdown: jsonb("context_breakdown").$type<AiSuggestionTelemetryContext>(),
  // Correlation back-fill (set at send time; null until correlated).
  sentText: text("sent_text"),
  edited: boolean("edited"),
  editDistance: integer("edit_distance"),
  correlatedAt: timestamp("correlated_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  // Correlation lookup: most-recent uncorrelated row for (org, member, actor).
  correlationIdx: index("idx_ai_suggestion_telemetry_correlation").on(
    table.orgUserId, table.memberId, table.actorUserId, table.createdAt,
  ),
}));

export const insertAiSuggestionTelemetrySchema = createInsertSchema(aiSuggestionTelemetry).omit({
  id: true, createdAt: true,
});
export type InsertAiSuggestionTelemetry = z.infer<typeof insertAiSuggestionTelemetrySchema>;
export type AiSuggestionTelemetry = typeof aiSuggestionTelemetry.$inferSelect;

// ─── Modular pricing — per-account module purchases ──────────────────────────
// One row per active à-la-carte module on an Essentials account. Module keys
// reference shared/modular-pricing.ts (OPERATOR_MODULES + DEVICE_MODULES).
// Pro accounts have no rows here — Pro includes every module by definition
// (the entitlement layer derives that from planTier, not from rows).
// Canceled modules keep their row with status='canceled' for history.
export const ACCOUNT_MODULE_STATUSES = ["active", "canceled"] as const;
export type AccountModuleStatus = (typeof ACCOUNT_MODULE_STATUSES)[number];

export const accountModules = pgTable("account_modules", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  moduleKey: text("module_key").notNull(),
  status: text("status").notNull().default("active").$type<AccountModuleStatus>(),
  activatedAt: timestamp("activated_at", { withTimezone: true }).defaultNow().notNull(),
  canceledAt: timestamp("canceled_at", { withTimezone: true }),
}, (table) => ({
  userModuleUniq: uniqueIndex("idx_account_modules_user_module").on(table.userId, table.moduleKey),
  userIdx: index("idx_account_modules_user").on(table.userId),
  userFk: foreignKey({ columns: [table.userId], foreignColumns: [appUsers.id], name: "fk_account_modules_user_id" }).onDelete("cascade"),
}));

export const insertAccountModuleSchema = createInsertSchema(accountModules).omit({
  id: true, activatedAt: true, canceledAt: true,
}).extend({
  status: z.enum(ACCOUNT_MODULE_STATUSES).default("active"),
});
export type InsertAccountModule = z.infer<typeof insertAccountModuleSchema>;
export type AccountModule = typeof accountModules.$inferSelect;

// ─── Modular pricing — owner-paid product subscriptions ──────────────────────
// Owner-level subscriptions to Research / Revenue / Analytics on a PM's book.
// An owner with ANY active row here has their $20/mo Owner Portal fee waived
// (per owner, for as long as the subscription stays active) — see
// OWNER_PRODUCT_KEYS in shared/modular-pricing.ts. This build models the
// billing EFFECT (the waiver + reconciliation); the owner-facing checkout UX
// is out of scope, so rows are managed via PM/admin endpoints for now.
// `stripeSubscriptionId` is reserved for the future owner-checkout build.
export const OWNER_SUBSCRIPTION_STATUSES = ["active", "canceled"] as const;
export type OwnerSubscriptionStatus = (typeof OWNER_SUBSCRIPTION_STATUSES)[number];

export const ownerSubscriptions = pgTable("owner_subscriptions", {
  id: serial("id").primaryKey(),
  ownerId: integer("owner_id").notNull(),
  productKey: text("product_key").notNull(),
  status: text("status").notNull().default("active").$type<OwnerSubscriptionStatus>(),
  stripeSubscriptionId: text("stripe_subscription_id"),
  startedAt: timestamp("started_at", { withTimezone: true }).defaultNow().notNull(),
  canceledAt: timestamp("canceled_at", { withTimezone: true }),
}, (table) => ({
  ownerProductUniq: uniqueIndex("idx_owner_subscriptions_owner_product").on(table.ownerId, table.productKey),
  ownerIdx: index("idx_owner_subscriptions_owner").on(table.ownerId),
  ownerFk: foreignKey({ columns: [table.ownerId], foreignColumns: [owners.id], name: "fk_owner_subscriptions_owner_id" }).onDelete("cascade"),
}));

export const insertOwnerSubscriptionSchema = createInsertSchema(ownerSubscriptions).omit({
  id: true, startedAt: true, canceledAt: true,
}).extend({
  status: z.enum(OWNER_SUBSCRIPTION_STATUSES).default("active"),
});
export type InsertOwnerSubscription = z.infer<typeof insertOwnerSubscriptionSchema>;
export type OwnerSubscription = typeof ownerSubscriptions.$inferSelect;

// ─── The PadPulse Promise — offboarding exit-survey + refund record ──────────
// One row per guarantee refund (self-serve, from billing settings). The three
// answer columns mirror PADPULSE_PROMISE.offboardingQuestions keys. Refund
// identifiers are recorded synchronously when the Stripe refund is issued so
// the charge.refunded webhook can recognize (and skip re-processing) it.
// The account itself is archived on refund — data is retained, never deleted.
export const offboardingResponses = pgTable("offboarding_responses", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  liked: text("liked").notNull(),
  disliked: text("disliked").notNull(),
  wished: text("wished").notNull(),
  refundStripeRefundId: text("refund_stripe_refund_id"),
  refundedInvoiceId: text("refunded_invoice_id"),
  refundedAmountCents: integer("refunded_amount_cents"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  userIdx: index("idx_offboarding_responses_user").on(table.userId),
  userFk: foreignKey({ columns: [table.userId], foreignColumns: [appUsers.id], name: "fk_offboarding_responses_user_id" }).onDelete("cascade"),
}));

export const insertOffboardingResponseSchema = createInsertSchema(offboardingResponses).omit({
  id: true, createdAt: true,
}).extend({
  liked: z.string().trim().min(1).max(4000),
  disliked: z.string().trim().min(1).max(4000),
  wished: z.string().trim().min(1).max(4000),
});
export type InsertOffboardingResponse = z.infer<typeof insertOffboardingResponseSchema>;
export type OffboardingResponse = typeof offboardingResponses.$inferSelect;

export type MessageDeliveryMonitorIncident = typeof messageDeliveryMonitorIncidents.$inferSelect;

export type MessageDeliveryMonitorRoute = typeof messageDeliveryMonitorRoutes.$inferSelect;

export const messageDeliveryMonitorIncidents = pgTable("message_delivery_monitor_incidents", {
  id: serial("id").primaryKey(),
  accountId: integer("account_id").notNull().references(() => appUsers.id, { onDelete: "cascade" }),
  condition: varchar("condition", { length: 64 }).notNull(),
  firstObservedAt: timestamp("first_observed_at").notNull(),
  lastObservedAt: timestamp("last_observed_at").notNull(),
  lastHealthyAt: timestamp("last_healthy_at"),
  healthyChecks: integer("healthy_checks").notNull().default(0),
  affectedCount: integer("affected_count").notNull().default(0),
  oldestEligibleAt: timestamp("oldest_eligible_at"),
  latestError: text("latest_error"),
  messageRefs: jsonb("message_refs").notNull().default([]),
  progressEvidence: jsonb("progress_evidence").notNull().default({}),
  alertAttempts: integer("alert_attempts").notNull().default(0),
  alertNextAt: timestamp("alert_next_at"),
  alertClaimToken: varchar("alert_claim_token", { length: 64 }),
  alertClaimedAt: timestamp("alert_claimed_at"),
  alertDeliveredAt: timestamp("alert_delivered_at"),
  alertDeliveryError: text("alert_delivery_error"),
  resolvedAt: timestamp("resolved_at"),
  recoveryAlertAttempts: integer("recovery_alert_attempts").notNull().default(0),
  recoveryNextAt: timestamp("recovery_next_at"),
  recoveryClaimToken: varchar("recovery_claim_token", { length: 64 }),
  recoveryClaimedAt: timestamp("recovery_claimed_at"),
  recoveryAlertDeliveredAt: timestamp("recovery_alert_delivered_at"),
  recoveryDeliveryError: text("recovery_delivery_error"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  unresolvedAccountCondition: uniqueIndex("message_delivery_monitor_open_uidx")
    .on(table.accountId, table.condition)
    .where(sql`${table.resolvedAt} IS NULL`),
  openIncidentIdx: index("message_delivery_monitor_open_idx")
    .on(table.resolvedAt, table.alertNextAt),
  recoveryDeliveryIdx: index("message_delivery_monitor_recovery_idx")
    .on(table.recoveryAlertDeliveredAt, table.recoveryNextAt),
}));

export const slackConnections = pgTable("slack_connections", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  teamId: text("team_id").notNull(),
  teamName: text("team_name"),
  appId: text("app_id"),
  botUserId: text("bot_user_id"),
  botTokenEncrypted: text("bot_token_encrypted"),
  scopes: text("scopes"),
  installedByUserId: integer("installed_by_user_id"),
  channelId: text("channel_id"),
  channelName: text("channel_name"),
  channelIsPrivate: boolean("channel_is_private").notNull().default(false),
  alertFailedMessages: boolean("alert_failed_messages").notNull().default(true),
  alertStuckMessages: boolean("alert_stuck_messages").notNull().default(true),
  alertBroadcastProblems: boolean("alert_broadcast_problems").notNull().default(true),
  status: text("status").$type<SlackConnectionStatus>().notNull().default("active"),
  lastError: text("last_error"),
  lastErrorAt: timestamp("last_error_at"),
  lastPostedAt: timestamp("last_posted_at"),
  connectedAt: timestamp("connected_at").defaultNow().notNull(),
  disconnectedAt: timestamp("disconnected_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userFk: foreignKey({
    name: "slack_connections_user_id_fk",
    columns: [table.userId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
  installedByFk: foreignKey({
    name: "slack_connections_installed_by_user_id_fk",
    columns: [table.installedByUserId],
    foreignColumns: [appUsers.id],
  }).onDelete("set null"),
  userUnique: uniqueIndex("slack_connections_user_unique").on(table.userId),
  teamIdx: index("idx_slack_connections_team").on(table.teamId),
}));

export const SLACK_ALERT_STATUSES = ["pending", "posted", "failed", "skipped"] as const;

export const slackAlertOutbox = pgTable("slack_alert_outbox", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  alertKey: text("alert_key").notNull(),
  parentKey: text("parent_key"),
  kind: text("kind").$type<SlackAlertKind>().notNull(),
  category: text("category").$type<SlackAlertCategory>().notNull(),
  source: text("source").notNull(),
  payload: jsonb("payload").notNull(),
  status: text("status").$type<SlackAlertStatus>().notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at").defaultNow().notNull(),
  claimToken: text("claim_token"),
  claimedAt: timestamp("claimed_at"),
  channelId: text("channel_id"),
  messageTs: text("message_ts"),
  threadTs: text("thread_ts"),
  rolledUp: boolean("rolled_up").notNull().default(false),
  lastError: text("last_error"),
  postedAt: timestamp("posted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  userFk: foreignKey({
    name: "slack_alert_outbox_user_id_fk",
    columns: [table.userId],
    foreignColumns: [appUsers.id],
  }).onDelete("cascade"),
  userKeyUnique: uniqueIndex("slack_alert_outbox_user_key_unique").on(table.userId, table.alertKey),
  dueIdx: index("idx_slack_alert_outbox_due").on(table.nextAttemptAt).where(sql`status = 'pending'`),
}));

export const SLACK_ALERT_CATEGORIES = [
  "failed_messages",
  "stuck_messages",
  "broadcast_problems",
] as const;

export const SLACK_ALERT_KINDS = ["alert", "recovery", "rollup"] as const;

export type SlackConnectionStatus = typeof SLACK_CONNECTION_STATUSES[number];

export type SlackAlertCategory = typeof SLACK_ALERT_CATEGORIES[number];

export type SlackConnection = typeof slackConnections.$inferSelect;

export type SlackAlertStatus = typeof SLACK_ALERT_STATUSES[number];

export type SlackAlertOutboxRow = typeof slackAlertOutbox.$inferSelect;

export type SlackAlertKind = typeof SLACK_ALERT_KINDS[number];

export const roomHarvestCoverage = pgTable("room_harvest_coverage", {
  id: serial("id").primaryKey(),
  cycleDate: date("cycle_date").notNull(),
  roomId: integer("room_id").notNull().references(() => rooms.id, { onDelete: "cascade" }),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  hostUserId: integer("host_user_id").references(() => appUsers.id, { onDelete: "set null" }),
  padsplitRoomId: text("padsplit_room_id").notNull(),
  position: integer("position").notNull(),
  eligible: boolean("eligible").default(true).notNull(),
  state: text("state").default("pending").notNull(),
  attempts: integer("attempts").default(0).notNull(),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }),
  skipReason: text("skip_reason"),
}, (t) => ({
  cycleFk: foreignKey({ name: "room_harvest_cycle_fk", columns: [t.cycleDate],
    foreignColumns: [roomHarvestCycles.cycleDate] }).onDelete("cascade"),
  roomCycle: uniqueIndex("room_harvest_coverage_room_cycle_idx").on(t.cycleDate, t.roomId),
}));

export const roomHarvestAttempts = pgTable("room_harvest_attempts", {
  id: varchar("id").primaryKey(),
  coverageId: integer("coverage_id").notNull().references(() => roomHarvestCoverage.id, { onDelete: "cascade" }),
  requestedAt: timestamp("requested_at", { withTimezone: true }).defaultNow().notNull(),
  respondedAt: timestamp("responded_at", { withTimezone: true }),
  outcome: text("outcome").default("requested").notNull(),
  statusCode: integer("status_code"),
  error: text("error"),
  baselineHarvestedAt: timestamp("baseline_harvested_at"),
  observationDeadline: timestamp("observation_deadline", { withTimezone: true }).notNull(),
  observedHarvestedAt: timestamp("observed_harvested_at"),
  observation: text("observation").default("pending").notNull(),
}, (t) => ({
  coverageTime: index("room_harvest_attempts_coverage_time_idx").on(t.coverageId, t.requestedAt),
}));

export type MessageBroadcast = typeof messageBroadcasts.$inferSelect;

// Records which deploy-migrate.sql revisions (by SHA256) were applied by the
// deferred post-start migration (scripts/deploy-migrate-deferred.sql). When a
// publish adds schema objects, deploy-build.sh defers deploy-migrate.sql until
// after Replit's publish migration; every new instance checks this table so the
// script runs once per release instead of on every cold start.
export const deployMigrateRuns = pgTable("deploy_migrate_runs", {
  sha: text("sha").primaryKey(),
  appliedAt: timestamp("applied_at").defaultNow().notNull(),
});

export type InsertMessageBroadcast = typeof messageBroadcasts.$inferInsert;

export type LifetimePurchase = typeof lifetimePurchases.$inferSelect;

export const roomPricingModelsProperties = pgTable("room_pricing_models_properties", {
  id: serial("id").primaryKey(),
  roomPricingModelId: integer("room_pricing_model_id").notNull(),
  propertyId: integer("property_id").notNull().references(() => properties.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").default(sql`CURRENT_TIMESTAMP`),
  updatedAt: timestamp("updated_at").default(sql`CURRENT_TIMESTAMP`),
}, (t) => ({
  modelFk: foreignKey({ name: "rpmp_model_fk", columns: [t.roomPricingModelId], foreignColumns: [roomPricingModels.id] }).onDelete("cascade"),
  modelPropertyUnique: uniqueIndex("rpmp_model_property_unique").on(t.roomPricingModelId, t.propertyId),
}));

export type BuyBoxVersion = typeof buyBoxVersions.$inferSelect;

export type BuyBoxAgentTag = {
  agentId: number | null;
  agentNumber: number | null;
  versionId: number | null;
  versionNumber: number | null;
  versionLabel: string | null;
  city: string;
  state: string;
  runDate: string;
  reportId: number | null;
};
